import { DvalaError, NodeTypes, ParseError, PersistentVector, TokenizerError, TypeError as TypeError$1, asUserDefinedSymbolNode, assertNumberOfParams, bindingTargetTypes, canBeOperator, isBuiltinSymbolNode, isMacroFunction, isNodeType, isNumberReservedSymbol, isPersistentMap, isPersistentVector, isSpecialSymbolNode, isSpreadNode, isUserDefinedSymbolNode, numberReservedSymbolRecord, reservedSymbolRecord, resolveSourceCodeInfo, specialExpressionTypes, valueToString } from "@mojir/dvala-types";
import { allStandardEffectDefinitions, arrayNormalExpression, assertModule, assertionNormalExpression, astModule, bitwiseNormalExpression, bitwiseUtilsModule, builtin, collectionNormalExpression, collectionUtilsModule, convertModule, createContextStack, evaluate, evaluateNodeForFold, evaluateWithEffects, evaluateWithSyncEffects, fromJS, functionalNormalExpression, functionalUtilsModule, getAllBindingTargetNames, getMetaNormalExpression, getUndefinedSymbols as getUndefinedSymbols$1, gridModule, handlerModule, hostHandler, initCoreDvalaSources, isFunctionDocs, isFunctionReference, isFunctionReference as isFunctionReference$1, jsonModule, linearAlgebraModule, macrosModule, mathNormalExpression, mathUtilsModule, matrixModule, miscNormalExpression, normalExpressionKeys, normalExpressionTypes, normalExpressions, numberTheoryModule, objectNormalExpression, predicatesNormalExpression, reconstructCallStack, regexpNormalExpression, resume, scopeToGlobalContext, sequenceNormalExpression, sequenceUtilsModule, setEffectReference, setNormalExpressionReference, specialExpressionKeys, specialExpressions, standardEffectNames, stringNormalExpression, stringUtilsModule, timeModule, toJS, vectorModule } from "@mojir/dvala-engine";
import { createPackageRuntimeBridge as createPackageRuntimeBridge$1 } from "@mojir/dvala-runtime";
//#region src/tokenizer/operators.ts
const binaryOperators = [
	"^",
	"*",
	"/",
	"%",
	"+",
	"-",
	"<<",
	">>",
	">>>",
	"++",
	"<",
	"<=",
	"≤",
	">",
	">=",
	"≥",
	"==",
	"!=",
	"!=",
	"&",
	"xor",
	"|",
	"&&",
	"||",
	"??",
	"|>"
];
const unaryPrefixOperators = ["!"];
const otherOperators = [
	"@",
	":",
	"->",
	"...",
	"?.",
	".",
	"?",
	",",
	"=",
	":",
	";"
];
const symbolicOperators = [
	...binaryOperators,
	...unaryPrefixOperators,
	...otherOperators
];
const nonFunctionOperatorSet = new Set([
	"comment",
	"block",
	"if",
	"let",
	"loop",
	"recur",
	"while",
	"handler",
	"transform",
	"resume"
]);
function isFunctionOperator(operator) {
	return !nonFunctionOperatorSet.has(operator);
}
const binaryOperatorSet = new Set(binaryOperators);
function isBinaryOperator(operator) {
	return binaryOperatorSet.has(operator);
}
const symbolicOperatorSet = new Set(symbolicOperators);
function isSymbolicOperator(operator) {
	return symbolicOperatorSet.has(operator);
}
//#endregion
//#region src/tokenizer/token.ts
function isAtomToken(token) {
	return token?.[0] === "Atom";
}
function isSymbolToken(token, symbolName) {
	if (token?.[0] !== "Symbol") return false;
	if (symbolName && token[1] !== symbolName) return false;
	return true;
}
function assertSymbolToken(token, symbolName) {
	if (!isSymbolToken(token, symbolName)) throwUnexpectedToken("Symbol", void 0, token);
}
function asSymbolToken(token, symbolName) {
	assertSymbolToken(token, symbolName);
	return token;
}
function isEffectNameToken(token) {
	return token?.[0] === "EffectName";
}
function assertEffectNameToken(token) {
	if (!isEffectNameToken(token)) throwUnexpectedToken("EffectName", void 0, token);
}
function asEffectNameToken(token) {
	assertEffectNameToken(token);
	return token;
}
function isReservedSymbolToken(token, symbolName) {
	if (token?.[0] !== "ReservedSymbol") return false;
	if (symbolName && token[1] !== symbolName) return false;
	return true;
}
function assertReservedSymbolToken(token, symbolName) {
	if (!isReservedSymbolToken(token, symbolName)) throwUnexpectedToken("ReservedSymbol", symbolName, token);
}
function asReservedSymbolToken(token, symbolName) {
	assertReservedSymbolToken(token, symbolName);
	return token;
}
function isShebangToken(token) {
	return token?.[0] === "Shebang";
}
function isSingleLineCommentToken(token) {
	return token?.[0] === "SingleLineComment";
}
function isMultiLineCommentToken(token) {
	return token?.[0] === "MultiLineComment";
}
function isOperatorToken(token, operatorName) {
	if (token?.[0] !== "Operator") return false;
	if (operatorName && token[1] !== operatorName) return false;
	return true;
}
function assertOperatorToken(token, operatorName) {
	if (!isOperatorToken(token, operatorName)) throwUnexpectedToken("Operator", operatorName, token);
}
function isWhitespaceToken(token) {
	return token?.[0] === "Whitespace";
}
function isNumberToken(token) {
	return token?.[0] === "Number";
}
function isBasePrefixedNumberToken(token) {
	return token?.[0] === "BasePrefixedNumber";
}
function isLParenToken(token) {
	return token?.[0] === "LParen";
}
function assertLParenToken(token) {
	if (!isLParenToken(token)) throwUnexpectedToken("LParen", void 0, token);
}
function isRParenToken(token) {
	return token?.[0] === "RParen";
}
function assertRParenToken(token) {
	if (!isRParenToken(token)) throwUnexpectedToken("RParen", void 0, token);
}
function isLBracketToken(token) {
	return token?.[0] === "LBracket";
}
function assertLBracketToken(token) {
	if (!isLBracketToken(token)) throwUnexpectedToken("LBracket", void 0, token);
}
function asLBracketToken(token) {
	assertLBracketToken(token);
	return token;
}
function isRBracketToken(token) {
	return token?.[0] === "RBracket";
}
function assertRBracketToken(token) {
	if (!isRBracketToken(token)) throwUnexpectedToken("RBracket", void 0, token);
}
function isLBraceToken(token) {
	return token?.[0] === "LBrace";
}
function assertLBraceToken(token) {
	if (!isLBraceToken(token)) throwUnexpectedToken("LBrace", void 0, token);
}
function asLBraceToken(token) {
	assertLBraceToken(token);
	return token;
}
function isRBraceToken(token) {
	return token?.[0] === "RBrace";
}
function assertRBraceToken(token) {
	if (!isRBraceToken(token)) throwUnexpectedToken("RBrace", void 0, token);
}
function isStringToken(token) {
	return token?.[0] === "string";
}
function isA_BinaryOperatorToken(token) {
	return token?.[0] === "Operator" && isBinaryOperator(token[1]);
}
function isTemplateStringToken(token) {
	return token?.[0] === "TemplateString";
}
function assertTemplateStringToken(token) {
	if (!isTemplateStringToken(token)) throwUnexpectedToken("TemplateString", void 0, token);
}
function asTemplateStringToken(token) {
	assertTemplateStringToken(token);
	return token;
}
/** Convert lightweight token debug info to SourceCodeInfo for error reporting. */
function debugInfoToSourceCodeInfo(debugInfo, source, filePath) {
	if (!debugInfo) return void 0;
	const [line, column] = debugInfo;
	const code = source ? source.split("\n")[line] ?? "" : "";
	return {
		position: {
			line: line + 1,
			column: column + 1
		},
		code,
		filePath
	};
}
/** Extract TokenDebugInfo from SourceCodeInfo (inverse of debugInfoToSourceCodeInfo). */
function sourceCodeInfoToDebugInfo(sci) {
	if (!sci) return void 0;
	return [sci.position.line - 1, sci.position.column - 1];
}
function throwUnexpectedToken(expected, expectedValue, actual) {
	const actualOutput = actual ? `${actual[0]} '${actual[1]}'` : "end of input";
	const sourceCodeInfo = actual?.[2] ? debugInfoToSourceCodeInfo(actual[2]) : void 0;
	throw new TokenizerError(`Unexpected token: ${actualOutput}, expected ${expected}${expectedValue ? ` '${expectedValue}'` : ""}`, sourceCodeInfo);
}
//#endregion
//#region src/tokenizer/minifyTokenStream.ts
function minifyTokenStream(tokenStream, { removeWhiteSpace }) {
	const tokens = tokenStream.tokens.filter((token) => {
		if (isSingleLineCommentToken(token) || isMultiLineCommentToken(token) || isShebangToken(token) || removeWhiteSpace && isWhitespaceToken(token)) return false;
		return true;
	});
	return {
		...tokenStream,
		tokens
	};
}
//#endregion
//#region src/cst/builder.ts
var CstBuilder = class {
	/** Stack of nodes being built. The bottom is the root. */
	stack = [];
	/** Whether finish() has been called. */
	finished = false;
	/**
	* Open a new CST node of the given kind. All subsequent tokens and
	* child nodes will be added as children of this node until `endNode()`
	* is called.
	*/
	startNode(kind) {
		this.stack.push({
			kind,
			children: []
		});
	}
	/**
	* Add a leaf token to the current node.
	*/
	token(cstToken) {
		this.current().children.push(cstToken);
	}
	/**
	* Save a checkpoint at the current position in the current node's children.
	* Used with `startNodeAt()` to retroactively wrap already-parsed children
	* in a new node — needed by the Pratt parser for binary operators, property
	* access, and function calls where the left operand is parsed before the
	* wrapper node kind is known.
	*/
	checkpoint() {
		return this.current().children.length;
	}
	/**
	* Open a new node that retroactively wraps children from a prior checkpoint.
	* Children from `checkpoint` to the end of the current node's children list
	* are moved into the new node, which becomes the current node.
	* Call `endNode()` to close it (like any other node).
	*/
	startNodeAt(checkpoint, kind) {
		const node = {
			kind,
			children: this.current().children.splice(checkpoint)
		};
		this.stack.push(node);
	}
	/**
	* Close the current node and attach it as a child of its parent.
	* If this closes the root node, the tree is complete.
	*/
	endNode() {
		if (this.stack.length < 2) return;
		const completed = this.stack.pop();
		this.current().children.push(completed);
	}
	/**
	* Return the node currently open at the top of the builder's stack.
	* Used by parseQuote to snapshot the body children emitted during the
	* token-collection pass before truncating them.
	*/
	peekCurrent() {
		return this.current();
	}
	/**
	* Truncate children of the current node back to the given checkpoint.
	* Used by parseQuote to discard the raw body tokens emitted during the
	* token-collection pass before replacing them with a CST-structured body.
	*/
	truncateCurrent(checkpoint) {
		this.current().children.length = checkpoint;
	}
	/**
	* Append a pre-built child (token or node) to the current node.
	* Used by parseQuote to attach the structured body CST produced by the
	* sub-parse pass.
	*/
	appendChild(child) {
		this.current().children.push(child);
	}
	/**
	* Return the completed untyped CST tree.
	* Must be called exactly once after all events have been emitted.
	*/
	finish() {
		if (this.finished) throw new Error("CstBuilder.finish() called more than once");
		if (this.stack.length !== 1) throw new Error(`CstBuilder.finish(): expected exactly 1 node on stack, found ${this.stack.length}. Mismatched startNode/endNode calls.`);
		this.finished = true;
		return this.stack[0];
	}
	/** The node currently being built (top of stack). */
	current() {
		if (this.stack.length === 0) throw new Error("CstBuilder: no open node — call startNode() first");
		return this.stack[this.stack.length - 1];
	}
};
//#endregion
//#region src/parser/helpers.ts
/**
* Assign a node ID from the parser context. When debug mode is active,
* the source position is recorded in the source map.
*/
const structuralLeafTypes = new Set([
	NodeTypes.Sym,
	NodeTypes.Builtin,
	NodeTypes.Special,
	NodeTypes.Reserved,
	NodeTypes.Effect,
	NodeTypes.Binding,
	NodeTypes.Spread,
	"symbol",
	"rest",
	"object",
	"array",
	"literal",
	"wildcard"
]);
function withSourceCodeInfo(node, debugInfo, ctx) {
	const isLeaf = structuralLeafTypes.has(node[0]);
	node[2] = ctx.allocateNodeId(debugInfo, isLeaf || void 0);
	return node;
}
function stringToSymbolNode(value, debugInfo, ctx) {
	if (specialExpressionTypes[value] !== void 0 && value !== "fn" && value !== "defn") {
		const node = withSourceCodeInfo([
			NodeTypes.Special,
			specialExpressionTypes[value],
			0
		], debugInfo, ctx);
		ctx.setNodeEnd(node[2]);
		return node;
	}
	if (normalExpressionTypes.has(value)) {
		const node = withSourceCodeInfo([
			NodeTypes.Builtin,
			value,
			0
		], debugInfo, ctx);
		ctx.setNodeEnd(node[2]);
		return node;
	}
	const node = withSourceCodeInfo([
		NodeTypes.Sym,
		value,
		0
	], debugInfo, ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
function stringFromQuotedSymbol(value) {
	return value.substring(1, value.length - 1).replace(/(\\{2})|(\\')|\\(.)/g, (_, backslash, singleQuote, normalChar) => {
		if (backslash) return "\\";
		if (singleQuote) return "'";
		return `\\${normalChar}`;
	});
}
/**
* Extract the symbol name from any symbol node type.
* All symbol node types now store the string name directly.
*/
function getSymbolName(symbol) {
	return symbol[1];
}
/**
* Get the start position of an already-parsed AstNode as TokenDebugInfo,
* by looking it up in the source map. Used by binary-op parsing so the
* wrapping `Call` / `And` / `Or` / `Qq` node's range starts at the LEFT
* operand's first token rather than the operator (which would skew the
* range and break LS features that rely on full-expression spans).
* Returns undefined if the node isn't in the source map (typecheck-only
* or no-source-map parse).
*/
function nodeStartDebugInfo(node, ctx) {
	const sourceMap = ctx.sourceMap;
	if (!sourceMap) return void 0;
	const nodeId = node[node.length - 1];
	const pos = sourceMap.positions.get(nodeId);
	if (!pos) return void 0;
	return [pos.start[0], pos.start[1]];
}
function createNamedNormalExpressionNode(symbolNode, params, debugInfo, ctx, hints) {
	const node = withSourceCodeInfo([
		NodeTypes.Call,
		[
			symbolNode,
			params,
			hints
		],
		0
	], debugInfo, ctx);
	if (isBuiltinSymbolNode(symbolNode)) assertNumberOfParams(normalExpressions[symbolNode[1]].arity, node[1][1].length, ctx.resolveTokenDebugInfo(debugInfo));
	ctx.setNodeEnd(node[2]);
	return node;
}
function isAtExpressionEnd(ctx) {
	if (ctx.isAtEnd()) return true;
	const token = ctx.tryPeek();
	if (isOperatorToken(token)) return [
		";",
		",",
		":"
	].includes(token[1]);
	if (isReservedSymbolToken(token)) return [
		"else",
		"when",
		"while",
		"case",
		"let",
		"then",
		"end",
		"do"
	].includes(token[1]);
	return false;
}
function fromBinaryOperatorToNode(operator, symbolNode, left, right, debugInfo, ctx) {
	const operatorName = operator[1];
	switch (operatorName) {
		case "^":
		case "*":
		case "/":
		case "%":
		case "+":
		case "-":
		case "<<":
		case ">>":
		case ">>>":
		case "++":
		case "<":
		case "<=":
		case "≤":
		case ">":
		case ">=":
		case "≥":
		case "==":
		case "!=":
		case "&":
		case "xor":
		case "|": return createNamedNormalExpressionNode(symbolNode, [left, right], debugInfo, ctx);
		case "|>": {
			const node = withSourceCodeInfo([
				NodeTypes.Call,
				[
					right,
					[left],
					{ isPipe: true }
				],
				0
			], debugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		case "&&": {
			const node = withSourceCodeInfo([
				NodeTypes.And,
				[left, right],
				0
			], debugInfo, ctx);
			ctx.clearStructuralLeaf(right[2]);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		case "||": {
			const node = withSourceCodeInfo([
				NodeTypes.Or,
				[left, right],
				0
			], debugInfo, ctx);
			ctx.clearStructuralLeaf(right[2]);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		case "??": {
			const node = withSourceCodeInfo([
				NodeTypes.Qq,
				[left, right],
				0
			], debugInfo, ctx);
			ctx.clearStructuralLeaf(right[2]);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		/* v8 ignore next 12 */
		case ".":
		case "?.":
		case "?":
		case ";":
		case ":":
		case "@":
		case "=":
		case ",":
		case "->":
		case "...":
		case "!": throw new ParseError(`Unknown binary operator: ${operatorName}`, ctx.resolveTokenDebugInfo(debugInfo));
		/* v8 ignore next 2 */
		default: throw new ParseError(`Unknown binary operator: ${operatorName}`, ctx.resolveTokenDebugInfo(debugInfo));
	}
}
//#endregion
//#region src/parser/getPrecedence.ts
function getPrecedence(operatorSign, sourceCodeInfo) {
	switch (operatorSign) {
		case "^": return 12;
		case "*":
		case "/":
		case "%": return 11;
		case "+":
		case "-": return 10;
		case "<<":
		case ">>":
		case ">>>": return 9;
		case "++": return 8;
		case "<":
		case "<=":
		case "≤":
		case ">":
		case ">=":
		case "≥": return 7;
		case "==":
		case "!=": return 6;
		case "&":
		case "xor":
		case "|": return 5;
		case "&&":
		case "||":
		case "??": return 4;
		case "|>": return 2;
		/* v8 ignore next 2 */
		default: throw new ParseError(`Unknown binary operator: ${operatorSign}`, sourceCodeInfo);
	}
}
//#endregion
//#region src/cst/attachTrivia.ts
function isTrivia(token) {
	const type = token[0];
	return type === "Whitespace" || type === "SingleLineComment" || type === "MultiLineComment" || type === "Shebang";
}
/**
* Get the raw source text for a token. Most tokens store raw text in
* token[1], but a few strip their prefix during tokenization:
*   - Atom: value "ok" → raw ":ok"
*   - EffectName: value "my.eff" → raw "@my.eff"
*   - MacroPrefix: value "name" → raw "#name"
*/
function rawTokenText(token) {
	switch (token[0]) {
		case "Atom": return `:${token[1]}`;
		case "EffectName": return `@${token[1]}`;
		case "MacroPrefix": return `#${token[1]}`;
		default: return token[1];
	}
}
function toTriviaNode(token) {
	switch (token[0]) {
		case "Whitespace": return {
			kind: "whitespace",
			text: token[1]
		};
		case "SingleLineComment": return {
			kind: "lineComment",
			text: token[1]
		};
		case "MultiLineComment": return {
			kind: "blockComment",
			text: token[1]
		};
		case "Shebang": return {
			kind: "shebang",
			text: token[1]
		};
		default: throw new Error(`Not a trivia token: ${token[0]}`);
	}
}
/**
* Split a trivia sequence at the first newline boundary.
*
* Everything up to and including the first `\n` character goes into
* `trailing`. Everything after goes into `leading`. If a single trivia
* node spans the boundary (e.g. whitespace "  \n  "), it is split into
* two nodes at the newline.
*
* If no newline exists in the trivia, everything is trailing (same-line).
*/
function splitTriviaAtNewline(trivia) {
	for (let i = 0; i < trivia.length; i++) {
		const t = trivia[i];
		const nlIndex = t.text.indexOf("\n");
		if (nlIndex === -1) continue;
		const before = t.text.substring(0, nlIndex + 1);
		const after = t.text.substring(nlIndex + 1);
		const trailing = trivia.slice(0, i);
		if (before) trailing.push({
			kind: t.kind,
			text: before
		});
		const leading = [];
		if (after) leading.push({
			kind: t.kind,
			text: after
		});
		leading.push(...trivia.slice(i + 1));
		return {
			trailing,
			leading
		};
	}
	return {
		trailing: [...trivia],
		leading: []
	};
}
//#endregion
//#region src/parser/ParserContext.ts
var ParserContext = class {
	tokens;
	position;
	parseExpression;
	sourceMap;
	/** Full source text (available in debug mode). */
	source;
	/** File path of the source (available in debug mode). */
	filePath;
	/** Allocates a unique node ID — provided by the caller to scope uniqueness per instance. */
	allocateId;
	/** Type annotations collected during parsing, keyed by nodeId. */
	typeAnnotations = /* @__PURE__ */ new Map();
	/** Effect declarations: effect @name(ArgType) -> RetType */
	effectDeclarations = /* @__PURE__ */ new Map();
	/** Type alias declarations: type Name<Params> = TypeExpr */
	typeAliases = /* @__PURE__ */ new Map();
	/**
	* Binding-scoped type parameters from `let f<T: U> = ...` (Phase 0b).
	* Keyed by the binding-target nodeId; the typechecker reads this
	* side-table when inferring a let binding and registers the bounded
	* type vars for the whole RHS scope. Empty by default — the map only
	* holds entries for let-bindings that actually declared type params.
	*/
	typeParams = /* @__PURE__ */ new Map();
	/** When present, the parser operates in CST mode on the full token stream. */
	builder;
	/** Leading trivia accumulated for the next token to be consumed. */
	pendingLeadingTrivia = [];
	/** Position of the last consumed non-trivia token (for setNodeEnd in CST mode). */
	lastNonTriviaPosition = -1;
	constructor(tokenStream, allocateId, builder) {
		this.allocateId = allocateId;
		this.tokens = tokenStream.tokens;
		this.position = 0;
		this.source = tokenStream.source;
		this.filePath = tokenStream.filePath;
		this.builder = builder;
		if (tokenStream.source !== void 0) this.sourceMap = {
			sources: [{
				path: tokenStream.filePath ?? "<anonymous>",
				content: tokenStream.source
			}],
			positions: /* @__PURE__ */ new Map()
		};
		if (builder) this.skipTrivia();
	}
	allocateNodeId(debugInfo, structuralLeaf) {
		const id = this.allocateId();
		if (this.sourceMap && debugInfo) {
			const position = {
				source: 0,
				start: [debugInfo[0], debugInfo[1]],
				end: [debugInfo[0], debugInfo[1]],
				...structuralLeaf ? { structuralLeaf: true } : {}
			};
			this.sourceMap.positions.set(id, position);
		}
		return id;
	}
	/**
	* Update the end position of a node after it has been fully parsed.
	* Uses the debug info of the last consumed token (i.e. the token
	* just before the current position).
	*/
	setNodeEnd(nodeId) {
		if (!this.sourceMap) return;
		const pos = this.sourceMap.positions.get(nodeId);
		if (!pos) return;
		const lastPos = this.builder ? this.lastNonTriviaPosition : this.position - 1;
		const lastToken = this.tokens[lastPos];
		const debugInfo = lastToken?.[2];
		if (debugInfo) {
			const tokenLen = lastToken[1].length;
			pos.end = [debugInfo[0], debugInfo[1] + tokenLen];
		}
	}
	/**
	* Mark a node's source-map position as a structural leaf. Used when a parsed
	* node is unwrapped out of the final AST (e.g. a `do…end` block consumed as a
	* lambda body — its statements are kept, the block node is discarded). Its
	* position was already recorded by `allocateNodeId`, so coverage would count
	* it as a found-but-never-evaluated expression; flagging it structuralLeaf
	* drops it from coverage counting (the evaluator never fires onNodeEval for it
	* either), keeping the found/hit sides consistent.
	*/
	markStructuralLeaf(nodeId) {
		const pos = this.sourceMap?.positions.get(nodeId);
		if (pos) pos.structuralLeaf = true;
	}
	/**
	* Clear a node's structural-leaf flag, making it a first-class coverable unit.
	* Used for the arms of conditional constructs (`if`/`match`/`&&`/`||`/`??`): a
	* bare-symbol arm like `else acc` runs only when its branch is taken, so — unlike
	* an unconditional leaf — it carries real coverage signal. Clearing the flag puts
	* it in the coverage "found" set, so it shows uncovered (red) when its branch is
	* never taken and covered (green) when it is (the evaluator records it via the
	* `forceRecord` branch-arm path). No-op for non-leaf arms.
	*/
	clearStructuralLeaf(nodeId) {
		const pos = this.sourceMap?.positions.get(nodeId);
		if (pos) delete pos.structuralLeaf;
	}
	advance() {
		if (this.builder) this.advanceCst();
		else this.position += 1;
	}
	/**
	* CST-mode advance: consume the current non-trivia token, collect trivia,
	* create a CstToken, and feed it to the builder.
	*/
	advanceCst() {
		const token = this.tokens[this.position];
		const cstToken = {
			leadingTrivia: this.pendingLeadingTrivia,
			text: rawTokenText(token),
			trailingTrivia: []
		};
		this.lastNonTriviaPosition = this.position;
		this.position += 1;
		this.skipTrivia();
		const collected = this.pendingLeadingTrivia;
		if (collected.length > 0) {
			const { trailing, leading } = splitTriviaAtNewline(collected);
			cstToken.trailingTrivia = trailing;
			this.pendingLeadingTrivia = leading;
		} else {
			cstToken.trailingTrivia = [];
			this.pendingLeadingTrivia = [];
		}
		this.builder.token(cstToken);
	}
	/**
	* Skip trivia tokens from current position, accumulating them in
	* pendingLeadingTrivia. After this call, position points at the
	* next non-trivia token (or past end).
	*/
	skipTrivia() {
		const trivia = [];
		while (this.position < this.tokens.length && isTrivia(this.tokens[this.position])) {
			trivia.push(toTriviaNode(this.tokens[this.position]));
			this.position += 1;
		}
		this.pendingLeadingTrivia = trivia;
	}
	tryPeek() {
		return this.tokens[this.position];
	}
	peek() {
		const token = this.tokens[this.position];
		/* v8 ignore next 4 */
		if (!token) {
			const lastToken = this.tokens.at(-1);
			throw new ParseError("Unexpected end of input", this.resolveTokenDebugInfo(lastToken?.[2]));
		}
		return token;
	}
	isAtEnd() {
		return this.position >= this.tokens.length;
	}
	/**
	* Get the raw token debug info [line, column] for the current (or last) token.
	* Use this when passing to withSourceCodeInfo / allocateNodeId.
	*/
	peekDebugInfo() {
		const currentToken = this.tryPeek();
		return currentToken ? currentToken[2] : this.tokens.at(-1)?.[2];
	}
	/**
	* Get SourceCodeInfo for the current (or last) token position.
	* Constructs a full SourceCodeInfo from the token's [line, column] + source text.
	* Use this for error reporting (DvalaError).
	*/
	peekSourceCodeInfo() {
		return this.resolveTokenDebugInfo(this.peekDebugInfo());
	}
	/** Convert token debug info to SourceCodeInfo using stored source text. */
	resolveTokenDebugInfo(debugInfo) {
		return debugInfoToSourceCodeInfo(debugInfo, this.source, this.filePath);
	}
	peekAhead(count) {
		if (this.builder) {
			let skipped = 0;
			let offset = 1;
			while (this.position + offset < this.tokens.length) {
				const token = this.tokens[this.position + offset];
				if (!isTrivia(token)) {
					skipped += 1;
					if (skipped === count) return token;
				}
				offset += 1;
			}
			return;
		}
		return this.tokens[this.position + count];
	}
	getPosition() {
		return this.position;
	}
	getTokenAt(pos) {
		return this.tokens[pos];
	}
	/**
	* Get any remaining trivia that hasn't been attached to a token yet.
	* Called after parsing is complete to get file-trailing trivia.
	* Only meaningful in CST mode.
	*/
	getRemainingTrivia() {
		return this.pendingLeadingTrivia;
	}
};
//#endregion
//#region src/parser/subParsers/parseDo.ts
function parseDo(ctx) {
	ctx.builder?.startNode("Block");
	const token = asReservedSymbolToken(ctx.tryPeek(), "do");
	ctx.advance();
	const expressions = [];
	while (!ctx.isAtEnd() && !isReservedSymbolToken(ctx.tryPeek(), "end")) {
		if (isReservedSymbolToken(ctx.tryPeek(), "with")) {
			const withNode = parseWithHandler(ctx);
			expressions.push(withNode);
			break;
		}
		expressions.push(ctx.parseExpression());
		if (isOperatorToken(ctx.tryPeek(), ";")) ctx.advance();
		else if (!isReservedSymbolToken(ctx.tryPeek(), "end")) throw new ParseError("Expected end", ctx.peekSourceCodeInfo());
	}
	if (expressions.length === 0) throw new ParseError("Empty block is not allowed — every block must produce a value", ctx.peekSourceCodeInfo());
	assertReservedSymbolToken(ctx.tryPeek(), "end");
	ctx.advance();
	const node = withSourceCodeInfo([
		NodeTypes.Block,
		expressions,
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
/**
* Parse `with <expr>; <body>` inside a do...end block.
*
* Consumes `with`, parses the handler expression, consumes `;`,
* then collects remaining expressions until `end` as the handler's body.
* The handler is active for the body (like `let` scoping).
*/
function parseWithHandler(ctx) {
	const token = asReservedSymbolToken(ctx.tryPeek(), "with");
	ctx.advance();
	const handlerExpr = ctx.parseExpression();
	if (!isOperatorToken(ctx.tryPeek(), ";")) throw new ParseError("Expected \";\" after with handler expression", ctx.peekSourceCodeInfo());
	ctx.advance();
	const bodyExprs = [];
	while (!ctx.isAtEnd() && !isReservedSymbolToken(ctx.tryPeek(), "end")) {
		if (isReservedSymbolToken(ctx.tryPeek(), "with")) {
			bodyExprs.push(parseWithHandler(ctx));
			break;
		}
		bodyExprs.push(ctx.parseExpression());
		if (isOperatorToken(ctx.tryPeek(), ";")) ctx.advance();
		else if (!isReservedSymbolToken(ctx.tryPeek(), "end")) throw new ParseError("Expected \";\" or \"end\"", ctx.peekSourceCodeInfo());
	}
	const node = withSourceCodeInfo([
		NodeTypes.WithHandler,
		[handlerExpr, bodyExprs],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
//#endregion
//#region src/parser/subParsers/parseTypeAnnotationTokens.ts
/**
* Collect a type annotation string from the token stream.
* Reads tokens until a delimiter is found: `=`, `,`, `)`, `->`, `;`, or `end`.
* Handles balanced parens/brackets/braces so nested types work.
*
* Used by parseBindingTarget (for `let x: T = ...` and `(a: T) -> ...`)
* and parseLambdaFunction (for `(a): T -> ...`).
*/
/**
* Is the token a right-angle-bracket-family operator (`>`, `>>`, `>>>`)?
* The tokenizer emits `>>` and `>>>` as single tokens for the shift
* operators, but inside type annotations these characters represent
* stacked closers for nested generics (`Box<Array<Number>>` → `>>`).
* The collector splits them back into individual `>` characters so
* angle-depth tracking and the stopAtGt delimiter behave correctly.
*/
function rightAngleRunLength(token) {
	if (!token) return 0;
	if (isOperatorToken(token, ">")) return 1;
	if (isOperatorToken(token, ">>")) return 2;
	if (isOperatorToken(token, ">>>")) return 3;
	return 0;
}
function collectTypeAnnotation(ctx, options) {
	const parts = [];
	let depth = 0;
	let angleDepth = 0;
	const braceStack = [];
	const stopAtArrow = options?.stopAtArrow ?? false;
	const stopAtRParen = options?.stopAtRParen ?? true;
	const stopAtGt = options?.stopAtGt ?? false;
	const inRefinementBrace = () => braceStack[braceStack.length - 1] === "refinement";
	while (!ctx.isAtEnd()) {
		const token = ctx.tryPeek();
		if (!token) break;
		const gtRun = rightAngleRunLength(token);
		if (gtRun > 0 && !inRefinementBrace()) {
			let consumed = 0;
			while (consumed < gtRun) {
				if (depth === 0 && angleDepth === 0 && stopAtGt) break;
				if (angleDepth > 0) angleDepth--;
				parts.push(">");
				consumed++;
			}
			if (consumed === gtRun) {
				ctx.advance();
				continue;
			}
			const remaining = gtRun - consumed;
			token[1] = ">".repeat(remaining);
			break;
		}
		if (depth === 0 && angleDepth === 0) {
			if (isOperatorToken(token, "=")) break;
			if (isOperatorToken(token, ",")) break;
			if (stopAtArrow && isOperatorToken(token, "->")) break;
			if (isOperatorToken(token, ";")) break;
			if (stopAtRParen && isRParenToken(token)) break;
			if (isReservedSymbolToken(token, "end")) break;
		}
		if (token[0] === "LParen" || token[0] === "LBracket") depth++;
		if (token[0] === "LBrace") {
			depth++;
			const next = ctx.peekAhead(1);
			const after = ctx.peekAhead(2);
			const isRefinement = next !== void 0 && (isSymbolToken(next) || isReservedSymbolToken(next)) && after !== void 0 && isOperatorToken(after, "|");
			braceStack.push(isRefinement ? "refinement" : "other");
		}
		if (token[0] === "RParen" || token[0] === "RBracket") depth--;
		if (token[0] === "RBrace") {
			depth--;
			braceStack.pop();
		}
		if (isOperatorToken(token, "<") && !inRefinementBrace()) angleDepth++;
		if (token[0] === "Atom") parts.push(`:${token[1]}`);
		else if (token[0] === "EffectName") parts.push(`@${token[1]}`);
		else parts.push(token[1]);
		ctx.advance();
	}
	return parts.join(" ").trim();
}
/**
* Check if the current token is `:` indicating a type annotation.
* Only valid in the symbol binding case (not inside object patterns,
* where `:` means nested destructuring — that's handled separately).
*/
function isTypeAnnotationColon(ctx) {
	return isOperatorToken(ctx.tryPeek(), ":");
}
//#endregion
//#region src/parser/subParsers/parseEffectDeclaration.ts
/**
* Parse an effect declaration: effect @name(ArgType) -> RetType
*
* Effect declarations are type-only — they tell the typechecker what
* types flow through perform() and resume() for a given effect.
* They produce a null AST node at runtime (erased).
*
* Syntax: effect @name(ArgType) -> RetType
* Example: effect @log(String) -> Null
*/
function parseEffectDeclaration(ctx) {
	const token = ctx.peek();
	ctx.advance();
	const effectToken = ctx.peek();
	if (!isEffectNameToken(effectToken)) throw new ParseError("Expected effect name after \"effect\"", ctx.peekSourceCodeInfo());
	const effectName = effectToken[1];
	ctx.advance();
	if (!isLParenToken(ctx.peek())) throw new ParseError("Expected \"(\" after effect name", ctx.peekSourceCodeInfo());
	ctx.advance();
	const argType = collectTypeAnnotation(ctx);
	if (!argType) throw new ParseError("Expected argument type in effect declaration", ctx.peekSourceCodeInfo());
	if (!isRParenToken(ctx.peek())) throw new ParseError("Expected \")\" after argument type", ctx.peekSourceCodeInfo());
	ctx.advance();
	assertOperatorToken(ctx.peek(), "->");
	ctx.advance();
	const retType = collectTypeAnnotation(ctx);
	if (!retType) throw new ParseError("Expected return type in effect declaration", ctx.peekSourceCodeInfo());
	ctx.effectDeclarations.set(effectName, {
		argType,
		retType
	});
	return withSourceCodeInfo([
		NodeTypes.Reserved,
		"null",
		0
	], token[2], ctx);
}
//#endregion
//#region src/parser/subParsers/parseSymbol.ts
function parseSymbol(ctx) {
	ctx.builder?.startNode("Symbol");
	const token = ctx.peek();
	ctx.advance();
	if (!isSymbolToken(token)) throw new ParseError(`Expected symbol token, got ${token[0]}`, ctx.resolveTokenDebugInfo(token[2]));
	if (token[1][0] === "'") {
		const node = stringToSymbolNode(stringFromQuotedSymbol(token[1]), token[2], ctx);
		ctx.setNodeEnd(node[2]);
		ctx.builder?.endNode();
		return node;
	} else {
		const node = stringToSymbolNode(token[1], token[2], ctx);
		ctx.setNodeEnd(node[2]);
		ctx.builder?.endNode();
		return node;
	}
}
//#endregion
//#region src/parser/subParsers/parseString.ts
function parseString(ctx, token) {
	ctx.builder?.startNode("StringLiteral");
	ctx.advance();
	const value = token[1].substring(1, token[1].length - 1).replace(/(\\{2})|(\\")|(\\n)|(\\t)|(\\r)|(\\b)|(\\f)|\\(.)/g, (_, backslash, doubleQuote, newline, tab, carriageReturn, backspace, formFeed, normalChar) => {
		if (backslash) return "\\";
		else if (newline) return "\n";
		else if (tab) return "	";
		else if (carriageReturn) return "\r";
		else if (backspace) return "\b";
		else if (formFeed) return "\f";
		else if (doubleQuote) return "\"";
		return normalChar;
	});
	const node = withSourceCodeInfo([
		NodeTypes.Str,
		value,
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
//#endregion
//#region src/parser/subParsers/parseNumber.ts
function parseNumber(ctx) {
	ctx.builder?.startNode("NumberLiteral");
	const token = ctx.peek();
	ctx.advance();
	const value = token[1];
	const negative = value[0] === "-";
	const numberString = (negative ? value.substring(1) : value).replace(/_/g, "");
	const node = withSourceCodeInfo([
		NodeTypes.Num,
		negative ? -Number(numberString) : Number(numberString),
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
//#endregion
//#region src/tokenizer/tokenizers.ts
const jsIdentifierFirstCharRegExp = /[a-zA-Z_$]/;
const jsIdentifierCharRegExp = /[a-zA-Z0-9_$]/;
const whitespaceRegExp = /\s/;
const NO_MATCH = [0];
const tokenizeLParen = (input, position) => tokenizeToken("LParen", "(", input, position);
const tokenizeRParen = (input, position) => tokenizeToken("RParen", ")", input, position);
const tokenizeLBracket = (input, position) => tokenizeToken("LBracket", "[", input, position);
const tokenizeRBracket = (input, position) => tokenizeToken("RBracket", "]", input, position);
const tokenizeLBrace = (input, position) => tokenizeToken("LBrace", "{", input, position);
const tokenizeRBrace = (input, position) => tokenizeToken("RBrace", "}", input, position);
const tokenizeString = (input, position) => {
	if (input[position] !== "\"") return NO_MATCH;
	let value = "\"";
	let length = 1;
	let char = input[position + length];
	let escaping = false;
	while (char && (char !== "\"" || escaping)) {
		length += 1;
		if (escaping) {
			escaping = false;
			value += char;
		} else {
			if (char === "\\") escaping = true;
			value += char;
		}
		char = input[position + length];
	}
	if (!char) return [length, [
		"Error",
		value,
		void 0,
		`Unclosed string at position ${position}`
	]];
	value += "\"";
	return [length + 1, ["string", value]];
};
const tokenizeRegexpShorthand = (input, position) => {
	if (input[position] !== "#") return NO_MATCH;
	const [stringLength, token] = tokenizeString(input, position + 1);
	if (!token) return NO_MATCH;
	if (token[0] === "Error") {
		const errorToken = [
			"Error",
			`#${token[1]}`,
			void 0,
			`Unclosed regexp at position ${position}`
		];
		return [stringLength + 1, errorToken];
	}
	position += stringLength + 1;
	let length = stringLength + 1;
	let options = "";
	while (input[position] === "g" || input[position] === "i") {
		options += input[position];
		length += 1;
		position += 1;
		if (options.includes(input[position])) return [length, [
			"Error",
			`#${token[1]}${options}`,
			void 0,
			`Duplicated regexp option "${input[position]}"`
		]];
	}
	return [length, ["RegexpShorthand", `#${token[1]}${options}`]];
};
const tokenizeMacroPrefix = (input, position) => {
	if (input[position] !== "#") return NO_MATCH;
	const nextChar = input[position + 1];
	if (!nextChar || !jsIdentifierFirstCharRegExp.test(nextChar)) return NO_MATCH;
	let i = position + 2;
	while (i < input.length && jsIdentifierCharRegExp.test(input[i])) i++;
	const name = input.slice(position + 1, i);
	return [i - position, ["MacroPrefix", name]];
};
const tokenizeAtom = (input, position) => {
	if (input[position] !== ":") return NO_MATCH;
	if (position > 0 && jsIdentifierCharRegExp.test(input[position - 1])) return NO_MATCH;
	const nextChar = input[position + 1];
	if (!nextChar || !jsIdentifierFirstCharRegExp.test(nextChar)) return NO_MATCH;
	let i = position + 2;
	while (i < input.length && jsIdentifierCharRegExp.test(input[i])) i++;
	const name = input.slice(position + 1, i);
	return [i - position, ["Atom", name]];
};
function tokenizeToken(type, value, input, position) {
	if (value === input.slice(position, position + value.length)) return [value.length, [type, value]];
	else return NO_MATCH;
}
const tokenizeWhitespace = (input, position) => {
	let char = input[position];
	if (!char || !whitespaceRegExp.test(char)) return NO_MATCH;
	let value = char;
	position += 1;
	char = input[position];
	while (char && whitespaceRegExp.test(char)) {
		value += char;
		position += 1;
		char = input[position];
	}
	return [value.length, ["Whitespace", value]];
};
const decimalNumberRegExp = /\d/;
const octalNumberRegExp = /[0-7]/;
const hexNumberRegExp = /[0-9a-f]/i;
const binaryNumberRegExp = /[01]/;
const postNumberRegExp = /[\s)\]}(,;+\-*/%^<>=!&|.?~:#]/;
const tokenizeNumber = (input, position, prevToken) => {
	let i;
	const negate = input[position] === "-";
	const plusPrefix = input[position] === "+";
	if ((negate || plusPrefix) && prevToken) {
		const prevType = prevToken[0];
		if (prevType === "Number" || prevType === "BasePrefixedNumber" || prevType === "Symbol" || prevType === "Atom" || prevType === "ReservedSymbol" || prevType === "RParen" || prevType === "RBracket" || prevType === "RBrace") return NO_MATCH;
	}
	const start = negate || plusPrefix ? position + 1 : position;
	let hasDecimalPoint = false;
	let hasExponent = false;
	for (i = start; i < input.length; i += 1) {
		const char = input[i];
		if (char === "_") {
			if (!decimalNumberRegExp.test(input[i - 1]) || !decimalNumberRegExp.test(input[i + 1])) {
				if (i === start) return NO_MATCH;
				return [i - position + 1, [
					"Error",
					input.substring(position, i + 1),
					void 0,
					`Invalid number format at position ${i + 1}`
				]];
			}
		} else if (char === ".") {
			if (i === start) return NO_MATCH;
			if (hasDecimalPoint || hasExponent) return [i - position + 1, [
				"Error",
				input.substring(position, i + 1),
				void 0,
				`Invalid number format at position ${i + 1}`
			]];
			hasDecimalPoint = true;
		} else if (char === "e" || char === "E") {
			if (i === start) return NO_MATCH;
			if (hasExponent) return [i - position + 1, [
				"Error",
				input.substring(position, i + 1),
				void 0,
				`Invalid number format at position ${i + 1}`
			]];
			if (input[i - 1] === "." || input[i - 1] === "+" || input[i - 1] === "-") return [i - position + 1, [
				"Error",
				input.substring(position, i + 1),
				void 0,
				`Invalid number format at position ${i + 1}`
			]];
			if (input[i + 1] === "+" || input[i + 1] === "-") i += 1;
			hasExponent = true;
		} else if (!decimalNumberRegExp.test(char)) break;
	}
	if ((negate || plusPrefix) && i === start) return NO_MATCH;
	const length = i - position;
	if (length === 0) return NO_MATCH;
	const nextChar = input[i];
	if (nextChar && nextChar !== ":" && !postNumberRegExp.test(nextChar)) return [i - position + 1, [
		"Error",
		input.substring(position, i + 1),
		void 0,
		`Invalid number format at position ${i + 1}`
	]];
	return [length, ["Number", input.substring(position, i)]];
};
const tokenizeBasePrefixedNumber = (input, position) => {
	if (input[position] !== "0") return NO_MATCH;
	const baseChar = input[position + 1];
	const type = baseChar === "b" || baseChar === "B" ? "binary" : baseChar === "o" || baseChar === "O" ? "octal" : baseChar === "x" || baseChar === "X" ? "hex" : null;
	if (type === null) return NO_MATCH;
	let i;
	for (i = position + 2; i < input.length; i += 1) {
		const char = input[i];
		if (type === "binary" && !binaryNumberRegExp.test(char)) break;
		if (type === "octal" && !octalNumberRegExp.test(char)) break;
		if (type === "hex" && !hexNumberRegExp.test(char)) break;
	}
	const length = i - position;
	if (length <= 2) return NO_MATCH;
	const nextChar = input[i];
	if (nextChar && !postNumberRegExp.test(nextChar)) return NO_MATCH;
	return [length, ["BasePrefixedNumber", input.substring(position, i)]];
};
/**
* Tokenize effect name literal: @segment.segment.segment
* Each segment follows symbol naming rules. Dots separate segments.
* The token value is the name without the @ prefix.
*/
const tokenizeEffectName = (input, position) => {
	if (input[position] !== "@") return NO_MATCH;
	let i = position + 1;
	const firstChar = input[i];
	if (!firstChar || !jsIdentifierFirstCharRegExp.test(firstChar) && firstChar !== "*") return NO_MATCH;
	let name = "";
	while (i < input.length && (jsIdentifierCharRegExp.test(input[i]) || input[i] === "*")) {
		name += input[i];
		i++;
	}
	while (i < input.length && input[i] === ".") {
		const dotPos = i;
		i++;
		if (i >= input.length || !jsIdentifierFirstCharRegExp.test(input[i]) && input[i] !== "*") {
			i = dotPos;
			break;
		}
		const segStart = i;
		while (i < input.length && (jsIdentifierCharRegExp.test(input[i]) || input[i] === "*")) i++;
		name += `.${input.slice(segStart, i)}`;
	}
	return [i - position, ["EffectName", name]];
};
const tokenizeSymbol = (input, position) => {
	let value = input[position];
	if (value === "'") {
		let length = 1;
		let char = input[position + length];
		let escaping = false;
		while (char !== "'" || escaping) {
			if (char === void 0) return [length, [
				"Error",
				value,
				void 0,
				`Unclosed quoted symbol at position ${position}`
			]];
			length += 1;
			if (escaping) {
				escaping = false;
				value += char;
			} else {
				if (char === "\\") escaping = true;
				value += char;
			}
			char = input[position + length];
		}
		value += "'";
		return [length + 1, ["Symbol", value]];
	}
	if (jsIdentifierFirstCharRegExp.test(value)) {
		const initialPosition = position;
		position += 1;
		let char = input[position];
		while (char && jsIdentifierCharRegExp.test(char)) {
			value += char;
			position += 1;
			char = input[position];
		}
		return [position - initialPosition, ["Symbol", value]];
	}
	return NO_MATCH;
};
const tokenizeReservedSymbolToken = (input, position) => {
	const symbolMeta = tokenizeSymbol(input, position);
	if (symbolMeta[0] === 0 || !symbolMeta[1]) return NO_MATCH;
	let rawName = symbolMeta[1][1];
	rawName = rawName.startsWith("'") ? rawName.slice(1, rawName.length - 1) : rawName;
	const symbolName = rawName;
	if (reservedSymbolRecord[symbolName] === void 0) return NO_MATCH;
	return [symbolMeta[0], ["ReservedSymbol", symbolName]];
};
const tokenizeOperator = (input, position) => {
	const threeChars = input.slice(position, position + 3);
	if (position + 2 < input.length && isSymbolicOperator(threeChars)) return [3, ["Operator", threeChars]];
	const twoChars = input.slice(position, position + 2);
	if (position + 1 < input.length && isSymbolicOperator(twoChars)) return [2, ["Operator", twoChars]];
	const oneChar = input[position] ?? "";
	if (isSymbolicOperator(oneChar)) return [1, ["Operator", oneChar]];
	return NO_MATCH;
};
const tokenizeMultiLineComment = (input, position) => {
	if (input[position] === "/" && input[position + 1] === "*") {
		let length = 2;
		let value = "/*";
		while ((input[position + length] !== "*" || input[position + length + 1] !== "/") && position + length + 1 < input.length) {
			value += input[position + length];
			length += 1;
		}
		if (position + length + 1 >= input.length) return [length, [
			"Error",
			value,
			void 0,
			`Unclosed multi-line comment at position ${position}`
		]];
		value += "*/";
		length += 2;
		return [length, ["MultiLineComment", value]];
	}
	return NO_MATCH;
};
const tokenizeShebang = (input, position) => {
	if (input[position] === "#" && input[position + 1] === "!") {
		let length = 2;
		let value = "#!";
		while (input[position + length] !== "\n" && position + length < input.length) {
			value += input[position + length];
			length += 1;
		}
		return [length, ["Shebang", value]];
	}
	return NO_MATCH;
};
/**
* Tokenize a quote splice marker: $^+{
* Matches $ followed by one or more ^ followed by {.
* The value includes the full marker (e.g. "$^{", "$^^{").
* The opening { is consumed — the parser finds the matching } via RBrace tokens.
*/
const tokenizeQuoteSplice = (input, position) => {
	if (input[position] !== "$") return NO_MATCH;
	let caretCount = 0;
	while (input[position + 1 + caretCount] === "^") caretCount++;
	if (caretCount === 0 || input[position + 1 + caretCount] !== "{") return NO_MATCH;
	const length = 1 + caretCount + 1;
	return [length, ["QuoteSplice", input.slice(position, position + length)]];
};
const tokenizeTemplateString = (input, position) => {
	if (input[position] !== "`") return NO_MATCH;
	let value = "`";
	let length = 1;
	while (position + length < input.length) {
		const char = input[position + length];
		if (char === "`") {
			value += "`";
			length += 1;
			return [length, ["TemplateString", value]];
		}
		if (char === "$" && input[position + length + 1] === "{") {
			value += "${";
			length += 2;
			let braceDepth = 1;
			while (position + length < input.length && braceDepth > 0) {
				const c = input[position + length];
				if (c === "{") {
					braceDepth += 1;
					value += c;
					length += 1;
				} else if (c === "}") {
					braceDepth -= 1;
					value += c;
					length += 1;
				} else if (c === "\"") {
					value += c;
					length += 1;
					let escaping = false;
					while (position + length < input.length) {
						const sc = input[position + length];
						value += sc;
						length += 1;
						if (escaping) escaping = false;
						else if (sc === "\\") escaping = true;
						else if (sc === "\"") break;
					}
				} else if (c === "'") {
					value += c;
					length += 1;
					let escaping = false;
					while (position + length < input.length) {
						const sc = input[position + length];
						value += sc;
						length += 1;
						if (escaping) escaping = false;
						else if (sc === "\\") escaping = true;
						else if (sc === "'") break;
					}
				} else if (c === "`") {
					const [nestedLength, nestedToken] = tokenizeTemplateString(input, position + length);
					if (nestedLength === 0 || !nestedToken) return [length, [
						"Error",
						value,
						void 0,
						`Unclosed nested template string at position ${position + length}`
					]];
					if (nestedToken[0] === "Error") return [length + nestedLength, [
						"Error",
						value + nestedToken[1],
						void 0,
						nestedToken[3]
					]];
					value += nestedToken[1];
					length += nestedLength;
				} else {
					value += c;
					length += 1;
				}
			}
			if (braceDepth > 0) return [length, [
				"Error",
				value,
				void 0,
				`Unclosed interpolation in template string at position ${position}`
			]];
		} else {
			value += char;
			length += 1;
		}
	}
	return [length, [
		"Error",
		value,
		void 0,
		`Unclosed template string at position ${position}`
	]];
};
const tokenizeSingleLineComment = (input, position) => {
	if (input[position] === "/" && input[position + 1] === "/") {
		let length = 2;
		let value = "//";
		while (input[position + length] !== "\n" && position + length < input.length) {
			value += input[position + length];
			length += 1;
		}
		return [length, ["SingleLineComment", value]];
	}
	return NO_MATCH;
};
const tokenizers = [
	tokenizeWhitespace,
	tokenizeMultiLineComment,
	tokenizeSingleLineComment,
	tokenizeReservedSymbolToken,
	tokenizeLParen,
	tokenizeRParen,
	tokenizeLBracket,
	tokenizeRBracket,
	tokenizeLBrace,
	tokenizeRBrace,
	tokenizeString,
	tokenizeQuoteSplice,
	tokenizeTemplateString,
	tokenizeRegexpShorthand,
	tokenizeMacroPrefix,
	tokenizeAtom,
	tokenizeBasePrefixedNumber,
	tokenizeNumber,
	tokenizeEffectName,
	tokenizeOperator,
	tokenizeSymbol
];
//#endregion
//#region src/tokenizer/tokenize.ts
function tokenize(input, debug, filePath) {
	let position = 0;
	const tokenStream = {
		tokens: [],
		filePath,
		source: debug ? input : void 0
	};
	let prevToken;
	while (position < input.length) {
		const debugInfo = debug ? createDebugInfo(input, position) : void 0;
		const [count, token] = getCurrentToken(input, position, prevToken);
		position += count;
		if (token) {
			/* v8 ignore next 3 */
			if (debugInfo) token[2] = debugInfo;
			tokenStream.tokens.push(token);
			if (token[0] !== "Whitespace") prevToken = token;
		}
	}
	return tokenStream;
}
/** Create a 0-based [line, column] debug position from a character offset. */
function createDebugInfo(input, position) {
	const lines = input.substring(0, position + 1).split(/\r\n|\r|\n/);
	const lastLine = lines[lines.length - 1];
	return [lines.length - 1, lastLine.length - 1];
}
function getCurrentToken(input, position, prevToken) {
	const initialPosition = position;
	if (position === 0) {
		const [nbrOfCharacters, token] = tokenizeShebang(input, position);
		position += nbrOfCharacters;
		if (nbrOfCharacters > 0) return [position - initialPosition, token];
	}
	for (const tokenizer of tokenizers) {
		const [nbrOfCharacters, token] = tokenizer(input, position, prevToken);
		position += nbrOfCharacters;
		if (nbrOfCharacters === 0) continue;
		return [position - initialPosition, token];
	}
	return [1, [
		"Error",
		input[initialPosition],
		void 0,
		"Unrecognized character"
	]];
}
//#endregion
//#region src/parser/subParsers/parseTemplateString.ts
/** Count newline characters in a string. */
function countNewlines(s) {
	let count = 0;
	for (let i = 0; i < s.length; i++) if (s[i] === "\n") count++;
	return count;
}
/**
* Scan from `start` inside a `${...}` interpolation until the matching `}`.
* Returns the expression source text (without the outer `${` and `}`) and
* the number of characters consumed (including the closing `}`).
*/
function scanExpression(raw, start) {
	let i = start;
	let expr = "";
	let depth = 1;
	while (i < raw.length && depth > 0) {
		const c = raw[i];
		if (c === "{") {
			depth++;
			expr += c;
			i++;
		} else if (c === "}") {
			depth--;
			if (depth > 0) expr += c;
			i++;
		} else if (c === "\"") {
			const { str, consumed } = scanString(raw, i);
			expr += str;
			i += consumed;
		} else if (c === "'") {
			const { str, consumed } = scanQuotedSymbol(raw, i);
			expr += str;
			i += consumed;
		} else if (c === "`") {
			const { str, consumed } = scanNestedTemplate(raw, i);
			expr += str;
			i += consumed;
		} else {
			expr += c;
			i++;
		}
	}
	return {
		expr,
		consumed: i - start
	};
}
function scanString(raw, start) {
	let i = start + 1;
	let str = "\"";
	let escaping = false;
	while (i < raw.length) {
		const c = raw[i];
		str += c;
		i++;
		if (escaping) escaping = false;
		else if (c === "\\") escaping = true;
		else if (c === "\"") break;
	}
	return {
		str,
		consumed: i - start
	};
}
function scanQuotedSymbol(raw, start) {
	let i = start + 1;
	let str = "'";
	let escaping = false;
	while (i < raw.length) {
		const c = raw[i];
		str += c;
		i++;
		if (escaping) escaping = false;
		else if (c === "\\") escaping = true;
		else if (c === "'") break;
	}
	return {
		str,
		consumed: i - start
	};
}
/**
* Scan a full nested template string starting at `start` (pointing at the opening backtick).
* Handles ${...} spans inside the template recursively.
*/
function scanNestedTemplate(raw, start) {
	let i = start + 1;
	let str = "`";
	while (i < raw.length) {
		const c = raw[i];
		if (c === "`") {
			str += c;
			i++;
			break;
		} else if (c === "$" && raw[i + 1] === "{") {
			str += "${";
			i += 2;
			const { expr, consumed } = scanExpression(raw, i);
			str += `${expr}}`;
			i += consumed;
		} else {
			str += c;
			i++;
		}
	}
	return {
		str,
		consumed: i - start
	};
}
/**
* Split the raw content of a template string (between the surrounding backticks)
* into alternating literal and expression segments.
*/
function splitSegments(raw) {
	const segments = [];
	let i = 0;
	let literal = "";
	while (i < raw.length) if (raw[i] === "$" && raw[i + 1] === "{") {
		if (literal.length > 0 && literal[literal.length - 1] === "$") {
			let dollarCount = 1;
			while (literal.length >= dollarCount && literal[literal.length - dollarCount] === "$") dollarCount++;
			dollarCount--;
			literal = literal.slice(0, -dollarCount);
			if (literal.length > 0) {
				segments.push({
					type: "literal",
					value: literal
				});
				literal = "";
			}
			i += 2;
			const exprOffset = i;
			const { expr, consumed } = scanExpression(raw, i);
			i += consumed;
			segments.push({
				type: "deferred",
				value: expr,
				dollarCount: dollarCount + 1,
				offset: exprOffset
			});
			continue;
		}
		if (literal.length > 0) {
			segments.push({
				type: "literal",
				value: literal
			});
			literal = "";
		}
		i += 2;
		const exprOffset = i;
		const { expr, consumed } = scanExpression(raw, i);
		i += consumed;
		segments.push({
			type: "expression",
			value: expr,
			offset: exprOffset
		});
	} else {
		literal += raw[i];
		i++;
	}
	if (literal.length > 0) segments.push({
		type: "literal",
		value: literal
	});
	return segments;
}
function parseTemplateString(ctx, token) {
	ctx.builder?.startNode("TemplateString");
	ctx.advance();
	const debugInfo = token[2];
	const resolvedSci = ctx.resolveTokenDebugInfo(debugInfo);
	const raw = token[1].slice(1, -1);
	const segments = splitSegments(raw);
	if (segments.length === 0) {
		const node = withSourceCodeInfo([
			NodeTypes.Str,
			"",
			0
		], debugInfo, ctx);
		ctx.setNodeEnd(node[2]);
		ctx.builder?.endNode();
		return node;
	}
	if (segments.length === 1 && segments[0].type === "literal") {
		const node = withSourceCodeInfo([
			NodeTypes.Str,
			segments[0].value,
			0
		], debugInfo, ctx);
		ctx.setNodeEnd(node[2]);
		ctx.builder?.endNode();
		return node;
	}
	const segmentNodes = [];
	for (const segment of segments) if (segment.type === "literal") {
		if (segment.value.length === 0) continue;
		segmentNodes.push(withSourceCodeInfo([
			NodeTypes.Str,
			segment.value,
			0
		], debugInfo, ctx));
	} else {
		if (segment.value.trim().length === 0) throw new ParseError("Empty interpolation in template string", resolvedSci);
		const hasDebug = ctx.sourceMap !== void 0;
		const minified = minifyTokenStream(tokenize(segment.value, hasDebug, resolvedSci?.filePath), { removeWhiteSpace: true });
		for (const t of minified.tokens) if (t[0] === "Error") throw new ParseError(`Template string interpolation error: ${t[3]}`, resolvedSci);
		const innerCtx = createParserContext(minified, ctx.allocateId);
		const expr = parseExpression(innerCtx, 0);
		if (ctx.sourceMap && innerCtx.sourceMap && debugInfo) {
			const rawBefore = raw.substring(0, segment.offset);
			const newlinesBefore = countNewlines(rawBefore);
			const lastNewlineIdx = rawBefore.lastIndexOf("\n");
			const segStartLine = debugInfo[0] + newlinesBefore;
			const segStartCol = newlinesBefore === 0 ? debugInfo[1] + 1 + segment.offset : segment.offset - lastNewlineIdx - 1;
			for (const [nodeId, innerPos] of innerCtx.sourceMap.positions) ctx.sourceMap.positions.set(nodeId, {
				source: 0,
				start: [segStartLine + innerPos.start[0], innerPos.start[0] === 0 ? segStartCol + innerPos.start[1] : innerPos.start[1]],
				end: [segStartLine + innerPos.end[0], innerPos.end[0] === 0 ? segStartCol + innerPos.end[1] : innerPos.end[1]],
				...innerPos.structuralLeaf ? { structuralLeaf: true } : {}
			});
		}
		segmentNodes.push(expr);
	}
	const node = withSourceCodeInfo([
		NodeTypes.TmplStr,
		segmentNodes,
		0
	], debugInfo, ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
//#endregion
//#region src/parser/subParsers/parseBindingTarget.ts
/**
* Convert any symbol to a Sym for use in binding targets.
* Normal builtins (map, filter, etc.) are allowed — they can be shadowed.
* Special expressions (if, let, for, etc.) cannot be used as variable names.
*/
function toUserDefinedSymbol(symbol, debugInfo, ctx) {
	if (isSpecialSymbolNode(symbol)) throw new ParseError("Expected user defined symbol", ctx.resolveTokenDebugInfo(debugInfo));
	if (isUserDefinedSymbolNode(symbol)) return symbol;
	const name = getSymbolName(symbol);
	return withSourceCodeInfo([
		NodeTypes.Sym,
		name,
		0
	], debugInfo, ctx);
}
function parseBindingTarget(ctx, { requireDefaultValue, noRest, allowLiteralPatterns, stopTypeAnnotationAtRParen, allowTypeParams } = {}) {
	const firstToken = ctx.tryPeek();
	if (allowLiteralPatterns && isReservedSymbolToken(firstToken, "_")) {
		ctx.advance();
		const target = withSourceCodeInfo([
			bindingTargetTypes.wildcard,
			[],
			0
		], firstToken[2], ctx);
		ctx.setNodeEnd(target[2]);
		return target;
	}
	if (allowLiteralPatterns && isLiteralToken(firstToken)) {
		if (isNumberToken(firstToken) || isBasePrefixedNumberToken(firstToken)) {
			const node = parseNumber(ctx);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
		if (isTemplateStringToken(firstToken)) {
			const node = parseTemplateString(ctx, firstToken);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
		if (isStringToken(firstToken)) {
			const node = parseString(ctx, firstToken);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
		if (isAtomToken(firstToken)) {
			ctx.advance();
			const node = withSourceCodeInfo([
				NodeTypes.Atom,
				firstToken[1],
				0
			], firstToken[2], ctx);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
		if (isReservedSymbolToken(firstToken, "true")) {
			ctx.advance();
			const node = withSourceCodeInfo([
				NodeTypes.Reserved,
				"true",
				0
			], firstToken[2], ctx);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
		if (isReservedSymbolToken(firstToken, "false")) {
			ctx.advance();
			const node = withSourceCodeInfo([
				NodeTypes.Reserved,
				"false",
				0
			], firstToken[2], ctx);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
		/* v8 ignore next 7 */
		if (isReservedSymbolToken(firstToken, "null")) {
			ctx.advance();
			const node = withSourceCodeInfo([
				NodeTypes.Reserved,
				"null",
				0
			], firstToken[2], ctx);
			const target = withSourceCodeInfo([
				bindingTargetTypes.literal,
				[node],
				0
			], firstToken[2], ctx);
			ctx.setNodeEnd(target[2]);
			return target;
		}
	}
	if (isSymbolToken(firstToken)) {
		const symbol = toUserDefinedSymbol(parseSymbol(ctx), firstToken[2], ctx);
		let typeParams;
		if (allowTypeParams && isOperatorToken(ctx.tryPeek(), "<")) typeParams = parseBindingTypeParams(ctx);
		let typeAnnotation;
		if (isTypeAnnotationColon(ctx)) {
			ctx.advance();
			typeAnnotation = collectTypeAnnotation(ctx, { stopAtRParen: stopTypeAnnotationAtRParen ?? false });
			if (!typeAnnotation) throw new ParseError("Expected type after \":\"", ctx.peekSourceCodeInfo());
		}
		const defaultValue = parseOptionalDefaulValue(ctx);
		if (requireDefaultValue && !defaultValue) throw new ParseError("Expected assignment", ctx.peekSourceCodeInfo());
		const target = withSourceCodeInfo([
			bindingTargetTypes.symbol,
			[symbol, defaultValue],
			0
		], firstToken[2], ctx);
		ctx.setNodeEnd(target[2]);
		if (typeAnnotation) ctx.typeAnnotations.set(target[2], typeAnnotation);
		if (typeParams) ctx.typeParams.set(target[2], typeParams);
		return target;
	}
	if (isOperatorToken(firstToken, "...")) {
		if (noRest) throw new ParseError("Rest element not allowed", ctx.resolveTokenDebugInfo(firstToken[2]));
		ctx.advance();
		const symbol = toUserDefinedSymbol(parseSymbol(ctx), firstToken[2], ctx);
		if (isOperatorToken(ctx.tryPeek(), "=")) throw new ParseError("Rest argument can not have default value", ctx.peekSourceCodeInfo());
		const target = withSourceCodeInfo([
			bindingTargetTypes.rest,
			[symbol[1], void 0],
			0
		], firstToken[2], ctx);
		ctx.setNodeEnd(target[2]);
		return target;
	}
	if (isLBracketToken(firstToken)) {
		ctx.advance();
		const elements = [];
		let token = ctx.peek();
		let rest = false;
		while (!isRBracketToken(token)) {
			if (rest) throw new ParseError("Rest argument must be last", ctx.resolveTokenDebugInfo(token[2]));
			if (isOperatorToken(token, ",")) {
				elements.push(null);
				ctx.advance();
				token = ctx.peek();
				continue;
			}
			const target = parseBindingTarget(ctx, { allowLiteralPatterns });
			if (target[0] === bindingTargetTypes.rest) rest = true;
			elements.push(target);
			token = ctx.peek();
			if (!isRBracketToken(token)) {
				assertOperatorToken(token, ",");
				ctx.advance();
			}
			token = ctx.peek();
		}
		ctx.advance();
		const target = withSourceCodeInfo([
			bindingTargetTypes.array,
			[elements, void 0],
			0
		], firstToken[2], ctx);
		ctx.setNodeEnd(target[2]);
		const defaultValue = parseOptionalDefaulValue(ctx);
		if (requireDefaultValue && !defaultValue) throw new ParseError("Expected assignment", ctx.peekSourceCodeInfo());
		target[1][1] = defaultValue;
		return target;
	}
	if (isLBraceToken(firstToken)) {
		ctx.advance();
		const elements = [];
		const seenKeys = /* @__PURE__ */ new Set();
		let token = ctx.peek();
		let rest = false;
		while (!isRBraceToken(token)) {
			if (rest) throw new ParseError("Rest argument must be last", ctx.resolveTokenDebugInfo(token[2]));
			if (isOperatorToken(token, "...")) {
				rest = true;
				ctx.advance();
			}
			const keySymbol = parseSymbol(ctx);
			const keyName = getSymbolName(keySymbol);
			const keyNodeId = keySymbol[2] ?? 0;
			const keyDebugInfo = keySymbol[2] !== void 0 ? ctx.sourceMap?.positions.get(keySymbol[2]) : void 0;
			const keyTokenDebug = keyDebugInfo ? [keyDebugInfo.start[0], keyDebugInfo.start[1]] : void 0;
			token = ctx.peek();
			const assertUniqueKey = (errToken) => {
				if (seenKeys.has(keyName)) throw new ParseError(`Duplicate binding name: ${keyName}`, ctx.resolveTokenDebugInfo(errToken));
				seenKeys.add(keyName);
			};
			if (isReservedSymbolToken(token, "as")) {
				if (rest) throw new ParseError("Rest argument can not have alias", ctx.resolveTokenDebugInfo(token[2]));
				ctx.advance();
				const name = toUserDefinedSymbol(parseSymbol(ctx), token[2], ctx);
				assertUniqueKey(token[2]);
				const symTarget = withSourceCodeInfo([
					bindingTargetTypes.symbol,
					[name, parseOptionalDefaulValue(ctx)],
					0
				], token[2], ctx);
				ctx.setNodeEnd(symTarget[2]);
				elements.push({
					key: keyName,
					keyNodeId,
					target: symTarget
				});
			} else if (isRBraceToken(token) || isOperatorToken(token, ",") || isOperatorToken(token, "=")) {
				const key = toUserDefinedSymbol(keySymbol, keyTokenDebug, ctx);
				assertUniqueKey(token[2]);
				if (rest && isOperatorToken(ctx.tryPeek(), "=")) throw new ParseError("Rest argument can not have default value", ctx.peekSourceCodeInfo());
				if (rest) {
					const restTarget = withSourceCodeInfo([
						bindingTargetTypes.rest,
						[key[1], parseOptionalDefaulValue(ctx)],
						0
					], keyTokenDebug, ctx);
					ctx.setNodeEnd(restTarget[2]);
					elements.push({
						key: key[1],
						keyNodeId,
						target: restTarget
					});
				} else {
					const symTarget = withSourceCodeInfo([
						bindingTargetTypes.symbol,
						[key, parseOptionalDefaulValue(ctx)],
						0
					], keyTokenDebug, ctx);
					ctx.setNodeEnd(symTarget[2]);
					elements.push({
						key: key[1],
						keyNodeId,
						target: symTarget
					});
				}
			} else if (isOperatorToken(token, ":")) {
				ctx.advance();
				token = ctx.peek();
				if (allowLiteralPatterns) {
					if (!isLBraceToken(token) && !isLBracketToken(token) && !isLiteralToken(token)) throw new ParseError("Expected literal, object or array pattern", ctx.resolveTokenDebugInfo(token[2]));
				} else if (!isLBraceToken(token) && !isLBracketToken(token)) throw new ParseError("Expected object or array", ctx.resolveTokenDebugInfo(token[2]));
				assertUniqueKey(keyTokenDebug);
				elements.push({
					key: keyName,
					keyNodeId,
					target: parseBindingTarget(ctx, { allowLiteralPatterns })
				});
			}
			if (!isRBraceToken(ctx.peek())) {
				assertOperatorToken(ctx.peek(), ",");
				ctx.advance();
			}
			token = ctx.peek();
		}
		ctx.advance();
		const target = withSourceCodeInfo([
			bindingTargetTypes.object,
			[elements, void 0],
			0
		], firstToken[2], ctx);
		ctx.setNodeEnd(target[2]);
		token = ctx.peek();
		const defaultValue = parseOptionalDefaulValue(ctx);
		if (requireDefaultValue && !defaultValue) throw new ParseError("Expected assignment", ctx.resolveTokenDebugInfo(token[2]));
		target[1][1] = defaultValue;
		return target;
	}
	throw new ParseError("Expected symbol", ctx.peekSourceCodeInfo());
}
function parseOptionalDefaulValue(ctx) {
	if (isOperatorToken(ctx.tryPeek(), "=")) {
		ctx.advance();
		return ctx.parseExpression();
	}
}
/**
* Parse a binding-scoped type-parameter list `<T, U: Bound, ...>` after
* the symbol in a `let f<T> = ...` binding. Shares the shape with the
* type-alias generic-param parser in `parseTypeDeclaration.ts` — both
* emit `AliasParam[]` where each entry has a name and optional bound
* source text. The bound source is parsed lazily by the typechecker
* (same rationale as for alias bounds: avoid circular-parse dependencies
* when a bound references a name defined later in the same file).
*
* Duplicates (`<T, T>` or `<T: A, T: B>`) are rejected here so the
* typechecker downstream never sees an ambiguous type-var map.
*/
function parseBindingTypeParams(ctx) {
	ctx.advance();
	const params = [];
	const seen = /* @__PURE__ */ new Set();
	while (!ctx.isAtEnd()) {
		const paramToken = ctx.peek();
		if (!isSymbolToken(paramToken) && !isReservedSymbolToken(paramToken)) throw new ParseError("Expected type parameter name", ctx.peekSourceCodeInfo());
		const paramName = paramToken[1];
		if (seen.has(paramName)) throw new ParseError(`Duplicate type parameter '${paramName}' in generic parameter list`, ctx.peekSourceCodeInfo());
		seen.add(paramName);
		ctx.advance();
		let bound;
		if (isOperatorToken(ctx.tryPeek(), ":")) {
			ctx.advance();
			const boundExpr = collectTypeAnnotation(ctx, { stopAtGt: true });
			if (!boundExpr) throw new ParseError(`Expected bound type after ":" for parameter "${paramName}"`, ctx.peekSourceCodeInfo());
			bound = boundExpr;
		}
		params.push(bound === void 0 ? { name: paramName } : {
			name: paramName,
			bound
		});
		if (isOperatorToken(ctx.tryPeek(), ",")) {
			ctx.advance();
			continue;
		}
		break;
	}
	if (!isOperatorToken(ctx.tryPeek(), ">")) throw new ParseError("Expected \">\" after type parameters", ctx.peekSourceCodeInfo());
	ctx.advance();
	return params;
}
function isLiteralToken(token) {
	return isNumberToken(token) || isBasePrefixedNumberToken(token) || isStringToken(token) || isTemplateStringToken(token) || isAtomToken(token) || isReservedSymbolToken(token, "true") || isReservedSymbolToken(token, "false") || isReservedSymbolToken(token, "null");
}
//#endregion
//#region src/parser/subParsers/parseLet.ts
function parseLet(ctx, token) {
	ctx.builder?.startNode("Let");
	ctx.advance();
	const target = parseBindingTarget(ctx, {
		requireDefaultValue: true,
		noRest: true,
		allowTypeParams: true
	});
	const value = target[1][1];
	target[1][1] = void 0;
	const node = withSourceCodeInfo([
		NodeTypes.Let,
		[target, value],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
//#endregion
//#region src/parser/subParsers/parseForOrDoseq.ts
function parseForOrDoseq(ctx, firstToken) {
	ctx.builder?.startNode("For");
	ctx.advance();
	assertLParenToken(ctx.tryPeek());
	ctx.advance();
	const forLoopBindings = [];
	while (!ctx.isAtEnd() && !isRParenToken(ctx.tryPeek())) {
		const loopBinding = parseForLoopBinding(ctx);
		const existingBoundNames = forLoopBindings.flatMap((b) => Object.keys(getAllBindingTargetNames(b[0][0])));
		const newBoundNames = getAllBindingTargetNames(loopBinding[0][0]);
		if (Object.keys(newBoundNames).some((n) => existingBoundNames.includes(n))) throw new ParseError("Duplicate binding", void 0);
		forLoopBindings.push(loopBinding);
		if (isOperatorToken(ctx.tryPeek(), ",")) ctx.advance();
	}
	assertRParenToken(ctx.tryPeek());
	ctx.advance();
	assertOperatorToken(ctx.tryPeek(), "->");
	ctx.advance();
	const expression = ctx.parseExpression();
	const node = withSourceCodeInfo([
		NodeTypes.For,
		[forLoopBindings, expression],
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
function parseForLoopBinding(ctx) {
	const bindingNode = parseBinding(ctx);
	const modifiers = [];
	let token = ctx.peek();
	assertInternalLoopBindingDelimiter(token, [
		"let",
		"when",
		"while"
	], ctx.peekSourceCodeInfo());
	const letBindings = [];
	if (token[1] === "let") {
		modifiers.push("&let");
		while (isSymbolToken(token, "let")) {
			const letNode = parseLet(ctx, token);
			const existingBoundNames = letBindings.flatMap((b) => Object.keys(getAllBindingTargetNames(b[0])));
			const [letTarget, letValue] = letNode[1];
			if (Object.keys(getAllBindingTargetNames(letTarget)).some((n) => existingBoundNames.includes(n))) throw new ParseError("Duplicate binding", void 0);
			letBindings.push([letTarget, letValue]);
			token = ctx.peek();
			assertInternalLoopBindingDelimiter(token, [
				"let",
				"when",
				"while"
			], ctx.peekSourceCodeInfo());
			token = ctx.peek();
		}
	}
	let whenNode;
	let whileNode;
	while (isReservedSymbolToken(token, "when") || isReservedSymbolToken(token, "while")) {
		ctx.advance();
		if (token[1] === "when") {
			modifiers.push("&when");
			whenNode = ctx.parseExpression();
		} else {
			modifiers.push("&while");
			whileNode = ctx.parseExpression();
		}
		token = ctx.peek();
		const symbols = modifiers.includes("&when") && modifiers.includes("&while") ? [] : modifiers.includes("&when") ? ["while"] : ["when"];
		assertInternalLoopBindingDelimiter(token, symbols, ctx.peekSourceCodeInfo());
		token = ctx.peek();
	}
	assertInternalLoopBindingDelimiter(token, [], ctx.peekSourceCodeInfo());
	return [
		bindingNode,
		letBindings,
		whenNode,
		whileNode
	];
}
function parseBinding(ctx) {
	const firstToken = asSymbolToken(ctx.tryPeek());
	const name = asUserDefinedSymbolNode(parseSymbol(ctx));
	assertReservedSymbolToken(ctx.tryPeek(), "in");
	ctx.advance();
	const value = ctx.parseExpression();
	const target = withSourceCodeInfo([
		bindingTargetTypes.symbol,
		[name, void 0],
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(target[2]);
	return [target, value];
}
function assertInternalLoopBindingDelimiter(token, symbols, sourceCodeInfo) {
	if (!isInternalLoopBindingDelimiter(token, symbols)) throw new ParseError(`Expected symbol ${`${[...symbols, ","].map((symbol) => `"${symbol}"`).join(", ")} or ")"`}`, sourceCodeInfo);
}
function isInternalLoopBindingDelimiter(token, symbols) {
	if (isOperatorToken(token, ",") || isRParenToken(token)) return true;
	for (const symbol of symbols) {
		if (symbol === "let" && isSymbolToken(token, "let")) return true;
		if (["when", "while"].includes(symbol) && isReservedSymbolToken(token, symbol)) return true;
	}
	return false;
}
//#endregion
//#region src/parser/subParsers/parseTypeDeclaration.ts
/**
* Parse a type alias declaration: type Name = TypeExpr
* Or with type parameters: type Name<A, B> = TypeExpr
*
* Type declarations are type-only — they tell the typechecker about
* named type aliases. They produce a null AST node at runtime (erased).
*
* Syntax:
*   type Nullable = Number | Null
*   type Pair = [Number, Number]
*   type Result<T, E> = {tag: :ok, value: T} | {tag: :error, error: E}
*/
function skipWhitespace(ctx) {
	while (!ctx.isAtEnd()) {
		if (ctx.peek()[0] !== "Whitespace") break;
		ctx.advance();
	}
}
function parseTypeDeclaration(ctx) {
	ctx.builder?.startNode("TypeAlias");
	try {
		return parseTypeDeclarationBody(ctx);
	} finally {
		ctx.builder?.endNode();
	}
}
function parseTypeDeclarationBody(ctx) {
	const token = ctx.peek();
	ctx.advance();
	const nameToken = ctx.peek();
	if (!isSymbolToken(nameToken)) throw new ParseError("Expected type name after \"type\"", ctx.peekSourceCodeInfo());
	const name = nameToken[1];
	ctx.advance();
	const params = [];
	const seenNames = /* @__PURE__ */ new Set();
	skipWhitespace(ctx);
	if (isOperatorToken(ctx.tryPeek(), "<")) {
		ctx.advance();
		while (!ctx.isAtEnd()) {
			skipWhitespace(ctx);
			const paramToken = ctx.peek();
			if (!isSymbolToken(paramToken) && !isReservedSymbolToken(paramToken)) throw new ParseError("Expected type parameter name", ctx.peekSourceCodeInfo());
			const paramName = paramToken[1];
			if (seenNames.has(paramName)) throw new ParseError(`Duplicate type parameter '${paramName}' in generic parameter list`, ctx.peekSourceCodeInfo());
			seenNames.add(paramName);
			ctx.advance();
			skipWhitespace(ctx);
			let bound;
			if (isOperatorToken(ctx.tryPeek(), ":")) {
				ctx.advance();
				const boundExpr = collectTypeAnnotation(ctx, { stopAtGt: true });
				if (!boundExpr) throw new ParseError(`Expected bound type after ":" for parameter "${paramName}"`, ctx.peekSourceCodeInfo());
				bound = boundExpr;
				skipWhitespace(ctx);
			}
			params.push(bound === void 0 ? { name: paramName } : {
				name: paramName,
				bound
			});
			if (isOperatorToken(ctx.tryPeek(), ",")) ctx.advance();
			else break;
		}
		skipWhitespace(ctx);
		if (!isOperatorToken(ctx.tryPeek(), ">")) throw new ParseError("Expected \">\" after type parameters", ctx.peekSourceCodeInfo());
		ctx.advance();
	}
	skipWhitespace(ctx);
	if (!isOperatorToken(ctx.tryPeek(), "=")) throw new ParseError("Expected \"=\" after type name", ctx.peekSourceCodeInfo());
	ctx.advance();
	const typeExpr = collectTypeAnnotation(ctx);
	if (!typeExpr) throw new ParseError("Expected type expression after \"=\"", ctx.peekSourceCodeInfo());
	ctx.typeAliases.set(name, {
		params,
		body: typeExpr
	});
	return withSourceCodeInfo([
		NodeTypes.Reserved,
		"null",
		0
	], token[2], ctx);
}
//#endregion
//#region src/parser/subParsers/parseHandler.ts
/**
* Parse `handler <clauses> [transform x -> expr] end`.
*
* `handler` and `transform` are contextual keywords (regular symbols),
* not reserved words — they can be used as variable names elsewhere.
* `end` is a reserved keyword as usual.
*
* Clauses: `@effect(params...) -> expr` — the `@effect` token starts each clause.
* No separators between clauses (like match cases).
* Duplicate effect names are a parse error.
* Transform: optional, `transform x -> expr` (or `transform x -> do...end`).
*/
function parseHandler(ctx, shallow = false, linear = false) {
	if (!shallow && !linear) ctx.builder?.startNode("Handler");
	const token = ctx.tryPeek();
	ctx.advance();
	const clauses = [];
	const seenEffects = /* @__PURE__ */ new Set();
	while (!ctx.isAtEnd() && !isSymbolToken(ctx.tryPeek(), "transform") && !isReservedSymbolToken(ctx.tryPeek(), "end")) {
		const clauseToken = ctx.tryPeek();
		if (!isEffectNameToken(clauseToken)) throw new ParseError("Expected effect clause (@effect) or \"transform\" or \"end\"", ctx.peekSourceCodeInfo());
		const effectName = clauseToken[1];
		ctx.advance();
		if (seenEffects.has(effectName)) throw new ParseError(`Duplicate handler clause for effect '@${effectName}'`, ctx.peekSourceCodeInfo());
		seenEffects.add(effectName);
		const params = [];
		if (isLParenToken(ctx.tryPeek())) {
			ctx.advance();
			while (!ctx.isAtEnd() && !isRParenToken(ctx.tryPeek())) {
				params.push(parseBindingTarget(ctx, { stopTypeAnnotationAtRParen: true }));
				if (isOperatorToken(ctx.tryPeek(), ",")) ctx.advance();
			}
			if (!isRParenToken(ctx.tryPeek())) throw new ParseError("Expected \")\"", ctx.peekSourceCodeInfo());
			ctx.advance();
		}
		if (!isOperatorToken(ctx.tryPeek(), "->")) throw new ParseError("Expected \"->\" after handler clause parameters", ctx.peekSourceCodeInfo());
		ctx.advance();
		let body;
		if (isReservedSymbolToken(ctx.tryPeek(), "do")) {
			const doNode = parseDo(ctx);
			body = doNode[1];
			ctx.markStructuralLeaf(doNode[2]);
		} else body = [ctx.parseExpression()];
		if (linear && containsResumeOutsideNestedHandler(body)) throw new ParseError("`resume` is not available in linear handler clauses — return the value to resume, or `perform(@dvala.error, msg)` to fail.", ctx.peekSourceCodeInfo());
		if (linear && body.length > 0) {
			const lastExpr = body[body.length - 1];
			body[body.length - 1] = withSourceCodeInfo([
				NodeTypes.Resume,
				lastExpr,
				0
			], token[2], ctx);
		}
		clauses.push({
			effectName,
			params,
			body
		});
	}
	let transform = null;
	if (isSymbolToken(ctx.tryPeek(), "transform")) {
		ctx.advance();
		const transformParam = parseBindingTarget(ctx);
		if (!isOperatorToken(ctx.tryPeek(), "->")) throw new ParseError("Expected \"->\" after transform parameter", ctx.peekSourceCodeInfo());
		ctx.advance();
		let transformBody;
		if (isReservedSymbolToken(ctx.tryPeek(), "do")) {
			const doNode = parseDo(ctx);
			transformBody = doNode[1];
			ctx.markStructuralLeaf(doNode[2]);
		} else transformBody = [ctx.parseExpression()];
		transform = [transformParam, transformBody];
	}
	if (!isReservedSymbolToken(ctx.tryPeek(), "end")) throw new ParseError("Expected \"end\" to close handler", ctx.peekSourceCodeInfo());
	ctx.advance();
	const node = withSourceCodeInfo([
		NodeTypes.Handler,
		[
			clauses,
			transform,
			shallow,
			linear
		],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
/**
* True iff the parsed clause body uses the `resume` keyword anywhere that's
* still bound to *this* handler (rather than a nested one). The walk
* descends into all child AST nodes except nested `Handler` and `Macro`
* nodes, both of which rebind `resume` to themselves: a `resume` inside
* those bodies belongs to the inner handler/macro, not the outer linear
* one. Used by `parseHandler` to enforce the linear-handler contract from
* `design/active/2026-04-29_linear-handler.md` (Q6 / static check (a)).
*/
function containsResumeOutsideNestedHandler(body) {
	for (const node of body) if (containsResume(node)) return true;
	return false;
}
function containsResume(value) {
	if (Array.isArray(value)) {
		if (value.length === 3 && typeof value[0] === "string") {
			const type = value[0];
			if (type === NodeTypes.Resume) return true;
			if (type === NodeTypes.Handler || type === NodeTypes.Macro) return false;
			return containsResume(value[1]);
		}
		for (const child of value) if (containsResume(child)) return true;
		return false;
	}
	if (value && typeof value === "object") {
		for (const v of Object.values(value)) if (containsResume(v)) return true;
		return false;
	}
	return false;
}
//#endregion
//#region src/parser/subParsers/parseImplicitBlock.ts
function parseImplicitBlock(ctx, ends) {
	const nodes = [];
	while (!ctx.isAtEnd() && !isImplicitBlockEnd(ctx, ends)) if (isOperatorToken(ctx.tryPeek(), ";")) ctx.advance();
	else nodes.push(ctx.parseExpression());
	assertImplicitBlockEnd(ctx, ends);
	if (nodes.length === 0) throw new ParseError("Expected expression", ctx.peekSourceCodeInfo());
	if (nodes.length === 1) return nodes[0];
	const node = withSourceCodeInfo([
		NodeTypes.Block,
		nodes,
		0
	], ctx.peekDebugInfo(), ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
function assertImplicitBlockEnd(ctx, ends) {
	if (!isImplicitBlockEnd(ctx, ends)) throw new ParseError(`Expected ${ends.map((e) => e[1]).join(" or ")}`, ctx.peekSourceCodeInfo());
}
function isImplicitBlockEnd(ctx, ends) {
	for (const end of ends) if (isReservedSymbolToken(ctx.tryPeek(), end)) return true;
	return false;
}
//#endregion
//#region src/parser/subParsers/parseIf.ts
function parseIf(ctx, token) {
	ctx.builder?.startNode("If");
	ctx.advance();
	const condition = ctx.parseExpression();
	assertReservedSymbolToken(ctx.tryPeek(), "then");
	ctx.advance();
	const thenExpression = parseImplicitBlock(ctx, ["else", "end"]);
	let elseExpression;
	if (isReservedSymbolToken(ctx.tryPeek(), "else")) {
		ctx.advance();
		if (isSymbolToken(ctx.tryPeek()) && ctx.tryPeek()[1] === "if") elseExpression = parseElseIf(ctx);
		else elseExpression = parseImplicitBlock(ctx, ["end"]);
	} else throw new ParseError("`if` without `else` is not allowed — use `when condition do expr end` for side effects, or add an `else` clause", ctx.peekSourceCodeInfo());
	ctx.advance();
	ctx.clearStructuralLeaf(thenExpression[2]);
	ctx.clearStructuralLeaf(elseExpression[2]);
	const node = withSourceCodeInfo([
		NodeTypes.If,
		[
			condition,
			thenExpression,
			elseExpression
		],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
/**
* Parse `if condition then body [else if ... | else ...]` without consuming `end`.
* Used by `else if` chains — the outermost `if` consumes the single shared `end`.
*/
function parseElseIf(ctx) {
	const token = ctx.tryPeek();
	ctx.advance();
	const condition = ctx.parseExpression();
	assertReservedSymbolToken(ctx.tryPeek(), "then");
	ctx.advance();
	const thenExpression = parseImplicitBlock(ctx, ["else", "end"]);
	let elseExpression;
	if (isReservedSymbolToken(ctx.tryPeek(), "else")) {
		ctx.advance();
		if (isSymbolToken(ctx.tryPeek()) && ctx.tryPeek()[1] === "if") elseExpression = parseElseIf(ctx);
		else elseExpression = parseImplicitBlock(ctx, ["end"]);
	} else throw new ParseError("`if` without `else` is not allowed — use `when condition do expr end` for side effects, or add an `else` clause", ctx.peekSourceCodeInfo());
	ctx.clearStructuralLeaf(thenExpression[2]);
	ctx.clearStructuralLeaf(elseExpression[2]);
	const node = withSourceCodeInfo([
		NodeTypes.If,
		[
			condition,
			thenExpression,
			elseExpression
		],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
//#endregion
//#region src/parser/subParsers/parseLoop.ts
function parseLoop(ctx, firstToken) {
	ctx.builder?.startNode("Loop");
	ctx.advance();
	assertLParenToken(ctx.tryPeek());
	ctx.advance();
	const bindings = [];
	let token = ctx.tryPeek();
	while (!ctx.isAtEnd() && !isRParenToken(token)) {
		const target = parseBindingTarget(ctx, {
			requireDefaultValue: true,
			noRest: true
		});
		const value = target[1][1];
		target[1][1] = void 0;
		bindings.push([target, value]);
		if (isOperatorToken(ctx.tryPeek(), ",")) ctx.advance();
		token = ctx.tryPeek();
	}
	if (bindings.length === 0) throw new ParseError("Expected binding", ctx.peekSourceCodeInfo());
	assertRParenToken(token);
	ctx.advance();
	assertOperatorToken(ctx.tryPeek(), "->");
	ctx.advance();
	const expression = ctx.parseExpression();
	const node = withSourceCodeInfo([
		NodeTypes.Loop,
		[bindings, expression],
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
//#endregion
//#region src/parser/subParsers/parseFunction.ts
const placeholderRegexp = /^\$([1-9]\d?)?$/;
const maxShorthandLambdaArity = 20;
/**
* Reserved symbols that can NEVER start an expression — they are
* purely syntactic block-terminators or continuation keywords
* (`end` closes do/if/match/handler, `else`/`then` continue an `if`,
* `case` opens a match arm, etc.).
*
* Any of these appearing immediately after `->` means the user wrote
* an empty function body (the most common scenario is forgetting
* to supply a body before closing the enclosing construct). Runtime
* strictness Step 0a: reject this at parse time with a clear message
* rather than a later TypeError about "Reserved symbol cannot be
* evaluated." See `design/active/2026-04-12_type-system.md` § Step 0.
*/
const nonExpressionReservedSymbols = new Set([
	"end",
	"else",
	"then",
	"case",
	"in",
	"as",
	"_"
]);
function assertNonEmptyFunctionBody(ctx) {
	const token = ctx.peek();
	if (isReservedSymbolToken(token) && nonExpressionReservedSymbols.has(token[1])) throw new ParseError(`Empty function body — '${token[1]}' cannot start an expression. Provide a body expression or use \`do ... end\` for an explicit block.`, ctx.peekSourceCodeInfo());
}
function parseLambdaFunction(ctx) {
	ctx.builder?.startNode("Function");
	const firstToken = ctx.peek();
	const functionArguments = parseFunctionArguments(ctx);
	let returnTypeAnnotation;
	if (isOperatorToken(ctx.peek(), ":")) {
		ctx.advance();
		returnTypeAnnotation = collectTypeAnnotation(ctx, {
			stopAtArrow: true,
			stopAtRParen: false
		});
	}
	if (!isOperatorToken(ctx.peek(), "->")) throw new ParseError("Expected ->", ctx.peekSourceCodeInfo());
	ctx.advance();
	assertNonEmptyFunctionBody(ctx);
	let nodes;
	if (isReservedSymbolToken(ctx.peek(), "do")) {
		const doNode = parseDo(ctx);
		nodes = doNode[1];
		ctx.markStructuralLeaf(doNode[2]);
	} else nodes = [ctx.parseExpression()];
	const node = withSourceCodeInfo([
		NodeTypes.Function,
		[functionArguments, nodes],
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(node[2]);
	if (returnTypeAnnotation) ctx.typeAnnotations.set(node[2], `return:${returnTypeAnnotation}`);
	ctx.builder?.endNode();
	return node;
}
function parseFunctionArguments(ctx) {
	const firstToken = ctx.peek();
	if (isSymbolToken(firstToken)) return [withSourceCodeInfo([
		bindingTargetTypes.symbol,
		[parseSymbol(ctx), void 0],
		0
	], firstToken[2], ctx)];
	assertLParenToken(firstToken);
	ctx.advance();
	let rest = false;
	let defaults = false;
	const functionArguments = [];
	while (!ctx.isAtEnd() && !isRParenToken(ctx.peek()) && !isSymbolToken(ctx.peek(), "let")) {
		if (rest) throw new ParseError("Rest argument must be last", ctx.peekSourceCodeInfo());
		const bindingTarget = parseBindingTarget(ctx, { stopTypeAnnotationAtRParen: true });
		if (bindingTarget[1][1] !== void 0) defaults = true;
		if (bindingTarget[0] === bindingTargetTypes.rest) rest = true;
		if (defaults && !bindingTarget[1][1]) throw new ParseError("Default arguments must be last", ctx.peekSourceCodeInfo());
		functionArguments.push(bindingTarget);
		if (!isOperatorToken(ctx.peek(), ",") && !isRParenToken(ctx.peek()) && !isSymbolToken(ctx.peek(), "let")) throw new ParseError("Expected comma or closing parenthesis", ctx.peekSourceCodeInfo());
		if (isOperatorToken(ctx.peek(), ",")) ctx.advance();
	}
	if (!isRParenToken(ctx.peek())) throw new ParseError("Expected closing parenthesis", ctx.peekSourceCodeInfo());
	ctx.advance();
	return functionArguments;
}
function parseShorthandLambdaFunction(ctx) {
	ctx.builder?.startNode("Function");
	const firstToken = ctx.peek();
	ctx.advance();
	const startPos = ctx.getPosition();
	assertNonEmptyFunctionBody(ctx);
	let nodes;
	if (isReservedSymbolToken(ctx.peek(), "do")) {
		const doNode = parseDo(ctx);
		nodes = doNode[1];
		ctx.markStructuralLeaf(doNode[2]);
	} else nodes = [ctx.parseExpression()];
	const endPos = ctx.getPosition() - 1;
	let arity = 0;
	for (let pos = startPos; pos <= endPos; pos += 1) {
		const token = ctx.getTokenAt(pos);
		if (isSymbolToken(token)) {
			const match = placeholderRegexp.exec(token[1]);
			if (match) {
				const number = match[1] ?? "1";
				if (match[1] === "1") throw new ParseError("Use $ instead of $1 for the first argument", ctx.resolveTokenDebugInfo(firstToken[2]));
				arity = Math.max(arity, Number(number));
				if (arity > maxShorthandLambdaArity) throw new ParseError("Can't specify more than 20 arguments", ctx.resolveTokenDebugInfo(firstToken[2]));
			}
		}
	}
	const functionArguments = [];
	for (let i = 1; i <= arity; i += 1) {
		const name = i === 1 ? "$" : `$${i}`;
		functionArguments.push(withSourceCodeInfo([
			bindingTargetTypes.symbol,
			[[
				NodeTypes.Sym,
				name,
				0
			], void 0],
			0
		], firstToken[2], ctx));
	}
	const node = withSourceCodeInfo([
		NodeTypes.Function,
		[
			functionArguments,
			nodes,
			{ isShorthand: true }
		],
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
//#endregion
//#region src/parser/subParsers/parseMacro.ts
function parseMacro(ctx) {
	ctx.builder?.startNode("Macro");
	const token = ctx.peek();
	ctx.advance();
	let functionArguments;
	try {
		functionArguments = parseFunctionArguments(ctx);
	} catch {
		throw new ParseError("Expected function parameters after \"macro\"", ctx.peekSourceCodeInfo());
	}
	assertOperatorToken(ctx.peek(), "->");
	ctx.advance();
	let bodyNodes;
	if (isReservedSymbolToken(ctx.peek(), "do")) {
		const doNode = parseDo(ctx);
		bodyNodes = doNode[1];
		ctx.markStructuralLeaf(doNode[2]);
	} else bodyNodes = [ctx.parseExpression()];
	const node = withSourceCodeInfo([
		NodeTypes.Macro,
		[functionArguments, bodyNodes],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
//#endregion
//#region src/parser/subParsers/parseMatch.ts
function parseMatch(ctx, token) {
	ctx.builder?.startNode("Match");
	ctx.advance();
	const valueExpression = ctx.parseExpression();
	const params = [];
	while (!ctx.isAtEnd() && !isReservedSymbolToken(ctx.tryPeek(), "end")) {
		assertReservedSymbolToken(ctx.tryPeek(), "case");
		ctx.advance();
		const pattern = parseBindingTarget(ctx, { allowLiteralPatterns: true });
		let guard;
		if (isReservedSymbolToken(ctx.tryPeek(), "when")) {
			ctx.advance();
			guard = ctx.parseExpression();
		}
		assertReservedSymbolToken(ctx.tryPeek(), "then");
		ctx.advance();
		const thenExpression = parseImplicitBlock(ctx, ["case", "end"]);
		ctx.clearStructuralLeaf(thenExpression[2]);
		params.push([
			pattern,
			thenExpression,
			guard
		]);
		if (isReservedSymbolToken(ctx.tryPeek(), "end")) break;
	}
	assertReservedSymbolToken(ctx.tryPeek(), "end");
	ctx.advance();
	const node = withSourceCodeInfo([
		NodeTypes.Match,
		[valueExpression, params],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
//#endregion
//#region src/parser/subParsers/parseRegexpShorthand.ts
function parseRegexpShorthand(ctx) {
	ctx.builder?.startNode("RegexpShorthand");
	const token = ctx.peek();
	ctx.advance();
	const endStringPosition = token[1].lastIndexOf("\"");
	const regexpString = token[1].substring(2, endStringPosition);
	const optionsString = token[1].substring(endStringPosition + 1);
	const stringNode = withSourceCodeInfo([
		NodeTypes.Str,
		regexpString,
		0
	], token[2], ctx);
	const optionsNode = withSourceCodeInfo([
		NodeTypes.Str,
		optionsString,
		0
	], token[2], ctx);
	const node = withSourceCodeInfo([
		NodeTypes.Call,
		[withSourceCodeInfo([
			NodeTypes.Builtin,
			"regexp",
			0
		], token[2], ctx), [stringNode, optionsNode]],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
//#endregion
//#region src/parser/subParsers/parseReservedSymbol.ts
function parseReservedSymbol(ctx) {
	ctx.builder?.startNode("ReservedSymbol");
	const token = asReservedSymbolToken(ctx.tryPeek());
	ctx.advance();
	const symbol = token[1];
	if (isNumberReservedSymbol(symbol)) {
		const node = withSourceCodeInfo([
			NodeTypes.Num,
			numberReservedSymbolRecord[symbol],
			0
		], token[2], ctx);
		ctx.setNodeEnd(node[2]);
		ctx.builder?.endNode();
		return node;
	}
	const node = withSourceCodeInfo([
		NodeTypes.Reserved,
		token[1],
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
//#endregion
//#region src/parser/subParsers/parseArray.ts
function parseArray(ctx) {
	ctx.builder?.startNode("Array");
	const firstToken = asLBracketToken(ctx.tryPeek());
	ctx.advance();
	const params = [];
	while (!ctx.isAtEnd() && !isRBracketToken(ctx.tryPeek())) {
		if (isOperatorToken(ctx.tryPeek(), "...")) {
			ctx.builder?.startNode("Spread");
			ctx.advance();
			params.push(withSourceCodeInfo([
				NodeTypes.Spread,
				ctx.parseExpression(),
				0
			], ctx.peekDebugInfo(), ctx));
			ctx.builder?.endNode();
		} else params.push(ctx.parseExpression());
		const nextToken = ctx.tryPeek();
		if (!isOperatorToken(nextToken, ",") && !isRBracketToken(nextToken)) throw new ParseError("Expected comma or closing parenthesis", ctx.peekSourceCodeInfo());
		if (isOperatorToken(nextToken, ",")) ctx.advance();
	}
	assertRBracketToken(ctx.tryPeek());
	ctx.advance();
	const node = withSourceCodeInfo([
		NodeTypes.Array,
		params,
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
//#endregion
//#region src/parser/subParsers/parseFunctionCall.ts
function parseFunctionCall(ctx, symbol) {
	ctx.advance();
	const symbolSci = resolveSourceCodeInfo(symbol[2], ctx.sourceMap);
	const symbolDebugInfo = sourceCodeInfoToDebugInfo(symbolSci);
	if (isSpecialSymbolNode(symbol) && symbol[1] === specialExpressionTypes.effect) {
		const node = parseEffectArgs(ctx, symbolDebugInfo);
		ctx.setNodeEnd(node[2]);
		return node;
	}
	const params = [];
	while (!ctx.isAtEnd() && !isRParenToken(ctx.tryPeek())) {
		if (isOperatorToken(ctx.tryPeek(), "...")) {
			ctx.builder?.startNode("Spread");
			ctx.advance();
			params.push(withSourceCodeInfo([
				NodeTypes.Spread,
				ctx.parseExpression(),
				0
			], ctx.peekDebugInfo(), ctx));
			ctx.builder?.endNode();
		} else params.push(ctx.parseExpression());
		const nextToken = ctx.tryPeek();
		if (!isOperatorToken(nextToken, ",") && !isRParenToken(nextToken)) throw new ParseError("Expected comma or closing parenthesis", ctx.peekSourceCodeInfo());
		if (isOperatorToken(nextToken, ",")) ctx.advance();
	}
	if (!isRParenToken(ctx.tryPeek())) throw new ParseError("Expected closing parenthesis", ctx.peekSourceCodeInfo());
	ctx.advance();
	if (isSpecialSymbolNode(symbol)) {
		const specialExpressionType = symbol[1];
		if (specialExpressionType === specialExpressionTypes.import) {
			if (params.length !== 1) throw new ParseError(`import expects exactly 1 argument, got ${params.length}`, symbolSci);
			const param = params[0];
			if (param[0] !== NodeTypes.Str) throw new ParseError("import expects a string argument, e.g. import(\"math\")", resolveSourceCodeInfo(param[2], ctx.sourceMap) ?? symbolSci);
			const moduleName = param[1];
			ctx.markStructuralLeaf(param[2]);
			const node = withSourceCodeInfo([
				NodeTypes.Import,
				moduleName,
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.recur) {
			const node = withSourceCodeInfo([
				NodeTypes.Recur,
				params,
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.array) {
			const node = withSourceCodeInfo([
				NodeTypes.Array,
				params,
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.parallel) {
			assertNumberOfParams({
				min: 1,
				max: 1
			}, params.length, symbolSci);
			const node = withSourceCodeInfo([
				NodeTypes.Parallel,
				params[0],
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.race) {
			assertNumberOfParams({
				min: 1,
				max: 1
			}, params.length, symbolSci);
			const node = withSourceCodeInfo([
				NodeTypes.Race,
				params[0],
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.settled) {
			assertNumberOfParams({
				min: 1,
				max: 1
			}, params.length, symbolSci);
			const node = withSourceCodeInfo([
				NodeTypes.Settled,
				params[0],
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.perform) {
			assertNumberOfParams({
				min: 1,
				max: 2
			}, params.length, symbolSci);
			const [effectExpr, payloadExpr] = params;
			const node = withSourceCodeInfo([
				NodeTypes.Perform,
				[effectExpr, payloadExpr],
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		if (specialExpressionType === specialExpressionTypes.object) {
			const entries = [];
			let i = 0;
			while (i < params.length) {
				const param = params[i];
				if (isSpreadNode(param)) {
					entries.push(param);
					i += 1;
				} else {
					const valueParam = params[i + 1];
					if (valueParam === void 0) throw new ParseError("object() requires an even number of non-spread arguments (key-value pairs)", symbolSci);
					entries.push([param, valueParam]);
					i += 2;
				}
			}
			const node = withSourceCodeInfo([
				NodeTypes.Object,
				entries,
				0
			], symbolDebugInfo, ctx);
			ctx.setNodeEnd(node[2]);
			return node;
		}
		const type = specialExpressionType;
		const specialExpression = builtin.specialExpressions[type];
		assertNumberOfParams(specialExpression.arity, params.length, symbolSci);
		switch (type) {
			case specialExpressionTypes["||"]: {
				const node = withSourceCodeInfo([
					NodeTypes.Or,
					params,
					0
				], symbolDebugInfo, ctx);
				for (let i = 1; i < params.length; i++) ctx.clearStructuralLeaf(params[i][2]);
				ctx.setNodeEnd(node[2]);
				return node;
			}
			case specialExpressionTypes["&&"]: {
				const node = withSourceCodeInfo([
					NodeTypes.And,
					params,
					0
				], symbolDebugInfo, ctx);
				for (let i = 1; i < params.length; i++) ctx.clearStructuralLeaf(params[i][2]);
				ctx.setNodeEnd(node[2]);
				return node;
			}
			case specialExpressionTypes["??"]: {
				const node = withSourceCodeInfo([
					NodeTypes.Qq,
					params,
					0
				], symbolDebugInfo, ctx);
				for (let i = 1; i < params.length; i++) ctx.clearStructuralLeaf(params[i][2]);
				ctx.setNodeEnd(node[2]);
				return node;
			}
			case specialExpressionTypes["function"]: throw new ParseError(`${type} is not allowed`, symbolSci);
			/* v8 ignore next 2 */
			default: throw new ParseError(`Unknown special expression: ${type}`, symbolSci);
		}
	} else if (isBuiltinSymbolNode(symbol) || isUserDefinedSymbolNode(symbol)) {
		const node = createNamedNormalExpressionNode(symbol, params, symbolDebugInfo, ctx);
		ctx.setNodeEnd(node[2]);
		return node;
	} else {
		const node = withSourceCodeInfo([
			NodeTypes.Call,
			[symbol, params],
			0
		], symbolDebugInfo, ctx);
		ctx.setNodeEnd(node[2]);
		return node;
	}
}
/**
* Parse the argument to `effect(...)` — a dotted identifier like `llm.complete`
* or `com.myco.human.approve`. Consumes symbol tokens separated by `.` and
* builds the full name string.
*/
function parseEffectArgs(ctx, symbolDebugInfo) {
	const firstToken = ctx.peek();
	if (!isSymbolToken(firstToken)) throw new ParseError("effect expects a dotted name identifier", ctx.resolveTokenDebugInfo(firstToken[2]));
	let name = firstToken[1];
	ctx.advance();
	while (isOperatorToken(ctx.tryPeek(), ".")) {
		ctx.advance();
		const nextToken = ctx.peek();
		if (!isSymbolToken(nextToken)) throw new ParseError("Expected identifier after dot in effect name", ctx.resolveTokenDebugInfo(nextToken[2]));
		name += `.${nextToken[1]}`;
		ctx.advance();
	}
	if (!isRParenToken(ctx.tryPeek())) throw new ParseError("Expected closing parenthesis after effect name", ctx.peekSourceCodeInfo());
	ctx.advance();
	const node = withSourceCodeInfo([
		NodeTypes.Effect,
		name,
		0
	], symbolDebugInfo, ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
//#endregion
//#region src/parser/subParsers/parseObject.ts
function parseObject(ctx) {
	ctx.builder?.startNode("Object");
	const firstToken = asLBraceToken(ctx.tryPeek());
	ctx.advance();
	const entries = [];
	while (!ctx.isAtEnd() && !isRBraceToken(ctx.tryPeek())) {
		if (isOperatorToken(ctx.tryPeek(), "...")) {
			ctx.builder?.startNode("Spread");
			ctx.advance();
			entries.push(withSourceCodeInfo([
				NodeTypes.Spread,
				ctx.parseExpression(),
				0
			], ctx.peekDebugInfo(), ctx));
			ctx.builder?.endNode();
		} else {
			const token = ctx.tryPeek();
			let keyNode;
			if (isTemplateStringToken(token)) keyNode = parseTemplateString(ctx, token);
			else if (isStringToken(token)) keyNode = parseString(ctx, token);
			else if (isSymbolToken(token)) {
				const isQuoted = token[1].startsWith("'");
				const value = isQuoted ? stringFromQuotedSymbol(token[1]) : token[1];
				keyNode = withSourceCodeInfo([
					NodeTypes.Str,
					value,
					0
				], token[2], ctx);
				ctx.advance();
				ctx.setNodeEnd(keyNode[2]);
				if (!isQuoted && !isOperatorToken(ctx.tryPeek(), ":")) {
					const valueNode = withSourceCodeInfo([
						NodeTypes.Sym,
						value,
						0
					], token[2], ctx);
					ctx.setNodeEnd(valueNode[2]);
					entries.push([keyNode, valueNode]);
					const nextToken = ctx.tryPeek();
					if (!isOperatorToken(nextToken, ",") && !isRBraceToken(nextToken)) throw new ParseError("Expected comma or closing brace", ctx.peekSourceCodeInfo());
					if (isOperatorToken(nextToken, ",")) ctx.advance();
					continue;
				}
			} else if (isLBracketToken(token)) {
				ctx.advance();
				keyNode = ctx.parseExpression();
				assertRBracketToken(ctx.tryPeek());
				ctx.advance();
			} else throw new ParseError("Expected key to be a symbol or a string", ctx.peekSourceCodeInfo());
			assertOperatorToken(ctx.tryPeek(), ":");
			ctx.advance();
			const valueNode = ctx.parseExpression();
			entries.push([keyNode, valueNode]);
		}
		const nextToken = ctx.tryPeek();
		if (!isOperatorToken(nextToken, ",") && !isRBraceToken(nextToken)) throw new ParseError("Expected comma or closing brace", ctx.peekSourceCodeInfo());
		if (isOperatorToken(nextToken, ",")) ctx.advance();
	}
	assertRBraceToken(ctx.tryPeek());
	ctx.advance();
	const node = withSourceCodeInfo([
		NodeTypes.Object,
		entries,
		0
	], firstToken[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
//#endregion
//#region src/parser/subParsers/parseOperand.ts
const validDvalaEffects = new Set([
	"dvala.io.print",
	"dvala.io.error",
	"dvala.io.read",
	"dvala.io.readStdin",
	"dvala.io.pick",
	"dvala.io.confirm",
	"dvala.random",
	"dvala.random.uuid",
	"dvala.random.int",
	"dvala.random.item",
	"dvala.random.shuffle",
	"dvala.time.now",
	"dvala.time.zone",
	"dvala.sleep",
	"dvala.error",
	"dvala.checkpoint",
	"dvala.host",
	"dvala.env",
	"dvala.args"
]);
function parseOperand(ctx) {
	const checkpoint = ctx.builder?.checkpoint();
	let operand = parseOperandPart(ctx);
	let token = ctx.tryPeek();
	while (isOperatorToken(token, ".") || isOperatorToken(token, "?.") || isLBracketToken(token) || isLParenToken(token)) if (token[1] === "." || token[1] === "?.") {
		const safe = token[1] === "?.";
		ctx.builder?.startNodeAt(checkpoint, "PropertyAccess");
		ctx.advance();
		const symbolToken = ctx.tryPeek();
		if (!isSymbolToken(symbolToken)) throw new ParseError("Expected symbol", ctx.peekSourceCodeInfo());
		const stringNode = withSourceCodeInfo([
			NodeTypes.Str,
			symbolToken[1],
			0
		], symbolToken[2], ctx);
		if (safe) operand = createAccessorNode(ctx, operand, stringNode, token[2]);
		else operand = createStrictAccessorNode(ctx, operand, stringNode, token[2]);
		ctx.advance();
		ctx.builder?.endNode();
		token = ctx.tryPeek();
	} else if (isLBracketToken(token)) {
		ctx.builder?.startNodeAt(checkpoint, "IndexAccess");
		ctx.advance();
		const expression = ctx.parseExpression();
		if (!isRBracketToken(ctx.tryPeek())) throw new ParseError("Expected closing bracket", ctx.peekSourceCodeInfo());
		operand = createAccessorNode(ctx, operand, expression, token[2]);
		ctx.advance();
		ctx.builder?.endNode();
		token = ctx.tryPeek();
	} else if (isLParenToken(token)) {
		ctx.builder?.startNodeAt(checkpoint, "Call");
		operand = parseFunctionCall(ctx, operand);
		ctx.builder?.endNode();
		token = ctx.tryPeek();
	}
	ctx.setNodeEnd(operand[2]);
	return operand;
}
function parseOperandPart(ctx) {
	const token = ctx.peek();
	if (isLParenToken(token)) {
		if (looksLikeLambda(ctx)) return parseLambdaFunction(ctx);
		ctx.builder?.startNode("Parenthesized");
		ctx.advance();
		const expression = ctx.parseExpression();
		if (!isRParenToken(ctx.peek())) throw new ParseError("Expected closing parenthesis", ctx.peekSourceCodeInfo());
		ctx.advance();
		ctx.setNodeEnd(expression[2]);
		ctx.builder?.endNode();
		return expression;
	} else if (isOperatorToken(token)) {
		const operatorName = token[1];
		if (operatorName === "-") {
			const nextToken = ctx.peekAhead(1);
			const nextType = nextToken?.[0];
			if (nextType === "Number" || nextType === "Symbol" || nextType === "ReservedSymbol" && isNumberReservedSymbol(nextToken[1]) || nextType === "LBracket" || nextType === "LBrace" || nextType === "string" || nextType === "EffectName" || nextType === "BasePrefixedNumber") {
				ctx.builder?.startNode("PrefixOp");
				ctx.advance();
				const operand = parseOperand(ctx);
				const zeroNode = withSourceCodeInfo([
					NodeTypes.Num,
					0,
					0
				], token[2], ctx);
				const minusSymbol = withSourceCodeInfo([
					NodeTypes.Builtin,
					"-",
					0
				], token[2], ctx);
				const node = withSourceCodeInfo([
					NodeTypes.Call,
					[minusSymbol, [zeroNode, operand]],
					0
				], token[2], ctx);
				ctx.setNodeEnd(node[2]);
				ctx.builder?.endNode();
				return node;
			}
		}
		if (operatorName === "!") {
			const nextToken = ctx.peekAhead(1);
			const nextType = nextToken?.[0];
			if (nextType === "Number" || nextType === "BasePrefixedNumber" || nextType === "Symbol" || nextType === "ReservedSymbol" || nextType === "string" || nextType === "TemplateString" || nextType === "LBracket" || nextType === "LBrace" || nextType === "LParen" || nextType === "Atom" || nextType === "EffectName" || nextType === "RegexpShorthand" || nextType === "MacroPrefix" || nextType === "Operator" && (nextToken[1] === "-" || nextToken[1] === "!")) {
				ctx.builder?.startNode("PrefixOp");
				ctx.advance();
				const operand = parseOperand(ctx);
				const bangSymbol = withSourceCodeInfo([
					NodeTypes.Builtin,
					"!",
					0
				], token[2], ctx);
				const node = withSourceCodeInfo([
					NodeTypes.Call,
					[bangSymbol, [operand]],
					0
				], token[2], ctx);
				ctx.setNodeEnd(node[2]);
				ctx.builder?.endNode();
				return node;
			}
			ctx.builder?.startNode("Symbol");
			ctx.advance();
			const node = withSourceCodeInfo([
				NodeTypes.Builtin,
				"!",
				0
			], token[2], ctx);
			ctx.setNodeEnd(node[2]);
			ctx.builder?.endNode();
			return node;
		}
		if (isBinaryOperator(operatorName)) {
			ctx.builder?.startNode("Symbol");
			ctx.advance();
			if (specialExpressionTypes[operatorName] !== void 0) {
				const node = withSourceCodeInfo([
					NodeTypes.Special,
					specialExpressionTypes[operatorName],
					0
				], token[2], ctx);
				ctx.setNodeEnd(node[2]);
				ctx.builder?.endNode();
				return node;
			}
			const node = withSourceCodeInfo([
				NodeTypes.Builtin,
				operatorName,
				0
			], token[2], ctx);
			ctx.setNodeEnd(node[2]);
			ctx.builder?.endNode();
			return node;
		}
		if (operatorName === "->") return parseShorthandLambdaFunction(ctx);
		else throw new ParseError(`Illegal operator: ${operatorName}`, ctx.resolveTokenDebugInfo(token[2]));
	}
	if (isLBraceToken(token)) return parseObject(ctx);
	if (isLBracketToken(token)) return parseArray(ctx);
	if (token[0] === "MacroPrefix") {
		ctx.builder?.startNode("MacroCall");
		const debugInfo = token[2];
		const nodeId = ctx.allocateNodeId(debugInfo);
		ctx.advance();
		const operand = ctx.parseExpression();
		const symNode = withSourceCodeInfo([
			NodeTypes.Sym,
			token[1],
			0
		], debugInfo, ctx);
		const node = [
			NodeTypes.MacroCall,
			[symNode, [operand]],
			nodeId
		];
		ctx.setNodeEnd(nodeId);
		ctx.builder?.endNode();
		return node;
	}
	const tokenType = token[0];
	switch (tokenType) {
		case "Atom": {
			ctx.builder?.startNode("AtomLiteral");
			ctx.advance();
			const atomNode = withSourceCodeInfo([
				NodeTypes.Atom,
				token[1],
				0
			], token[2], ctx);
			ctx.setNodeEnd(atomNode[2]);
			ctx.builder?.endNode();
			return atomNode;
		}
		case "Number":
		case "BasePrefixedNumber": return parseNumber(ctx);
		case "string": return parseString(ctx, token);
		case "TemplateString": return parseTemplateString(ctx, token);
		case "Symbol":
			if (looksLikeLambda(ctx)) return parseLambdaFunction(ctx);
			return parseSymbol(ctx);
		case "ReservedSymbol": return parseReservedSymbol(ctx);
		case "RegexpShorthand": return parseRegexpShorthand(ctx);
		case "EffectName": {
			ctx.builder?.startNode("EffectName");
			const effectName = token[1];
			if (effectName.startsWith("dvala.") && !effectName.includes("*") && !validDvalaEffects.has(effectName)) throw new ParseError(`Unknown dvala effect: '${effectName}'`, ctx.resolveTokenDebugInfo(token[2]));
			ctx.advance();
			const node = withSourceCodeInfo([
				NodeTypes.Effect,
				effectName,
				0
			], token[2], ctx);
			ctx.setNodeEnd(node[2]);
			ctx.builder?.endNode();
			return node;
		}
		default: throw new ParseError(`Unknown token type: ${tokenType}`, ctx.resolveTokenDebugInfo(token[2]));
	}
}
function looksLikeLambda(ctx) {
	const token = ctx.tryPeek();
	if (!token) return false;
	if (isSymbolToken(token)) return isOperatorToken(ctx.peekAhead(1), "->");
	if (isLParenToken(token)) {
		let depth = 1;
		let offset = 1;
		while (depth > 0) {
			const t = ctx.peekAhead(offset);
			if (!t) return false;
			if (isLParenToken(t)) depth++;
			else if (isRParenToken(t)) depth--;
			offset++;
		}
		if (isOperatorToken(ctx.peekAhead(offset), "->")) return true;
		if (isOperatorToken(ctx.peekAhead(offset), ":")) {
			offset++;
			let typeDepth = 0;
			while (ctx.peekAhead(offset)) {
				const t = ctx.peekAhead(offset);
				if (typeDepth === 0 && isOperatorToken(t, "->")) return true;
				if (isLParenToken(t) || isLBracketToken(t)) typeDepth++;
				else if (isRParenToken(t) || isRBracketToken(t)) typeDepth--;
				if (typeDepth === 0 && (isOperatorToken(t, "=") || isOperatorToken(t, ";") || isReservedSymbolToken(t, "end") || isReservedSymbolToken(t, "do"))) break;
				offset++;
			}
		}
		return false;
	}
	return false;
}
function createAccessorNode(ctx, left, right, debugInfo) {
	const node = withSourceCodeInfo([
		NodeTypes.Call,
		[withSourceCodeInfo([
			NodeTypes.Builtin,
			"get",
			0
		], debugInfo, ctx), [left, right]],
		0
	], debugInfo, ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
/**
* Creates a strict property access node: `obj("key")` — a function call
* where the object is called as a function with the key as argument.
* Throws KeyError for missing keys (unlike get which returns null).
*/
function createStrictAccessorNode(ctx, left, right, debugInfo) {
	const node = withSourceCodeInfo([
		NodeTypes.Call,
		[left, [right]],
		0
	], debugInfo, ctx);
	ctx.setNodeEnd(node[2]);
	return node;
}
//#endregion
//#region src/parser/subParsers/parseQuote.ts
/**
* Parse a quote...end block.
*
* quote <body> end
*
* Body is Dvala code captured as AST data. $^{expr} splices escape one level
* back to runtime evaluation. $^^{expr} escapes two levels, etc.
*
* Returns the same CodeTmpl AST node as the old triple-backtick syntax.
*/
let quoteTemplateCounter = 0;
function parseQuote(ctx) {
	ctx.builder?.startNode("Quote");
	const debugInfo = ctx.peek()[2];
	ctx.advance();
	const bodyStartRawPos = ctx.getPosition();
	const bodyStartCstCheckpoint = ctx.builder?.checkpoint();
	const bodyTokens = [];
	let depth = 1;
	let bodyEndRawPos = -1;
	while (!ctx.isAtEnd() && depth > 0) {
		const t = ctx.peek();
		if (bodyTokens.length > 0 && isOperatorToken(bodyTokens[bodyTokens.length - 1], ".") && isSymbolToken(t)) {
			bodyTokens.push(t);
			ctx.advance();
			continue;
		}
		if (t[0] === "QuoteSplice") {
			ctx.builder?.startNode("Splice");
			bodyTokens.push(t);
			ctx.advance();
			let braceDepth = 1;
			while (!ctx.isAtEnd() && braceDepth > 0) {
				const inner = ctx.peek();
				if (isLBraceToken(inner)) braceDepth++;
				else if (isRBraceToken(inner)) braceDepth--;
				bodyTokens.push(inner);
				ctx.advance();
			}
			ctx.builder?.endNode();
			continue;
		}
		if (isBlockOpener(t)) {
			depth++;
			bodyTokens.push(t);
			ctx.advance();
		} else if (isReservedSymbolToken(t, "end")) {
			depth--;
			if (depth > 0) bodyTokens.push(t);
			else bodyEndRawPos = ctx.getPosition();
			ctx.advance();
		} else {
			bodyTokens.push(t);
			ctx.advance();
		}
	}
	if (depth > 0) throw new ParseError("Unterminated quote block — expected `end`", ctx.resolveTokenDebugInfo(debugInfo));
	const resolvedSci = ctx.resolveTokenDebugInfo(debugInfo);
	const templateId = quoteTemplateCounter++;
	const spliceExprs = [];
	const processedTokens = [];
	const blockStack = [];
	let i = 0;
	while (i < bodyTokens.length) {
		const bt = bodyTokens[i];
		if (processedTokens.length > 0 && isOperatorToken(processedTokens[processedTokens.length - 1], ".") && isSymbolToken(bt)) {
			processedTokens.push(bt);
			i++;
			continue;
		}
		if (isBlockOpener(bt)) {
			blockStack.push(isReservedSymbolToken(bt, "quote") ? "quote" : "other");
			processedTokens.push(bt);
			i++;
			continue;
		}
		if (isReservedSymbolToken(bt, "end")) {
			blockStack.pop();
			processedTokens.push(bt);
			i++;
			continue;
		}
		if (bt[0] === "QuoteSplice") {
			const level = countCarets(bt[1]);
			const innerQuoteDepth = blockStack.filter((b) => b === "quote").length;
			const effectiveLevel = level - innerQuoteDepth;
			if (effectiveLevel === 1) {
				i = extractSplice(bodyTokens, i, spliceExprs, processedTokens, templateId, resolvedSci, ctx.allocateId);
				continue;
			} else if (effectiveLevel > 1) throw new ParseError(`Splice level ${level} but only ${innerQuoteDepth + 1} quote level(s) deep`, resolvedSci);
			processedTokens.push(bt);
			i++;
			let braceDepth = 1;
			while (i < bodyTokens.length && braceDepth > 0) {
				const inner = bodyTokens[i];
				if (isLBraceToken(inner)) braceDepth++;
				else if (isRBraceToken(inner)) braceDepth--;
				processedTokens.push(inner);
				i++;
			}
			continue;
		}
		processedTokens.push(bt);
		i++;
	}
	const bodyCtx = createParserContext({ tokens: processedTokens }, ctx.allocateId);
	const bodyAst = [];
	while (!bodyCtx.isAtEnd()) {
		bodyAst.push(parseExpression(bodyCtx, 0));
		if (isOperatorToken(bodyCtx.tryPeek(), ";")) bodyCtx.advance();
	}
	const processedBody = bodyAst.map((node) => replacePlaceholders(node, spliceExprs, 0, templateId));
	if (ctx.builder && bodyEndRawPos >= 0 && bodyStartCstCheckpoint !== void 0) rebuildQuoteBodyCst(ctx, bodyStartRawPos, bodyEndRawPos, bodyStartCstCheckpoint, templateId);
	const resultNode = withSourceCodeInfo([
		NodeTypes.CodeTmpl,
		[processedBody, spliceExprs],
		0
	], debugInfo, ctx);
	ctx.setNodeEnd(resultNode[2]);
	ctx.builder?.endNode();
	return resultNode;
}
/**
* Rebuild the main Quote CST node's body children as a properly structured
* sub-tree (Function/Block/If/etc. sub-nodes) instead of the flat tokens
* emitted by pass 1.
*
* Approach:
*  1. Walk ctx.tokens over the body range, producing a trivia-preserving
*     token stream with this-level splices replaced by placeholder Symbol
*     tokens. Record, for each placeholder, which Splice CST sub-node from
*     the main Quote's body it should be swapped back to.
*  2. Sub-parse the stream with a fresh CstBuilder. The sub-CST has full
*     structure (Function, Block, ...) built by the normal parseExpression
*     path — same machinery that formats any non-quoted expression.
*  3. Walk the sub-CST, replacing placeholder Symbol sub-nodes with the
*     originals captured in step 1. This preserves their leading/trailing
*     trivia (comments around splices survive formatting).
*  4. Truncate the main Quote's children back to where the body started and
*     append the rebuilt children. The Quote node now contains structured
*     body nodes the formatter can recursively wrap.
*
* Splice placeholders use the template-id from pass 2 so this post-processing
* only touches this parseQuote call's splices; nested Quote CST nodes that
* pass through have their own templateId and get rebuilt on their own call.
*/
function rebuildQuoteBodyCst(ctx, bodyStartPos, bodyEndPos, bodyStartCheckpoint, templateId) {
	const builder = ctx.builder;
	const rawBodyChildren = builder.peekCurrent().children.slice(bodyStartCheckpoint);
	const mainSpliceSubNodes = rawBodyChildren.filter(isSpliceCstNode);
	const endToken = rawBodyChildren[rawBodyChildren.length - 1];
	if (!isCstToken(endToken) || endToken.text !== "end") throw new Error("parseQuote.rebuildQuoteBodyCst: expected last body child to be the outer `end` token");
	const { subTokens, placeholderToSplice } = buildSubParseStream(ctx, bodyStartPos, bodyEndPos, templateId, mainSpliceSubNodes);
	const subBuilder = new CstBuilder();
	subBuilder.startNode("QuoteBody");
	const subCtx = createCstParserContext({ tokens: subTokens }, ctx.allocateId, subBuilder);
	while (!subCtx.isAtEnd()) {
		parseExpression(subCtx, 0);
		if (isOperatorToken(subCtx.tryPeek(), ";")) subCtx.advance();
	}
	subBuilder.endNode();
	const subTree = subBuilder.finish();
	replacePlaceholderSymbolsInCst(subTree, templateId, placeholderToSplice);
	const pass1FirstLeaf = rawBodyChildren[0] !== void 0 && !isCstToken(rawBodyChildren[0]) ? findFirstLeafToken(rawBodyChildren[0]) : rawBodyChildren[0];
	if (pass1FirstLeaf && pass1FirstLeaf.leadingTrivia && pass1FirstLeaf.leadingTrivia.length > 0 && subTree.children.length > 0) {
		const subFirst = subTree.children[0];
		const subFirstLeaf = "kind" in subFirst ? findFirstLeafToken(subFirst) : subFirst;
		if (subFirstLeaf && subFirstLeaf.leadingTrivia.length === 0) subFirstLeaf.leadingTrivia = pass1FirstLeaf.leadingTrivia;
	}
	builder.truncateCurrent(bodyStartCheckpoint);
	for (const child of subTree.children) builder.appendChild(child);
	builder.appendChild(endToken);
}
/**
* True when a CST child is a leaf token rather than a sub-node. Tokens carry
* `text` + trivia arrays; sub-nodes carry `kind` + `children`.
*/
function isCstToken(v) {
	return typeof v === "object" && v !== null && "text" in v && !("kind" in v);
}
/** Find the first leaf CstToken in a CST sub-tree (pre-order). */
function findFirstLeafToken(node) {
	for (const child of node.children) {
		if (isCstToken(child)) return child;
		const nested = findFirstLeafToken(child);
		if (nested) return nested;
	}
}
function isSpliceCstNode(child) {
	return typeof child === "object" && child !== null && "kind" in child && child.kind === "Splice";
}
/**
* Walk the body range building a trivia-preserving token stream for the sub-parse.
* This-level splices (effectiveLevel === 1) become placeholder Symbol tokens so
* parseExpression treats them as plain identifiers; nested-level splices pass
* through so the recursive parseQuote call on the inner quote handles them.
*
* The returned placeholderToSplice array is indexed by this-level splice count
* and holds the original main-Quote Splice CST sub-node for each placeholder,
* so the caller can swap them back after sub-parsing.
*/
function buildSubParseStream(ctx, bodyStartPos, bodyEndPos, templateId, mainSpliceSubNodes) {
	const subTokens = [];
	const placeholderToSplice = [];
	const blockStack = [];
	let overallSpliceCount = 0;
	let i = bodyStartPos;
	while (i < bodyEndPos) {
		const t = ctx.getTokenAt(i);
		if (isTrivia(t)) {
			subTokens.push(t);
			i++;
			continue;
		}
		if (subTokens.length > 0 && isOperatorToken(subTokens[subTokens.length - 1], ".") && isSymbolToken(t)) {
			subTokens.push(t);
			i++;
			continue;
		}
		if (isBlockOpener(t)) {
			blockStack.push(isReservedSymbolToken(t, "quote") ? "quote" : "other");
			subTokens.push(t);
			i++;
			continue;
		}
		if (isReservedSymbolToken(t, "end")) {
			blockStack.pop();
			subTokens.push(t);
			i++;
			continue;
		}
		if (t[0] === "QuoteSplice") {
			const effectiveLevel = countCarets(t[1]) - blockStack.filter((b) => b === "quote").length;
			const spliceSubNode = mainSpliceSubNodes[overallSpliceCount];
			overallSpliceCount++;
			if (effectiveLevel === 1) {
				const placeholderName = `__splice_${templateId}_${placeholderToSplice.length}__`;
				const placeholderToken = t[2] ? [
					"Symbol",
					placeholderName,
					t[2]
				] : ["Symbol", placeholderName];
				subTokens.push(placeholderToken);
				if (spliceSubNode) placeholderToSplice.push(spliceSubNode);
				else placeholderToSplice.push({
					kind: "Symbol",
					children: []
				});
				i++;
				let braceDepth = 1;
				while (i < bodyEndPos && braceDepth > 0) {
					const inner = ctx.getTokenAt(i);
					if (isLBraceToken(inner)) braceDepth++;
					else if (isRBraceToken(inner)) braceDepth--;
					i++;
				}
				continue;
			}
			subTokens.push(t);
			i++;
			let braceDepth = 1;
			while (i < bodyEndPos && braceDepth > 0) {
				const inner = ctx.getTokenAt(i);
				if (isLBraceToken(inner)) braceDepth++;
				else if (isRBraceToken(inner)) braceDepth--;
				subTokens.push(inner);
				i++;
			}
			continue;
		}
		subTokens.push(t);
		i++;
	}
	return {
		subTokens,
		placeholderToSplice
	};
}
/**
* Walk a CST sub-tree and, wherever a Symbol node wraps a placeholder token
* named `__splice_${templateId}_N__`, replace the Symbol node with the
* corresponding Splice sub-node supplied by placeholderToSplice[N].
*
* Uses templateId so this only touches placeholders from this parseQuote call —
* nested Quote sub-nodes with their own templateId are left alone.
*/
function replacePlaceholderSymbolsInCst(node, templateId, placeholderToSplice) {
	for (let i = 0; i < node.children.length; i++) {
		const child = node.children[i];
		if (!("kind" in child)) continue;
		if (child.kind === "Symbol" && child.children.length === 1) {
			const inner = child.children[0];
			if (inner && "text" in inner) {
				const match = inner.text.match(/^__splice_(\d+)_(\d+)__$/);
				if (match && Number(match[1]) === templateId) {
					const splice = placeholderToSplice[Number(match[2])];
					if (splice) node.children[i] = splice;
					continue;
				}
			}
		}
		replacePlaceholderSymbolsInCst(child, templateId, placeholderToSplice);
	}
}
/** Count the number of ^ characters in a QuoteSplice value like "$^^{" */
function countCarets(value) {
	let count = 0;
	for (let j = 1; j < value.length; j++) if (value[j] === "^") count++;
	else break;
	return count;
}
/**
* Extract a splice expression from bodyTokens starting at position i (a QuoteSplice token).
* Parses the expression tokens between the splice opener and matching RBrace.
* Pushes a placeholder Symbol token into processedTokens.
* Returns the new position in bodyTokens (after the closing RBrace).
*/
function extractSplice(bodyTokens, i, spliceExprs, processedTokens, templateId, sci, allocateId) {
	const spliceDebugInfo = bodyTokens[i][2];
	i++;
	const exprTokens = [];
	let braceDepth = 1;
	while (i < bodyTokens.length && braceDepth > 0) {
		const t = bodyTokens[i];
		if (isLBraceToken(t)) braceDepth++;
		else if (isRBraceToken(t)) {
			braceDepth--;
			if (braceDepth === 0) {
				i++;
				break;
			}
		}
		exprTokens.push(t);
		i++;
	}
	if (braceDepth > 0) throw new ParseError("Unclosed splice expression in quote block", sci);
	if (exprTokens.length === 0) throw new ParseError("Empty splice expression in quote block", sci);
	const expr = parseExpression(createParserContext({ tokens: exprTokens }, allocateId), 0);
	const index = spliceExprs.length;
	spliceExprs.push(expr);
	const placeholder = spliceDebugInfo ? [
		"Symbol",
		`__splice_${templateId}_${index}__`,
		spliceDebugInfo
	] : ["Symbol", `__splice_${templateId}_${index}__`];
	processedTokens.push(placeholder);
	return i;
}
/** Check if a token opens a block that ends with 'end'. */
function isBlockOpener(t) {
	if (isReservedSymbolToken(t, "do")) return true;
	if (isSymbolToken(t, "handler")) return true;
	if (isReservedSymbolToken(t, "quote")) return true;
	if (isSymbolToken(t, "if")) return true;
	if (isSymbolToken(t, "match")) return true;
	if (isSymbolToken(t, "for")) return true;
	if (isSymbolToken(t, "loop")) return true;
	return false;
}
/**
* Recursively walk an AST node and replace UserDefinedSymbol nodes
* named `__splice_N_M__` with Splice nodes referencing the Mth splice expression.
*/
function replacePlaceholders(node, spliceExprs, indexOffset = 0, templateId) {
	const [type, payload, nodeId] = node;
	if (type === NodeTypes.Sym && typeof payload === "string") {
		const match = payload.match(/^__splice_(\d+)_(\d+)__$/);
		if (match && (templateId === void 0 || parseInt(match[1], 10) === templateId)) {
			const index = parseInt(match[2], 10) + indexOffset;
			return [
				NodeTypes.Splice,
				index,
				nodeId
			];
		}
	}
	if (type === NodeTypes.CodeTmpl && Array.isArray(payload)) {
		const [bodyAst, innerSpliceExprs] = payload;
		const innerCount = innerSpliceExprs.length;
		return [
			type,
			[bodyAst.map((n) => replacePlaceholders(n, spliceExprs, innerCount, templateId)), innerSpliceExprs],
			nodeId
		];
	}
	if (Array.isArray(payload)) return [
		type,
		payload.map((item) => Array.isArray(item) ? replacePlaceholdersInValue(item, spliceExprs, indexOffset, templateId) : item),
		nodeId
	];
	return node;
}
function replacePlaceholdersInValue(value, spliceExprs, indexOffset = 0, templateId) {
	if (value.length >= 2 && typeof value[0] === "string") return replacePlaceholders(value, spliceExprs, indexOffset, templateId);
	return value.map((item) => Array.isArray(item) ? replacePlaceholdersInValue(item, spliceExprs, indexOffset, templateId) : item);
}
//#endregion
//#region src/parser/subParsers/parseExpression.ts
function createParserContext(tokenStream, allocateId) {
	const ctx = new ParserContext(tokenStream, allocateId);
	ctx.parseExpression = (precedence = 0) => parseExpression(ctx, precedence);
	return ctx;
}
function createCstParserContext(tokenStream, allocateId, builder) {
	const ctx = new ParserContext(tokenStream, allocateId, builder);
	ctx.parseExpression = (precedence = 0) => parseExpression(ctx, precedence);
	return ctx;
}
function parseExpression(ctx, precedence = 0) {
	const checkpoint = ctx.builder?.checkpoint();
	const token = ctx.tryPeek();
	let left;
	if (isSymbolToken(token)) switch (token[1]) {
		case "let": return parseLet(ctx, token);
		case "if":
			left = parseIf(ctx, token);
			break;
		case "match":
			left = parseMatch(ctx, token);
			break;
		case "for":
			left = parseForOrDoseq(ctx, token);
			break;
		case "loop":
			left = parseLoop(ctx, token);
			break;
		case "macro":
			left = parseMacro(ctx);
			break;
		case "handler":
			if (isHandlerStart(ctx)) left = parseHandler(ctx);
			break;
		case "shallow":
			if (isShallowHandlerStart(ctx)) {
				ctx.builder?.startNode("Handler");
				ctx.advance();
				let linear = false;
				if (isSymbolToken(ctx.tryPeek(), "linear")) {
					ctx.advance();
					linear = true;
				}
				left = parseHandler(ctx, true, linear);
			}
			break;
		case "linear":
			if (isLinearHandlerStart(ctx)) {
				ctx.builder?.startNode("Handler");
				ctx.advance();
				let shallow = false;
				if (isSymbolToken(ctx.tryPeek(), "shallow")) {
					ctx.advance();
					shallow = true;
				}
				left = parseHandler(ctx, shallow, true);
			}
			break;
		case "resume":
			if (isResumeStart(ctx)) left = parseResume(ctx);
			break;
		case "effect":
			if (isEffectNameToken(ctx.peekAhead(1))) left = parseEffectDeclaration(ctx);
			break;
		case "type":
			if (isSymbolToken(ctx.peekAhead(1)) && /^[A-Z]/.test(ctx.peekAhead(1)[1])) left = parseTypeDeclaration(ctx);
			break;
	}
	else if (isReservedSymbolToken(token, "do")) left = parseDo(ctx);
	else if (isReservedSymbolToken(token, "quote")) left = parseQuote(ctx);
	left ||= parseOperand(ctx);
	let operator = ctx.tryPeek();
	while (!isAtExpressionEnd(ctx)) {
		if (isA_BinaryOperatorToken(operator)) {
			const name = operator[1];
			const newPrecedece = getPrecedence(name, ctx.resolveTokenDebugInfo(operator[2]));
			if (newPrecedece <= precedence && !(newPrecedece === 12 && precedence === 12)) break;
			ctx.builder?.startNodeAt(checkpoint, "BinaryOp");
			const symbol = specialExpressionTypes[name] ? withSourceCodeInfo([
				NodeTypes.Special,
				specialExpressionTypes[name],
				0
			], operator[2], ctx) : withSourceCodeInfo([
				NodeTypes.Builtin,
				name,
				0
			], operator[2], ctx);
			ctx.advance();
			const right = parseExpression(ctx, newPrecedece);
			const callRangeStart = nodeStartDebugInfo(left, ctx) ?? operator[2];
			left = fromBinaryOperatorToNode(operator, symbol, left, right, callRangeStart, ctx);
			ctx.builder?.endNode();
		} else if (isSymbolToken(operator)) {
			if (!isFunctionOperator(operator[1])) break;
			const newPrecedence = 3;
			if (newPrecedence <= precedence) break;
			ctx.builder?.startNodeAt(checkpoint, "BinaryOp");
			const operatorSymbol = parseSymbol(ctx);
			const right = parseExpression(ctx, newPrecedence);
			if (isSpecialSymbolNode(operatorSymbol)) throw new ParseError("Special expressions are not allowed in binary functional operators", ctx.resolveTokenDebugInfo(operator[2]));
			left = createNamedNormalExpressionNode(operatorSymbol, [left, right], operator[2], ctx, { isInfix: true });
			ctx.builder?.endNode();
		} else break;
		operator = ctx.tryPeek();
	}
	ctx.setNodeEnd(left[2]);
	return left;
}
/**
* Check if `handler` token starts a handler expression (not a variable reference).
* Handler expression: `handler @effect... end` or `handler transform... end` or `handler end`.
* The lookahead checks the token after `handler`.
*/
/**
* Check if `shallow handler` starts a shallow handler expression. Also
* accepts `shallow linear handler` (modifiers in either order).
* Requires: current token = `shallow`, then `[linear?] handler @effect|transform`.
*/
function isShallowHandlerStart(ctx) {
	let handlerOffset = 1;
	if (isSymbolToken(ctx.peekAhead(1), "linear")) handlerOffset = 2;
	if (!isSymbolToken(ctx.peekAhead(handlerOffset), "handler")) return false;
	const afterHandler = ctx.peekAhead(handlerOffset + 1);
	if (!afterHandler) return false;
	if (isEffectNameToken(afterHandler)) return true;
	if (isSymbolToken(afterHandler, "transform")) return true;
	return false;
}
/**
* Check if `linear handler` starts a linear handler expression. Also
* accepts `linear shallow handler` (modifiers in either order).
* Requires: current token = `linear`, then `[shallow?] handler @effect|transform`.
*/
function isLinearHandlerStart(ctx) {
	let handlerOffset = 1;
	if (isSymbolToken(ctx.peekAhead(1), "shallow")) handlerOffset = 2;
	if (!isSymbolToken(ctx.peekAhead(handlerOffset), "handler")) return false;
	const afterHandler = ctx.peekAhead(handlerOffset + 1);
	if (!afterHandler) return false;
	if (isEffectNameToken(afterHandler)) return true;
	if (isSymbolToken(afterHandler, "transform")) return true;
	return false;
}
function isHandlerStart(ctx) {
	const next = ctx.peekAhead(1);
	if (!next) return false;
	if (isEffectNameToken(next)) return true;
	if (isSymbolToken(next, "transform")) return true;
	return false;
}
/**
* Check if `resume` token starts a resume expression (not a variable reference).
* Resume expression: `resume(...)` or bare `resume` at expression end.
* When `resume` is followed by `(`, it's always a resume call (not a variable call).
* When at expression end, it's a bare resume reference.
*/
function isResumeStart(ctx) {
	const next = ctx.peekAhead(1);
	if (isLParenToken(next)) return true;
	if (!next) return true;
	if (isReservedSymbolToken(next)) return true;
	if (isA_BinaryOperatorToken(next)) return true;
	return false;
}
/**
* Parse `resume`, `resume()`, or `resume(value)`.
*
* ResumeNode payload encoding:
* - `resume(value)`: the argument AstNode
* - `resume()`: a Null literal node (call with no arg → resume with null)
* - bare `resume`: special string 'ref' (reference to resume function)
*/
function parseResume(ctx) {
	ctx.builder?.startNode("Resume");
	const token = ctx.tryPeek();
	ctx.advance();
	if (isLParenToken(ctx.tryPeek())) {
		ctx.advance();
		let argNode;
		if (!isRParenToken(ctx.tryPeek())) argNode = ctx.parseExpression();
		else argNode = withSourceCodeInfo([
			NodeTypes.Reserved,
			"null",
			0
		], token[2], ctx);
		if (!isRParenToken(ctx.tryPeek())) throw new ParseError("Expected \")\" after resume argument", ctx.peekSourceCodeInfo());
		ctx.advance();
		const node = withSourceCodeInfo([
			NodeTypes.Resume,
			argNode,
			0
		], token[2], ctx);
		ctx.setNodeEnd(node[2]);
		ctx.builder?.endNode();
		return node;
	}
	const node = withSourceCodeInfo([
		NodeTypes.Resume,
		"ref",
		0
	], token[2], ctx);
	ctx.setNodeEnd(node[2]);
	ctx.builder?.endNode();
	return node;
}
//#endregion
//#region src/parser/index.ts
/**
* Parse the full token stream into an untyped CST tree.
*
* The token stream must include whitespace and comment tokens (debug mode).
* The parser runs in CST mode: every consumed token is wrapped in a CstToken
* with attached trivia and fed to the CstBuilder.
*
* Throws on parse error — no error recovery (CstErrorNode is a future concern).
*/
function parseToCst(fullTokenStream) {
	for (const token of fullTokenStream.tokens) if (token[0] === "Error") throw new ParseError(token[3], debugInfoToSourceCodeInfo(token[2], fullTokenStream.source, fullTokenStream.filePath));
	const builder = new CstBuilder();
	let localCounter = 0;
	const ctx = createCstParserContext(fullTokenStream, () => localCounter++, builder);
	builder.startNode("Program");
	while (!ctx.isAtEnd()) {
		parseExpression(ctx, 0);
		if (isOperatorToken(ctx.tryPeek(), ";")) ctx.advance();
		else if (!ctx.isAtEnd()) throw new ParseError("Expected ;", ctx.peekSourceCodeInfo());
	}
	builder.endNode();
	return {
		tree: builder.finish(),
		trailingTrivia: ctx.getRemainingTrivia()
	};
}
function parseInternal(tokenStream, allocateId) {
	tokenStream.tokens.forEach((token) => {
		if (token[0] === "Error") throw new ParseError(token[3], debugInfoToSourceCodeInfo(token[2], tokenStream.source, tokenStream.filePath));
	});
	const nodes = [];
	let localCounter = 0;
	const ctx = createParserContext(tokenStream, allocateId ?? (() => localCounter++));
	while (!ctx.isAtEnd()) {
		nodes.push(parseExpression(ctx, 0));
		if (isOperatorToken(ctx.tryPeek(), ";")) ctx.advance();
		else if (!ctx.isAtEnd()) throw new ParseError("Expected ;", ctx.peekSourceCodeInfo());
	}
	const typeAnnotations = ctx.typeAnnotations.size > 0 ? ctx.typeAnnotations : void 0;
	const effectDeclarations = ctx.effectDeclarations.size > 0 ? ctx.effectDeclarations : void 0;
	const typeAliases = ctx.typeAliases.size > 0 ? ctx.typeAliases : void 0;
	const typeParams = ctx.typeParams.size > 0 ? ctx.typeParams : void 0;
	return {
		nodes,
		sourceMap: ctx.sourceMap,
		typeAnnotations,
		effectDeclarations,
		typeAliases,
		typeParams
	};
}
function parse(tokenStream, allocateId) {
	return parseInternal(tokenStream, allocateId).nodes;
}
function parseToAst(tokenStream, allocateId) {
	const { nodes, sourceMap, typeAnnotations, effectDeclarations, typeAliases, typeParams } = parseInternal(tokenStream, allocateId);
	return {
		body: nodes,
		sourceMap,
		typeAnnotations,
		effectDeclarations,
		typeAliases,
		typeParams
	};
}
/**
* Parse with statement-level error recovery.
* When a statement fails to parse, the error is collected and the parser
* skips to the next semicolon to continue parsing subsequent statements.
* This produces a partial AST that the language service can use even when
* the file has syntax errors.
*/
function parseRecoverable(tokenStream, allocateId) {
	const errors = [];
	for (const token of tokenStream.tokens) if (token[0] === "Error") errors.push(new ParseError(token[3], debugInfoToSourceCodeInfo(token[2], tokenStream.source, tokenStream.filePath)));
	const nodes = [];
	let localCounter = 0;
	const ctx = createParserContext(tokenStream, allocateId ?? (() => localCounter++));
	while (!ctx.isAtEnd()) try {
		nodes.push(parseExpression(ctx, 0));
		if (isOperatorToken(ctx.tryPeek(), ";")) ctx.advance();
		else if (!ctx.isAtEnd()) throw new ParseError("Expected ;", ctx.peekSourceCodeInfo());
	} catch (e) {
		if (e instanceof ParseError) errors.push(e);
		else errors.push(new ParseError(`${e}`, ctx.peekSourceCodeInfo()));
		skipToNextStatement(ctx);
	}
	return {
		body: nodes,
		sourceMap: ctx.sourceMap,
		errors
	};
}
/**
* Advance the parser past the current failed statement by scanning forward
* through tokens until a semicolon is found (and consumed) or end of input.
*/
function skipToNextStatement(ctx) {
	let depth = 0;
	while (!ctx.isAtEnd()) {
		const token = ctx.tryPeek();
		if (!token) break;
		const type = token[0];
		if (type === "LParen" || type === "LBracket" || type === "LBrace") depth++;
		else if (type === "RParen" || type === "RBracket" || type === "RBrace") depth = Math.max(0, depth - 1);
		else if (depth === 0 && isOperatorToken(token, ";")) {
			ctx.advance();
			return;
		}
		ctx.advance();
	}
}
//#endregion
//#region src/AutoCompleter/AutoCompleter.ts
const dvalaCommands = new Set([
	...normalExpressionKeys,
	...specialExpressionKeys,
	...Object.keys(reservedSymbolRecord)
]);
const DOT_PREFIX_RE = /((?:[a-zA-Z][a-zA-Z0-9_-]*\.)+)$/;
var AutoCompleter = class {
	originalProgram;
	originalPosition;
	prefixProgram = "";
	suffixProgram = "";
	searchString = "";
	dotPrefix = "";
	suggestions = [];
	suggestionIndex = null;
	constructor(originalProgram, originalPosition, params = {}) {
		this.originalProgram = originalProgram;
		this.originalPosition = originalPosition;
		const lastToken = tokenize(this.originalProgram.slice(0, this.originalPosition), false, void 0).tokens.at(-1);
		if (!lastToken) return;
		if (lastToken[0] === "Error") return;
		this.searchString = lastToken[1];
		this.prefixProgram = this.originalProgram.slice(0, this.originalPosition - this.searchString.length);
		this.suffixProgram = this.originalProgram.slice(this.prefixProgram.length + this.searchString.length);
		this.originalProgram.slice(this.prefixProgram.length + this.searchString.length);
		if (lastToken[0] === "Operator" && this.searchString === ".") {
			this.prefixProgram = this.originalProgram.slice(0, this.originalPosition);
			this.suffixProgram = this.originalProgram.slice(this.originalPosition);
			this.searchString = "";
		}
		const dotPrefixMatch = DOT_PREFIX_RE.exec(this.prefixProgram);
		this.dotPrefix = dotPrefixMatch?.[1] ?? "";
		this.suggestions = this.generateSuggestions(params);
	}
	getNextSuggestion() {
		return this.getAutoCompleteSuggestionResult(this.getNextSuggestionSymbol());
	}
	getPreviousSuggestion() {
		return this.getAutoCompleteSuggestionResult(this.getPreviousSuggestionSymbol());
	}
	getAutoCompleteSuggestionResult(suggestion) {
		if (suggestion === null) return null;
		return {
			program: this.prefixProgram + suggestion + this.suffixProgram,
			position: this.prefixProgram.length + suggestion.length
		};
	}
	getNextSuggestionSymbol() {
		if (this.suggestions.length === 0) return null;
		if (this.suggestionIndex === null) this.suggestionIndex = 0;
		else {
			this.suggestionIndex += 1;
			if (this.suggestionIndex >= this.suggestions.length) this.suggestionIndex = 0;
		}
		return this.suggestions[this.suggestionIndex];
	}
	getPreviousSuggestionSymbol() {
		if (this.suggestions.length === 0) return null;
		if (this.suggestionIndex === null) this.suggestionIndex = this.suggestions.length - 1;
		else {
			this.suggestionIndex -= 1;
			if (this.suggestionIndex < 0) this.suggestionIndex = this.suggestions.length - 1;
		}
		return this.suggestions[this.suggestionIndex];
	}
	getSuggestions() {
		return [...this.suggestions];
	}
	getSearchString() {
		return this.searchString;
	}
	generateSuggestions(params) {
		const blacklist = new Set(["0_defn", "function"]);
		const fullSearch = this.dotPrefix + this.searchString;
		if (this.dotPrefix)
 /* v8 ignore next -- both ?? branches tested, v8 misreports nullish coalescing */
		return this.generateDottedEffectSuggestions(params.effectNames ?? [], fullSearch);
		const startsWithCaseSensitive = this.generateWithPredicate(params, (suggestion) => !blacklist.has(suggestion) && suggestion.startsWith(this.searchString));
		startsWithCaseSensitive.forEach((suggestion) => blacklist.add(suggestion));
		const startsWithCaseInsensitive = this.generateWithPredicate(params, (suggestion) => !blacklist.has(suggestion) && suggestion.toLowerCase().startsWith(this.searchString.toLowerCase()));
		startsWithCaseInsensitive.forEach((suggestion) => blacklist.add(suggestion));
		const includesCaseSensitive = this.generateWithPredicate(params, (suggestion) => !blacklist.has(suggestion) && suggestion.includes(this.searchString));
		includesCaseSensitive.forEach((suggestion) => blacklist.add(suggestion));
		const includesCaseInsensitive = this.generateWithPredicate(params, (suggestion) => !blacklist.has(suggestion) && suggestion.includes(this.searchString.toLowerCase()));
		includesCaseInsensitive.forEach((suggestion) => blacklist.add(suggestion));
		return [
			...startsWithCaseSensitive,
			...startsWithCaseInsensitive,
			...includesCaseSensitive,
			...includesCaseInsensitive
		];
	}
	generateDottedEffectSuggestions(effectNames, fullSearch) {
		const seen = /* @__PURE__ */ new Set();
		const results = [];
		const predicates = [
			(name) => name.startsWith(fullSearch),
			(name) => name.toLowerCase().startsWith(fullSearch.toLowerCase()),
			(name) => name.includes(fullSearch),
			(name) => name.toLowerCase().includes(fullSearch.toLowerCase())
		];
		for (const pred of predicates) for (const name of effectNames) {
			const insertText = name.slice(this.dotPrefix.length);
			if (insertText && !seen.has(insertText) && pred(name)) {
				results.push(insertText);
				seen.add(insertText);
			}
		}
		return results;
	}
	generateWithPredicate(params, shouldInclude) {
		const suggestions = /* @__PURE__ */ new Set();
		dvalaCommands.forEach((suggestion) => {
			if (shouldInclude(suggestion)) suggestions.add(suggestion);
		});
		params.effectNames?.forEach((name) => {
			if (shouldInclude(name)) suggestions.add(name);
		});
		Object.keys(params.scope ?? {}).forEach((name) => {
			if (shouldInclude(name)) suggestions.add(name);
		});
		return [...suggestions].sort((a, b) => a.localeCompare(b));
	}
};
//#endregion
//#region src/untokenizer/index.ts
function untokenize(tokenStream) {
	return tokenStream.tokens.reduce((acc, token) => {
		return `${acc}${token[0] === "EffectName" ? "@" : ""}${token[1]}`;
	}, "");
}
//#endregion
//#region src/formatter/doc.ts
/** Literal text — never broken. */
function text(s) {
	return {
		type: "text",
		text: s
	};
}
/** In flat mode: space. In break mode: newline + indent. */
const line = { type: "line" };
/** In flat mode: nothing. In break mode: newline + indent. */
const softLine = { type: "softline" };
/** Always a newline + indent. Forces enclosing Group to break. */
const hardLine = { type: "hardline" };
/** A line comment — text followed by a mandatory hard break. */
function lineComment(s) {
	return {
		type: "lineComment",
		text: s
	};
}
/** Concatenate multiple docs. Flattens nested concats. */
function concat(...parts) {
	const flat = [];
	for (const part of parts) if (part.type === "concat") flat.push(...part.parts);
	else if (part.type === "text" && part.text === "") continue;
	else flat.push(part);
	if (flat.length === 0) return text("");
	if (flat.length === 1) return flat[0];
	return {
		type: "concat",
		parts: flat
	};
}
/** Increase indent by `n` for the inner doc. */
function nest(n, doc) {
	return {
		type: "nest",
		indent: n,
		doc
	};
}
/** Try to render `doc` flat (on one line). If it doesn't fit, break. */
function group(doc) {
	return {
		type: "group",
		doc
	};
}
/** Render `broken` when the enclosing Group breaks, `flat` when it fits. */
function ifBreak(broken, flat) {
	return {
		type: "ifBreak",
		broken,
		flat
	};
}
/** Join docs with a separator between each pair. */
function join(sep, docs) {
	if (docs.length === 0) return text("");
	const parts = [docs[0]];
	for (let i = 1; i < docs.length; i++) parts.push(sep, docs[i]);
	return concat(...parts);
}
/** Mode of a command on the rendering stack. */
const FLAT = 0;
const BREAK = 1;
/**
* Render a Doc tree to a string.
*
* Uses the Wadler-Lindig "best fit" algorithm: a stack-based traversal
* that decides, for each Group, whether to print it flat or broken.
*
* @param doc The document tree to render.
* @param width Target line width (default 80).
*/
function render(doc, width = 80) {
	const output = [];
	let pos = 0;
	const stack = [[
		0,
		BREAK,
		doc
	]];
	while (stack.length > 0) {
		const [indent, mode, d] = stack.pop();
		switch (d.type) {
			case "text":
				output.push(d.text);
				pos += d.text.length;
				break;
			case "line":
				if (mode === FLAT) {
					output.push(" ");
					pos += 1;
				} else {
					output.push(`\n${" ".repeat(indent)}`);
					pos = indent;
				}
				break;
			case "softline":
				if (mode === FLAT) {} else {
					output.push(`\n${" ".repeat(indent)}`);
					pos = indent;
				}
				break;
			case "hardline":
				output.push(`\n${" ".repeat(indent)}`);
				pos = indent;
				break;
			case "lineComment":
				output.push(d.text);
				output.push(`\n${" ".repeat(indent)}`);
				pos = indent;
				break;
			case "concat":
				for (let i = d.parts.length - 1; i >= 0; i--) stack.push([
					indent,
					mode,
					d.parts[i]
				]);
				break;
			case "nest":
				stack.push([
					indent + d.indent,
					mode,
					d.doc
				]);
				break;
			case "group":
				if (fits$1(width - pos, [[
					indent,
					FLAT,
					d.doc
				]])) stack.push([
					indent,
					FLAT,
					d.doc
				]);
				else stack.push([
					indent,
					BREAK,
					d.doc
				]);
				break;
			case "ifBreak":
				if (mode === BREAK) stack.push([
					indent,
					mode,
					d.broken
				]);
				else stack.push([
					indent,
					mode,
					d.flat
				]);
				break;
		}
	}
	return output.join("");
}
/**
* Check if a document fits within `remaining` columns when rendered flat.
* Stops early on encountering a HardLine or LineComment (never fits flat).
*/
function fits$1(remaining, stack) {
	let rem = remaining;
	while (stack.length > 0 && rem >= 0) {
		const [indent, mode, d] = stack.pop();
		switch (d.type) {
			case "text":
				rem -= d.text.length;
				break;
			case "line":
				if (mode === FLAT) rem -= 1;
				else return true;
				break;
			case "softline":
				if (mode === BREAK) return true;
				break;
			case "hardline": return false;
			case "lineComment": return false;
			case "concat":
				for (let i = d.parts.length - 1; i >= 0; i--) stack.push([
					indent,
					mode,
					d.parts[i]
				]);
				break;
			case "nest":
				stack.push([
					indent + d.indent,
					mode,
					d.doc
				]);
				break;
			case "group":
				stack.push([
					indent,
					FLAT,
					d.doc
				]);
				break;
			case "ifBreak":
				if (mode === BREAK) stack.push([
					indent,
					mode,
					d.broken
				]);
				else stack.push([
					indent,
					mode,
					d.flat
				]);
				break;
		}
	}
	return rem >= 0;
}
//#endregion
//#region src/formatter/cstFormat.ts
const INDENT = 2;
function isToken(child) {
	return "text" in child && !("kind" in child);
}
function isNode(child) {
	return "kind" in child;
}
/** Get the Nth token from a node's children. */
function nthToken(node, n) {
	let count = 0;
	for (const child of node.children) if (isToken(child)) {
		if (count === n) return child;
		count++;
	}
	throw new Error(`No token at index ${n} in ${node.kind}`);
}
/** Get the Nth child node from a node's children. */
function nthChild(node, n) {
	let count = 0;
	for (const child of node.children) if (isNode(child)) {
		if (count === n) return child;
		count++;
	}
	throw new Error(`No child node at index ${n} in ${node.kind}`);
}
/** Get all child nodes. */
function childNodes(node) {
	return node.children.filter(isNode);
}
/** Get all tokens. */
function tokens(node) {
	return node.children.filter(isToken);
}
/** Extract comment Docs from trivia, ignoring whitespace. */
function triviaComments(trivia) {
	const docs = [];
	for (const t of trivia) if (t.kind === "lineComment") docs.push(lineComment(t.text));
	else if (t.kind === "blockComment") docs.push(text(t.text));
	return docs;
}
/** Emit trailing comment trivia of a token as Docs. */
function trailingComments(token) {
	return triviaComments(token.trailingTrivia);
}
/** Collect leading block comment Docs from a trivia array. */
function leadingBlockComments(trivia) {
	const docs = [];
	for (const t of trivia) if (t.kind === "blockComment") docs.push(text(t.text));
	return docs;
}
/** Prepend leading block comments to a Doc. Returns the doc unchanged if none. */
function withLeadingBlockComments(trivia, doc) {
	const cmts = leadingBlockComments(trivia);
	if (cmts.length === 0) return doc;
	return concat(...cmts.flatMap((c) => [c, text(" ")]), doc);
}
/**
* Format a token with trailing block comments preserved.
*
* Only emits TRAILING block comments. Leading block comments are NOT emitted
* here — at statement boundaries they're handled by containers (program, body),
* and elsewhere by `formatClosingToken` or `formatExprChild`.
*
* Trailing line comments are NOT emitted — they force line breaks that conflict
* with the enclosing layout. Containers handle trailing line comments.
*/
function formatTokenWithTrivia(token) {
	const tokenDoc = text(token.text);
	const trailingBlocks = [];
	for (const t of token.trailingTrivia) if (t.kind === "blockComment") trailingBlocks.push(text(t.text));
	if (trailingBlocks.length === 0) return tokenDoc;
	const parts = [tokenDoc];
	for (const c of trailingBlocks) parts.push(text(" "), c);
	return concat(...parts);
}
/**
* Format a token with both leading AND trailing block comments.
*
* Use for tokens that are NOT at statement boundaries — closing delimiters
* (`end`, `else`, `)`, `}`), tokens inside binding patterns, and other
* expression-internal tokens where no container handles leading trivia.
*/
function formatClosingToken(token) {
	return withLeadingBlockComments(token.leadingTrivia, formatTokenWithTrivia(token));
}
/**
* Get the trailing line comment from the last token of a subtree, if any.
* Used by containers to emit trailing line comments after semicolons.
*/
function getTrailingLineComment(node) {
	const tok = lastToken(node);
	for (const t of tok.trailingTrivia) if (t.kind === "lineComment") return t.text;
}
/**
* Format a child node inside an expression with leading block comments.
*
* Use for expression-internal child nodes (call args, array elements, binary
* operands, object values) where no container handles leading block comments
* on the node's first token.
*/
function formatExprChild(node) {
	return withLeadingBlockComments(firstToken(node).leadingTrivia, formatNode(node));
}
/**
* Count the number of blank lines in a trivia array.
* A blank line is an empty line (two consecutive newlines in a whitespace node).
* Returns 0 if no blank lines are found.
*/
function countBlankLines(trivia) {
	let total = 0;
	for (const t of trivia) if (t.kind === "whitespace") {
		let newlines = 0;
		for (const ch of t.text) if (ch === "\n") newlines++;
		if (newlines >= 2) total += newlines - 1;
	}
	return total;
}
/**
* Count blank lines in the gap between two tokens.
*
* Trivia split puts some newlines on the trailing side and the rest on the
* leading side. To get the true blank-line count we must count all newlines
* across both sides and subtract 1 (the statement-separating newline).
*/
function countBlankLinesBetweenTokens(prevTrailing, nextLeading) {
	let totalNewlines = 0;
	const lastTrailing = prevTrailing[prevTrailing.length - 1];
	if (lastTrailing?.kind === "whitespace") {
		for (const ch of lastTrailing.text) if (ch === "\n") totalNewlines++;
	}
	const firstLeading = nextLeading[0];
	if (firstLeading?.kind === "whitespace") {
		for (const ch of firstLeading.text) if (ch === "\n") totalNewlines++;
	}
	return Math.max(0, totalNewlines - 1);
}
/** Push up to MAX_BLANK_LINES extra hardLines into `parts` to represent blank lines. */
function pushBlankLines(parts, count) {
	const clamped = Math.min(count, 1);
	for (let i = 0; i < clamped; i++) parts.push(hardLine);
}
function firstToken(node) {
	for (const child of node.children) {
		if (isToken(child)) return child;
		if (isNode(child)) return firstToken(child);
	}
	throw new Error(`No tokens in ${node.kind}`);
}
function lastToken(node) {
	for (let i = node.children.length - 1; i >= 0; i--) {
		const child = node.children[i];
		if (isToken(child)) return child;
		if (isNode(child)) return lastToken(child);
	}
	throw new Error(`No tokens in ${node.kind}`);
}
/**
* Iterator over an UntypedCstNode's children. Provides helpers to consume
* tokens by text and collect child nodes, making it easy to destructure
* untyped tree children positionally.
*/
var ChildIterator = class {
	children;
	pos = 0;
	constructor(children) {
		this.children = children;
	}
	/** True if all children have been consumed. */
	done() {
		return this.pos >= this.children.length;
	}
	/** Peek at the current child without consuming it. */
	peek() {
		return this.children[this.pos];
	}
	/** Consume and return the next child (token or node). */
	next() {
		return this.children[this.pos++];
	}
	/** Consume the next child, expecting it to be a token with the given text. */
	expectToken(tokenText) {
		const child = this.next();
		if (!isToken(child) || child.text !== tokenText) throw new Error(`Expected token '${tokenText}', got ${isToken(child) ? `'${child.text}'` : `node '${child.kind}'`}`);
		return child;
	}
	/** Consume a token with known text and return a Doc with trailing block comments. */
	emitToken(tokenText) {
		return formatTokenWithTrivia(this.expectToken(tokenText));
	}
	/**
	* Consume a token with known text and return a Doc with both leading
	* AND trailing block comments. Use for closing delimiters and tokens
	* that are NOT at statement boundaries (end, else, case, ), }, etc.).
	*/
	emitClosing(tokenText) {
		return formatClosingToken(this.expectToken(tokenText));
	}
	/** Consume the next token (unknown text) and return a Doc with both leading and trailing block comments. */
	emitAnyClosing() {
		return formatClosingToken(this.nextToken());
	}
	/** Consume the next child, expecting it to be a token. Returns the token. */
	nextToken() {
		const child = this.next();
		if (!isToken(child)) throw new Error(`Expected token, got node '${child.kind}'`);
		return child;
	}
	/** Consume the next child, expecting it to be a node. Returns the node. */
	nextNode() {
		const child = this.next();
		if (!isNode(child)) throw new Error(`Expected node, got token '${child.text}'`);
		return child;
	}
	/** Check if the current child is a token with the given text. */
	isToken(tokenText) {
		const child = this.peek();
		return child !== void 0 && isToken(child) && child.text === tokenText;
	}
	/** Save the current position for later restore. */
	save() {
		return this.pos;
	}
	/** Restore to a previously saved position. */
	restore(savedPos) {
		this.pos = savedPos;
	}
	/** Check if the current child is a node. */
	isNode() {
		const child = this.peek();
		return child !== void 0 && isNode(child);
	}
	/**
	* Collect expression nodes and semicolons from a body until a stop token.
	* Returns the formatted body as a Doc[] (one per statement).
	* Does NOT consume the stop token.
	*/
	collectBody(...stopTokens) {
		const stmts = [];
		while (!this.done()) {
			const child = this.peek();
			if (isToken(child) && stopTokens.includes(child.text)) break;
			if (isToken(child) && child.text === ";") {
				this.next();
				continue;
			}
			if (isNode(child)) stmts.push(formatNode(this.nextNode()));
			else this.next();
		}
		return stmts;
	}
};
/** Format a body (list of statements) with semicolon separators. */
function formatBody(stmts) {
	if (stmts.length === 0) return text("");
	if (stmts.length === 1) return stmts[0];
	const parts = [stmts[0]];
	for (let i = 1; i < stmts.length; i++) parts.push(text(";"), hardLine, stmts[i]);
	return concat(...parts);
}
function formatBodyFromIter(iter, ...stopTokens) {
	return formatBodyFromIterInternal(iter, true, stopTokens);
}
function formatBodyFromIterNoTrailingSemi(iter, ...stopTokens) {
	return formatBodyFromIterInternal(iter, false, stopTokens);
}
function formatBodyFromIterInternal(iter, trailingSemi, stopTokens) {
	const stmts = [];
	const semiTokens = [];
	while (!iter.done()) {
		const child = iter.peek();
		if (isToken(child) && stopTokens.includes(child.text)) break;
		if (isToken(child) && child.text === ";") {
			semiTokens.push(child);
			iter.next();
			continue;
		}
		if (isToken(child) && child.text === "with") {
			iter.next();
			if (iter.isNode()) {
				const withStmt = {
					kind: "WithStatement",
					children: [child, iter.nextNode()]
				};
				stmts.push(withStmt);
			}
		} else if (isNode(child)) stmts.push(iter.nextNode());
		else iter.next();
	}
	if (stmts.length === 0) return { doc: text("") };
	const parts = [];
	const firstTrivia = firstToken(stmts[0]).leadingTrivia;
	emitTriviaWithBlankLines(firstTrivia, parts);
	for (let i = 0; i < stmts.length; i++) {
		const stmt = stmts[i];
		parts.push(formatNode(stmt));
		if (i < stmts.length - 1 || trailingSemi) parts.push(text(";"));
		const stmtTrailingComment = getTrailingLineComment(stmt);
		const semiTrailingComment = i < semiTokens.length ? semiTokens[i].trailingTrivia.find((t) => t.kind === "lineComment")?.text : void 0;
		const trailingLc = stmtTrailingComment ?? semiTrailingComment;
		if (trailingLc && i < stmts.length - 1) parts.push(text(" "), lineComment(trailingLc));
		if (i < stmts.length - 1) {
			const nextFirst = firstToken(stmts[i + 1]);
			const blankCount = countBlankLinesBetweenTokens(((i < semiTokens.length ? semiTokens[i] : void 0) ?? lastToken(stmt)).trailingTrivia, nextFirst.leadingTrivia);
			if (trailingLc) {
				if (blankCount > 0) pushBlankLines(parts, blankCount);
			} else if (blankCount > 0) {
				parts.push(hardLine);
				pushBlankLines(parts, blankCount);
			} else parts.push(hardLine);
			const nextTrivia = nextFirst.leadingTrivia;
			for (let t = 0; t < nextTrivia.length; t++) {
				const tv = nextTrivia[t];
				if (tv.kind === "lineComment") {
					parts.push(lineComment(tv.text));
					const nextWs = nextTrivia[t + 1];
					if (nextWs?.kind === "whitespace") {
						const bc = countBlankLines([nextWs]);
						if (bc > 0) pushBlankLines(parts, bc);
					}
				} else if (tv.kind === "blockComment") parts.push(text(tv.text), hardLine);
				else if (tv.kind === "whitespace") {
					const bc = countBlankLines([tv]);
					const nextTv = nextTrivia[t + 1];
					if (bc > 0 && nextTv && (nextTv.kind === "lineComment" || nextTv.kind === "blockComment")) pushBlankLines(parts, bc);
				}
			}
		}
	}
	const lastStmt = stmts[stmts.length - 1];
	const lastSemi = semiTokens[stmts.length - 1];
	const lastTrailingLc = getTrailingLineComment(lastStmt) ?? lastSemi?.trailingTrivia.find((t) => t.kind === "lineComment")?.text;
	return {
		doc: concat(...parts),
		lastTrailingComment: lastTrailingLc
	};
}
/**
* Build the Doc tree for a CST program without rendering it.
* Useful for inspecting the formatter's intermediate representation.
*/
function buildDocTree(tree, trailingTrivia) {
	return formatProgram(tree, trailingTrivia);
}
function formatCst(tree, trailingTrivia) {
	let result = render(formatProgram(tree, trailingTrivia), 80);
	result = result.replace(/[ \t]+$/gm, "").replace(/^\n+/, "");
	if (result.length > 0 && !result.endsWith("\n")) result += "\n";
	return result;
}
function formatProgram(program, trailingTrivia) {
	const statements = childNodes(program);
	if (statements.length === 0) {
		const parts = [];
		for (let i = 0; i < trailingTrivia.length; i++) {
			const t = trailingTrivia[i];
			if (t.kind === "shebang") parts.push(text(t.text), hardLine);
			else if (t.kind === "lineComment") parts.push(lineComment(t.text));
			else if (t.kind === "blockComment") parts.push(text(t.text), hardLine);
			else if (t.kind === "whitespace") {
				const bc = countBlankLines([t]);
				const nextT = trailingTrivia[i + 1];
				if (bc > 0 && nextT && (nextT.kind === "lineComment" || nextT.kind === "blockComment")) pushBlankLines(parts, bc);
			}
		}
		if (parts.length === 0) return text("");
		return concat(...parts);
	}
	const first = firstToken(program);
	const shebangDocs = [];
	for (const t of first.leadingTrivia) if (t.kind === "shebang") shebangDocs.push(text(t.text), hardLine);
	const parts = [...shebangDocs];
	const firstTrivia = firstToken(statements[0]).leadingTrivia;
	emitTriviaWithBlankLines(firstTrivia, parts);
	const semicolonTokens = tokens(program);
	let lastTrailingComment;
	for (let i = 0; i < statements.length; i++) {
		const stmt = statements[i];
		parts.push(formatNode(stmt));
		parts.push(text(";"));
		const stmtTrailingComment = getTrailingLineComment(stmt);
		const semiToken = i < semicolonTokens.length ? semicolonTokens[i] : void 0;
		const semiTrailingLine = semiToken?.trailingTrivia.find((t) => t.kind === "lineComment")?.text;
		const semiTrailingBlocks = semiToken?.trailingTrivia.filter((t) => t.kind === "blockComment") ?? [];
		const trailingComment = stmtTrailingComment ?? semiTrailingLine;
		lastTrailingComment = trailingComment;
		for (const bc of semiTrailingBlocks) parts.push(text(" "), text(bc.text));
		if (trailingComment) parts.push(text(" "), lineComment(trailingComment));
		if (i < statements.length - 1) {
			const lastTok = lastToken(stmt);
			const nextFirst = firstToken(statements[i + 1]);
			const blankCount = countBlankLinesBetweenTokens((semiToken ?? lastTok).trailingTrivia, nextFirst.leadingTrivia);
			if (trailingComment) {
				if (blankCount > 0) pushBlankLines(parts, blankCount);
			} else if (blankCount > 0) {
				parts.push(hardLine);
				pushBlankLines(parts, blankCount);
			} else parts.push(hardLine);
			const nextTrivia = nextFirst.leadingTrivia;
			for (let t = 0; t < nextTrivia.length; t++) {
				const trivia = nextTrivia[t];
				if (trivia.kind === "lineComment") {
					parts.push(lineComment(trivia.text));
					const nextWs = nextTrivia[t + 1];
					if (nextWs && nextWs.kind === "whitespace") {
						const bc = countBlankLines([nextWs]);
						if (bc > 0) pushBlankLines(parts, bc);
					}
				} else if (trivia.kind === "blockComment") parts.push(text(trivia.text), hardLine);
				if (trivia.kind === "whitespace") {
					const bc = countBlankLines([trivia]);
					const nextTriv = nextTrivia[t + 1];
					if (bc > 0 && nextTriv && (nextTriv.kind === "lineComment" || nextTriv.kind === "blockComment")) pushBlankLines(parts, bc);
				}
			}
		}
	}
	if (trailingTrivia.some((t) => t.kind === "lineComment" || t.kind === "blockComment")) {
		if (!lastTrailingComment) parts.push(hardLine);
		emitTriviaWithBlankLines(trailingTrivia, parts);
	}
	return concat(...parts);
}
function formatNode(node) {
	switch (node.kind) {
		case "NumberLiteral":
		case "StringLiteral":
		case "AtomLiteral":
		case "TemplateString":
		case "RegexpShorthand":
		case "Symbol":
		case "EffectName":
		case "ReservedSymbol": return formatLeaf(node);
		case "Array": return formatArray(node);
		case "Object": return formatObject(node);
		case "BinaryOp": return formatBinaryOp(node);
		case "PrefixOp": return formatPrefixOp(node);
		case "PropertyAccess": return formatPropertyAccess(node);
		case "IndexAccess": return formatIndexAccess(node);
		case "Call": return formatCall(node);
		case "Parenthesized": return formatParenthesized(node);
		case "Spread": return formatSpread(node);
		case "Let": return formatLet(node);
		case "TypeAlias": return formatTypeAlias(node);
		case "If": return formatIf(node);
		case "Block": return formatBlock(node);
		case "Loop": return formatLoop(node);
		case "For": return formatFor(node);
		case "Match": return formatMatch(node);
		case "Function": return formatFunction(node);
		case "Handler": return formatHandler(node);
		case "Resume": return formatResume(node);
		case "Macro": return formatMacro(node);
		case "MacroCall": return formatMacroCall(node);
		case "Quote": return formatQuote(node);
		case "Splice": return formatSplice(node);
		case "WithStatement": return formatFromChildren(node);
		default: return formatFallback(node);
	}
}
function formatLeaf(node) {
	return formatTokenWithTrivia(nthToken(node, 0));
}
function formatArray(node) {
	const children = childNodes(node);
	if (children.length === 0) {
		const allToks = tokens(node);
		const openTok = allToks.find((t) => t.text === "[");
		const closeTok = allToks.find((t) => t.text === "]");
		const innerComments = [...openTok ? leadingBlockComments(openTok.trailingTrivia) : [], ...closeTok ? leadingBlockComments(closeTok.leadingTrivia) : []];
		if (innerComments.length > 0) return concat(text("["), ...innerComments.flatMap((c) => [text(" "), c]), text(" ]"));
		return text("[]");
	}
	const items = [];
	const toks = tokens(node);
	const commaTokens = toks.filter((t) => t.text === ",");
	for (let i = 0; i < children.length; i++) {
		const itemDoc = formatExprChild(children[i]);
		if (i > 0 && i - 1 < commaTokens.length) {
			const comma = commaTokens[i - 1];
			const trailingCmts = trailingComments(comma);
			if (trailingCmts.length > 0) {
				items.push(concat(...trailingCmts, text(" "), itemDoc));
				continue;
			}
		}
		items.push(itemDoc);
	}
	const separators = [];
	for (let i = 0; i < children.length - 1; i++) {
		const comma = i < commaTokens.length ? commaTokens[i] : void 0;
		const prevLast = lastToken(children[i]);
		const nextFirst = firstToken(children[i + 1]);
		const blankCount = comma ? countBlankLinesBetweenTokens(comma.trailingTrivia, nextFirst.leadingTrivia) : countBlankLinesBetweenTokens(prevLast.trailingTrivia, nextFirst.leadingTrivia);
		if (blankCount > 0) {
			const blanks = [text(","), hardLine];
			pushBlankLines(blanks, blankCount);
			separators.push(concat(...blanks));
		} else separators.push(concat(text(","), line));
	}
	const innerParts = [items[0]];
	for (let i = 1; i < items.length; i++) innerParts.push(separators[i - 1], items[i]);
	const trailingComma = ifBreak(text(","), text(""));
	const openBracketToken = toks.find((t) => t.text === "[");
	const closeBracketToken = toks.find((t) => t.text === "]");
	const openDoc = openBracketToken ? formatTokenWithTrivia(openBracketToken) : text("[");
	const closeDoc = closeBracketToken ? formatClosingToken(closeBracketToken) : text("]");
	return group(concat(openDoc, nest(INDENT, concat(softLine, concat(...innerParts), trailingComma)), softLine, closeDoc));
}
function formatObject(node) {
	const iter = new ChildIterator(node.children);
	const openBraceTrivia = trailingComments(iter.expectToken("{"));
	const entries = [];
	let pendingCommentDocs = [...openBraceTrivia.map((c) => concat(c, text(" ")))];
	while (!iter.done() && !iter.isToken("}")) {
		if (iter.isToken(",")) {
			const comma = iter.nextToken();
			for (const t of comma.trailingTrivia) if (t.kind === "blockComment") pendingCommentDocs.push(text(t.text), text(" "));
			continue;
		}
		if (iter.isNode() && iter.peek().kind === "Spread") {
			entries.push(formatExprChild(iter.nextNode()));
			continue;
		}
		const entryParts = [];
		if (iter.isNode()) entryParts.push(formatExprChild(iter.nextNode()));
		else entryParts.push(iter.emitAnyClosing());
		if (iter.isToken("[")) {}
		if (iter.isToken(":")) {
			const colonDoc = formatTokenWithTrivia(iter.nextToken());
			entryParts.push(colonDoc, text(" "));
			if (iter.isNode()) entryParts.push(formatExprChild(iter.nextNode()));
		}
		if (pendingCommentDocs.length > 0) {
			entries.push(concat(...pendingCommentDocs, ...entryParts));
			pendingCommentDocs = [];
		} else entries.push(concat(...entryParts));
	}
	const closeBraceDoc = iter.emitClosing("}");
	if (entries.length === 0) {
		if (openBraceTrivia.length > 0) return concat(text("{"), ...openBraceTrivia.flatMap((c) => [text(" "), c]), text(" "), closeBraceDoc);
		return concat(text("{"), closeBraceDoc);
	}
	const trailingComma = ifBreak(text(","), text(""));
	return group(concat(text("{"), nest(INDENT, concat(line, join(concat(text(","), line), entries), trailingComma)), line, closeBraceDoc));
}
function formatBinaryOp(node) {
	const children = childNodes(node);
	const toks = tokens(node);
	if (toks.length > 0) {
		const left = children[0];
		const right = children[1];
		const opDoc = formatTokenWithTrivia(toks[0]);
		return group(concat(formatNode(left), text(" "), opDoc, nest(INDENT, concat(line, formatExprChild(right)))));
	}
	if (children.length >= 3) {
		const left = children[0];
		const op = children[1];
		const right = children[2];
		return group(concat(formatNode(left), text(" "), formatExprChild(op), nest(INDENT, concat(line, formatExprChild(right)))));
	}
	return formatFromChildren(node);
}
function formatPrefixOp(node) {
	const operand = nthChild(node, 0);
	return concat(formatTokenWithTrivia(nthToken(node, 0)), formatExprChild(operand));
}
function formatPropertyAccess(node) {
	const object = nthChild(node, 0);
	const toks = tokens(node);
	const dotToken = toks[0];
	const propToken = toks.length >= 2 ? toks[1] : toks[0];
	return concat(formatNode(object), formatTokenWithTrivia(dotToken), toks.length >= 2 ? formatTokenWithTrivia(propToken) : text(""));
}
function formatIndexAccess(node) {
	const object = nthChild(node, 0);
	const index = nthChild(node, 1);
	const toks = tokens(node);
	const openBracket = toks.find((t) => t.text === "[");
	const closeBracket = toks.find((t) => t.text === "]");
	return concat(formatNode(object), openBracket ? formatTokenWithTrivia(openBracket) : text("["), formatExprChild(index), closeBracket ? formatClosingToken(closeBracket) : text("]"));
}
function formatCall(node) {
	const children = childNodes(node);
	const fn = children[0];
	const args = children.slice(1);
	const toks = tokens(node);
	const openParen = toks.find((t) => t.text === "(");
	const closeParen = toks.find((t) => t.text === ")");
	if (args.length === 0) return concat(formatNode(fn), openParen ? formatTokenWithTrivia(openParen) : text("("), closeParen ? formatClosingToken(closeParen) : text(")"));
	const commaTokens = toks.filter((t) => t.text === ",");
	const argDocs = [];
	for (let i = 0; i < args.length; i++) {
		const argDoc = formatExprChild(args[i]);
		if (i > 0 && i - 1 < commaTokens.length) {
			const trailingBlocks = commaTokens[i - 1].trailingTrivia.filter((t) => t.kind === "blockComment");
			if (trailingBlocks.length > 0) {
				argDocs.push(concat(...trailingBlocks.map((t) => concat(text(t.text), text(" "))), argDoc));
				continue;
			}
		}
		argDocs.push(argDoc);
	}
	const openDoc = openParen ? formatTokenWithTrivia(openParen) : text("(");
	const lastArg = args[args.length - 1];
	if (args.length >= 2 && lastArg.kind === "Function" && childNodes(lastArg).some((c) => c.kind === "Block")) {
		const leadingArgDocs = argDocs.slice(0, -1);
		const trailingArgDoc = argDocs[argDocs.length - 1];
		return group(concat(formatNode(fn), openDoc, join(concat(text(","), text(" ")), leadingArgDocs), text(", "), trailingArgDoc, closeParen ? formatClosingToken(closeParen) : text(")")));
	}
	return concat(formatNode(fn), group(concat(openDoc, nest(INDENT, concat(softLine, join(concat(text(","), line), argDocs))), softLine, closeParen ? formatClosingToken(closeParen) : text(")"))));
}
function formatParenthesized(node) {
	const inner = nthChild(node, 0);
	const toks = tokens(node);
	const openParen = toks.find((t) => t.text === "(");
	const closeParen = toks.find((t) => t.text === ")");
	return concat(openParen ? formatTokenWithTrivia(openParen) : text("("), formatExprChild(inner), closeParen ? formatClosingToken(closeParen) : text(")"));
}
function formatSpread(node) {
	const expr = nthChild(node, 0);
	return concat(formatTokenWithTrivia(nthToken(node, 0)), formatExprChild(expr));
}
function formatLet(node) {
	const iter = new ChildIterator(node.children);
	const letDoc = iter.emitToken("let");
	const targetParts = [];
	let lastTargetText = "";
	let bracketDepth = 0;
	while (!iter.done() && !(iter.isToken("=") && bracketDepth === 0)) {
		const peeked = iter.peek();
		if (isToken(peeked) && (peeked.text === "[" || peeked.text === "{")) bracketDepth++;
		if (isToken(peeked) && (peeked.text === "]" || peeked.text === "}")) bracketDepth--;
		const child = iter.next();
		if (isToken(child)) {
			const t = child.text;
			if (t === "{") {
				if (targetParts.length > 0) targetParts.push(text(" "));
				targetParts.push(formatTokenWithTrivia(child), text(" "));
				lastTargetText = "{ ";
				continue;
			}
			if (t === "}") {
				targetParts.push(text(" "), formatClosingToken(child));
				lastTargetText = "}";
				continue;
			}
			if (t === ",") {
				targetParts.push(formatTokenWithTrivia(child), text(" "));
				lastTargetText = ", ";
				continue;
			}
			const postfixBracket = t === "[" && lastTargetText.length > 0 && /[)\]A-Za-z_0-9]$/.test(lastTargetText);
			if (targetParts.length > 0 && !isPunctuation(t) && !isOpenBracket(lastTargetText) && lastTargetText !== "..." && !lastTargetText.endsWith(" ") && !postfixBracket) targetParts.push(text(" "));
			targetParts.push(formatClosingToken(child));
			lastTargetText = t;
		} else {
			if (targetParts.length > 0 && !isOpenBracket(lastTargetText) && lastTargetText !== "..." && !lastTargetText.endsWith(" ")) targetParts.push(text(" "));
			targetParts.push(formatExprChild(child));
			lastTargetText = "";
		}
	}
	const eqDoc = iter.emitToken("=");
	const value = iter.nextNode();
	const header = concat(letDoc, text(" "), concat(...targetParts), text(" "), eqDoc, text(" "));
	const valueDoc = formatExprChild(value);
	if (value.kind === "Function" || value.kind === "Block") return concat(header, valueDoc);
	if (value.kind === "Array" || value.kind === "Object") return concat(header, valueDoc);
	return group(concat(letDoc, text(" "), concat(...targetParts), text(" "), eqDoc, nest(INDENT, concat(line, valueDoc))));
}
function formatIf(node) {
	const iter = new ChildIterator(node.children);
	const branches = [];
	let elseBranchDoc;
	let elseBranchCount = 0;
	let elseLastComment;
	let elseDoc;
	let pendingElseDoc;
	while (!iter.done()) {
		const child = iter.peek();
		if (isToken(child) && child.text === "end") break;
		if (isToken(child) && child.text === "else") {
			const elseKwDoc = iter.emitClosing("else");
			if (iter.isToken("if")) pendingElseDoc = elseKwDoc;
			else {
				elseDoc = elseKwDoc;
				elseBranchCount = countNodesUntil(iter, "end");
				const elseResult = formatBodyFromIterNoTrailingSemi(iter, "end");
				elseBranchDoc = elseResult.doc;
				elseLastComment = elseResult.lastTrailingComment;
				break;
			}
		}
		if (isToken(iter.peek()) && iter.peek().text === "if") {
			const ifDoc = branches.length === 0 ? iter.emitToken("if") : iter.emitClosing("if");
			const condition = iter.nextNode();
			const thenDoc = iter.emitClosing("then");
			const bodyCount = countNodesUntil(iter, "else", "end");
			const bodyResult = formatBodyFromIterNoTrailingSemi(iter, "else", "end");
			branches.push({
				isElseIf: branches.length > 0,
				elseDoc: pendingElseDoc,
				ifDoc,
				condition: formatExprChild(condition),
				thenDoc,
				bodyDoc: bodyResult.doc,
				bodyCount,
				lastTrailingComment: bodyResult.lastTrailingComment
			});
			pendingElseDoc = void 0;
		}
	}
	const endDoc = iter.emitClosing("end");
	const allTrailingComments = [branches[0]?.lastTrailingComment, elseLastComment].filter(Boolean);
	const trailingCmtDoc = allTrailingComments.length > 0 ? concat(text(" "), text(allTrailingComments.join(" "))) : text("");
	if (branches.length === 1 && branches[0].bodyCount === 1 && (elseBranchDoc === void 0 || elseBranchCount === 1)) {
		const b = branches[0];
		const flatParts = [
			b.ifDoc,
			text(" "),
			b.condition,
			text(" "),
			b.thenDoc,
			text(" "),
			b.bodyDoc
		];
		if (elseBranchDoc && elseDoc) flatParts.push(text(" "), elseDoc, text(" "), elseBranchDoc);
		flatParts.push(text(" "), endDoc, trailingCmtDoc);
		const expandedParts = [
			b.ifDoc,
			text(" "),
			b.condition,
			text(" "),
			b.thenDoc
		];
		expandedParts.push(nest(INDENT, concat(hardLine, b.bodyDoc)));
		if (b.lastTrailingComment) expandedParts.push(text(" "), text(b.lastTrailingComment));
		if (elseBranchDoc && elseDoc) {
			expandedParts.push(hardLine, elseDoc);
			expandedParts.push(nest(INDENT, concat(hardLine, elseBranchDoc)));
			if (elseLastComment) expandedParts.push(text(" "), text(elseLastComment));
		}
		expandedParts.push(hardLine, endDoc);
		return group(concat(ifBreak(concat(...expandedParts), concat(...flatParts))));
	}
	const parts = [];
	for (const b of branches) {
		if (b.isElseIf) parts.push(hardLine, b.elseDoc ?? text("else"), text(" "), b.ifDoc, text(" "), b.condition, text(" "), b.thenDoc);
		else parts.push(b.ifDoc, text(" "), b.condition, text(" "), b.thenDoc);
		parts.push(nest(INDENT, concat(hardLine, b.bodyDoc)));
		if (b.lastTrailingComment) parts.push(text(" "), text(b.lastTrailingComment));
	}
	if (elseBranchDoc) {
		parts.push(hardLine, elseDoc ?? text("else"));
		parts.push(nest(INDENT, concat(hardLine, elseBranchDoc)));
		if (elseLastComment) parts.push(text(" "), text(elseLastComment));
	}
	parts.push(hardLine, endDoc);
	return concat(...parts);
}
function formatBlock(node) {
	const iter = new ChildIterator(node.children);
	const doDoc = iter.emitToken("do");
	let withClause;
	if (iter.isToken("with")) {
		const withDoc = iter.emitClosing("with");
		const handler = iter.nextNode();
		const semiDoc = iter.emitToken(";");
		withClause = concat(withDoc, text(" "), formatExprChild(handler), semiDoc);
	}
	const bodyStmtCount = countNodesUntil(iter, "end");
	const bodyResult = formatBodyFromIter(iter, "end");
	const bodyDoc = bodyResult.doc;
	const endDoc = iter.emitClosing("end");
	if (bodyDoc.type === "text" && bodyDoc.text === "") {
		if (withClause) return concat(doDoc, nest(INDENT, concat(hardLine, withClause)), hardLine, endDoc);
		return concat(doDoc, text(" "), endDoc);
	}
	if (bodyStmtCount === 1 && !withClause) {
		const bodyNodes = node.children.filter(isNode).filter((c) => c.kind !== "Block");
		if (!(bodyNodes.length > 0 ? firstToken(bodyNodes[bodyNodes.length - 1]) : void 0)?.leadingTrivia.some((t) => t.kind === "lineComment" || t.kind === "blockComment")) return group(concat(doDoc, nest(INDENT, concat(line, ifBreak(bodyDoc, bodyNodes.length > 0 ? formatExprChild(bodyNodes[bodyNodes.length - 1]) : bodyDoc))), bodyResult.lastTrailingComment ? concat(text(" "), text(bodyResult.lastTrailingComment), hardLine) : line, endDoc));
	}
	const inner = withClause ? concat(hardLine, withClause, hardLine, bodyDoc) : concat(hardLine, bodyDoc);
	const trailingCmt = bodyResult.lastTrailingComment ? concat(text(" "), text(bodyResult.lastTrailingComment)) : text("");
	return concat(doDoc, nest(INDENT, inner), trailingCmt, hardLine, endDoc);
}
function formatLoop(node) {
	return formatFromChildren(node);
}
function formatFor(node) {
	return formatFromChildren(node);
}
function formatMatch(node) {
	const iter = new ChildIterator(node.children);
	const matchDoc = iter.emitToken("match");
	const expr = iter.nextNode();
	const parts = [
		matchDoc,
		text(" "),
		formatExprChild(expr)
	];
	while (!iter.done() && !iter.isToken("end")) if (iter.isToken("case")) {
		const caseDoc = iter.emitClosing("case");
		const patternParts = [];
		let lastPatternText = "";
		while (!iter.done() && !iter.isToken("then") && !iter.isToken("when")) {
			const child = iter.next();
			if (isToken(child)) {
				if (patternParts.length > 0 && !isPunctuation(child.text) && lastPatternText !== "...") patternParts.push(text(" "));
				patternParts.push(formatClosingToken(child));
				lastPatternText = child.text;
			} else {
				if (patternParts.length > 0 && lastPatternText !== "...") patternParts.push(text(" "));
				patternParts.push(formatExprChild(child));
				lastPatternText = "";
			}
		}
		let guardDoc;
		if (iter.isToken("when")) {
			const whenDoc = iter.emitClosing("when");
			const guard = iter.nextNode();
			guardDoc = concat(text(" "), whenDoc, text(" "), formatExprChild(guard));
		}
		const thenDoc = iter.emitClosing("then");
		const body = iter.collectBody("case", "end");
		parts.push(nest(INDENT, concat(hardLine, caseDoc, text(" "), concat(...patternParts), guardDoc ?? text(""), text(" "), thenDoc, body.length === 1 ? concat(text(" "), body[0]) : nest(INDENT, concat(hardLine, formatBody(body))))));
	} else iter.next();
	const endDoc = iter.emitClosing("end");
	parts.push(hardLine, endDoc);
	return concat(...parts);
}
function formatFunction(node) {
	const iter = new ChildIterator(node.children);
	const paramParts = [];
	while (!iter.done() && !iter.isToken("->")) {
		const child = iter.next();
		if (isToken(child)) if (child.text === "(" || child.text === ")") paramParts.push(formatTokenWithTrivia(child));
		else if (child.text === ",") {
			paramParts.push(formatTokenWithTrivia(child));
			paramParts.push(text(" "));
		} else if (child.text === ":") paramParts.push(formatTokenWithTrivia(child));
		else if (child.text === "...") paramParts.push(formatTokenWithTrivia(child));
		else if (child.text === "=") paramParts.push(text(" "), formatTokenWithTrivia(child), text(" "));
		else {
			if (paramParts.length > 0 && !isPunctuation(child.text)) paramParts.push(text(" "));
			paramParts.push(formatTokenWithTrivia(child));
		}
		else paramParts.push(formatExprChild(child));
	}
	const arrowDoc = iter.emitToken("->");
	const body = iter.nextNode();
	return group(concat(...paramParts, paramParts.length > 0 ? text(" ") : text(""), arrowDoc, text(" "), formatExprChild(body)));
}
function formatHandler(node) {
	const iter = new ChildIterator(node.children);
	const parts = [];
	while (iter.isToken("shallow") || iter.isToken("linear")) if (iter.isToken("shallow")) parts.push(iter.emitClosing("shallow"), text(" "));
	else parts.push(iter.emitClosing("linear"), text(" "));
	parts.push(iter.emitToken("handler"));
	const clauseParts = [];
	while (!iter.done() && !iter.isToken("end")) if (iter.isToken("transform")) {
		const transformParts = [iter.emitClosing("transform")];
		while (!iter.done() && !iter.isToken("end")) {
			const child = iter.next();
			if (isToken(child)) if (child.text === "->") transformParts.push(text(" "), formatTokenWithTrivia(child), text(" "));
			else transformParts.push(text(" "), formatTokenWithTrivia(child));
			else transformParts.push(text(" "), formatExprChild(child));
		}
		clauseParts.push(concat(...transformParts));
	} else {
		const clauseTokens = [];
		while (!iter.done() && !iter.isToken("end") && !iter.isToken("transform")) {
			const child = iter.peek();
			if (isToken(child) && child.text.startsWith("@") && clauseTokens.length > 0) break;
			iter.next();
			if (isToken(child)) if (child.text === "->") clauseTokens.push(text(" "), formatTokenWithTrivia(child), text(" "));
			else if (child.text === ":") clauseTokens.push(formatTokenWithTrivia(child));
			else if (child.text === "," || child.text === "(" || child.text === ")") clauseTokens.push(formatTokenWithTrivia(child));
			else {
				if (clauseTokens.length > 0) clauseTokens.push(text(" "));
				clauseTokens.push(formatTokenWithTrivia(child));
			}
			else clauseTokens.push(formatExprChild(child));
		}
		if (clauseTokens.length > 0) clauseParts.push(concat(...clauseTokens));
	}
	const endDoc = iter.emitClosing("end");
	if (clauseParts.length === 0) return concat(...parts, text(" "), endDoc);
	return concat(...parts, nest(INDENT, concat(...clauseParts.map((c) => concat(hardLine, c)))), hardLine, endDoc);
}
function formatResume(node) {
	const iter = new ChildIterator(node.children);
	const resumeDoc = iter.emitToken("resume");
	if (iter.isToken("(")) {
		const openDoc = iter.emitToken("(");
		if (iter.isNode()) {
			const arg = iter.nextNode();
			const closeDoc = iter.emitClosing(")");
			return concat(resumeDoc, openDoc, formatExprChild(arg), closeDoc);
		}
		return concat(resumeDoc, openDoc, iter.emitClosing(")"));
	}
	return resumeDoc;
}
function formatMacro(node) {
	const iter = new ChildIterator(node.children);
	const paramParts = [iter.emitToken("macro")];
	let firstParamToken = true;
	while (!iter.done() && !iter.isToken("->")) {
		const child = iter.next();
		if (isToken(child)) {
			if (child.text === "(" || child.text === ")") paramParts.push(formatTokenWithTrivia(child));
			else if (child.text === ",") {
				paramParts.push(formatTokenWithTrivia(child));
				paramParts.push(text(" "));
			} else if (child.text === ":") paramParts.push(formatTokenWithTrivia(child));
			else if (child.text === "...") paramParts.push(formatTokenWithTrivia(child));
			else if (child.text === "=") paramParts.push(text(" "), formatTokenWithTrivia(child), text(" "));
			else {
				if (!firstParamToken && !isPunctuation(child.text)) paramParts.push(text(" "));
				paramParts.push(formatTokenWithTrivia(child));
			}
			firstParamToken = false;
		} else {
			paramParts.push(formatExprChild(child));
			firstParamToken = false;
		}
	}
	const arrowDoc = iter.emitToken("->");
	const body = iter.nextNode();
	return group(concat(...paramParts, text(" "), arrowDoc, text(" "), formatExprChild(body)));
}
function formatMacroCall(node) {
	const children = childNodes(node);
	const prefixDoc = formatTokenWithTrivia(nthToken(node, 0));
	if (children.length > 0) return concat(prefixDoc, text(" "), formatExprChild(children[0]));
	return prefixDoc;
}
function formatQuote(node) {
	const iter = new ChildIterator(node.children);
	const quoteDoc = iter.emitToken("quote");
	const statements = [[]];
	while (!iter.done()) {
		const child = iter.peek();
		if (isToken(child)) {
			if (child.text === "end") break;
			if (child.text === ";") {
				iter.next();
				statements.push([]);
				continue;
			}
		}
		statements[statements.length - 1].push(iter.next());
	}
	if (statements.length > 1 && statements[statements.length - 1].length === 0) statements.pop();
	const endDoc = iter.emitClosing("end");
	if (statements.length === 1 && statements[0].length === 0) return concat(quoteDoc, text(" "), endDoc);
	if (statements.length === 1) return group(concat(quoteDoc, nest(INDENT, concat(line, formatTokenSequence(statements[0]), ifBreak(text(";"), text("")))), line, endDoc));
	return concat(quoteDoc, nest(INDENT, concat(...statements.map((stmt) => concat(formatTokenSequence(stmt), text(";"))).map((d) => concat(hardLine, d)))), hardLine, endDoc);
}
/**
* Format an arbitrary slice of CST children using the same spacing rules as
* formatFromChildren. Used by formatQuote to render each split statement
* independently while keeping splice/identifier/operator spacing consistent.
*
* The isFirst handling mirrors formatFromChildren: the first child uses
* formatTokenWithTrivia (trailing trivia only), subsequent children use
* formatClosingToken (both leading and trailing). This is asymmetric on
* purpose — formatFromChildren assumes its container handles leading trivia
* at a statement boundary. Here, each split statement IS a statement
* boundary, so the first child matches that contract.
*
* Known limitation: line comments that land between tokens of the same
* statement are dropped, because the body was re-parsed from a trivia
* stream that only carries block comments and whitespace through to the
* rebuilt CstTokens. Mid-body comment preservation would require walking
* splice sub-nodes' own trivia or attaching line-comment trivia to
* adjacent non-trivia tokens — out of scope for the quote-body rebuild.
*/
function formatTokenSequence(children) {
	const parts = [];
	let prevText = "";
	let isFirst = true;
	for (const child of children) {
		if (isToken(child)) {
			if (prevText && needsSpaceBetween(prevText, child.text)) parts.push(text(" "));
			parts.push(isFirst ? formatTokenWithTrivia(child) : formatClosingToken(child));
			prevText = child.text;
		} else {
			if (prevText && !isOpenBracket(prevText) && prevText !== ".") parts.push(text(" "));
			parts.push(isFirst ? formatNode(child) : formatExprChild(child));
			prevText = "_";
		}
		isFirst = false;
	}
	return concat(...parts);
}
function formatSplice(node) {
	return formatFromChildren(node);
}
/**
* Generic formatter that reconstructs output from children in order.
* Tokens are emitted with proper spacing, child nodes are recursively formatted.
* This is used for complex constructs where specialized formatting hasn't been
* implemented yet — it produces correct (lossless-ish) but unformatted output.
*/
/**
* Format a `type X[<T...>] = TypeExpr` declaration. Type expressions
* have their own grammar and aren't fully formatted here — the
* implementation walks the raw token stream and uses the standard
* `needsSpaceBetween` rules with a small override for generic angle
* brackets, which the regular fallback would space-pad incorrectly
* (turning `Foo<T>` into `Foo < T >`).
*
* Tokens flow into the CST in source order via `ctx.advance()`
* during type-decl parsing, so the children list contains every
* token of the declaration body in sequence. No nested CstNodes
* appear here today — type expressions don't go through the
* expression parser.
*/
function formatTypeAlias(node) {
	const parts = [];
	let prevText = "";
	let isFirst = true;
	let angleDepth = 0;
	let braceDepth = 0;
	for (const child of node.children) {
		if (!isToken(child)) {
			parts.push(isFirst ? formatNode(child) : formatExprChild(child));
			prevText = "_";
			isFirst = false;
			continue;
		}
		const t = child.text;
		const inGenerics = angleDepth > 0 && braceDepth === 0;
		let space = "";
		if (parts.length > 0) {
			if (t === "<" && /^[A-Z_a-z]/.test(prevText) && braceDepth === 0) space = "";
			else if (t === ">" && inGenerics) space = "";
			else if (prevText === "<" && braceDepth === 0) space = "";
			else if (prevText === ":" && inGenerics) space = " ";
			else if (t === ":" && inGenerics) space = "";
			else if (t === "," && inGenerics) space = "";
			else if (prevText === "," && inGenerics) space = " ";
			else if (needsSpaceBetween(prevText, t)) space = " ";
		}
		if (space) parts.push(text(space));
		parts.push(isFirst ? formatTokenWithTrivia(child) : formatClosingToken(child));
		if (t === "<" && /^[A-Z_a-z]/.test(prevText) && braceDepth === 0) angleDepth++;
		else if (t === ">" && inGenerics) angleDepth--;
		else if (t === "{") braceDepth++;
		else if (t === "}") braceDepth--;
		prevText = t;
		isFirst = false;
	}
	return concat(...parts);
}
function formatFromChildren(node) {
	const parts = [];
	let prevText = "";
	let isFirst = true;
	for (const child of node.children) {
		if (isToken(child)) {
			if (prevText && needsSpaceBetween(prevText, child.text)) parts.push(text(" "));
			parts.push(isFirst ? formatTokenWithTrivia(child) : formatClosingToken(child));
			prevText = child.text;
		} else {
			if (prevText && !isOpenBracket(prevText) && prevText !== ".") parts.push(text(" "));
			parts.push(isFirst ? formatNode(child) : formatExprChild(child));
			prevText = "_";
		}
		isFirst = false;
	}
	return concat(...parts);
}
function formatFallback(node) {
	return formatFromChildren(node);
}
/**
* Emit trivia (comments with blank lines preserved) into a Doc parts array.
* Used for preamble, epilogue, and inter-statement trivia.
*/
function emitTriviaWithBlankLines(trivia, parts) {
	for (let i = 0; i < trivia.length; i++) {
		const t = trivia[i];
		if (t.kind === "lineComment") parts.push(lineComment(t.text));
		else if (t.kind === "blockComment") parts.push(text(t.text), hardLine);
		else if (t.kind === "whitespace") {
			const bc = countBlankLines([t]);
			const nextT = trivia[i + 1];
			if (bc > 0 && (!nextT || nextT.kind === "lineComment" || nextT.kind === "blockComment")) pushBlankLines(parts, bc);
		}
	}
}
/** Count child nodes in an iterator until a stop token (without consuming). */
function countNodesUntil(iter, ...stopTokens) {
	let count = 0;
	const saved = iter.save();
	while (!iter.done()) {
		const child = iter.peek();
		if (isToken(child) && stopTokens.includes(child.text)) break;
		if (isToken(child) && child.text === ";") {
			iter.next();
			continue;
		}
		if (isNode(child)) {
			count++;
			iter.next();
			continue;
		}
		iter.next();
	}
	iter.restore(saved);
	return count;
}
/** Check if a token text is punctuation that shouldn't have a space before it. */
function isPunctuation(s) {
	return s === "," || s === ";" || s === ")" || s === "]" || s === "}" || s === "." || s === ":";
}
/** Check if a token ends with an opening bracket — no space should follow it. */
function isOpenBracket(s) {
	const last = s[s.length - 1];
	return last === "(" || last === "[" || last === "{";
}
/**
* Determine whether a space is needed before the current token, given the
* previous token text. Handles function calls (no space before `(`),
* property access (no space after `.`), and brackets (no space after open,
* no space before close).
*/
function needsSpaceBetween(prev, cur) {
	if (isPunctuation(cur)) return false;
	if (isOpenBracket(prev)) return false;
	if (prev === ".") return false;
	if (cur === "(" && !isOperatorText(prev)) return false;
	if (cur === "[" && !isOperatorText(prev)) return false;
	return true;
}
/** Check if text looks like an operator (not an identifier or literal). */
function isOperatorText(s) {
	if (s.length === 0) return false;
	const ch = s[0];
	return "+-*/%<>=!&|^~?".includes(ch);
}
//#endregion
//#region src/formatter/format.ts
/**
* Dvala source formatter — CST-based.
*
* Parses source into a concrete syntax tree (CST) that preserves all tokens
* including comments and whitespace, then formats it using a Wadler-Lindig
* document algebra. Comments are preserved at their logical positions.
* Structural whitespace is normalized to canonical form.
*
* On parse failure the original source is returned unchanged so format-on-save
* never destroys partially-written code.
*/
function format(source) {
	if (source.trim() === "") return "";
	const fullTokenStream = tokenize(source, true, void 0);
	try {
		const { tree, trailingTrivia } = parseToCst(fullTokenStream);
		return formatCst(tree, trailingTrivia);
	} catch {
		return source;
	}
}
//#endregion
//#region src/standaloneTooling.ts
/**
* Standalone tooling functions re-exported from the minimal bundle entry (`src/index.ts`).
*
* These are thin wrappers around internal utilities that do not require a
* Dvala instance and have no dependency on the built-in reference catalogue
* (`reference/index.ts`).  Keeping them here — rather than in `src/tooling.ts`
* — is what prevents `reference/index.ts` (and its eager `docsToReference()`
* calls) from being dragged into the minimal bundle by rolldown.
*
* `src/tooling.ts` re-exports everything from this file so that consumers of
* `@mojir/dvala/tooling` continue to receive these symbols unchanged.
*/
/**
* Tokenize a Dvala source string into a token stream.
* Pass `debug: true` to capture source positions (needed for the debugger).
*/
function tokenizeSource(source, debug = false, filePath) {
	return tokenize(source, debug, filePath);
}
/**
* Parse a token stream into an AST.
* The stream is automatically minified (whitespace removed) before parsing.
*/
function parseTokenStream(tokenStream) {
	return parseToAst(minifyTokenStream(tokenStream, { removeWhiteSpace: true }));
}
/**
* Get all undefined symbols in a Dvala program.
*
* @param source - Dvala source code
* @param options - optional context
* @param options.scope - host bindings to treat as defined
* @param options.modules - modules to treat as available
*/
function getUndefinedSymbols(source, options) {
	const modulesMap = options?.modules ? new Map(options.modules.map((m) => [m.name, m])) : void 0;
	const contextStack = createContextStack({
		globalContext: options?.scope ? Object.fromEntries(Object.keys(options.scope).map((k) => [k, { value: null }])) : void 0,
		modules: modulesMap
	});
	return getUndefinedSymbols$1(parseToAst(minifyTokenStream(tokenize(source, false, void 0), { removeWhiteSpace: true })), contextStack, builtin);
}
/**
* Create an auto-completer for the given program at the given cursor position.
*
* @param program - Full Dvala source code
* @param position - Cursor position (character offset)
* @param params - Optional params (bindings to include as suggestions)
*/
function getAutoCompleter(program, position, params = {}) {
	const effectNames = params.effectNames ? [...standardEffectNames, ...params.effectNames] : [...standardEffectNames];
	return new AutoCompleter(program, position, {
		...params,
		effectNames
	});
}
//#endregion
//#region src/tokenizer/identifierName.ts
/**
* `true` iff `name` would tokenize as a single user-defined symbol — the
* JS-style identifier shape Dvala uses: `[a-zA-Z_$][a-zA-Z0-9_$]*`.
*
* Used by the formatter (object-key shorthand eligibility), the VS Code
* extension (rename input validation), and the language-service backend
* (distinguishing named-function builtins from operator builtins like `+`,
* `==`, `&&` when emitting inlay hints). Each of those sites previously
* inlined the same regex; centralising it removes the drift risk if the
* tokenizer's identifier alphabet changes (e.g. Unicode support).
*
* NOT a substitute for the tokenizer's stateful single-character check —
* the tokenizer uses character-class regexes (`jsIdentifierFirstCharRegExp`,
* `jsIdentifierCharRegExp`) inside a state machine, which is a different
* shape from this whole-string predicate.
*/
const IDENTIFIER_NAME_REGEX = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/;
function isDvalaIdentifierName(name) {
	return IDENTIFIER_NAME_REGEX.test(name);
}
//#endregion
//#region src/symbolPatterns.ts
const polishSymbolCharacterClass = "[\\w@%^?=!$<>+*/:&|~-]";
const polishSymbolFirstCharacterClass = "[a-zA-Z_@%^?=!$<>+*/&|~-]";
//#endregion
//#region src/languageService/SymbolTableBuilder.ts
/**
* Builds a symbol table from a Dvala AST.
*
* Walks the AST collecting definitions (let bindings, function params, handler
* clause params, for/match bindings) and references (every Sym node). Maintains
* a static scope stack for symbol resolution — references are linked to the
* nearest enclosing definition, or flagged as unresolved.
*
* Modeled on getUndefinedSymbols but instead of returning a set of names,
* produces full SymbolDef[] and SymbolRef[] with source locations.
*/
/**
* Build a symbol table from AST nodes.
* Returns all definitions and references found, with resolved cross-references.
*/
function buildSymbolTable(nodes, sourceMap, filePath, builtinNames) {
	const state = {
		definitions: [],
		references: [],
		scopes: [/* @__PURE__ */ new Map()],
		scopeDepth: 0,
		sourceMap,
		filePath,
		builtinNames,
		scopeRanges: [],
		activeScopeRanges: []
	};
	walkSequence(nodes, state);
	return {
		definitions: state.definitions,
		references: state.references,
		scopeRanges: state.scopeRanges
	};
}
/** Walk a sequence of nodes where let bindings accumulate in the current scope. */
function walkSequence(nodes, state) {
	for (const node of nodes) walkNode(node, state);
}
function walkNode(node, state) {
	const [type, payload, nodeId] = node;
	switch (type) {
		case NodeTypes.Sym: {
			const name = payload;
			if (state.builtinNames.has(name)) break;
			const def = lookupScope(name, state);
			const location = nodeLocation(nodeId, state);
			state.references.push({
				name,
				nodeId,
				location,
				resolvedDef: def
			});
			break;
		}
		case NodeTypes.Let: {
			const [target, valueExpr] = payload;
			walkNode(valueExpr, state);
			registerBindingTarget(target, state, "variable", valueExpr);
			break;
		}
		case NodeTypes.Function:
		case NodeTypes.Macro: {
			const [params, body] = payload;
			pushScope(state, nodeId);
			for (const param of params) registerBindingTarget(param, state, "parameter");
			walkSequence(body, state);
			popScope(state);
			break;
		}
		case NodeTypes.Block: {
			const bodyNodes = payload;
			pushScope(state, nodeId);
			walkSequence(bodyNodes, state);
			popScope(state);
			break;
		}
		case NodeTypes.Handler: {
			const [clauses, transform] = payload;
			for (const clause of clauses) {
				pushScope(state, nodeId);
				for (const param of clause.params) registerBindingTarget(param, state, "parameter");
				registerDef({
					name: "resume",
					kind: "function",
					nodeId,
					location: nodeLocation(nodeId, state),
					scope: state.scopeDepth
				}, state);
				walkSequence(clause.body, state);
				popScope(state);
			}
			if (transform) {
				pushScope(state, nodeId);
				registerBindingTarget(transform[0], state, "parameter");
				walkSequence(transform[1], state);
				popScope(state);
			}
			break;
		}
		case NodeTypes.WithHandler: {
			const [handlerExpr, bodyNodes] = payload;
			walkNode(handlerExpr, state);
			pushScope(state, nodeId);
			walkSequence(bodyNodes, state);
			popScope(state);
			break;
		}
		case NodeTypes.For: {
			const [bindings, bodyExpr] = payload;
			pushScope(state, nodeId);
			for (const binding of bindings) {
				const [[target, collectionExpr], letBindings, whenExpr, whileExpr] = binding;
				walkNode(collectionExpr, state);
				registerBindingTarget(target, state, "variable");
				for (const [letTarget, letValue] of letBindings) {
					walkNode(letValue, state);
					registerBindingTarget(letTarget, state, "variable");
				}
				if (whenExpr) walkNode(whenExpr, state);
				if (whileExpr) walkNode(whileExpr, state);
			}
			walkNode(bodyExpr, state);
			popScope(state);
			break;
		}
		case NodeTypes.Loop: {
			const [bindings, bodyExpr] = payload;
			pushScope(state, nodeId);
			for (const [target, initExpr] of bindings) {
				walkNode(initExpr, state);
				registerBindingTarget(target, state, "variable");
			}
			walkNode(bodyExpr, state);
			popScope(state);
			break;
		}
		case NodeTypes.Match: {
			const [expr, cases] = payload;
			walkNode(expr, state);
			for (const [pattern, body, guard] of cases) {
				pushScope(state, nodeId);
				registerBindingTarget(pattern, state, "variable");
				if (guard) walkNode(guard, state);
				walkNode(body, state);
				popScope(state);
			}
			break;
		}
		case NodeTypes.If: {
			const parts = payload;
			for (const part of parts) if (part) walkNode(part, state);
			break;
		}
		case NodeTypes.Call: {
			const [callee, args] = payload;
			walkNode(callee, state);
			for (const arg of args) walkNode(arg, state);
			break;
		}
		case NodeTypes.MacroCall: {
			const [symNode, args] = payload;
			walkNode(symNode, state);
			for (const arg of args) walkNode(arg, state);
			break;
		}
		case NodeTypes.Array:
		case NodeTypes.Recur: {
			const elements = payload;
			for (const element of elements) walkNode(element, state);
			break;
		}
		case NodeTypes.Parallel:
		case NodeTypes.Race:
		case NodeTypes.Settled:
			walkNode(payload, state);
			break;
		case NodeTypes.Object: {
			const entries = payload;
			for (const entry of entries) if (Array.isArray(entry) && Array.isArray(entry[0])) {
				const [key, value] = entry;
				walkNode(key, state);
				walkNode(value, state);
			} else walkNode(entry, state);
			break;
		}
		case NodeTypes.TmplStr: {
			const segments = payload;
			for (const segment of segments) walkNode(segment, state);
			break;
		}
		case NodeTypes.Spread:
			walkNode(payload, state);
			break;
		case NodeTypes.And:
		case NodeTypes.Or:
		case NodeTypes.Qq: {
			const operands = payload;
			for (const operand of operands) walkNode(operand, state);
			break;
		}
		case NodeTypes.Perform: {
			const [effectExpr, argExpr] = payload;
			walkNode(effectExpr, state);
			if (argExpr) walkNode(argExpr, state);
			break;
		}
		case NodeTypes.Resume: {
			const arg = payload;
			if (arg !== "ref") walkNode(arg, state);
			break;
		}
		case NodeTypes.Import: break;
		case NodeTypes.CodeTmpl: {
			const [, spliceExprs] = payload;
			for (const expr of spliceExprs) walkNode(expr, state);
			break;
		}
		case NodeTypes.Num:
		case NodeTypes.Str:
		case NodeTypes.Atom:
		case NodeTypes.Builtin:
		case NodeTypes.Special:
		case NodeTypes.Reserved:
		case NodeTypes.Binding:
		case NodeTypes.Effect:
		case NodeTypes.Splice:
		case NodeTypes.InlinedData:
		case NodeTypes.SpecialExpression: break;
	}
}
/** Push a new scope, associated with the AST node that creates it. */
function pushScope(state, nodeId) {
	state.scopeDepth++;
	state.scopes.push(/* @__PURE__ */ new Map());
	state.activeScopeRanges.push({
		nodeId: nodeId ?? -1,
		definitions: []
	});
}
/** Pop a scope and finalize its range using the source map position of the owning node. */
function popScope(state) {
	state.scopes.pop();
	state.scopeDepth--;
	const active = state.activeScopeRanges.pop();
	if (active && active.nodeId >= 0 && active.definitions.length > 0 && state.sourceMap) {
		const pos = state.sourceMap.positions.get(active.nodeId);
		if (pos) state.scopeRanges.push({
			startLine: pos.start[0] + 1,
			startColumn: pos.start[1] + 1,
			endLine: pos.end[0] + 1,
			endColumn: pos.end[1] + 1,
			definitions: active.definitions
		});
	}
}
function lookupScope(name, state) {
	for (let i = state.scopes.length - 1; i >= 0; i--) {
		const def = state.scopes[i].get(name);
		if (def) return def;
	}
	return null;
}
function currentScope(state) {
	return state.scopes[state.scopes.length - 1];
}
/**
* Register all names from a binding target as definitions in the current scope.
* Determines the kind based on the RHS expression when available.
* Extracts parameter names for function/macro definitions.
*/
function registerBindingTarget(target, state, defaultKind, rhsNode) {
	const kind = rhsNode ? classifyRhs(rhsNode, defaultKind) : defaultKind;
	const defsBefore = state.definitions.length;
	walkBindingTarget(target, state, kind);
	if (rhsNode && (kind === "function" || kind === "macro")) {
		const params = extractParamNames(rhsNode);
		if (params) for (let i = defsBefore; i < state.definitions.length; i++) state.definitions[i].params = params;
	}
	if (rhsNode && kind === "import" && rhsNode[0] === NodeTypes.Import) {
		const importPath = rhsNode[1];
		for (let i = defsBefore; i < state.definitions.length; i++) state.definitions[i].importPath = importPath;
	}
}
/** Extract parameter names from a Function or Macro AST node. */
function extractParamNames(rhs) {
	const type = rhs[0];
	if (type !== NodeTypes.Function && type !== NodeTypes.Macro) return void 0;
	const [params] = rhs[1];
	return params.map((p) => {
		if (p[0] === bindingTargetTypes.symbol) {
			const [symbolNode] = p[1];
			return symbolNode[1];
		}
		if (p[0] === bindingTargetTypes.rest) return `...${p[1][0]}`;
		if (p[0] === bindingTargetTypes.object) return "{...}";
		if (p[0] === bindingTargetTypes.array) return "[...]";
		return "_";
	});
}
/** Classify a let binding's kind from the RHS AST node. */
function classifyRhs(rhs, fallback) {
	const type = rhs[0];
	if (type === NodeTypes.Function) return "function";
	if (type === NodeTypes.Macro) return "macro";
	if (type === NodeTypes.Handler) return "handler";
	if (type === NodeTypes.Import) return "import";
	return fallback;
}
/** Register a single definition: adds to definitions list, current scope, and active scope range. */
function registerDef(def, state) {
	state.definitions.push(def);
	currentScope(state).set(def.name, def);
	const activeRange = state.activeScopeRanges[state.activeScopeRanges.length - 1];
	if (activeRange) activeRange.definitions.push(def);
}
/** Recursively walk a binding target, registering each name as a definition. */
function walkBindingTarget(target, state, kind, keyInfo) {
	const [targetType, targetPayload, targetNodeId] = target;
	switch (targetType) {
		case bindingTargetTypes.symbol: {
			const [symbolNode, defaultExpr] = targetPayload;
			const name = symbolNode[1];
			const location = nodeLocation(symbolNode[2], state);
			const def = {
				name,
				kind,
				nodeId: symbolNode[2],
				location,
				scope: state.scopeDepth
			};
			if (keyInfo) {
				def.importedName = keyInfo.key;
				def.keyNodeId = keyInfo.keyNodeId;
				const keyLoc = nodeLocation(keyInfo.keyNodeId, state);
				if (keyLoc.line !== location.line || keyLoc.column !== location.column || keyLoc.file !== location.file) def.keyLocation = keyLoc;
			}
			registerDef(def, state);
			if (defaultExpr) walkNode(defaultExpr, state);
			break;
		}
		case bindingTargetTypes.rest: {
			const [name, defaultExpr] = targetPayload;
			const def = {
				name,
				kind,
				nodeId: targetNodeId,
				location: nodeLocation(targetNodeId, state),
				scope: state.scopeDepth
			};
			if (keyInfo) {
				def.importedName = keyInfo.key;
				def.keyNodeId = keyInfo.keyNodeId;
			}
			registerDef(def, state);
			if (defaultExpr) walkNode(defaultExpr, state);
			break;
		}
		case bindingTargetTypes.object: {
			const [entries, defaultExpr] = targetPayload;
			for (const entry of entries) walkBindingTarget(entry.target, state, kind, {
				key: entry.key,
				keyNodeId: entry.keyNodeId
			});
			if (defaultExpr) walkNode(defaultExpr, state);
			break;
		}
		case bindingTargetTypes.array: {
			const [targets, defaultExpr] = targetPayload;
			for (const subTarget of targets) if (subTarget) walkBindingTarget(subTarget, state, kind);
			if (defaultExpr) walkNode(defaultExpr, state);
			break;
		}
		case bindingTargetTypes.literal:
		case bindingTargetTypes.wildcard: break;
	}
}
function nodeLocation(nodeId, state) {
	if (!state.sourceMap) return {
		file: state.filePath,
		line: 0,
		column: 0
	};
	const pos = state.sourceMap.positions.get(nodeId);
	if (!pos) return {
		file: state.filePath,
		line: 0,
		column: 0
	};
	return {
		file: state.sourceMap.sources[pos.source]?.path ?? state.filePath,
		line: pos.start[0] + 1,
		column: pos.start[1] + 1
	};
}
//#endregion
//#region src/languageService/tokenScan.ts
/**
* Scan a token array for top-level let bindings.
* Extracts simple `let <name>` patterns — does not handle destructuring.
*/
function scanTokensForDefinitions(tokens, filePath) {
	const meaningful = tokens.filter((t) => t[0] !== "Whitespace" && t[0] !== "SingleLineComment" && t[0] !== "MultiLineComment" && t[0] !== "Shebang");
	const defs = [];
	for (let i = 0; i < meaningful.length; i++) {
		const token = meaningful[i];
		if (token[0] !== "Symbol" || token[1] !== "let") continue;
		const next = meaningful[i + 1];
		if (!next || next[0] !== "Symbol") continue;
		const name = next[1];
		const debugInfo = next[2];
		const line = debugInfo ? debugInfo[0] + 1 : 0;
		const column = debugInfo ? debugInfo[1] + 1 : 0;
		const kind = classifyLetBinding(meaningful, i + 2);
		const params = kind === "function" ? scanFunctionParams(meaningful, i + 3) : void 0;
		defs.push({
			name,
			kind,
			nodeId: -1,
			location: {
				file: filePath,
				line,
				column
			},
			scope: 0,
			...params ? { params } : {}
		});
	}
	return defs;
}
/**
* Look at tokens after `let <name>` to classify the binding kind.
* Starts at the token index after the name (should be `=`).
*/
function classifyLetBinding(tokens, startIndex) {
	const eq = tokens[startIndex];
	if (!eq || eq[0] !== "Operator" || eq[1] !== "=") return "variable";
	const rhs = tokens[startIndex + 1];
	if (!rhs) return "variable";
	if (rhs[0] === "Symbol" && rhs[1] === "macro") return "macro";
	if (rhs[0] === "Symbol" && rhs[1] === "handler") return "handler";
	if (rhs[0] === "Symbol" && rhs[1] === "shallow") return "handler";
	if (rhs[0] === "Symbol" && rhs[1] === "linear") return "handler";
	if (rhs[0] === "LParen") return "function";
	if (rhs[0] === "Symbol" && rhs[1] === "import") return "import";
	return "variable";
}
function scanFunctionParams(tokens, startIndex) {
	const first = tokens[startIndex];
	if (!first || first[0] !== "LParen") return void 0;
	const params = [];
	let depth = 0;
	for (let i = startIndex; i < tokens.length; i++) {
		const token = tokens[i];
		if (token[0] === "LParen") {
			depth++;
			continue;
		}
		if (token[0] === "RParen") {
			depth--;
			if (depth === 0) return params;
			continue;
		}
		if (depth !== 1) continue;
		if (token[0] === "Symbol") {
			params.push(token[1]);
			continue;
		}
		if (token[0] === "Operator" && token[1] === ",") continue;
		return;
	}
}
//#endregion
//#region src/languageService/WorkspaceIndex.ts
/**
* Workspace-level index that connects per-file symbol tables across imports.
*
* Caches FileSymbols per file, tracks the import dependency graph, and provides
* lookup APIs for Go to Definition, Find References, Document Symbols, and
* Diagnostics. Falls back to token-scanned definitions when the parser fails.
*
* **Pure data manipulation — zero filesystem and path-module access.** Source
* text, import resolution, and path canonicalization are all the caller's
* responsibility. The Node-side `nodeWorkspaceIndexer.ts` wrapper provides
* CLI ergonomics on top of this class (canonicalizes via `path.resolve`,
* resolves imports via `fs.existsSync`); the playground builds its own
* browser-side wrapper using `FileBackend` and an in-memory path scheme.
*
* Cache keys are whatever path strings the caller passes — keep them
* consistent within a session or cross-file lookups will miss. Both
* existing wrappers always pass canonical absolute paths, so consumers
* never need to think about this.
*/
const builtinNames = new Set([
	...Object.keys(builtin.normalExpressions),
	...Object.keys(builtin.specialExpressions),
	...Object.keys(reservedSymbolRecord)
]);
var WorkspaceIndex = class {
	cache = /* @__PURE__ */ new Map();
	/** Reverse import graph: file → set of files that import it */
	reverseImports = /* @__PURE__ */ new Map();
	/**
	* Update the index for a single file. The caller supplies the source
	* text, a (canonical) file path used as the cache key, and a
	* `resolveImport` callback that maps raw import-path strings
	* (e.g. `"./lib"`) to absolute file paths. Pure: no filesystem access
	* happens inside this method.
	*
	* The callback receives the raw path exactly as written and the importing
	* file's path; it returns `null` when the path can't be resolved.
	* Non-relative imports (builtin module names like `"functional"`) skip
	* the callback entirely — they're filtered out as not-a-file before
	* resolution.
	*
	* Always returns the file symbols (the parser is recoverable, so even a
	* broken source produces a `FileSymbols` with `parseErrors` populated).
	*/
	updateFile(filePath, source, resolveImport = () => null) {
		const contentHash = simpleHash(source);
		const cached = this.cache.get(filePath);
		if (cached && cached.contentHash === contentHash && cached.symbols) return cached.symbols;
		const tokens = tokenize(source, true, filePath);
		const tokenDefs = scanTokensForDefinitions(tokens.tokens, filePath);
		const parseResult = parseRecoverable(minifyTokenStream(tokens, { removeWhiteSpace: true }));
		const { definitions, references, scopeRanges } = buildSymbolTable(parseResult.body, parseResult.sourceMap, filePath, builtinNames);
		const imports = /* @__PURE__ */ new Map();
		extractImports(parseResult.body, filePath, imports, resolveImport);
		const fileSymbols = {
			filePath,
			definitions,
			references,
			imports,
			exports: extractExports(parseResult.body, parseResult.sourceMap, filePath),
			parseErrors: parseResult.errors,
			scopeRanges
		};
		this.cache.set(filePath, {
			contentHash,
			symbols: fileSymbols,
			tokenDefs
		});
		this.updateReverseImports(filePath, imports);
		return fileSymbols;
	}
	/**
	* Get file symbols, using cache if available.
	* Does NOT trigger a re-parse — call updateFile() first if the file may have changed.
	*/
	getFileSymbols(filePath) {
		return this.cache.get(filePath)?.symbols ?? null;
	}
	/**
	* Get definitions for a file — returns AST definitions if available,
	* falls back to token-scanned definitions.
	*/
	getDefinitions(filePath) {
		const cached = this.cache.get(filePath);
		if (!cached) return [];
		if (!cached.symbols) return cached.tokenDefs;
		if (cached.symbols.definitions.length > 0) return cached.symbols.definitions;
		if (cached.symbols.parseErrors.length > 0) return cached.tokenDefs;
		return cached.symbols.definitions;
	}
	/**
	* Find the definition of a symbol at a given position.
	* Searches the file's symbol table, then follows imports for cross-file resolution.
	*/
	findDefinition(filePath, line, column) {
		const fileSymbols = this.cache.get(filePath)?.symbols;
		if (!fileSymbols) return null;
		const ref = findRefAtPosition(fileSymbols.references, line, column);
		if (ref?.resolvedDef) return ref.resolvedDef;
		if (ref) for (const importedPath of fileSymbols.imports.values()) {
			const importedSymbols = this.cache.get(importedPath)?.symbols;
			if (!importedSymbols) continue;
			const def = importedSymbols.definitions.find((d) => d.name === ref.name && d.scope === 0);
			if (def) return def;
		}
		return null;
	}
	/**
	* Find all references to a symbol.
	*
	* @deprecated Prefer `findAllOccurrences` for any user-facing workflow.
	* This method matches importer references by name only and so includes
	* unrelated locals that happen to share the name (e.g. a local `let pi`
	* in an importer that doesn't come from the target file). It also does
	* not walk `exports`, so the export-object key `{ pi }` is missed.
	* Retained for existing callers and tests; do not wire new features
	* through it.
	*/
	findReferences(filePath, symbolName) {
		const results = [];
		const fileSymbols = this.cache.get(filePath)?.symbols;
		if (fileSymbols) results.push(...fileSymbols.references.filter((r) => r.name === symbolName));
		const importers = this.reverseImports.get(filePath);
		if (importers) for (const importerPath of importers) {
			const importerSymbols = this.cache.get(importerPath)?.symbols;
			if (importerSymbols) results.push(...importerSymbols.references.filter((r) => r.name === symbolName));
		}
		return results;
	}
	/**
	* Get the symbol name at a given position (could be a definition or reference site).
	* Returns the name and the definition it resolves to (if any).
	*
	* For aliased destructuring `let { pi as p } = import("./math")`, the
	* binding's `location` points at `p` while `keyLocation` points at `pi`.
	* This method checks both: if the cursor is on the key token, the returned
	* symbol carries the *imported* name (`pi`) and `onKey: true` — signalling
	* to `resolveCanonicalFile` that the user is renaming from the key side
	* (equivalent to originating the rename at the origin). If the cursor is
	* on the local, the returned symbol carries the local name (`p`) and
	* `onKey: false` — signalling that this is a local-only rename.
	*/
	getSymbolAtPosition(filePath, line, column) {
		const fileSymbols = this.cache.get(filePath)?.symbols;
		if (!fileSymbols) return null;
		for (const def of fileSymbols.definitions) {
			if (positionMatches(def.location, line, column, def.name.length)) return {
				name: def.name,
				def,
				onKey: false
			};
			if (def.keyLocation && def.importedName && positionMatches(def.keyLocation, line, column, def.importedName.length)) return {
				name: def.importedName,
				def,
				onKey: true
			};
		}
		const refAtPos = findRefAtPosition(fileSymbols.references, line, column);
		if (refAtPos) return {
			name: refAtPos.name,
			def: refAtPos.resolvedDef
		};
		const exportAtPos = findDefAtPosition(fileSymbols.exports, line, column);
		if (exportAtPos) return {
			name: exportAtPos.name,
			def: exportAtPos
		};
		return null;
	}
	/**
	* Resolve the cursor's symbol to the file that owns its canonical definition.
	*
	* When the cursor sits on an import-kind binding (either the destructuring
	* site `let { pi } = import("./lib")` or a use-site like `pi * 2` that
	* resolves to it), this walks the import chain to the ultimate origin so
	* that `findAllOccurrences` starts from the top of the re-export graph and
	* can cover every file in the chain via `reverseImports` traversal.
	*
	* For chains like C → B → A (C imports from B, which re-exports from A),
	* walking one hop at a time wouldn't reach A: `reverseImports[B]` doesn't
	* list A because A doesn't import from B. Hence the iterative walk here.
	*
	* Returns the current file when the symbol is defined locally or when the
	* reference is unresolved. When the cursor is on an import-kind binding
	* whose module path isn't resolvable (e.g. the imported file is missing or
	* not yet indexed), `unresolvedImport` carries the raw path string so the
	* caller can warn the user that the rename is about to be scoped to a
	* single file instead of the full workspace.
	*/
	resolveCanonicalFile(filePath, line, column) {
		const symbol = this.getSymbolAtPosition(filePath, line, column);
		if (!symbol) return null;
		if (symbol.def?.keyLocation && symbol.onKey !== true) return {
			file: symbol.def.location.file,
			name: symbol.name
		};
		let currentDef = symbol.def;
		const visited = /* @__PURE__ */ new Set();
		while (currentDef?.kind === "import" && currentDef.importPath) {
			const currentFile = currentDef.location.file;
			if (visited.has(currentFile)) break;
			visited.add(currentFile);
			const resolvedPath = this.getFileSymbols(currentFile)?.imports.get(currentDef.importPath);
			if (!resolvedPath) return {
				file: currentFile,
				name: symbol.name,
				unresolvedImport: currentDef.importPath
			};
			const nextDef = this.getFileSymbols(resolvedPath)?.definitions.find((d) => d.name === symbol.name && d.scope === 0);
			if (!nextDef) return {
				file: resolvedPath,
				name: symbol.name
			};
			currentDef = nextDef;
		}
		return {
			file: currentDef?.location.file ?? filePath,
			name: symbol.name
		};
	}
	/**
	* Find all locations of a symbol: the definition site + all reference sites
	* + export-object keys + destructuring bindings in importing files, and
	* recursively in files that re-export through the chain.
	* Used by "Find All References" and cross-file Rename.
	*
	* `filePath` should be the file that *owns* the definition (i.e. the
	* canonical source). For rename, the provider resolves the cursor's symbol
	* first via `resolveCanonicalFile` and passes that file so the BFS starts
	* from the top of the re-export chain.
	*
	* The BFS walks files that import (directly or transitively via re-exports)
	* the canonical file. An importer B is treated as a re-exporter — and
	* enqueued as a new BFS root — when B has a matching import-kind binding
	* from the current file AND B's trailing object exports the same name
	* with the value-side Sym resolving back to that import-kind binding.
	*
	* Occurrences are deduplicated by (file, line, column) because Dvala's
	* shorthand `{ pi }` produces two AST nodes (Str key + Sym value) at the
	* same source position.
	*/
	findAllOccurrences(filePath, symbolName) {
		const results = [];
		const seen = /* @__PURE__ */ new Set();
		const push = (loc, nameLength) => {
			const key = `${loc.file}:${loc.line}:${loc.column}`;
			if (seen.has(key)) return;
			seen.add(key);
			results.push({
				...loc,
				nameLength
			});
		};
		const originSymbols = this.cache.get(filePath)?.symbols;
		if (originSymbols) {
			for (const def of originSymbols.definitions) {
				if (def.name === symbolName) push(def.location, def.name.length);
				if (def.keyLocation && def.importedName === symbolName && def.name !== symbolName) push(def.keyLocation, def.importedName.length);
			}
			for (const ref of originSymbols.references) if (ref.name === symbolName) push(ref.location, ref.name.length);
			for (const exp of originSymbols.exports) if (exp.name === symbolName) push(exp.location, exp.name.length);
		}
		const worklist = [filePath];
		const visited = new Set([filePath]);
		while (worklist.length > 0) {
			const current = worklist.shift();
			const importers = this.reverseImports.get(current);
			if (!importers) continue;
			for (const importerPath of importers) {
				const importerSymbols = this.cache.get(importerPath)?.symbols;
				if (!importerSymbols) continue;
				const matchingImportDefs = /* @__PURE__ */ new Set();
				for (const def of importerSymbols.definitions) {
					if (def.kind !== "import" || !def.importPath) continue;
					if (importerSymbols.imports.get(def.importPath) !== current) continue;
					const isAliased = def.keyLocation !== void 0;
					if (!isAliased && def.name === symbolName) {
						matchingImportDefs.add(def);
						push(def.location, def.name.length);
					} else if (isAliased && def.importedName === symbolName) {
						matchingImportDefs.add(def);
						push(def.keyLocation, def.importedName.length);
					}
				}
				for (const ref of importerSymbols.references) {
					if (ref.name !== symbolName) continue;
					if (ref.resolvedDef && matchingImportDefs.has(ref.resolvedDef)) push(ref.location, ref.name.length);
				}
				if (matchingImportDefs.size > 0 && isReexport(importerSymbols, symbolName, matchingImportDefs)) {
					for (const exp of importerSymbols.exports) if (exp.name === symbolName) push(exp.location, exp.name.length);
					if (!visited.has(importerPath)) {
						visited.add(importerPath);
						worklist.push(importerPath);
					}
				}
			}
		}
		return results;
	}
	/**
	* Get top-level document symbols for the outline view.
	*/
	getDocumentSymbols(filePath) {
		return this.getDefinitions(filePath).filter((d) => d.scope === 0);
	}
	/**
	* Get all symbols visible at a given position in a file.
	* Includes top-level definitions defined before the cursor,
	* plus definitions from all enclosing scope ranges.
	*/
	getSymbolsInScope(filePath, line, column) {
		const fileSymbols = this.cache.get(filePath)?.symbols;
		if (!fileSymbols) return this.cache.get(filePath)?.tokenDefs ?? [];
		const result = [];
		const seen = /* @__PURE__ */ new Set();
		const enclosing = fileSymbols.scopeRanges.filter((sr) => positionInRange(line, column, sr)).sort((a, b) => rangeArea(a) - rangeArea(b));
		for (const scope of enclosing) for (const def of scope.definitions) if (!seen.has(def.name)) {
			seen.add(def.name);
			result.push(def);
		}
		for (const def of fileSymbols.definitions) {
			if (def.scope !== 0) continue;
			if (seen.has(def.name)) continue;
			if (def.location.line < line || def.location.line === line && def.location.column <= column) {
				seen.add(def.name);
				result.push(def);
			}
		}
		return result;
	}
	/**
	* Get diagnostics for a file: parse errors + unresolved symbol references.
	*/
	getDiagnostics(filePath) {
		const fileSymbols = this.cache.get(filePath)?.symbols;
		if (!fileSymbols) return {
			parseErrors: [],
			unresolvedRefs: []
		};
		const unresolvedRefs = fileSymbols.references.filter((r) => r.resolvedDef === null);
		return {
			parseErrors: fileSymbols.parseErrors,
			unresolvedRefs
		};
	}
	/**
	* Invalidate a file and all files that depend on it.
	*/
	invalidateFile(filePath) {
		this.cache.delete(filePath);
		const dependents = this.reverseImports.get(filePath);
		if (dependents) for (const dep of dependents) this.cache.delete(dep);
	}
	updateReverseImports(filePath, imports) {
		for (const [, importers] of this.reverseImports) importers.delete(filePath);
		for (const resolvedPath of imports.values()) {
			let importers = this.reverseImports.get(resolvedPath);
			if (!importers) {
				importers = /* @__PURE__ */ new Set();
				this.reverseImports.set(resolvedPath, importers);
			}
			importers.add(filePath);
		}
	}
};
/** True when (line, column) falls within the `nameLength` span starting at `loc`. */
function positionMatches(loc, line, column, nameLength) {
	return loc.line === line && column >= loc.column && column < loc.column + nameLength;
}
/** Find the definition at a given source position. */
function findDefAtPosition(defs, line, column) {
	for (const def of defs) if (positionMatches(def.location, line, column, def.name.length)) return def;
	return null;
}
/** Find the reference at a given source position (cursor must be within the name span). */
function findRefAtPosition(refs, line, column) {
	for (const ref of refs) if (ref.location.line === line && column >= ref.location.column && column < ref.location.column + ref.name.length) return ref;
	return null;
}
/**
* Extract exported symbol names from the file's return value.
* Detects the Dvala module pattern where the last expression is an object literal:
*   `let ...; { pi: ..., e: ... }`
* The object keys become the file's exports.
*/
function extractExports(nodes, sourceMap, filePath) {
	if (nodes.length === 0) return [];
	const lastNode = nodes[nodes.length - 1];
	if (lastNode[0] !== NodeTypes.Object) return [];
	const exports = [];
	const entries = lastNode[1];
	for (const entry of entries) if (Array.isArray(entry) && Array.isArray(entry[0])) {
		const [keyNode, valueNode] = entry;
		if (keyNode[0] === NodeTypes.Str) {
			const name = keyNode[1];
			const nodeId = keyNode[2];
			const exportDef = {
				name,
				kind: "variable",
				nodeId,
				location: resolveLocation(nodeId, sourceMap, filePath),
				scope: 0
			};
			if (valueNode && valueNode[0] === NodeTypes.Sym) exportDef.valueNodeId = valueNode[2];
			exports.push(exportDef);
		}
	}
	return exports;
}
/** Resolve a node ID to a source location. */
function resolveLocation(nodeId, sourceMap, filePath) {
	if (!sourceMap) return {
		file: filePath,
		line: 0,
		column: 0
	};
	const pos = sourceMap.positions.get(nodeId);
	if (!pos) return {
		file: filePath,
		line: 0,
		column: 0
	};
	return {
		file: sourceMap.sources[pos.source]?.path ?? filePath,
		line: pos.start[0] + 1,
		column: pos.start[1] + 1
	};
}
/** Extract import paths from AST nodes and resolve them via the caller-supplied callback. */
function extractImports(nodes, fromFile, imports, resolveImport) {
	for (const node of nodes) walkForImports(node, fromFile, imports, resolveImport);
}
/** Known AST node type strings — used to identify AstNode tuples in payload arrays. */
const knownNodeTypes = new Set(Object.values(NodeTypes));
/** Check if a value looks like an AstNode: [knownType, payload, number]. */
function isAstNode$2(value) {
	return Array.isArray(value) && value.length === 3 && typeof value[0] === "string" && knownNodeTypes.has(value[0]) && typeof value[2] === "number";
}
function walkForImports(node, fromFile, imports, resolveImport) {
	const [type, payload] = node;
	if (type === NodeTypes.Import) {
		const importPath = payload;
		if (importPath.startsWith(".") || importPath.startsWith("/")) {
			const resolved = resolveImport(importPath, fromFile);
			if (resolved) imports.set(importPath, resolved);
		}
		return;
	}
	if (Array.isArray(payload)) {
		for (const child of payload) if (isAstNode$2(child)) walkForImports(child, fromFile, imports, resolveImport);
		else if (Array.isArray(child)) {
			for (const c of child) if (isAstNode$2(c)) walkForImports(c, fromFile, imports, resolveImport);
		}
	}
}
/** Check if a 1-based position is inside a scope range. */
function positionInRange(line, column, range) {
	if (line < range.startLine || line > range.endLine) return false;
	if (line === range.startLine && column < range.startColumn) return false;
	if (line === range.endLine && column > range.endColumn) return false;
	return true;
}
/** Approximate area of a scope range (for sorting inner-before-outer). */
function rangeArea(range) {
	return (range.endLine - range.startLine) * 1e5 + (range.endColumn - range.startColumn);
}
/**
* Decide whether `file` re-exports `name` from one of the given import defs.
*
* A re-export requires:
*  1. An entry in `file.exports` with matching name (the trailing object
*     has `{ name, ... }` or `{ name: ... }`).
*  2. The value-side expression of that export entry is a Sym whose ref
*     resolves back to one of the import defs (i.e., the exported value
*     really is the imported binding, not an unrelated same-named local).
*
* Without (2), a file that imports `pi` from A but exports an unrelated
* local under the name `pi` (`{ pi: pi2 }`) would be misclassified as a
* re-exporter, dragging A's rename into it.
*
* The Sym lookup uses the export's `valueNodeId`, captured during
* `extractExports`. When the export value is a non-Sym expression
* (`{ pi: 1 + 1 }`), `valueNodeId` is undefined and this check returns false.
*/
function isReexport(fileSymbols, name, matchingImportDefs) {
	for (const exp of fileSymbols.exports) {
		if (exp.name !== name) continue;
		if (exp.valueNodeId === void 0) continue;
		const valueRef = fileSymbols.references.find((r) => r.nodeId === exp.valueNodeId);
		if (valueRef?.resolvedDef && matchingImportDefs.has(valueRef.resolvedDef)) return true;
	}
	return false;
}
/** Simple string hash for change detection. */
function simpleHash(str) {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash |= 0;
	}
	return hash.toString(36);
}
//#endregion
//#region src/reference/dvala.ts
/**
* Tagged template literal helper for embedding Dvala code snippets.
*
* Usage:
*   import { dvala } from './dvala'
*
*   const code = dvala`
*     let x = 1;
*     x + 1
*   `
*
* The helper uses String.raw so backticks inside the Dvala code do not need
* escaping. The content is trimmed and run through the formatter, so the
* resulting string always satisfies format(code) === code.
*
* Using this helper is the canonical way to write Dvala snippets in TypeScript
* source files. The round-trip test suite (format.roundtrip.test.ts) enforces
* that all snippets produced this way remain stable — if the formatter changes,
* re-running `npm run format-snippets` brings them back into sync.
*/
/**
* Format a single-expression Dvala snippet, stripping the trailing semicolon
* that the formatter adds. Use this for short inline examples in datatype.ts
* and shorthand.ts where the reference tests require no trailing semicolon.
*/
function snippet(code) {
	return format(code.trim()).trimEnd().replace(/;$/, "");
}
//#endregion
//#region src/reference/datatype.ts
const datatype = {
	"-type-number": {
		datatype: true,
		title: "number",
		category: "datatype",
		description: "A `number`",
		examples: [snippet("42"), snippet("3.14")]
	},
	"-type-string": {
		datatype: true,
		title: "string",
		category: "datatype",
		description: "A `string`. Strings are written with double quotes. Template strings use backticks and support `${...}` interpolation — any expression can appear inside the braces.",
		examples: [
			snippet("\"hello\""),
			snippet("\"\""),
			snippet("`Hello, World!`"),
			snippet("`${2} * ${3} = ${2 * 3}`")
		]
	},
	"-type-object": {
		datatype: true,
		title: "object",
		category: "datatype",
		description: "An `object`, a collection of key-value pairs where keys are `strings`",
		examples: [snippet("{}"), snippet("{ a: 1, b: 2}")]
	},
	"-type-array": {
		datatype: true,
		title: "array",
		category: "datatype",
		description: "An `array`",
		examples: [
			snippet("[]"),
			snippet("[1, 2, 3]"),
			snippet("[\"a\", null, true]")
		]
	},
	"-type-vector": {
		datatype: true,
		title: "vector",
		category: "datatype",
		description: "An `array` of `numbers`",
		examples: [snippet("[]"), snippet("[1, 2, 3]")]
	},
	"-type-matrix": {
		datatype: true,
		title: "matrix",
		category: "datatype",
		description: "A `matrix`, a two-dimensional `array` with `numbers` where each row has the same number of columns. A `matrix` is also a `grid`.",
		examples: [
			snippet("[[42]]"),
			snippet("[[1, 2], [3, 4]]"),
			snippet("[[1, 2], [3, 4], [5, 6]]")
		]
	},
	"-type-grid": {
		datatype: true,
		title: "grid",
		category: "datatype",
		description: "A `grid`, a two-dimensional `array` where each row has the same number of columns",
		examples: [
			snippet("[[]]"),
			snippet("[[1, 2], [3, 4]]"),
			snippet("[[\"a\", \"b\"], [3, 4], [5, 6]]")
		]
	},
	"-type-boolean": {
		datatype: true,
		title: "boolean",
		category: "datatype",
		description: "A `boolean`",
		examples: [snippet("true"), snippet("false")]
	},
	"-type-atom": {
		datatype: true,
		title: "atom",
		category: "datatype",
		description: "An atom — a self-evaluating named constant used as a tag or label. Atoms start with `:` followed by a name. Two atoms with the same name are always equal. Common atoms include `:ok` and `:error`.",
		examples: [
			snippet(":ok"),
			snippet(":error"),
			snippet("[:ok, 42]"),
			snippet(":ok == :ok")
		]
	},
	"-type-function": {
		datatype: true,
		title: "function",
		category: "datatype",
		description: "A `function`",
		examples: [
			snippet("x -> x + 1"),
			snippet("(a, b, c) -> (a + b) * c"),
			snippet("() -> 42"),
			snippet("-> $ + $2")
		]
	},
	"-type-integer": {
		datatype: true,
		title: "integer",
		category: "datatype",
		description: "An `integer`",
		examples: [snippet("42"), snippet("-42")]
	},
	"-type-any": {
		datatype: true,
		title: "any",
		category: "datatype",
		description: "`Any` value",
		examples: [
			snippet("42"),
			snippet("\"hello\""),
			snippet("true"),
			snippet("null")
		]
	},
	"-type-null": {
		datatype: true,
		title: "null",
		category: "datatype",
		description: "The value `null`",
		examples: [snippet("null")]
	},
	"-type-collection": {
		datatype: true,
		title: "collection",
		category: "datatype",
		description: "A collection, an `object`, an `array` or a `string`",
		examples: [
			snippet("{ foo: 42 }"),
			snippet("[1, 2, 3]"),
			snippet("\"hello\"")
		]
	},
	"-type-sequence": {
		datatype: true,
		title: "sequence",
		category: "datatype",
		description: "A sequence, an `array` or a `string`",
		examples: [snippet("[1, 2, 3]"), snippet("\"hello\"")]
	},
	"-type-regexp": {
		datatype: true,
		title: "regexp",
		category: "datatype",
		description: "A regular expression",
		examples: [
			snippet("regexp(\"^\\\\s*(.*)$\")"),
			snippet("#\"^\\s*(.*)$\""),
			snippet("#\"albert\"ig")
		]
	},
	"-type-effect": {
		datatype: true,
		title: "effect",
		category: "datatype",
		description: "An effect, created with the `effect` special expression. Effects are used with `perform` to trigger algebraic effects.",
		examples: [snippet("@dvala.io.print"), snippet("@dvala.time.now")]
	},
	"-type-never": {
		datatype: true,
		title: "never",
		category: "datatype",
		description: "A value that can never be created",
		examples: [`
// perform(@dvala.error, { message: "error" }) will never return a value
do with handler @dvala.error(arg) -> resume("never") end; perform(@dvala.error, { message: "error" }) end`]
	}
};
//#endregion
//#region src/reference/prelude.ts
/**
* Reference entries for the standard prelude type aliases declared in
* `src/prelude.dvala`. Keep `definition` in sync with the alias body
* there — these are documentation surfaces and the prelude file is the
* source of truth for typechecker semantics.
*/
const prelude = {
	"-prelude-Positive": {
		prelude: true,
		title: "Positive",
		category: "prelude",
		definition: "Number & {n | n > 0}",
		description: "Strictly positive numbers (n > 0). Use as a precondition where zero and negatives are both invalid — e.g. divisor in a count-style formula, quantities, lengths.",
		examples: [snippet("let x: Positive = 5"), snippet("let f = (n: Positive) -> 100 / n")],
		seeAlso: ["-prelude-NonNegative", "-prelude-NonZero"]
	},
	"-prelude-NonNegative": {
		prelude: true,
		title: "NonNegative",
		category: "prelude",
		definition: "Number & {n | n >= 0}",
		description: "Non-negative numbers (n >= 0). The natural-number sense from CS where 0 is included; common precondition for array indices and counts.",
		examples: [snippet("let i: NonNegative = 0"), snippet("let f = (idx: NonNegative) -> idx + 1")],
		seeAlso: ["-prelude-Positive", "-prelude-NonZero"]
	},
	"-prelude-NonZero": {
		prelude: true,
		title: "NonZero",
		category: "prelude",
		definition: "Number & {n | n != 0}",
		description: "Non-zero numbers (n != 0). The minimal precondition for division — `Positive` is stricter than needed when negative divisors are also fine.",
		examples: [snippet("let d: NonZero = -3"), snippet("let safeDivide = (a: Number, b: NonZero) -> a / b")],
		seeAlso: ["-prelude-Positive", "-prelude-NonNegative"]
	},
	"-prelude-NonEmpty": {
		prelude: true,
		title: "NonEmpty",
		category: "prelude",
		definition: "T & {xs | count(xs) > 0} where T: Sequence",
		description: "Non-empty sequences (count(xs) > 0). Generic over the `Sequence` upper bound, so it works for both arrays and strings — `NonEmpty<Array<Number>>` and `NonEmpty<String>` are both valid.",
		examples: [snippet("let xs: NonEmpty<Number[]> = [1, 2, 3]"), snippet("let s: NonEmpty<String> = \"hi\"")]
	}
};
//#endregion
//#region src/reference/shorthand.ts
const shorthand = {
	"-short-regexp": {
		shorthand: true,
		title: "#\"pattern\"",
		category: "shorthand",
		description: "Shorthand for `regexp(pattern)`. Only difference is that escaping is not needed.",
		examples: [snippet("#\"^\\s*(.*)$\""), snippet("#\"albert\"ig")],
		seeAlso: [
			"regexp",
			"reMatch",
			"replace",
			"replaceAll"
		]
	},
	"-short-fn": {
		shorthand: true,
		title: "-> expression",
		category: "shorthand",
		description: `
Shorthand for \`(args, ...) -> expression\`.
\`$\` is the first argument, and \`$2, $3, ...\` are the second, third, ... argument.`,
		examples: [snippet("-> $ + $2"), snippet("(-> $ * $)(9)")]
	},
	"-short-object": {
		shorthand: true,
		title: "{ foo }",
		category: "shorthand",
		description: "Shorthand for `{ foo: foo }`. When a key and variable name are the same, you can omit the value.",
		examples: [snippet("let x = 1; let y = 2; { x, y }"), snippet("let name = \"Alice\"; let age = 30; { name, age }")]
	}
};
//#endregion
//#region src/reference/index.ts
function docsToReference(expressions) {
	const result = {};
	for (const [key, expr] of Object.entries(expressions)) {
		const docs = expr.docs;
		if (!docs) throw new Error(`Missing docs for expression "${key}"`);
		result[key] = {
			title: key,
			category: docs.category,
			description: docs.description,
			returns: docs.returns,
			args: docs.args,
			variants: docs.variants,
			examples: docs.examples,
			...docs.seeAlso ? { seeAlso: docs.seeAlso } : {},
			...docs.hideOperatorForm ? { noOperatorDocumentation: true } : {}
		};
	}
	return result;
}
function moduledDocsToReference(module) {
	const result = {};
	for (const [key, docs] of Object.entries(module.docs ?? {})) {
		const qualifiedKey = `${module.name}.${key}`;
		result[qualifiedKey] = {
			title: qualifiedKey,
			category: docs.category,
			description: docs.description,
			returns: docs.returns,
			args: docs.args,
			variants: docs.variants,
			examples: docs.examples,
			...docs.seeAlso ? { seeAlso: docs.seeAlso } : {},
			...docs.hideOperatorForm ? { noOperatorDocumentation: true } : {}
		};
	}
	return result;
}
const assertionRef = docsToReference(assertionNormalExpression);
const bitwiseReference = docsToReference(bitwiseNormalExpression);
const arrayRef = docsToReference(arrayNormalExpression);
const collectionRef = docsToReference(collectionNormalExpression);
const functionalRef = docsToReference(functionalNormalExpression);
const mathRef = docsToReference(mathNormalExpression);
const metaRef = docsToReference(getMetaNormalExpression({}, {}));
const miscRef = docsToReference(miscNormalExpression);
const objectRef = docsToReference(objectNormalExpression);
const predicatesRef = docsToReference(predicatesNormalExpression);
const regexpRef = docsToReference(regexpNormalExpression);
const sequenceRef = docsToReference(sequenceNormalExpression);
const stringRef = docsToReference(stringNormalExpression);
function specialExpressionDocsToReference() {
	const result = {};
	for (const [name, type] of Object.entries(specialExpressionTypes)) {
		const docs = specialExpressions[type]?.docs;
		if (!docs) continue;
		if (isFunctionDocs(docs)) result[name] = {
			title: name,
			category: docs.category,
			description: docs.description,
			returns: docs.returns,
			args: docs.args,
			variants: docs.variants,
			examples: docs.examples,
			...docs.seeAlso ? { seeAlso: docs.seeAlso } : {},
			...docs.hideOperatorForm ? { noOperatorDocumentation: true } : {}
		};
		else result[name] = {
			title: name,
			category: docs.category,
			description: docs.description,
			customVariants: docs.customVariants,
			...docs.details ? { details: docs.details } : {},
			...docs.returns ? { returns: docs.returns } : {},
			examples: docs.examples,
			...docs.seeAlso ? { seeAlso: docs.seeAlso } : {}
		};
	}
	return result;
}
const specialExpressionsReference = specialExpressionDocsToReference();
const normalExpressionReference = {
	...assertionRef,
	...bitwiseReference,
	...collectionRef,
	...arrayRef,
	...sequenceRef,
	...mathRef,
	...functionalRef,
	...metaRef,
	...miscRef,
	...objectRef,
	...predicatesRef,
	...regexpRef,
	...stringRef
};
const moduleReference = {
	...moduledDocsToReference(assertModule),
	...moduledDocsToReference(gridModule),
	...moduledDocsToReference(vectorModule),
	...moduledDocsToReference(linearAlgebraModule),
	...moduledDocsToReference(matrixModule),
	...moduledDocsToReference(numberTheoryModule),
	...moduledDocsToReference(stringUtilsModule),
	...moduledDocsToReference(collectionUtilsModule),
	...moduledDocsToReference(sequenceUtilsModule),
	...moduledDocsToReference(mathUtilsModule),
	...moduledDocsToReference(functionalUtilsModule),
	...moduledDocsToReference(bitwiseUtilsModule),
	...moduledDocsToReference(convertModule),
	...moduledDocsToReference(jsonModule),
	...moduledDocsToReference(timeModule),
	...moduledDocsToReference(handlerModule),
	...moduledDocsToReference(astModule),
	...moduledDocsToReference(macrosModule)
};
Object.entries(normalExpressionReference).forEach(([key, obj]) => {
	if (!normalExpressions[key]) throw new Error(`Missing normal expression ${key} in normalExpressions`);
	const arity = normalExpressions[key].arity;
	if (!obj.noOperatorDocumentation && canBeOperator(arity)) {
		obj._isOperator = true;
		if (isSymbolicOperator(key)) obj._prefereOperator = true;
	}
});
Object.entries(specialExpressionsReference).forEach(([key, obj]) => {
	if (isFunctionReference(obj)) {
		const arity = specialExpressions[specialExpressionTypes[key]]?.arity;
		if (arity && canBeOperator(arity)) obj._isOperator = true;
	}
});
const apiReference = sortByCategory({
	...normalExpressionReference,
	...specialExpressionsReference,
	...shorthand,
	...datatype,
	...prelude
});
function deriveEffectReference() {
	const result = {};
	for (const [name, def] of Object.entries(allStandardEffectDefinitions)) {
		const key = `-effect-${name}`;
		result[key] = {
			effect: true,
			title: name,
			category: "effect",
			description: def.docs.description,
			args: def.docs.args,
			returns: def.docs.returns,
			variants: def.docs.variants,
			examples: def.docs.examples,
			...def.docs.seeAlso ? { seeAlso: def.docs.seeAlso } : {}
		};
	}
	return result;
}
const effectReference = deriveEffectReference();
const allReference = sortByCategory({
	...apiReference,
	...moduleReference,
	...effectReference
});
function sortByCategory(ref) {
	return Object.fromEntries(Object.entries(ref).sort(([keyA, refA], [keyB, refB]) => {
		const catA = refA.category === "special-expression" ? "" : refA.category;
		const catB = refB.category === "special-expression" ? "" : refB.category;
		if (catA !== catB) return catA.localeCompare(catB);
		return keyA.localeCompare(keyB);
	}));
}
//#endregion
//#region src/shared/completionBuilder.ts
/**
* Portable completion-item construction. Produces editor-agnostic
* `CompletionItem` records from the builtin reference data and from
* user-defined `SymbolDef`s. Editor adapters (VS Code, Monaco) translate
* the portable `kind` and `insertText` fields into their host-specific
* shapes.
*
* The `kind` field uses lowercase names that map 1:1 onto both
* `vscode.CompletionItemKind` and `monaco.languages.CompletionItemKind`
* — picking the host enum value is a single switch in the adapter.
*
* Snippet templates: when `params` is present, `insertText` carries the
* snippet body with `${N:paramName}` placeholders. Adapters that don't
* support snippets can fall back to the raw `label`.
*/
/** Map a reference category to a portable completion kind. */
function categoryToKind(category) {
	switch (category) {
		case "special-expression": return "keyword";
		case "effect":
		case "playground-effect": return "event";
		case "shorthand": return "operator";
		case "datatype":
		case "prelude": return "class";
		default: return "function";
	}
}
/** Map a SymbolDef.kind to a portable completion kind. */
function symbolKindToCompletionKind(kind) {
	switch (kind) {
		case "function": return "function";
		case "macro": return "method";
		case "handler": return "event";
		case "import": return "module";
		case "parameter":
		case "variable": return "variable";
	}
}
/** Build the snippet body `name(${1:arg1}, ${2:arg2})`, or `name($0)` if no args. */
function buildSnippet(name, argNames) {
	if (argNames.length === 0) return `${name}($0)`;
	return `${name}(${argNames.map((argName, i) => `\${${i + 1}:${argName}}`).join(", ")})`;
}
/**
* Build a completion item from a single builtin reference. Pure function;
* rich documentation rendering belongs to the host adapter.
*/
function referenceToCompletion(name, ref) {
	const item = {
		label: name,
		kind: categoryToKind(ref.category),
		detail: ref.category
	};
	if (isFunctionReference$1(ref) && ref.variants.length > 0) {
		const argNames = ref.variants[0].argumentNames;
		item.params = argNames;
		item.insertText = buildSnippet(name, argNames);
	}
	return item;
}
/**
* Build a completion item from a user-defined SymbolDef.
*
* Symbols sort after builtins (sortText starts with `'1_'`) so the
* builtin set always appears first when both groups match a prefix.
*/
function symbolDefToCompletion(def) {
	const item = {
		label: def.name,
		kind: symbolKindToCompletionKind(def.kind),
		detail: def.kind,
		sortText: `1_${def.name}`
	};
	if (def.params && def.params.length > 0) {
		item.params = def.params;
		item.insertText = buildSnippet(def.name, def.params);
	}
	return item;
}
/**
* Build the static set of builtin completions from `allReference`.
* Deduplicates by label so the same name registered in multiple modules
* appears only once.
*/
function buildBuiltinCompletions() {
	const seen = /* @__PURE__ */ new Set();
	const items = [];
	for (const ref of Object.values(allReference)) {
		if (seen.has(ref.title)) continue;
		seen.add(ref.title);
		items.push(referenceToCompletion(ref.title, ref));
	}
	return items;
}
//#endregion
//#region src/shared/callContext.ts
/**
* Find the function call context at a 1-based cursor position.
*
* Returns null when the cursor is not inside a parenthesized argument
* list, or when the unmatched `(` has no identifier immediately before it
* (e.g. an anonymous IIFE call site).
*/
function findCallContext(source, position) {
	const line = position.line;
	const column = position.column;
	let offset = 0;
	let currentLine = 1;
	let currentCol = 1;
	while (offset < source.length) {
		if (currentLine === line && currentCol === column) break;
		if (source[offset] === "\n") {
			currentLine++;
			currentCol = 1;
		} else currentCol++;
		offset++;
	}
	let depth = 0;
	let commaCount = 0;
	for (let i = offset - 1; i >= 0; i--) {
		const ch = source[i];
		if (ch === ")") depth++;
		else if (ch === "(") {
			if (depth === 0) {
				const nameMatch = source.substring(0, i).trimEnd().match(/([a-zA-Z_$][a-zA-Z0-9_$]*)$/);
				if (nameMatch) return {
					functionName: nameMatch[1],
					activeParam: commaCount
				};
				return null;
			}
			depth--;
		} else if (ch === "," && depth === 0) commaCount++;
	}
	return null;
}
//#endregion
//#region src/shared/diagnosticBuilder.ts
/** Build a 1-based single-character range starting at the given position. */
function pointRange(line, column) {
	const safeLine = Math.max(1, line);
	const safeCol = Math.max(1, column);
	return {
		start: {
			line: safeLine,
			column: safeCol
		},
		end: {
			line: safeLine,
			column: safeCol + 1
		}
	};
}
/**
* Build diagnostics for parse errors. Errors without source info are dropped
* (no place to anchor a marker) — the same behavior the VS Code extension
* inlined.
*/
function buildParseDiagnostics(parseErrors) {
	const diagnostics = [];
	for (const err of parseErrors) {
		if (!err.sourceCodeInfo) continue;
		diagnostics.push({
			message: err.message,
			range: pointRange(err.sourceCodeInfo.position.line, err.sourceCodeInfo.position.column),
			severity: "error",
			source: "dvala"
		});
	}
	return diagnostics;
}
/**
* Build diagnostics for unresolved symbol references. Each ref produces a
* range covering the full symbol-name span so the squiggle aligns with the
* identifier rather than a single cell.
*/
function buildSymbolDiagnostics(unresolvedRefs) {
	const diagnostics = [];
	for (const ref of unresolvedRefs) {
		const line = Math.max(1, ref.location.line);
		const column = Math.max(1, ref.location.column);
		diagnostics.push({
			message: `Undefined symbol '${ref.name}'`,
			range: {
				start: {
					line,
					column
				},
				end: {
					line,
					column: column + ref.name.length
				}
			},
			severity: "error",
			source: "dvala"
		});
	}
	return diagnostics;
}
/**
* Build diagnostics from a typecheck result. Type errors are downgraded to
* `'warning'` and warnings to `'info'` because the type system is
* intentionally non-blocking — code still runs even with type mismatches,
* mirroring the TypeScript convention. Diagnostics without source info are
* dropped.
*/
function buildTypeDiagnostics(typeResult) {
	const diagnostics = [];
	for (const diag of typeResult.diagnostics) {
		if (!diag.sourceCodeInfo) continue;
		if (diag.sourceRange) {
			diagnostics.push({
				message: diag.message,
				range: diag.sourceRange,
				severity: diag.severity === "error" ? "warning" : "info",
				source: "dvala-types"
			});
			continue;
		}
		const line = Math.max(1, diag.sourceCodeInfo.position.line);
		const col = Math.max(1, diag.sourceCodeInfo.position.column);
		const nameLength = diag.sourceCodeInfo.code.length;
		diagnostics.push({
			message: diag.message,
			range: {
				start: {
					line,
					column: col
				},
				end: {
					line,
					column: col + nameLength
				}
			},
			severity: diag.severity === "error" ? "warning" : "info",
			source: "dvala-types"
		});
	}
	return diagnostics;
}
//#endregion
//#region src/typechecker/types.ts
/** Singletons for the two identity-free tails. */
const ClosedTail = Object.freeze({ tag: "Closed" });
const OpenTail = Object.freeze({ tag: "Open" });
/** The empty (pure) effect set. Frozen to prevent accidental mutation. */
const PureEffects = Object.freeze({
	effects: Object.freeze(/* @__PURE__ */ new Set()),
	tail: ClosedTail
});
const NumberType = {
	tag: "Primitive",
	name: "Number"
};
const IntegerType = {
	tag: "Primitive",
	name: "Integer"
};
const StringType = {
	tag: "Primitive",
	name: "String"
};
const BooleanType = {
	tag: "Primitive",
	name: "Boolean"
};
const NullType = {
	tag: "Primitive",
	name: "Null"
};
const Unknown = { tag: "Unknown" };
const Never = { tag: "Never" };
const RegexType = { tag: "Regex" };
const AnyFunction = { tag: "AnyFunction" };
function atom(name) {
	return {
		tag: "Atom",
		name
	};
}
function literal(value) {
	return {
		tag: "Literal",
		value
	};
}
function fn(params, ret, effects = PureEffects, handlerWrapper, restParam, asserts) {
	return {
		tag: "Function",
		params,
		ret,
		effects,
		...handlerWrapper ? { handlerWrapper } : {},
		...restParam !== void 0 ? { restParam } : {},
		...asserts ? { asserts } : {}
	};
}
function functionAcceptsArity(t, arity) {
	return t.restParam !== void 0 ? arity >= t.params.length : arity === t.params.length;
}
function getFunctionParamType(t, index) {
	if (index < t.params.length) return t.params[index];
	return t.restParam;
}
function functionArityLabel(t) {
	return t.restParam !== void 0 ? `at least ${t.params.length}` : `${t.params.length}`;
}
function handlerType(body, output, handled, introduced = PureEffects) {
	return {
		tag: "Handler",
		body,
		output,
		handled,
		introduced
	};
}
function tuple(elements) {
	return {
		tag: "Tuple",
		elements
	};
}
function record(fields, open = false) {
	return {
		tag: "Record",
		fields: new Map(Object.entries(fields)),
		open
	};
}
function array(element) {
	return {
		tag: "Array",
		element
	};
}
function sequence(prefix, rest, minLength = prefix.length, maxLength) {
	return normalizeSequenceType({
		tag: "Sequence",
		prefix,
		rest,
		minLength,
		...maxLength !== void 0 ? { maxLength } : {}
	});
}
function toSequenceType(type) {
	switch (type.tag) {
		case "Sequence": return normalizeSequenceType(type);
		case "Array": return sequence([], type.element, 0);
		case "Tuple": return sequence(type.elements, Never);
		default: return;
	}
}
function normalizeSequenceType(type) {
	const minLength = type.rest.tag === "Never" ? type.prefix.length : Math.max(type.minLength, type.prefix.length);
	const maxLength = type.rest.tag === "Never" ? type.prefix.length : type.maxLength;
	return {
		tag: "Sequence",
		prefix: type.prefix,
		rest: type.rest,
		minLength,
		...maxLength !== void 0 ? { maxLength } : {}
	};
}
function sequenceElementAt(type, index) {
	return index < type.prefix.length ? type.prefix[index] : type.rest;
}
function sequenceMayHaveIndex(type, index) {
	return type.maxLength === void 0 || index < type.maxLength;
}
function union(...members) {
	const flat = [];
	for (const m of members) if (m.tag === "Union") flat.push(...m.members);
	else flat.push(m);
	const filtered = flat.filter((t) => t.tag !== "Never");
	if (filtered.length === 0) return Never;
	if (filtered.some((t) => t.tag === "Unknown")) return Unknown;
	const deduped = dedup(filtered);
	if (deduped.length === 1) return deduped[0];
	return {
		tag: "Union",
		members: deduped
	};
}
function inter(...members) {
	const flat = [];
	for (const m of members) if (m.tag === "Inter") flat.push(...m.members);
	else flat.push(m);
	const filtered = flat.filter((t) => t.tag !== "Unknown");
	if (filtered.length === 0) return Unknown;
	if (filtered.some((t) => t.tag === "Never")) return Never;
	const deduped = dedup(filtered);
	if (deduped.length === 1) return deduped[0];
	return {
		tag: "Inter",
		members: deduped
	};
}
function neg(inner) {
	if (inner.tag === "Neg") return inner.inner;
	if (inner.tag === "Never") return Unknown;
	if (inner.tag === "Unknown") return Never;
	return {
		tag: "Neg",
		inner
	};
}
/**
* `keyof T` — the union of literal-string keys of `T` when `T` is a
* concrete record; a placeholder Keyof node otherwise. Closed records
* produce a closed-form union; open records can have additional runtime
* keys, so the result is `String` (widened) rather than just the
* declared fields — callers relying on full-key enumeration should
* constrain the record to closed before using `keyof`.
*/
function keyofType(inner) {
	if (inner.tag === "Record") {
		const keys = [...inner.fields.keys()];
		if (keys.length === 0) return inner.open ? StringType : Never;
		const declared = union(...keys.map((k) => literal(k)));
		return inner.open ? StringType : declared;
	}
	if (inner.tag === "Never") return Never;
	if (inner.tag === "Unknown") return StringType;
	return {
		tag: "Keyof",
		inner
	};
}
/**
* `T[K]` — indexed access. When `K` is a concrete literal-string key
* (or a union of such keys) and `T` is a concrete record, resolve to
* the field type(s). Otherwise return a placeholder Index node.
* Missing keys on a closed record → Never; on an open record → Unknown
* (the field might exist at runtime). Optional fields are widened to
* `T | Null` (mirrors `?.` safe-access semantics).
*/
function indexType(target, key) {
	if (target.tag === "Never" || key.tag === "Never") return Never;
	if (target.tag === "Unknown") return Unknown;
	if (key.tag === "Union") return union(...key.members.map((m) => indexType(target, m)));
	if (target.tag === "Record" && key.tag === "Literal" && typeof key.value === "string") {
		const name = key.value;
		const field = target.fields.get(name);
		if (field === void 0) return target.open ? Unknown : Never;
		if (target.optionalFields?.has(name)) return union(field, NullType);
		return field;
	}
	if (target.tag === "Tuple" && key.tag === "Literal" && typeof key.value === "number" && Number.isInteger(key.value)) {
		const idx = key.value;
		if (idx < 0) return Never;
		const elem = target.elements[idx];
		return elem === void 0 ? Never : elem;
	}
	if (target.tag === "Array" && key.tag === "Literal" && typeof key.value === "number" && Number.isInteger(key.value)) {
		if (key.value < 0) return Never;
		return target.element;
	}
	if (target.tag === "Sequence" && key.tag === "Literal" && typeof key.value === "number" && Number.isInteger(key.value)) {
		const idx = key.value;
		if (idx < 0) return Never;
		if (idx < target.prefix.length) return target.prefix[idx];
		if (target.maxLength !== void 0 && idx >= target.maxLength) return Never;
		return target.rest;
	}
	if (target.tag === "Primitive" && target.name === "String" && key.tag === "Literal" && typeof key.value === "number" && Number.isInteger(key.value)) return StringType;
	return {
		tag: "Index",
		target,
		key
	};
}
function typeToString(t) {
	switch (t.tag) {
		case "Primitive": return t.name;
		case "Atom": return `:${t.name}`;
		case "Literal": return typeof t.value === "string" ? `"${t.value}"` : String(t.value);
		case "Function": {
			const params = [...t.params.map((p, i) => {
				return t.asserts && t.asserts.paramIndex === i ? `${t.asserts.binder}: ${typeToString(p)}` : typeToString(p);
			}), ...t.restParam !== void 0 ? [`...${typeToString(array(t.restParam))}`] : []].join(", ");
			const effectStr = effectSetToString(t.effects);
			const ret = t.asserts ? `asserts {${t.asserts.source}}` : typeToString(t.ret);
			return effectStr ? `(${params}) -> ${effectStr} ${ret}` : `(${params}) -> ${ret}`;
		}
		case "Handler": {
			const handledEffects = effectSetToString(effectSet([...t.handled.keys()]));
			const introducedStr = effectSetToString(t.introduced);
			return `Handler<${[
				typeToString(t.body),
				typeToString(t.output),
				handledEffects || "@{}",
				...introducedStr ? [introducedStr] : []
			].join(", ")}>`;
		}
		case "Tuple": return `[${t.elements.map(typeToString).join(", ")}]`;
		case "Record": {
			const entries = [...t.fields.entries()].map(([k, v]) => {
				return `${k}${t.optionalFields?.has(k) ? "?" : ""}: ${typeToString(v)}`;
			});
			return t.open ? `{${entries.join(", ")}, ...}` : `{${entries.join(", ")}}`;
		}
		case "Array": return `${typeToString(t.element)}[]`;
		case "Sequence": {
			if (t.rest.tag === "Never" && t.minLength === t.prefix.length && t.maxLength === t.prefix.length) return `[${t.prefix.map(typeToString).join(", ")}]`;
			if (t.prefix.length === 0 && t.minLength === 0 && t.maxLength === void 0) return `${typeToString(t.rest)}[]`;
			const parts = t.prefix.map(typeToString);
			if (t.rest.tag !== "Never") parts.push(`...${typeToString(array(t.rest))}`);
			const base = `[${parts.join(", ")}]`;
			const impliedMin = t.prefix.length;
			const impliedMax = t.rest.tag === "Never" ? t.prefix.length : void 0;
			if (!(t.minLength !== impliedMin || t.maxLength !== impliedMax)) return base;
			return `${base} (length ${t.maxLength === void 0 ? `${t.minLength}+` : `${t.minLength}..${t.maxLength}`})`;
		}
		case "Regex": return "Regex";
		case "AnyFunction": return "Function";
		case "Union": return t.members.map((m) => typeToString(m)).join(" | ");
		case "Inter": return t.members.map((m) => typeToString(m)).join(" & ");
		case "Neg": return `!${typeToString(t.inner)}`;
		case "Unknown": return "Unknown";
		case "Never": return "Never";
		case "Var": return `α${t.id}`;
		case "Alias": return t.args.length > 0 ? `${t.name}<${t.args.map(typeToString).join(", ")}>` : t.name;
		case "Recursive": return `μ${t.id}.${typeToString(t.body)}`;
		case "Keyof": return `keyof ${typeToString(t.inner)}`;
		case "Index": return `${typeToString(t.target)}[${typeToString(t.key)}]`;
		case "Refined": return `${typeToString(t.base)} & {${t.source}}`;
	}
}
/** Display an effect set. Returns empty string for pure (empty closed) sets. */
function effectSetToString(e) {
	if (e.effects.size === 0 && e.tail.tag === "Closed") return "";
	const names = [...e.effects].sort().join(", ");
	switch (e.tail.tag) {
		case "Closed": return `@{${names}}`;
		case "Open": return names ? `@{${names}, ...}` : "@{...}";
		case "RowVar": {
			const rho = `ρ${e.tail.id}`;
			return names ? `@{${names} | ${rho}}` : `@{${rho}}`;
		}
	}
}
function typeEquals(a, b) {
	if (a.tag !== b.tag) return false;
	switch (a.tag) {
		case "Primitive": return a.name === b.name;
		case "Atom": return a.name === b.name;
		case "Literal": return a.value === b.value;
		case "Function": {
			const bf = b;
			return a.params.length === bf.params.length && a.params.every((p, i) => typeEquals(p, bf.params[i])) && (a.restParam === void 0 && bf.restParam === void 0 || a.restParam !== void 0 && bf.restParam !== void 0 && typeEquals(a.restParam, bf.restParam)) && typeEquals(a.ret, bf.ret) && effectSetEquals(a.effects, bf.effects) && handlerWrapperEquals(a.handlerWrapper, bf.handlerWrapper) && assertsInfoEquals(a.asserts, bf.asserts);
		}
		case "Handler": {
			const bh = b;
			if (!typeEquals(a.body, bh.body) || !typeEquals(a.output, bh.output)) return false;
			if (a.handled.size !== bh.handled.size) return false;
			for (const [name, sig] of a.handled) {
				const other = bh.handled.get(name);
				if (!other) return false;
				if (!typeEquals(sig.argType, other.argType) || !typeEquals(sig.retType, other.retType)) return false;
			}
			if (!effectSetEquals(a.introduced, bh.introduced)) return false;
			return true;
		}
		case "Tuple": {
			const bt = b;
			return a.elements.length === bt.elements.length && a.elements.every((e, i) => typeEquals(e, bt.elements[i]));
		}
		case "Record": {
			const br = b;
			if (a.open !== br.open) return false;
			if (a.fields.size !== br.fields.size) return false;
			for (const [k, v] of a.fields) {
				const bv = br.fields.get(k);
				if (!bv || !typeEquals(v, bv)) return false;
			}
			const aOpt = a.optionalFields ?? /* @__PURE__ */ new Set();
			const bOpt = br.optionalFields ?? /* @__PURE__ */ new Set();
			if (aOpt.size !== bOpt.size) return false;
			for (const k of aOpt) if (!bOpt.has(k)) return false;
			return true;
		}
		case "Array": return typeEquals(a.element, b.element);
		case "Sequence": {
			const bs = b;
			return a.prefix.length === bs.prefix.length && a.prefix.every((member, index) => typeEquals(member, bs.prefix[index])) && typeEquals(a.rest, bs.rest) && a.minLength === bs.minLength && a.maxLength === bs.maxLength;
		}
		case "Regex": return true;
		case "AnyFunction": return true;
		case "Union":
		case "Inter": {
			const bm = b.members;
			return a.members.length === bm.length && a.members.every((m, i) => typeEquals(m, bm[i]));
		}
		case "Neg": return typeEquals(a.inner, b.inner);
		case "Unknown": return true;
		case "Never": return true;
		case "Var": return a === b;
		case "Alias": {
			const ba = b;
			return a.name === ba.name && a.args.length === ba.args.length && a.args.every((arg, i) => typeEquals(arg, ba.args[i]));
		}
		case "Recursive": {
			const brec = b;
			return a.id === brec.id && typeEquals(a.body, brec.body);
		}
		case "Keyof": return typeEquals(a.inner, b.inner);
		case "Index": {
			const bi = b;
			return typeEquals(a.target, bi.target) && typeEquals(a.key, bi.key);
		}
		case "Refined": {
			const br = b;
			return a.source === br.source && typeEquals(a.base, br.base);
		}
	}
}
/** Check if two tails are equal. RowVars are compared by id (bounds ignored). */
function tailEquals(a, b) {
	if (a.tag !== b.tag) return false;
	if (a.tag === "RowVar" && b.tag === "RowVar") return a.id === b.id;
	return true;
}
/** Check if two effect sets are equal. Structural — used for dedup/caching. */
function effectSetEquals(a, b) {
	if (!tailEquals(a.tail, b.tail)) return false;
	if (a.effects.size !== b.effects.size) return false;
	for (const e of a.effects) if (!b.effects.has(e)) return false;
	return true;
}
/**
* Create an effect set from named effects.
*
* `open` parameter is kept for source-compat with existing call sites that
* produce Closed/Open effect sets. For row-variable tails, use
* `effectSetWithRowVar` instead.
*/
function effectSet(effects, open = false) {
	return {
		effects: new Set(effects),
		tail: open ? OpenTail : ClosedTail
	};
}
/**
* Subtract handled effects from an effect set. Works across all tail shapes:
* - Closed/Open: concrete subtraction, tail passes through.
* - RowVar: subtract from the concrete side; the row var's lower bounds are
*   NOT touched here. The well-formedness check on wrapper signatures
*   (see `checkWrapperSigWellFormed` in infer.ts) guarantees that handled
*   effects never appear in a row var's lower bounds at subtraction time,
*   so ignoring the tail is correct by construction.
*/
function subtractEffects(from, handled) {
	return {
		effects: new Set([...from.effects].filter((e) => !handled.has(e))),
		tail: from.tail
	};
}
/**
* Check if an effect set is a subset of another (fewer effects = subtype).
* Phase B: row vars use their known bounds for a decision; when bounds are
* insufficient the check is side-effect-free and returns `false` conservatively.
* Real constraint propagation runs through `constrainEffectSet` in infer.ts.
*/
function isEffectSubset(sub, sup) {
	if (sup.tail.tag === "Open") return true;
	if (sub.tail.tag === "Open") return false;
	if (sub.tail.tag === "RowVar" || sup.tail.tag === "RowVar") {
		for (const e of sub.effects) if (!sup.effects.has(e)) return false;
		if (sub.tail.tag === "RowVar" && sup.tail.tag === "RowVar" && sub.tail.id === sup.tail.id) return true;
		if (sup.tail.tag === "RowVar" && sub.tail.tag === "Closed") return true;
		return false;
	}
	for (const e of sub.effects) if (!sup.effects.has(e)) return false;
	return true;
}
function dedup(types) {
	const result = [];
	for (const t of types) if (!result.some((r) => typeEquals(r, t))) result.push(t);
	return result;
}
function assertsInfoEquals(a, b) {
	if (!a && !b) return true;
	if (!a || !b) return false;
	return a.paramIndex === b.paramIndex && a.source === b.source;
}
function handlerWrapperEquals(a, b) {
	if (!a && !b) return true;
	if (!a || !b) return false;
	if (a.paramIndex !== b.paramIndex) return false;
	if (a.handled.size !== b.handled.size) return false;
	for (const [name, sig] of a.handled) {
		const other = b.handled.get(name);
		if (!other) return false;
		if (!typeEquals(sig.argType, other.argType) || !typeEquals(sig.retType, other.retType)) return false;
	}
	return true;
}
//#endregion
//#region src/prettyPrint.ts
/**
* Smart AST pretty printer.
*
* Converts Dvala AST nodes (arrays) to readable source code.
* Chooses idiomatic syntax regardless of how the AST was constructed.
* Wraps lines at 80 columns (hard for structure, soft for atoms).
*
* Exposed as:
* - TS function: `prettyPrint(node)` — for playground and tooling
* - Dvala function: `let { prettyPrint } = import("ast")` — for in-language use
*/
const INDENT_SIZE = 2;
const NodeTypes$1 = {
	Num: "Num",
	Str: "Str",
	Atom: "Atom",
	Call: "Call",
	Sym: "Sym",
	Builtin: "Builtin",
	Special: "Special",
	Reserved: "Reserved",
	Spread: "Spread",
	TmplStr: "TmplStr",
	If: "If",
	Block: "Block",
	Effect: "Effect",
	Recur: "Recur",
	Array: "Array",
	Parallel: "Parallel",
	Race: "Race",
	Settled: "Settled",
	Perform: "Perform",
	Object: "Object",
	Function: "Function",
	Let: "Let",
	And: "And",
	Or: "Or",
	Qq: "Qq",
	Loop: "Loop",
	For: "For",
	Match: "Match",
	Import: "Import",
	Macro: "Macro",
	Handler: "Handler",
	Resume: "Resume",
	WithHandler: "WithHandler",
	CodeTmpl: "CodeTmpl",
	Splice: "Splice",
	MacroCall: "MacroCall"
};
const infixOperators = new Set([
	"+",
	"-",
	"*",
	"/",
	"^",
	"%",
	"==",
	"!=",
	"<",
	">",
	"<=",
	">=",
	"++",
	"&",
	"|",
	"xor",
	"<<",
	">>",
	">>>"
]);
const operatorPrecedence = {
	"|>": 2,
	"&&": 4,
	"||": 4,
	"??": 4,
	"&": 5,
	"|": 5,
	xor: 5,
	"==": 6,
	"!=": 6,
	"<": 7,
	">": 7,
	"<=": 7,
	">=": 7,
	"++": 8,
	"<<": 9,
	">>": 9,
	">>>": 9,
	"+": 10,
	"-": 10,
	"*": 11,
	"/": 11,
	"%": 11,
	"^": 12
};
function indent(level) {
	return " ".repeat(level * INDENT_SIZE);
}
/** Check if a single-line rendering fits within the max width. */
function fits(text, currentIndent) {
	return currentIndent * INDENT_SIZE + text.length <= 80;
}
/**
* Returns true when every string is single-line (no embedded newlines).
* Used to guard flat-form attempts: a flat string containing `\n` would
* produce broken indentation even when its total character count fits.
*/
function allSingleLine(...strs) {
	return strs.every((s) => !s.includes("\n"));
}
function appendToLastLine(text, suffix) {
	const lastBreak = text.lastIndexOf("\n");
	if (lastBreak === -1) return `${text}${suffix}`;
	return `${text.slice(0, lastBreak + 1)}${text.slice(lastBreak + 1)}${suffix}`;
}
function fitsOnLastLine(text, suffix) {
	return text.slice(text.lastIndexOf("\n") + 1).length + suffix.length <= 80;
}
function getBlankLinesBefore(_nodeId) {
	return 0;
}
function hasNodeLevelComments(_nodeId) {
	return false;
}
function renderLeadingComments(_nodeId, _indentPrefix, _skip) {
	return "";
}
function appendTrailingComments(_nodeId, line) {
	return `${line};`;
}
function appendNodeTrailingComments(_nodeId, line) {
	return line;
}
function renderCommentedNode(node, ind) {
	const nodeStr = printNode(node, ind);
	const indentPrefix = indent(ind);
	const leading = renderLeadingComments(node[2], indentPrefix, false);
	const base = `${indentPrefix}${nodeStr}`;
	if (!nodeStr.includes("\n")) return `${leading}${appendNodeTrailingComments(node[2], base)}`;
	return `${leading}${base}`;
}
function renderIndentedNode(node, ind) {
	if (hasNodeLevelComments(node[2])) return renderCommentedNode(node, ind);
	return `${indent(ind)}${printNode(node, ind)}`;
}
function formatStatementLines(nodeIds, lines) {
	let body = "";
	lines.forEach((line, index) => {
		if (index > 0) {
			body += "\n";
			const blankLines = getBlankLinesBefore(nodeIds[index]);
			if (blankLines > 0) body += "\n".repeat(blankLines);
		}
		const indentPrefix = /^(\s*)/.exec(line)?.[1] ?? "";
		body += renderLeadingComments(nodeIds[index], indentPrefix, index > 0);
		body += appendTrailingComments(nodeIds[index], line);
	});
	return body;
}
/** Convert an AST node to readable Dvala source. */
function prettyPrint(node) {
	return printNode(node, 0, true);
}
/**
* @param isRoot  True when this expression occupies its own "slot" — i.e. it
*   is either the top-level expression or a direct child of a do-block / function
*   body.  MacroCall uses this to decide whether to apply decorator-style
*   formatting (`#foo` on its own line, operand at the same indent).
*/
function printNode(node, ind, isRoot = false) {
	const [type, payload] = node;
	switch (type) {
		case NodeTypes$1.Num: return String(payload);
		case NodeTypes$1.Atom: return `:${String(payload)}`;
		case NodeTypes$1.Str: return `"${String(payload).replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/\r/g, "\\r").replace(/\t/g, "\\t").replace(/"/g, "\\\"")}"`;
		case NodeTypes$1.Reserved: return String(payload);
		case NodeTypes$1.Sym: return String(payload);
		case NodeTypes$1.Builtin: return String(payload);
		case NodeTypes$1.Effect: return `@${payload}`;
		case NodeTypes$1.Call: return printCall(payload, ind);
		case NodeTypes$1.MacroCall: {
			const [fnNode, args] = payload;
			const macroStr = `#${printNode(fnNode, ind)}`;
			const operand = args[0];
			if (isRoot && (operand[0] === NodeTypes$1.Let || operand[0] === NodeTypes$1.MacroCall)) return `${macroStr}\n${indent(ind)}${printNode(operand, ind, true)}`;
			const operandStr = printNode(operand, ind);
			if (allSingleLine(operandStr)) {
				const flat = `${macroStr} ${operandStr}`;
				if (fits(flat, ind)) return flat;
			}
			return `${macroStr}\n${indent(ind + 1)}${printNode(operand, ind + 1)}`;
		}
		case NodeTypes$1.If: return printIf(payload, ind);
		case NodeTypes$1.Block: return printBlock(payload, ind);
		case NodeTypes$1.Let: return printLet(payload, ind);
		case NodeTypes$1.Function: return printFunction(payload, ind);
		case NodeTypes$1.Macro: return printMacro(payload, ind);
		case NodeTypes$1.Perform: return printPerform(payload, ind);
		case NodeTypes$1.Array: return printArray(payload, ind);
		case NodeTypes$1.Object: return printObject(payload, ind);
		case NodeTypes$1.Spread: return `...${printNode(payload, ind)}`;
		case NodeTypes$1.And: return printBinaryChain(payload, "&&", ind);
		case NodeTypes$1.Or: return printBinaryChain(payload, "||", ind);
		case NodeTypes$1.Qq: return printBinaryChain(payload, "??", ind);
		case NodeTypes$1.Recur: return printCommaSeparated("recur", payload, ind);
		case NodeTypes$1.Loop: return printLoop(payload, ind);
		case NodeTypes$1.For: return printFor(payload, ind);
		case NodeTypes$1.Match: return printMatch(payload, ind);
		case NodeTypes$1.Import: return `import("${payload}")`;
		case NodeTypes$1.TmplStr: return printTemplateString(payload);
		case NodeTypes$1.Parallel: return `parallel(${printNode(payload, ind)})`;
		case NodeTypes$1.Race: return `race(${printNode(payload, ind)})`;
		case NodeTypes$1.Settled: return `settled(${printNode(payload, ind)})`;
		case NodeTypes$1.CodeTmpl: return printCodeTemplate(payload, ind);
		case NodeTypes$1.Splice: return "<Splice>";
		case NodeTypes$1.Handler: return printHandler(payload, ind);
		case NodeTypes$1.Resume: return payload === "ref" ? "resume" : `resume(${printNode(payload, ind)})`;
		case NodeTypes$1.WithHandler: {
			const [handlerExpr, bodyExprs] = payload;
			return `with ${printNode(handlerExpr, ind)};\n${bodyExprs.map((b) => printNode(b, ind)).map((s) => `${indent(ind)}${s}`).join(";\n")}`;
		}
		case "symbol":
		case "rest":
		case "array":
		case "object":
		case "wildcard":
		case "literal": return printBindingTarget(node);
		default: return `<${type}>`;
	}
}
function printCall(payload, ind) {
	const [fn, args, rawHints] = payload;
	const fnNode = fn;
	const argNodes = args;
	const hints = rawHints;
	if (fnNode[0] === NodeTypes$1.Builtin && fnNode[1] === "-" && argNodes.length === 2) {
		const first = argNodes[0];
		if (first[0] === NodeTypes$1.Num && first[1] === 0) return `-${printNode(argNodes[1], ind)}`;
	}
	if (fnNode[0] === NodeTypes$1.Builtin && fnNode[1] === "get" && argNodes.length === 2) {
		const keyNode = argNodes[1];
		if (keyNode[0] === NodeTypes$1.Str && isDvalaIdentifierName(keyNode[1])) return `${printNode(argNodes[0], ind)}?.${keyNode[1]}`;
	}
	if (hints?.isPipe) {
		const pipeChain = extractPipeChain(fnNode, argNodes);
		if (pipeChain) {
			const flatParts = pipeChain.map((n) => printNode(n, ind));
			if (allSingleLine(...flatParts)) {
				const flat = flatParts.join(" |> ");
				if (fits(flat, ind)) return flat;
			}
			const parts = pipeChain.map((n) => printNode(n, ind + 1));
			return `${parts[0]}\n${parts.slice(1).map((p) => `${indent(ind + 1)}|> ${p}`).join("\n")}`;
		}
	}
	const hasPlaceholder = argNodes.some((a) => a[0] === NodeTypes$1.Reserved && a[1] === "_");
	if (fnNode[0] === NodeTypes$1.Builtin && argNodes.length === 2 && !hasPlaceholder) {
		const op = fnNode[1];
		if (infixOperators.has(op)) {
			const outerPrec = operatorPrecedence[op] ?? 0;
			const printArg = (argNode, isRight) => {
				const str = printNode(argNode, ind);
				if (argNode[0] === NodeTypes$1.Call) {
					const callPayload = argNode[1];
					const innerFn = callPayload[0];
					const innerArgs = callPayload[1];
					if (innerFn[0] === NodeTypes$1.Builtin && innerArgs.length === 2 && infixOperators.has(innerFn[1])) {
						const innerPrec = operatorPrecedence[innerFn[1]] ?? 0;
						if (innerPrec < outerPrec || isRight && innerPrec === outerPrec) return `(${str})`;
					}
				}
				return str;
			};
			return `${printArg(argNodes[0], false)} ${op} ${printArg(argNodes[1], true)}`;
		}
	}
	if (hints?.isInfix && (fnNode[0] === NodeTypes$1.Sym || fnNode[0] === NodeTypes$1.Builtin) && argNodes.length === 2) {
		const operator = fnNode[1];
		const leftStr = printNode(argNodes[0], ind);
		const rightStr = printNode(argNodes[1], ind);
		const isLeftSingleLine = allSingleLine(leftStr);
		const isRightSingleLine = allSingleLine(rightStr);
		if (isLeftSingleLine && isRightSingleLine) {
			const flat = `${leftStr} ${operator} ${rightStr}`;
			if (fits(flat, ind)) return flat;
			return `${leftStr}\n${indent(ind + 1)}${operator} ${rightStr}`;
		}
		if (!isRightSingleLine) {
			const rightMultiline = printNode(argNodes[1], ind + 1);
			if (isLeftSingleLine) return `${leftStr} ${operator}\n${indent(ind + 1)}${rightMultiline}`;
			return `${leftStr}\n${indent(ind + 1)}${operator}\n${indent(ind + 1)}${rightMultiline}`;
		}
		const trailingInfix = ` ${operator} ${rightStr}`;
		if (fitsOnLastLine(leftStr, trailingInfix)) return appendToLastLine(leftStr, trailingInfix);
		return `${leftStr}\n${indent(ind + 1)}${operator} ${rightStr}`;
	}
	const rawFnStr = printNode(fnNode, ind);
	const fnStr = fnNode[0] === NodeTypes$1.Function || fnNode[0] === NodeTypes$1.Macro ? `(${rawFnStr})` : rawFnStr;
	const argStrs = argNodes.map((a) => printNode(a, ind));
	if (!argNodes.some((a) => hasNodeLevelComments(a[2])) && allSingleLine(rawFnStr, ...argStrs)) {
		const flat = `${fnStr}(${argStrs.join(", ")})`;
		if (fits(flat, ind)) return flat;
	}
	const lastArg = argNodes.at(-1);
	if (argNodes.length >= 2 && lastArg[0] === NodeTypes$1.Function) {
		const leadingArgStrs = argNodes.slice(0, -1).map((a) => printNode(a, ind));
		if (allSingleLine(...leadingArgStrs)) {
			const lambdaStr = printNode(lastArg, ind);
			if (!allSingleLine(lambdaStr) && lambdaStr.endsWith(`${indent(ind)}end`)) {
				const cutAt = lambdaStr.indexOf("\n");
				const openingLine = `${fnStr}(${leadingArgStrs.join(", ")}, ${lambdaStr.slice(0, cutAt)}`;
				if (fits(openingLine, ind)) return `${openingLine}${lambdaStr.slice(cutAt)})`;
			}
		}
	}
	return `${fnStr}(\n${argNodes.map((a) => renderIndentedNode(a, ind + 1)).join(",\n")},\n${indent(ind)})`;
}
function printIf(parts, ind) {
	const cond = printNode(parts[0], ind);
	const thenNode = parts[1];
	const elseNode = parts.length > 2 && parts[2] ? parts[2] : null;
	if (!elseNode) throw new TypeError$1("If AST requires an else branch");
	const hasThenComments = hasNodeLevelComments(thenNode[2]);
	const hasElseComments = elseNode ? hasNodeLevelComments(elseNode[2]) : false;
	const isElseIf = elseNode && elseNode[0] === NodeTypes$1.If;
	const thenStr = printNode(thenNode, ind);
	const elseStr = isElseIf ? printIf(elseNode[1], ind) : printNode(elseNode, ind);
	if (!hasThenComments && !hasElseComments && allSingleLine(cond, thenStr, elseStr)) {
		const flat = isElseIf ? `if ${cond} then ${thenStr} else ${elseStr}` : `if ${cond} then ${thenStr} else ${elseStr} end`;
		if (fits(flat, ind)) return flat;
	}
	const thenMulti = renderCommentedNode(thenNode, ind + 1);
	if (isElseIf) {
		const elseIfStr = printIf(elseNode[1], ind);
		return `if ${cond} then\n${thenMulti}\n${indent(ind)}else ${elseIfStr}`;
	}
	const elseMulti = renderCommentedNode(elseNode, ind + 1);
	return `if ${cond} then\n${thenMulti}\n${indent(ind)}else\n${elseMulti}\n${indent(ind)}end`;
}
function printBlock(stmts, ind) {
	if (stmts.length === 0) throw new TypeError$1("Block AST requires at least one expression");
	if (stmts.length === 1) {
		const stmtNode = stmts[0];
		if (!hasNodeLevelComments(stmtNode[2])) {
			const stmt = printNode(stmtNode, ind);
			if (allSingleLine(stmt)) {
				const flat = `do ${stmt} end`;
				if (fits(flat, ind)) return flat;
			}
		}
	}
	const lines = stmts.map((s) => `${indent(ind + 1)}${printNode(s, ind + 1, true)}`);
	return `do\n${formatStatementLines(stmts.map((s) => s[2]), lines)}\n${indent(ind)}end`;
}
function printLet(payload, ind) {
	const [target, value] = payload;
	const prefix = `let ${printBindingTarget(target)} = `;
	const valueStr = printNode(value, ind);
	if (allSingleLine(valueStr) && !fits(`${prefix}${valueStr}`, ind)) return `${prefix}\n${indent(ind + 1)}${printNode(value, ind + 1)}`;
	return `${prefix}${valueStr}`;
}
function printFunction(payload, ind) {
	const [params, body, rawHints] = payload;
	if (rawHints?.isShorthand) {
		if (body.length === 1 && body[0][0] !== NodeTypes$1.WithHandler && !hasNodeLevelComments(body[0][2])) {
			const bodyStr = printNode(body[0], ind);
			if (allSingleLine(bodyStr)) {
				const flat = `-> ${bodyStr}`;
				if (fits(flat, ind)) return flat;
			}
			return `->\n${indent(ind + 1)}${printNode(body[0], ind + 1)}`;
		}
		const lines = body.map((b) => `${indent(ind + 1)}${printNode(b, ind + 1, true)}`);
		return `-> do\n${formatStatementLines(body.map((b) => b[2]), lines)}\n${indent(ind)}end`;
	}
	const paramStr = params.map((p) => printBindingTarget(p)).join(", ");
	if (body.length === 1) {
		if (body[0][0] !== NodeTypes$1.WithHandler && !hasNodeLevelComments(body[0][2])) {
			const bodyStr = printNode(body[0], ind);
			if (allSingleLine(bodyStr)) {
				const flat = `(${paramStr}) -> ${bodyStr}`;
				if (fits(flat, ind)) return flat;
			}
			return `(${paramStr}) ->\n${indent(ind + 1)}${printNode(body[0], ind + 1)}`;
		}
	}
	const lines = body.map((b) => `${indent(ind + 1)}${printNode(b, ind + 1, true)}`);
	return `(${paramStr}) -> do\n${formatStatementLines(body.map((b) => b[2]), lines)}\n${indent(ind)}end`;
}
function printMacro(payload, ind) {
	const [params, body] = payload;
	const paramStr = params.map((p) => printBindingTarget(p)).join(", ");
	if (body.length === 1 && body[0][0] !== NodeTypes$1.WithHandler && !hasNodeLevelComments(body[0][2])) {
		const bodyStr = printNode(body[0], ind);
		if (allSingleLine(bodyStr)) {
			const flat = `macro (${paramStr}) -> ${bodyStr}`;
			if (fits(flat, ind)) return flat;
		}
		return `macro (${paramStr}) ->\n${indent(ind + 1)}${printNode(body[0], ind + 1)}`;
	}
	const lines = body.map((b) => `${indent(ind + 1)}${printNode(b, ind + 1, true)}`);
	return `macro (${paramStr}) -> do\n${formatStatementLines(body.map((b) => b[2]), lines)}\n${indent(ind)}end`;
}
function printPerform(payload, ind) {
	const [eff, arg] = payload;
	const effStr = printNode(eff, ind);
	if (arg) {
		const argStr = printNode(arg, ind);
		if (allSingleLine(effStr, argStr)) {
			const flat = `perform(${effStr}, ${argStr})`;
			if (fits(flat, ind)) return flat;
		}
		return `perform(\n${indent(ind + 1)}${effStr},\n${indent(ind + 1)}${printNode(arg, ind + 1)},\n${indent(ind)})`;
	}
	return `perform(${effStr})`;
}
function printArray(elements, ind) {
	if (elements.length === 0) return "[]";
	if (elements.length <= 3) {
		const flatParts = elements.map((e) => printNode(e, ind));
		if (flatParts.every((p) => !p.includes("\n"))) {
			const flat = `[${flatParts.join(", ")}]`;
			if (fits(flat, ind)) return flat;
		}
	}
	let body = "";
	elements.forEach((element, index) => {
		if (index > 0) {
			body += ",\n";
			const blankLines = getBlankLinesBefore(element[2]);
			if (blankLines > 0) body += "\n".repeat(blankLines);
		}
		body += `${indent(ind + 1)}${printNode(element, ind + 1)}`;
	});
	return `[\n${body},\n${indent(ind)}]`;
}
function printObject(entries, ind) {
	if (entries.length === 0) return "{}";
	if (entries.length <= 3) {
		const flatParts = entries.map((entry) => formatObjectEntry(entry, ind));
		if (allSingleLine(...flatParts)) {
			const flat = `{ ${flatParts.join(", ")} }`;
			if (fits(flat, ind)) return flat;
		}
	}
	let body = "";
	entries.forEach((entry, index) => {
		if (index > 0) {
			body += ",\n";
			const blankLines = getBlankLinesBefore(getObjectEntryNodeId(entry));
			if (blankLines > 0) body += "\n".repeat(blankLines);
		}
		body += `${indent(ind + 1)}${formatObjectEntry(entry, ind + 1)}`;
	});
	return `{\n${body},\n${indent(ind)}}`;
}
function getObjectEntryNodeId(entry) {
	if (entry[0] === NodeTypes$1.Spread) return entry[2];
	return entry[0][2];
}
function formatObjectEntry(entry, ind) {
	if (entry[0] === NodeTypes$1.Spread) return `...${printNode(entry[1], ind)}`;
	const [keyNode, valueNode] = entry;
	if (keyNode[0] === NodeTypes$1.Str) {
		const key = keyNode[1];
		const isIdentifier = isDvalaIdentifierName(key);
		if (isIdentifier && valueNode[0] === NodeTypes$1.Sym && valueNode[1] === key) return key;
		return `${isIdentifier ? key : `"${key}"`}: ${printNode(valueNode, ind)}`;
	}
	return `[${printNode(keyNode, ind)}]: ${printNode(valueNode, ind)}`;
}
function printBinaryChain(nodes, op, ind) {
	const parts = nodes.map((n) => printNode(n, ind));
	if (allSingleLine(...parts)) {
		const flat = parts.join(` ${op} `);
		if (fits(flat, ind)) return flat;
	}
	const restParts = nodes.slice(1).map((n) => printNode(n, ind + 1));
	return `${parts[0]}\n${restParts.map((p) => `${indent(ind + 1)}${op} ${p}`).join("\n")}`;
}
/**
* Print a handler clause body or transform body. Multi-statement bodies must
* be wrapped in `do...end` because the handler grammar does not allow bare
* semicolons at clause level — `@eff(x) -> let y = 1; y` is invalid syntax.
*/
function printHandlerBody(body, ind) {
	if (body.length === 1 && body[0][0] !== NodeTypes$1.WithHandler && !hasNodeLevelComments(body[0][2])) return printNode(body[0], ind);
	const lines = body.map((b) => `${indent(ind + 1)}${printNode(b, ind + 1, true)}`);
	return `do\n${formatStatementLines(body.map((b) => b[2]), lines)}\n${indent(ind)}end`;
}
function printHandler(payload, ind) {
	const [clauses, transform] = payload;
	const clauseStrs = clauses.map((clause) => {
		const paramsStr = clause.params.length > 0 ? `(${clause.params.map((p) => printBindingTarget(p)).join(", ")})` : "()";
		const bodyStr = printHandlerBody(clause.body, ind + 1);
		return {
			inline: `@${clause.effectName}${paramsStr} -> ${bodyStr}`,
			bodyStr
		};
	});
	if (!transform && clauseStrs.every((c) => !c.bodyStr.includes("\n"))) {
		const flat = `handler ${clauseStrs.map((c) => c.inline).join(" ")} end`;
		if (fits(flat, ind)) return flat;
	}
	if (clauses.length === 1) {
		const clause = clauses[0];
		const paramsStr = clause.params.length > 0 ? `(${clause.params.map((p) => printBindingTarget(p)).join(", ")})` : "()";
		const bodyStr = printHandlerBody(clause.body, ind);
		const parts = [`handler @${clause.effectName}${paramsStr} -> ${bodyStr}`];
		if (transform) {
			const [param, transformBody] = transform;
			const paramStr = printBindingTarget(param);
			const transformBodyStr = printHandlerBody(transformBody, ind + 1);
			parts.push(`${indent(ind)}transform`);
			parts.push(`${indent(ind + 1)}${paramStr} -> ${transformBodyStr}`);
		}
		parts.push(`${indent(ind)}end`);
		return parts.join("\n");
	}
	const parts = ["handler"];
	for (const { inline } of clauseStrs) parts.push(`${indent(ind + 1)}${inline}`);
	if (transform) {
		const [param, body] = transform;
		const paramStr = printBindingTarget(param);
		const bodyStr = printHandlerBody(body, ind + 1);
		parts.push(`${indent(ind)}transform`);
		parts.push(`${indent(ind + 1)}${paramStr} -> ${bodyStr}`);
	}
	parts.push(`${indent(ind)}end`);
	return parts.join("\n");
}
function printLoop(payload, ind) {
	const [bindingsArr, body] = payload;
	const bindings = bindingsArr.map(([target, value]) => {
		return `${printBindingTarget(target)} = ${printNode(value, ind)}`;
	});
	const bodyStr = printNode(body, ind, true);
	if (allSingleLine(...bindings, bodyStr)) {
		const flat = `loop (${bindings.join(", ")}) -> ${bodyStr}`;
		if (fits(flat, ind)) return flat;
	}
	return `loop (${bindings.join(", ")}) ->\n${indent(ind + 1)}${printNode(body, ind + 1, true)}`;
}
function printFor(payload, ind) {
	const [bindingLevels, body] = payload;
	const levels = [];
	for (const level of bindingLevels) {
		const [binding, letBindings, whenGuard, whileGuard] = level;
		const [target, collection] = binding;
		let s = `${printBindingTarget(target)} in ${printNode(collection, ind)}`;
		/* v8 ignore next -- both branches tested; v8 miscounts Array.isArray on null vs [] */
		if (Array.isArray(letBindings)) for (const lb of letBindings) {
			const [lt, lv] = lb;
			s += ` let ${printBindingTarget(lt)} = ${printNode(lv, ind)}`;
		}
		if (whenGuard) s += ` when ${printNode(whenGuard, ind)}`;
		if (whileGuard) s += ` while ${printNode(whileGuard, ind)}`;
		levels.push(s);
	}
	const bodyStr = printNode(body, ind, true);
	if (allSingleLine(...levels, bodyStr)) {
		const flat = `for (${levels.join(", ")}) -> ${bodyStr}`;
		if (fits(flat, ind)) return flat;
	}
	return `for (${levels.join(", ")}) ->\n${indent(ind + 1)}${printNode(body, ind + 1, true)}`;
}
function printMatch(payload, ind) {
	const [valueNode, cases] = payload;
	const valueStr = printNode(valueNode, ind);
	const flatCaseParts = cases.map((c) => {
		const [pattern, body, guard] = c;
		const patternStr = printMatchPattern(pattern);
		const bodyStr = printNode(body, ind);
		if (guard) return `case ${patternStr} when ${printNode(guard, ind)} then ${bodyStr}`;
		return `case ${patternStr} then ${bodyStr}`;
	});
	if (flatCaseParts.every((p) => !p.includes("\n"))) {
		const flat = `match ${valueStr} ${flatCaseParts.join(" ")} end`;
		if (fits(flat, ind)) return flat;
	}
	return `match ${valueStr}\n${cases.map((c) => {
		const [pattern, body, guard] = c;
		const patternStr = printMatchPattern(pattern);
		const bodyStr = printNode(body, ind + 1);
		const prefix = guard ? `case ${patternStr} when ${printNode(guard, ind + 1)} then` : `case ${patternStr} then`;
		if (!bodyStr.includes("\n")) return `${prefix} ${bodyStr}`;
		const bodyDeep = printNode(body, ind + 2);
		return `${prefix}\n${indent(ind + 2)}${bodyDeep}`;
	}).map((c) => `${indent(ind + 1)}${c}`).join("\n")}\n${indent(ind)}end`;
}
function printCommaSeparated(name, items, ind) {
	const itemStrs = items.map((a) => printNode(a, ind));
	if (allSingleLine(...itemStrs)) {
		const flat = `${name}(${itemStrs.join(", ")})`;
		if (fits(flat, ind)) return flat;
	}
	return `${name}(\n${items.map((a) => `${indent(ind + 1)}${printNode(a, ind + 1)}`).join(",\n")},\n${indent(ind)})`;
}
function printTemplateString(segments) {
	let result = "`";
	for (const seg of segments) {
		const segNode = seg;
		if (segNode[0] === NodeTypes$1.Str) result += segNode[1];
		else result += `\${${printNode(segNode, 0)}}`;
	}
	return `${result}\``;
}
function printCodeTemplate(payload, ind) {
	const [bodyAst, spliceExprs] = payload;
	if (bodyAst.length === 1) {
		const flatPart = printNodeWithSplices(bodyAst[0], spliceExprs, ind, true);
		if (allSingleLine(flatPart)) {
			const flat = `quote ${flatPart} end`;
			if (fits(flat, ind)) return flat;
		}
	}
	const lines = bodyAst.map((node) => `${indent(ind + 1)}${printNodeWithSplices(node, spliceExprs, ind + 1, true)}`);
	return `quote\n${formatStatementLines(bodyAst.map((node) => node[2]), lines)}\n${indent(ind)}end`;
}
/** Print an AST node, but render Splice nodes as $^{expr} using the splice expressions list. */
function printNodeWithSplices(node, spliceExprs, ind, isRoot = false) {
	const [type, payload] = node;
	if (type === NodeTypes$1.Splice) {
		const index = payload;
		const expr = spliceExprs[index];
		if (expr) return `$^{${printNode(expr, ind)}}`;
		throw new Error(`Invalid splice index ${index} in code template`);
	}
	return printNode(substituteSplices(node, spliceExprs), ind, isRoot);
}
/** Recursively replace Splice nodes with synthetic AST that prints as $^{expr}. */
function substituteSplices(node, spliceExprs) {
	const [type, payload, id] = node;
	if (type === NodeTypes$1.Splice) {
		const index = payload;
		const expr = spliceExprs[index];
		const spliceText = expr ? `$^{${printNode(expr, 0)}}` : `$^{<splice${index}>}`;
		return [
			NodeTypes$1.Sym,
			spliceText,
			id
		];
	}
	if (!Array.isArray(payload)) return node;
	return [
		type,
		payload.map((item) => {
			if (Array.isArray(item)) {
				if (item.length >= 2 && typeof item[0] === "string") return substituteSplices(item, spliceExprs);
				return item.map((inner) => Array.isArray(inner) && inner.length >= 2 && typeof inner[0] === "string" ? substituteSplices(inner, spliceExprs) : inner);
			}
			return item;
		}),
		id
	];
}
/**
* Detect pipe chains: nested single-arg calls where each callee is a named
* symbol or builtin. Returns [innerValue, fn1, fn2, ...] or null.
*/
function extractPipeChain(fnNode, argNodes) {
	if (argNodes.length !== 1) return null;
	if (fnNode[0] !== NodeTypes$1.Sym && fnNode[0] !== NodeTypes$1.Builtin) return null;
	const chain = [fnNode];
	let current = argNodes[0];
	while (current[0] === NodeTypes$1.Call) {
		const [innerFn, innerArgs, innerHints] = current[1];
		if (!innerHints?.isPipe) break;
		if (innerArgs.length !== 1) break;
		if (innerFn[0] !== NodeTypes$1.Sym && innerFn[0] !== NodeTypes$1.Builtin) break;
		chain.push(innerFn);
		current = innerArgs[0];
	}
	if (chain.length < 2) return null;
	chain.reverse();
	return [current, ...chain];
}
function printBindingTarget(target) {
	const targetType = target[0];
	const targetPayload = target[1];
	switch (targetType) {
		case "symbol": {
			const [symNode, defaultExpr] = targetPayload;
			const name = symNode[1];
			if (defaultExpr) return `${name} = ${printNode(defaultExpr, 0)}`;
			return name;
		}
		case "rest": {
			const [name, defaultExpr] = targetPayload;
			if (defaultExpr) return `...${name} = ${printNode(defaultExpr, 0)}`;
			return `...${name}`;
		}
		case "array": {
			const [targets, defaultExpr] = targetPayload;
			const inner = targets.map((t) => t ? printBindingTarget(t) : "").join(", ");
			if (defaultExpr) return `[${inner}] = ${printNode(defaultExpr, 0)}`;
			return `[${inner}]`;
		}
		case "object": {
			const [rawEntries, defaultExpr] = targetPayload;
			const parts = rawEntries.map(({ key, target: bt }) => {
				if (bt[0] === "rest") return `...${bt[1][0]}`;
				const btStr = printBindingTarget(bt);
				if (bt[0] === "symbol") {
					if (bt[1][0][1] === key) return btStr;
					return `${key} as ${btStr}`;
				}
				return `${key}: ${btStr}`;
			});
			if (defaultExpr) return `{ ${parts.join(", ")} } = ${printNode(defaultExpr, 0)}`;
			return `{ ${parts.join(", ")} }`;
		}
		case "literal": {
			const [expr] = targetPayload;
			return printNode(expr, 0);
		}
		case "wildcard": return "_";
		default: return `<unknown-binding:${targetType}>`;
	}
}
function printMatchPattern(pattern) {
	return printBindingTarget(pattern);
}
//#endregion
//#region src/typechecker/refinementMerge.ts
/**
* Refinement-types Phase 2.2 — multi-refinement merging.
*
* When the simplify pass encounters `Refined(Refined(B, s1, P), s2, Q)` —
* the shape produced by `Base & {s1 | P} & {s2 | Q}` — collapse the nested
* pair into a single `Refined(B, s1, P && Q[s2 := s1])`. This canonicalises
* stacked refinements so downstream consumers (solver, typeEquals,
* typeToString) see one node per base.
*
* Design reference: `design/active/2026-04-23_refinement-types.md`,
* Phase 2.2 scope and the "Multi-refinement merging" rule in the
* Simplification section. The merge rule is:
*
*     Input:  Base & {s | P} & {x | Q}
*     Stored: Refined(base: Base, binder: s,
*                     predicate: P && Q[x := s],
*                     source: "s | <P && Q[x:=s]>")
*
* Binder-canonicalisation picks the INNER refinement's binder (the one
* that appeared first in source order). The outer refinement's predicate
* gets alpha-renamed so every reference to its binder now points at the
* canonical one; the outer binder name is discarded.
*
* Alpha-renaming only touches the predicate AST — the Dvala expression
* parsed from between `|` and `}`. Because Phase 1's fragment-checker
* already rejected control-flow constructs (`let`, `if`, etc.) inside
* predicates, there are no rebinding sites — every `Sym(binder)` in the
* predicate is a free reference to the binder. A naïve whole-tree Sym
* replacement is therefore correct; no scope analysis needed.
*
* Source reconstruction goes through `prettyPrint` on the merged AST
* (not textual regex replacement). Textual replacement would misbehave
* when a string literal inside the predicate happens to contain the
* binder name (e.g. `{s | s == "s"}`); `prettyPrint` emits correct
* Dvala source from the renamed AST regardless of content.
*/
/**
* Merge `Refined(Refined(B, innerBinder, innerPred), outerBinder, outerPred)`
* into `Refined(B, innerBinder, innerPred && outerPred[outerBinder := innerBinder])`.
*
* `innerSource` / `outerSource` are the parser-captured source strings
* (with the `binder | ` prefix). They're used only for the fast path
* where innerBinder === outerBinder — in that case we can skip the
* alpha-rename and reuse the original strings.
*/
function mergeRefinementPredicates(innerBinder, innerPredicate, outerBinder, outerPredicate) {
	const renamedOuter = innerBinder === outerBinder ? outerPredicate : alphaRenameSym(outerPredicate, outerBinder, innerBinder);
	const mergedOperands = [...flattenAnd(innerPredicate), ...flattenAnd(renamedOuter)];
	const mergedPredicate = [
		NodeTypes.And,
		mergedOperands,
		0
	];
	return {
		predicate: mergedPredicate,
		source: `${innerBinder} | ${prettyPrint(mergedPredicate).trim()}`
	};
}
/**
* Walk a predicate AST and replace every `Sym(from)` with `Sym(to)`.
*
* Phase 1's fragment-checker guarantees the AST only contains node
* types from a restricted set (guard calls, relations, `count(var)`,
* `And` / `Or` / `!` compositions, Sym/Num/Str/Atom/Reserved leaves).
* Any node type outside that set is unreachable in a merge — we assert
* that and return unchanged for defensiveness.
*
* NodeIds are preserved on rewritten nodes (including the new `Sym`),
* so source-code-info lookups from surrounding error reporting still
* succeed. The rewritten `Sym` keeps the original's id because, after
* alpha-rename, it refers to the same logical binding site.
*/
function alphaRenameSym(node, from, to) {
	switch (node[0]) {
		case NodeTypes.Sym:
			if (node[1] === from) return [
				NodeTypes.Sym,
				to,
				node[2]
			];
			return node;
		case NodeTypes.Call: {
			const [callee, args] = node[1];
			const newCallee = alphaRenameSym(callee, from, to);
			const newArgs = args.map((a) => alphaRenameSym(a, from, to));
			if (newCallee === callee && newArgs.every((a, i) => a === args[i])) return node;
			return [
				NodeTypes.Call,
				[newCallee, newArgs],
				node[2]
			];
		}
		case NodeTypes.And:
		case NodeTypes.Or: {
			const operands = node[1];
			const renamed = operands.map((op) => alphaRenameSym(op, from, to));
			if (renamed.every((op, i) => op === operands[i])) return node;
			return [
				node[0],
				renamed,
				node[2]
			];
		}
		case NodeTypes.Num:
		case NodeTypes.Str:
		case NodeTypes.Atom:
		case NodeTypes.Reserved:
		case NodeTypes.Builtin:
		case NodeTypes.TmplStr: return node;
		default: return node;
	}
}
/**
* If `node` is an `And`, return its operand list; otherwise return a
* singleton `[node]`. Used by `mergeRefinementPredicates` to keep the
* merged `And` flat.
*/
function flattenAnd(node) {
	if (node[0] === NodeTypes.And) return node[1];
	return [node];
}
//#endregion
//#region src/typechecker/refinementSolver.ts
const MAX_FINITE_INTEGER_DOMAIN_SIZE = 8;
function solveRefinedSubtype(source, target) {
	const targetDomain = applyIntegralConstraint(analyzeRefinementPredicate(target.predicate, target.binder), target.base);
	if (!targetDomain) return { tag: "OutOfFragment" };
	const sourceDomain = extractSourceDomain(source, targetDomain.subject);
	if (!sourceDomain) return { tag: "OutOfFragment" };
	if (isDomainEmpty(sourceDomain)) return { tag: "Proved" };
	if (isDomainSubset(sourceDomain, targetDomain)) return { tag: "Proved" };
	const witness = pickWitnessOutside(source, sourceDomain, targetDomain);
	return witness ? {
		tag: "Disproved",
		witness
	} : { tag: "OutOfFragment" };
}
function simplifyRefinedType(refined) {
	const predicateDomain = applyIntegralConstraint(analyzeRefinementPredicate(refined.predicate, refined.binder), refined.base);
	if (!predicateDomain) return null;
	if (isDomainEmpty(predicateDomain)) return { tag: "Never" };
	const baseDomain = extractSourceDomain(refined.base, predicateDomain.subject);
	if (!baseDomain) return null;
	if (isDomainSubset(baseDomain, predicateDomain)) return refined.base;
	const intersection = intersectDomains(baseDomain, predicateDomain);
	if (intersection && isDomainEmpty(intersection)) return { tag: "Never" };
	return null;
}
function extractSourceDomain(source, subject) {
	if (source.tag === "Refined") {
		const refinedDomain = applyIntegralConstraint(analyzeRefinementPredicate(source.predicate, source.binder), source.base);
		if (!refinedDomain || refinedDomain.subject !== subject) return null;
		const baseDomain = bareSourceDomain(source.base, subject);
		if (!baseDomain) return refinedDomain;
		return intersectDomains(baseDomain, refinedDomain);
	}
	return bareSourceDomain(source, subject);
}
function bareSourceDomain(source, subject) {
	if (source.tag === "Literal" || source.tag === "Atom") return subject === "self" ? {
		kind: "set",
		subject: "self",
		values: [source]
	} : null;
	if (source.tag === "Primitive") {
		if (source.name === "Null") return subject === "self" ? {
			kind: "set",
			subject: "self",
			values: [NullType]
		} : null;
		if (subject === "self") {
			if (source.name === "Number") return fullRealInterval("self", false);
			if (source.name === "Integer") return fullRealInterval("self", true);
			if (source.name === "Boolean") return {
				kind: "set",
				subject: "self",
				values: [literal(true), literal(false)]
			};
			return null;
		}
		return source.name === "String" ? fullSequenceCountDomain(0, void 0) : null;
	}
	if (subject !== "count") return null;
	if (source.tag === "Array") return fullSequenceCountDomain(0, void 0);
	if (source.tag === "Tuple") return {
		kind: "set",
		subject: "count",
		values: [literal(source.elements.length)]
	};
	if (source.tag === "Sequence") return fullSequenceCountDomain(source.minLength, source.maxLength);
	return null;
}
function fullRealInterval(subject, integral) {
	return {
		kind: "interval",
		subject,
		integral,
		min: null,
		minInclusive: false,
		max: null,
		maxInclusive: false
	};
}
function fullSequenceCountDomain(minLength, maxLength) {
	return {
		kind: "interval",
		subject: "count",
		integral: true,
		min: minLength,
		minInclusive: true,
		max: maxLength ?? null,
		maxInclusive: maxLength !== void 0
	};
}
function analyzeRefinementPredicate(node, binder) {
	if (node[0] === NodeTypes.Sym && node[1] === binder) return {
		kind: "set",
		subject: "self",
		values: [literal(true)]
	};
	if (node[0] === NodeTypes.Call && Array.isArray(node[1])) {
		const [callee, args] = node[1];
		if (callee[0] === NodeTypes.Builtin && callee[1] === "!" && args.length === 1 && args[0][0] === NodeTypes.Sym && args[0][1] === binder) return {
			kind: "set",
			subject: "self",
			values: [literal(false)]
		};
	}
	if (node[0] === NodeTypes.And && Array.isArray(node[1])) {
		const operands = node[1];
		let current = null;
		for (const operand of operands) {
			const operandDomain = analyzeRefinementPredicate(operand, binder);
			if (!operandDomain) return null;
			current = current ? intersectDomains(current, operandDomain) : operandDomain;
		}
		return current;
	}
	if (node[0] === NodeTypes.Or && Array.isArray(node[1])) {
		const operands = node[1];
		let current = null;
		for (const operand of operands) {
			const operandDomain = analyzeRefinementPredicate(operand, binder);
			if (!operandDomain) return null;
			current = current ? unionDomains(current, operandDomain) : operandDomain;
			if (!current) return null;
		}
		return current;
	}
	if (node[0] !== NodeTypes.Call || !Array.isArray(node[1])) return null;
	const [callee, args] = node[1];
	if (callee[0] !== NodeTypes.Builtin) return null;
	const operator = callee[1];
	if (operator === "!") return null;
	if (args.length !== 2) return null;
	const normalized = normalizeRelationArgs(args[0], operator, args[1], binder);
	if (!normalized) return null;
	return relationDomain(normalized.subject, normalized.operator, normalized.rhs);
}
function normalizeRelationArgs(left, operator, right, binder) {
	const leftSubject = classifySubject(left, binder);
	if (leftSubject) return {
		subject: leftSubject,
		operator,
		rhs: right
	};
	const rightSubject = classifySubject(right, binder);
	if (!rightSubject) return null;
	const flipped = flipRelationOperator(operator);
	return flipped ? {
		subject: rightSubject,
		operator: flipped,
		rhs: left
	} : null;
}
function flipRelationOperator(operator) {
	switch (operator) {
		case "==": return "==";
		case "!=": return "!=";
		case ">": return "<";
		case ">=": return "<=";
		case "<": return ">";
		case "<=": return ">=";
		default: return null;
	}
}
function classifySubject(node, binder) {
	if (node[0] === NodeTypes.Sym && node[1] === binder) return "self";
	if (node[0] !== NodeTypes.Call || !Array.isArray(node[1])) return null;
	const [callee, args] = node[1];
	if (callee[0] === NodeTypes.Builtin && callee[1] === "count" && args.length === 1 && args[0][0] === NodeTypes.Sym && args[0][1] === binder) return "count";
	return null;
}
function relationDomain(subject, operator, rhs) {
	const rhsType = literalNodeToSingletonType(rhs);
	if (!rhsType) return null;
	if (subject === "count") {
		if (rhsType.tag !== "Literal" || typeof rhsType.value !== "number" || !Number.isInteger(rhsType.value)) return null;
		if (operator === "==") return {
			kind: "set",
			subject,
			values: [rhsType]
		};
		if (operator === "!=") return {
			kind: "excludedSet",
			subject,
			excluded: [rhsType]
		};
		const relation = intervalFromRelation(subject, operator, rhsType.value);
		if (!relation || relation.kind !== "interval") return null;
		return normalizeInterval({
			kind: "interval",
			subject,
			integral: true,
			min: 0,
			minInclusive: true,
			max: null,
			maxInclusive: false
		}, relation);
	}
	if (operator === "==" || operator === "!=") {
		if (operator === "!=") return {
			kind: "excludedSet",
			subject: "self",
			excluded: [rhsType]
		};
		return {
			kind: "set",
			subject: "self",
			values: [rhsType]
		};
	}
	if (rhsType.tag !== "Literal" || typeof rhsType.value !== "number") return null;
	return intervalFromRelation(subject, operator, rhsType.value);
}
function intervalFromRelation(subject, operator, value) {
	switch (operator) {
		case "==": return {
			kind: "interval",
			subject,
			integral: subject === "count",
			min: value,
			minInclusive: true,
			max: value,
			maxInclusive: true
		};
		case ">": return {
			kind: "interval",
			subject,
			integral: subject === "count",
			min: value,
			minInclusive: false,
			max: null,
			maxInclusive: false
		};
		case ">=": return {
			kind: "interval",
			subject,
			integral: subject === "count",
			min: value,
			minInclusive: true,
			max: null,
			maxInclusive: false
		};
		case "<": return {
			kind: "interval",
			subject,
			integral: subject === "count",
			min: null,
			minInclusive: false,
			max: value,
			maxInclusive: false
		};
		case "<=": return {
			kind: "interval",
			subject,
			integral: subject === "count",
			min: null,
			minInclusive: false,
			max: value,
			maxInclusive: true
		};
		default: return null;
	}
}
function literalNodeToSingletonType(node) {
	switch (node[0]) {
		case NodeTypes.Num: return literal(node[1]);
		case NodeTypes.Str: return literal(node[1]);
		case NodeTypes.Atom: return {
			tag: "Atom",
			name: node[1]
		};
		case NodeTypes.Reserved: {
			const value = node[1];
			if (value === "true") return literal(true);
			if (value === "false") return literal(false);
			if (value === "null") return NullType;
			return null;
		}
		default: return null;
	}
}
function intersectDomains(left, right) {
	if (left.subject !== right.subject) return null;
	if (left.kind === "interval" && right.kind === "interval") return normalizeInterval(left, {
		kind: "interval",
		subject: left.subject,
		integral: left.integral || right.integral,
		min: tighterMin(left, right).value,
		minInclusive: tighterMin(left, right).inclusive,
		max: tighterMax(left, right).value,
		maxInclusive: tighterMax(left, right).inclusive
	});
	if (left.kind === "interval" && right.kind === "excludedSet") return toIntervalExclusion(left, right);
	if (left.kind === "excludedSet" && right.kind === "interval") return toIntervalExclusion(right, left);
	if (left.kind === "intervalExclusion" && right.kind === "interval") {
		const narrowed = normalizeInterval(left.interval, right);
		return narrowed?.kind === "interval" ? toIntervalExclusion(narrowed, {
			kind: "excludedSet",
			subject: left.subject,
			excluded: left.excluded
		}) : null;
	}
	if (left.kind === "interval" && right.kind === "intervalExclusion") return intersectDomains(right, left);
	if (left.kind === "intervalExclusion" && right.kind === "excludedSet") return toIntervalExclusion(left.interval, {
		kind: "excludedSet",
		subject: left.subject,
		excluded: mergeExcludedValues(left.excluded, right.excluded)
	});
	if (left.kind === "excludedSet" && right.kind === "intervalExclusion") return intersectDomains(right, left);
	if (left.kind === "set" && right.kind === "set") return {
		kind: "set",
		subject: left.subject,
		values: left.values.filter((leftValue) => right.values.some((rightValue) => typeEquals(leftValue, rightValue)))
	};
	if (left.kind === "set" && right.kind === "intervalExclusion") return {
		kind: "set",
		subject: left.subject,
		values: left.values.filter((value) => valueInDomain(value, right))
	};
	if (left.kind === "intervalExclusion" && right.kind === "set") return intersectDomains(right, left);
	if (left.kind === "excludedSet" && right.kind === "excludedSet") {
		const excluded = [...left.excluded];
		for (const value of right.excluded) if (!excluded.some((existing) => typeEquals(existing, value))) excluded.push(value);
		return {
			kind: "excludedSet",
			subject: left.subject,
			excluded
		};
	}
	if (left.kind === "set" && right.kind === "excludedSet") return {
		kind: "set",
		subject: left.subject,
		values: left.values.filter((value) => !excludedSetContains(right, value))
	};
	if (left.kind === "excludedSet" && right.kind === "set") return intersectDomains(right, left);
	if (left.kind === "set" && right.kind === "interval") return {
		kind: "set",
		subject: left.subject,
		values: left.values.filter((value) => singletonInInterval(value, right))
	};
	if (left.kind === "interval" && right.kind === "set") return intersectDomains(right, left);
	return null;
}
function unionDomains(left, right) {
	if (left.subject !== right.subject) return null;
	if (left.kind !== "set" || right.kind !== "set") return null;
	const values = [...left.values];
	for (const value of right.values) if (!values.some((existing) => typeEquals(existing, value))) values.push(value);
	return {
		kind: "set",
		subject: left.subject,
		values
	};
}
function normalizeInterval(left, right) {
	if (left.kind !== "interval" || right.kind !== "interval" || left.subject !== right.subject) return null;
	const lower = tighterMin(left, right);
	const upper = tighterMax(left, right);
	return {
		kind: "interval",
		subject: left.subject,
		integral: left.integral || right.integral,
		min: lower.value,
		minInclusive: lower.inclusive,
		max: upper.value,
		maxInclusive: upper.inclusive
	};
}
function tighterMin(left, right) {
	if (left.min === null) return {
		value: right.min,
		inclusive: right.minInclusive
	};
	if (right.min === null) return {
		value: left.min,
		inclusive: left.minInclusive
	};
	if (left.min > right.min) return {
		value: left.min,
		inclusive: left.minInclusive
	};
	if (right.min > left.min) return {
		value: right.min,
		inclusive: right.minInclusive
	};
	return {
		value: left.min,
		inclusive: left.minInclusive && right.minInclusive
	};
}
function tighterMax(left, right) {
	if (left.max === null) return {
		value: right.max,
		inclusive: right.maxInclusive
	};
	if (right.max === null) return {
		value: left.max,
		inclusive: left.maxInclusive
	};
	if (left.max < right.max) return {
		value: left.max,
		inclusive: left.maxInclusive
	};
	if (right.max < left.max) return {
		value: right.max,
		inclusive: right.maxInclusive
	};
	return {
		value: left.max,
		inclusive: left.maxInclusive && right.maxInclusive
	};
}
function isDomainSubset(source, target) {
	if (source.subject !== target.subject) return false;
	const finiteSource = toFiniteSet(source);
	if (finiteSource && source.kind !== "set") return isDomainSubset(finiteSource, target);
	if (source.kind === "set" && target.kind === "set") return source.values.every((sourceValue) => target.values.some((targetValue) => typeEquals(sourceValue, targetValue)));
	if (source.kind === "set" && target.kind === "intervalExclusion") return source.values.every((value) => valueInDomain(value, target));
	if (source.kind === "set" && target.kind === "excludedSet") return source.values.every((value) => !excludedSetContains(target, value));
	if (source.kind === "set" && target.kind === "interval") return source.values.every((value) => singletonInInterval(value, target));
	if (source.kind === "interval" && target.kind === "interval") return intervalSubsetOfInterval(source, target);
	if (source.kind === "interval" && target.kind === "intervalExclusion") return intervalSubsetOfInterval(source, target.interval) && intervalAvoidsExcludedValue(source, {
		kind: "excludedSet",
		subject: target.subject,
		excluded: target.excluded
	});
	if (source.kind === "interval" && target.kind === "excludedSet") return intervalAvoidsExcludedValue(source, target);
	if (source.kind === "interval" && target.kind === "set") {
		if (!isIntervalSingleton(source)) return false;
		const singleton = intervalSingletonValue(source);
		return singleton !== null && target.values.some((value) => typeEquals(value, literal(singleton)));
	}
	if (source.kind === "intervalExclusion" && target.kind === "interval") return intervalSubsetOfInterval(source.interval, target);
	if (source.kind === "intervalExclusion" && target.kind === "intervalExclusion") return intervalSubsetOfInterval(source.interval, target.interval) && intervalExclusionAvoidsExcludedValues(source, target.excluded);
	if (source.kind === "intervalExclusion" && target.kind === "excludedSet") return intervalExclusionAvoidsExcludedValues(source, target.excluded);
	if (source.kind === "excludedSet" && target.kind === "excludedSet") return target.excluded.every((targetValue) => source.excluded.some((sourceValue) => typeEquals(sourceValue, targetValue)));
	return false;
}
function intervalSubsetOfInterval(source, target) {
	if (source.integral) {
		const first = firstIntegerInInterval(source);
		if (first === null) return true;
		const last = lastIntegerInInterval(source);
		return last !== null && numberInInterval(first, target) && numberInInterval(last, target);
	}
	if (target.integral) {
		const singleton = exactRealSingletonValue(source);
		return singleton !== null && numberInInterval(singleton, target);
	}
	return boundaryAtLeastAsRestrictive(source.min, source.minInclusive, target.min, target.minInclusive, "lower") && boundaryAtLeastAsRestrictive(source.max, source.maxInclusive, target.max, target.maxInclusive, "upper");
}
/**
* Is the source's bound at-least-as-restrictive as the target's, for the
* given side (`lower` = `min`, `upper` = `max`)? "At-least-as-restrictive"
* means: every numeric value the source admits past this bound, the
* target also admits. A source bound that equals the target's must not
* be a *wider* closed end — see `intervalSubsetOfInterval` for the
* three-cases summary.
*/
function boundaryAtLeastAsRestrictive(sourceBound, sourceInclusive, targetBound, targetInclusive, kind) {
	if (targetBound === null) return true;
	if (sourceBound === null) return false;
	if (kind === "lower" ? sourceBound > targetBound : sourceBound < targetBound) return true;
	if (sourceBound !== targetBound) return false;
	return !sourceInclusive || targetInclusive;
}
function singletonInInterval(value, interval) {
	if (value.tag !== "Literal" || typeof value.value !== "number") return false;
	return numberInInterval(value.value, interval);
}
function numberInInterval(value, interval) {
	if (interval.integral && !Number.isInteger(value)) return false;
	if (interval.min !== null) {
		if (value < interval.min) return false;
		if (value === interval.min && !interval.minInclusive) return false;
	}
	if (interval.max !== null) {
		if (value > interval.max) return false;
		if (value === interval.max && !interval.maxInclusive) return false;
	}
	return true;
}
function isDomainEmpty(domain) {
	if (domain.kind === "set") return domain.values.length === 0;
	if (domain.kind === "excludedSet") return false;
	if (domain.kind === "intervalExclusion") return isIntervalSingleton(domain.interval) && domain.excluded.some((value) => typeEquals(value, literal(intervalSingletonValue(domain.interval))));
	if (domain.integral) return firstIntegerInInterval(domain) === null;
	if (domain.min === null || domain.max === null) return false;
	if (domain.min < domain.max) return false;
	if (domain.min > domain.max) return true;
	return !(domain.minInclusive && domain.maxInclusive);
}
function isIntervalSingleton(interval) {
	if (interval.integral) {
		const first = firstIntegerInInterval(interval);
		const last = lastIntegerInInterval(interval);
		return first !== null && last !== null && first === last;
	}
	return interval.min !== null && interval.max !== null && interval.min === interval.max && interval.minInclusive && interval.maxInclusive;
}
function intervalSingletonValue(interval) {
	if (interval.integral) {
		const first = firstIntegerInInterval(interval);
		const last = lastIntegerInInterval(interval);
		return first !== null && last !== null && first === last ? first : null;
	}
	return isIntervalSingleton(interval) ? interval.min : null;
}
function pickWitnessOutside(sourceType, source, target) {
	const finiteSource = toFiniteSet(source);
	if (finiteSource && source.kind !== "set") return pickWitnessOutside(sourceType, finiteSource, target);
	if (source.kind === "set") return source.values.find((value) => !isDomainSubset({
		kind: "set",
		subject: source.subject,
		values: [value]
	}, target)) ?? null;
	if (source.kind === "excludedSet" && target.kind === "set") return pickExcludedSetWitness(sourceType, source, target);
	if (source.kind === "excludedSet" && target.kind === "interval") return pickExcludedSetNumericWitness(sourceType, source, target);
	if (source.kind === "excludedSet" && target.kind === "intervalExclusion") return pickExcludedSetNumericWitness(sourceType, source, target);
	if (source.kind === "excludedSet" && target.kind === "excludedSet") return target.excluded.find((value) => !excludedSetContains(source, value)) ?? null;
	if (source.kind === "interval" && target.kind === "excludedSet") return witnessForExcludedSetInterval(source, target);
	if (source.kind !== "interval" && source.kind !== "intervalExclusion") return null;
	const sourceInterval = source.kind === "interval" ? source : source.interval;
	if (target.kind === "set" && isSingleNumericValueDomain(source)) return literal(singleNumericValueDomain(source));
	const base = sourceType.tag === "Refined" ? sourceType.base : sourceType;
	const candidates = [...intervalWitnessCandidates(sourceInterval, target), ...witnessCandidatesForDomain(base, source.subject).map((value) => numericLiteralValue(value)).filter((value) => value !== null)];
	for (const candidate of candidates) if (domainContainsNumber(source, candidate) && !domainContainsNumber(target, candidate)) return literal(candidate);
	return null;
}
function intervalWitnessCandidates(source, target) {
	const delta = source.integral || source.subject === "count" ? 1 : .5;
	const candidates = /* @__PURE__ */ new Set();
	const maybeAdd = (value) => {
		if (value !== null && value !== void 0 && Number.isFinite(value)) candidates.add(value);
	};
	maybeAdd(source.min);
	maybeAdd(source.max);
	maybeAdd(source.min !== null ? source.min + delta : null);
	maybeAdd(source.max !== null ? source.max - delta : null);
	if (target.kind === "interval") {
		maybeAdd(target.min);
		maybeAdd(target.max);
		maybeAdd(target.min !== null ? target.min - delta : null);
		maybeAdd(target.min !== null ? target.min + delta : null);
		maybeAdd(target.max !== null ? target.max - delta : null);
		maybeAdd(target.max !== null ? target.max + delta : null);
	}
	if (target.kind === "excludedSet") for (const excluded of target.excluded) {
		const value = numericLiteralValue(excluded);
		maybeAdd(value);
		maybeAdd(value !== null ? value - delta : null);
		maybeAdd(value !== null ? value + delta : null);
	}
	if (target.kind === "intervalExclusion") {
		maybeAdd(target.interval.min);
		maybeAdd(target.interval.max);
		maybeAdd(target.interval.min !== null ? target.interval.min - delta : null);
		maybeAdd(target.interval.min !== null ? target.interval.min + delta : null);
		maybeAdd(target.interval.max !== null ? target.interval.max - delta : null);
		maybeAdd(target.interval.max !== null ? target.interval.max + delta : null);
		for (const excluded of target.excluded) {
			const value = numericLiteralValue(excluded);
			maybeAdd(value);
			maybeAdd(value !== null ? value - delta : null);
			maybeAdd(value !== null ? value + delta : null);
		}
	}
	maybeAdd(0);
	maybeAdd(1);
	maybeAdd(-1);
	return [...candidates];
}
function domainContainsNumber(domain, value) {
	if (domain.kind === "interval") return numberInInterval(value, domain);
	if (domain.kind === "intervalExclusion") return numberInInterval(value, domain.interval) && !excludedSetContains(domainAsExcludedSet(domain), literal(value));
	if (domain.kind === "set") return domain.values.some((domainValue) => domainValue.tag === "Literal" && domainValue.value === value);
	if (domain.subject === "count" && (!Number.isInteger(value) || value < 0)) return false;
	return !excludedSetContains(domain, literal(value));
}
function toIntervalExclusion(interval, excludedSet) {
	const excluded = excludedSet.excluded.filter((value) => {
		const numericValue = numericLiteralValue(value);
		return numericValue !== null && numberInInterval(numericValue, interval);
	});
	return excluded.length === 0 ? interval : {
		kind: "intervalExclusion",
		subject: interval.subject,
		interval,
		excluded
	};
}
function mergeExcludedValues(left, right) {
	const merged = [...left];
	for (const value of right) if (!merged.some((existing) => typeEquals(existing, value))) merged.push(value);
	return merged;
}
function valueInDomain(value, domain) {
	const numericValue = numericLiteralValue(value);
	return numericValue !== null && numberInInterval(numericValue, domain.interval) && !excludedSetContains(domainAsExcludedSet(domain), value);
}
function numericLiteralValue(value) {
	return value.tag === "Literal" && typeof value.value === "number" ? value.value : null;
}
function applyIntegralConstraint(domain, base) {
	if (!domain || !baseConstrainsIntegers(base)) return domain;
	if (domain.kind === "interval") return {
		...domain,
		integral: true
	};
	if (domain.kind === "intervalExclusion") return {
		...domain,
		interval: {
			...domain.interval,
			integral: true
		}
	};
	return domain;
}
function baseConstrainsIntegers(base) {
	if (base.tag === "Primitive") return base.name === "Integer";
	if (base.tag === "Literal") return typeof base.value === "number" && Number.isInteger(base.value);
	if (base.tag === "Refined") return baseConstrainsIntegers(base.base);
	return false;
}
function excludedSetContains(domain, value) {
	return domain.excluded.some((excludedValue) => typeEquals(excludedValue, value));
}
function pickExcludedSetWitness(sourceType, source, target) {
	return [...witnessCandidatesForDomain(sourceType.tag === "Refined" ? sourceType.base : sourceType, source.subject), ...witnessCandidatesForValues([...source.excluded, ...target.values])].find((candidate) => !excludedSetContains(source, candidate) && !target.values.some((value) => typeEquals(value, candidate))) ?? null;
}
function pickExcludedSetNumericWitness(sourceType, source, target) {
	const base = sourceType.tag === "Refined" ? sourceType.base : sourceType;
	const interval = target.kind === "interval" ? target : target.interval;
	const candidates = [...witnessCandidatesForDomain(base, source.subject), ...intervalWitnessCandidates(interval, target).map((value) => literal(value))];
	for (const candidate of candidates) {
		if (candidate.tag !== "Literal" || typeof candidate.value !== "number") continue;
		if (!domainContainsNumber(source, candidate.value)) continue;
		if (!domainContainsNumber(target, candidate.value)) return candidate;
	}
	return null;
}
function witnessCandidatesForDomain(base, subject) {
	if (subject === "count") return [
		literal(0),
		literal(1),
		literal(2),
		literal(3)
	];
	if (base.tag === "Atom") return [
		{
			tag: "Atom",
			name: "other"
		},
		{
			tag: "Atom",
			name: "ok"
		},
		{
			tag: "Atom",
			name: "warn"
		},
		{
			tag: "Atom",
			name: "__dvala_refinement_witness__"
		}
	];
	if (base.tag === "Primitive") switch (base.name) {
		case "String": return [
			literal("a"),
			literal(""),
			literal("__dvala_refinement_witness__")
		];
		case "Boolean": return [literal(false), literal(true)];
		case "Null": return [NullType];
		default: return [];
	}
	return [];
}
function witnessCandidatesForValues(values) {
	const exemplar = values[0];
	if (!exemplar) return [];
	if (exemplar.tag === "Atom") return [
		{
			tag: "Atom",
			name: "other"
		},
		{
			tag: "Atom",
			name: "ok"
		},
		{
			tag: "Atom",
			name: "warn"
		},
		{
			tag: "Atom",
			name: "__dvala_refinement_witness__"
		}
	];
	if (exemplar.tag === "Literal") switch (typeof exemplar.value) {
		case "string": return [
			literal("a"),
			literal(""),
			literal("__dvala_refinement_witness__")
		];
		case "boolean": return [literal(false), literal(true)];
		case "number": return [
			literal(0),
			literal(1),
			literal(2),
			literal(-1)
		];
		default: return [];
	}
	if (exemplar.tag === "Primitive" && exemplar.name === "Null") return [NullType];
	return [];
}
function domainAsExcludedSet(domain) {
	return {
		kind: "excludedSet",
		subject: domain.subject,
		excluded: domain.excluded
	};
}
function intervalAvoidsExcludedValue(interval, excludedSet) {
	return excludedSet.excluded.every((value) => {
		if (value.tag !== "Literal" || typeof value.value !== "number") return true;
		if (!numberInInterval(value.value, interval)) return true;
		return isIntervalSingleton(interval) && intervalSingletonValue(interval) !== value.value;
	});
}
function intervalExclusionAvoidsExcludedValues(source, excludedValues) {
	return excludedValues.every((value) => {
		const numericValue = numericLiteralValue(value);
		if (numericValue === null) return true;
		if (!numberInInterval(numericValue, source.interval)) return true;
		return excludedSetContains(domainAsExcludedSet(source), literal(numericValue));
	});
}
function witnessForExcludedSetInterval(source, target) {
	for (const excluded of target.excluded) {
		if (excluded.tag !== "Literal" || typeof excluded.value !== "number") continue;
		if (numberInInterval(excluded.value, source)) return excluded;
	}
	return null;
}
function firstIntegerInInterval(interval) {
	const start = interval.min === null ? Number.MIN_SAFE_INTEGER : interval.minInclusive ? Math.ceil(interval.min) : Math.floor(interval.min) + 1;
	if (!Number.isFinite(start)) return null;
	return numberInInterval(start, {
		...interval,
		integral: false
	}) ? start : null;
}
function lastIntegerInInterval(interval) {
	const end = interval.max === null ? Number.MAX_SAFE_INTEGER : interval.maxInclusive ? Math.floor(interval.max) : Math.ceil(interval.max) - 1;
	if (!Number.isFinite(end)) return null;
	return numberInInterval(end, {
		...interval,
		integral: false
	}) ? end : null;
}
function exactRealSingletonValue(interval) {
	return interval.min !== null && interval.max !== null && interval.minInclusive && interval.maxInclusive && interval.min === interval.max ? interval.min : null;
}
function isSingleNumericValueDomain(domain) {
	return singleNumericValueDomain(domain) !== null;
}
function singleNumericValueDomain(domain) {
	const value = intervalSingletonValue(domain.kind === "interval" ? domain : domain.interval);
	if (value === null) return null;
	if (domain.kind === "intervalExclusion" && excludedSetContains(domainAsExcludedSet(domain), literal(value))) return null;
	return value;
}
function toFiniteSet(domain) {
	if (domain.kind === "set") return domain;
	if (domain.kind === "interval") return finiteSetFromInterval(domain, []);
	if (domain.kind === "intervalExclusion") return finiteSetFromInterval(domain.interval, domain.excluded);
	return null;
}
function finiteSetFromInterval(interval, excluded) {
	if (!interval.integral) return null;
	const first = firstIntegerInInterval(interval);
	const last = lastIntegerInInterval(interval);
	if (first === null || last === null) return {
		kind: "set",
		subject: interval.subject,
		values: []
	};
	if (last - first + 1 > MAX_FINITE_INTEGER_DOMAIN_SIZE) return null;
	const values = [];
	for (let value = first; value <= last; value += 1) {
		const literalValue = literal(value);
		if (!excluded.some((excludedValue) => typeEquals(excludedValue, literalValue))) values.push(literalValue);
	}
	return {
		kind: "set",
		subject: interval.subject,
		values
	};
}
//#endregion
//#region src/typechecker/constantFold.ts
/**
* Constant folding entry point for the type inference engine.
*
* Given a direct `Call` to a builtin with all-literal argument types and
* an empty inferred effect set, synthesize a Call AST whose args are
* literal nodes, hand it to the fold sandbox, and wrap the runtime result
* back as a `Literal` type.
*
* Phase C v1 scope (primitives only):
*   - Callee: `NodeTypes.Builtin` references only — user-defined and
*     module-imported functions come in follow-ups (decision #5, #13).
*   - Argument types: primitive literals (`Literal` of number/string/boolean),
*     atoms (`Atom`), and `Null`. Closed tuples and records come in follow-ups
*     (decision #10).
*
* Gate: callers must check `FOLD_ENABLED` before invoking. This file has no
* knowledge of the toggle — it's pure fold mechanics.
*
* See design docs:
*   - design/archive/2026-04-16_constant-folding-in-types.md
*   - design/archive/2026-04-16_builtin-effect-audit.md
*   - design/archive/2026-04-16_fold-toggle-and-differential-tests.md
*/
/**
* Reconstruct a literal AST node from an inferred type.
*
* Supports primitives (Literal, Atom, Null) and closed composites (Tuple,
* closed Record) with arbitrary nesting — per decision #10. Bails on:
*   - Plain `Number` / `String` / `Boolean` (no concrete value).
*   - Open records (`open: true`) — can't know all fields.
*   - `Array` types (element-only, no length info).
*   - Function values, `Unknown`, type vars.
*
* Exported so the C6a closure-capture reconstruction path in `infer.ts`
* can use the same machinery to build let-binding values for captures.
*/
function literalTypeToAstNode(t) {
	if (t.tag === "Literal") {
		const value = t.value;
		if (typeof value === "number") return [
			NodeTypes.Num,
			value,
			0
		];
		if (typeof value === "string") return [
			NodeTypes.Str,
			value,
			0
		];
		if (typeof value === "boolean") return [
			NodeTypes.Reserved,
			value ? "true" : "false",
			0
		];
	}
	if (t.tag === "Atom") return [
		NodeTypes.Atom,
		t.name,
		0
	];
	if (t.tag === "Primitive" && t.name === "Null") return [
		NodeTypes.Reserved,
		"null",
		0
	];
	if (t.tag === "Tuple") {
		const elements = [];
		for (const elemType of t.elements) {
			const elem = literalTypeToAstNode(elemType);
			if (!elem) return null;
			elements.push(elem);
		}
		return [
			NodeTypes.Array,
			elements,
			0
		];
	}
	if (t.tag === "Record" && !t.open) {
		const entries = [];
		for (const [key, fieldType] of t.fields) {
			const valueAst = literalTypeToAstNode(fieldType);
			if (!valueAst) return null;
			const keyAst = [
				NodeTypes.Str,
				key,
				0
			];
			entries.push([keyAst, valueAst]);
		}
		return [
			NodeTypes.Object,
			entries,
			0
		];
	}
	return null;
}
/**
* Lift a runtime value back into a `Literal`-ish type.
*
* Supports the same shapes as `literalTypeToAstNode`: primitives, atoms,
* null, PersistentVector → closed Tuple, PersistentMap → closed Record.
* Recurses through composite elements / fields.
*/
function valueToLiteralType(value) {
	if (value === null || value === void 0) return NullType;
	if (typeof value === "number" && Number.isFinite(value)) return literal(value);
	if (typeof value === "string") return literal(value);
	if (typeof value === "boolean") return literal(value);
	if (typeof value === "object" && value !== null && "name" in value && typeof value.name === "string" && "^^atom^^" in value) return atom(value.name);
	if (isPersistentVector(value)) {
		const elementTypes = [];
		for (const elem of value) {
			const elemType = valueToLiteralType(elem);
			if (!elemType) return null;
			elementTypes.push(elemType);
		}
		return tuple(elementTypes);
	}
	if (isPersistentMap(value)) {
		const fields = {};
		for (const [key, val] of value) {
			if (typeof key !== "string") return null;
			const fieldType = valueToLiteralType(val);
			if (!fieldType) return null;
			fields[key] = fieldType;
		}
		return record(fields, false);
	}
	return null;
}
/**
* Build the list of arg AST nodes for a fold call, or return null if any
* argument isn't a reconstructible literal type.
*/
function reconstructArgAsts(argTypes) {
	const argAsts = [];
	for (const argType of argTypes) {
		const litNode = literalTypeToAstNode(argType);
		if (!litNode) return null;
		argAsts.push(litNode);
	}
	return argAsts;
}
/**
* Run a synthesized Call through the fold sandbox and translate the result
* into a FoldOutcome. Shared by the builtin and user-function entry points.
*/
function runFold(syntheticCall) {
	const result = evaluateNodeForFold(syntheticCall, createContextStack());
	if (result.ok) {
		const type = valueToLiteralType(result.value);
		return type ? { type } : null;
	}
	if (result.reason === "effect") return { effectName: result.effectName };
	return null;
}
/**
* Attempt to fold a direct builtin Call with all-primitive-literal args.
*
* @param calleeNode   The AST node of the callee (must be a Builtin node).
* @param argTypes     The inferred types of each argument.
* @returns
*   - `{ type }`       on success: the literal type of the folded result.
*   - `{ effectName }` when the fold performed an effect the sandbox caught
*                      (caller emits a `severity: 'warning'` diagnostic).
*   - `null`           when the call isn't eligible for folding (non-builtin
*                      callee, non-literal args, budget exhaustion, or any
*                      unhandled sandbox failure — silent fallback).
*/
function tryFoldBuiltinCall(calleeNode, argTypes) {
	if (calleeNode[0] !== NodeTypes.Builtin) return null;
	const argAsts = reconstructArgAsts(argTypes);
	if (!argAsts) return null;
	const calleeClone = [
		NodeTypes.Builtin,
		calleeNode[1],
		0
	];
	return runFold([
		NodeTypes.Call,
		[
			calleeClone,
			argAsts,
			null
		],
		0
	]);
}
/**
* Recursively collect free symbol references (`NodeTypes.Sym`) reachable
* from an AST subtree — i.e. references that are NOT bound by a
* containing parameter or `let` within the same AST. Used by C6a to
* enumerate closure-capture candidates.
*
* Why filter locals out: if the function body shadows an outer name via
* `let x = …`, including `x` in the capture set would make
* `infer.ts`'s capture loop query the outer type — and if that type
* happens to be non-literal, the whole fold bails. The body wouldn't
* actually use the outer value (the inner `let` shadows it), so
* dropping the reference keeps more programs foldable without loss of
* correctness. Same reasoning applies to function parameters and to
* names bound by enclosing `let`s inside the function body.
*
* Implementation note: a single-pass walker that tracks a running set
* of locally-bound names. Descends into `Let` bindings and `Function`
* nodes with the appropriate scope introduction. The AST's uniformly
* `[type, payload, id]` shape lets us recognise Sym nodes by their
* first element.
*/
function collectSymRefs$1(ast) {
	const refs = /* @__PURE__ */ new Set();
	walkForFreeSymRefs(ast, /* @__PURE__ */ new Set(), refs);
	return refs;
}
function walkForFreeSymRefs(value, bound, into) {
	if (!Array.isArray(value)) return;
	if (value[0] === NodeTypes.Sym && typeof value[1] === "string") {
		if (!bound.has(value[1])) into.add(value[1]);
		return;
	}
	if (value[0] === NodeTypes.Function && Array.isArray(value[1])) {
		const [params, body] = value[1];
		const nestedBound = new Set(bound);
		collectBindingTargetNames(params, nestedBound);
		if (Array.isArray(body)) for (const stmt of body) {
			walkForFreeSymRefs(stmt, nestedBound, into);
			if (Array.isArray(stmt) && stmt[0] === NodeTypes.Let && Array.isArray(stmt[1])) {
				const [target] = stmt[1];
				collectBindingTargetNames(target, nestedBound);
			}
		}
		return;
	}
	if (value[0] === NodeTypes.Let && Array.isArray(value[1])) {
		const [, valueNode] = value[1];
		if (Array.isArray(valueNode)) walkForFreeSymRefs(valueNode, bound, into);
		return;
	}
	if (value[0] === NodeTypes.Block && Array.isArray(value[1])) {
		const nestedBound = new Set(bound);
		for (const stmt of value[1]) {
			walkForFreeSymRefs(stmt, nestedBound, into);
			if (Array.isArray(stmt) && stmt[0] === NodeTypes.Let && Array.isArray(stmt[1])) {
				const [target] = stmt[1];
				collectBindingTargetNames(target, nestedBound);
			}
		}
		return;
	}
	for (const child of value) if (Array.isArray(child)) walkForFreeSymRefs(child, bound, into);
	else if (child && typeof child === "object") for (const v of Object.values(child)) walkForFreeSymRefs(v, bound, into);
}
/** Extract names bound by a BindingTarget (or array thereof), mutating `into`. */
function collectBindingTargetNames(target, into) {
	if (!Array.isArray(target)) return;
	if (target.length > 0 && !(typeof target[0] === "string")) {
		for (const t of target) collectBindingTargetNames(t, into);
		return;
	}
	const [kind, payload] = target;
	if (kind === "symbol" && Array.isArray(payload)) {
		const symNode = payload[0];
		if (Array.isArray(symNode) && typeof symNode[1] === "string") into.add(symNode[1]);
	} else if (kind === "rest" && Array.isArray(payload)) {
		if (typeof payload[0] === "string") into.add(payload[0]);
	} else if (kind === "object" && Array.isArray(payload)) {
		const entries = payload[0];
		if (Array.isArray(entries)) {
			for (const entry of entries) if (entry && typeof entry === "object" && "target" in entry) collectBindingTargetNames(entry.target, into);
		}
	} else if (kind === "array" && Array.isArray(payload)) {
		const items = payload[0];
		if (Array.isArray(items)) {
			for (const t of items) if (t !== null) collectBindingTargetNames(t, into);
		}
	}
}
/**
* Attempt to fold a Call to a user-defined function (C6 + C6a).
*
* @param functionAst  The Function-node AST that the caller's binding
*                     resolves to (captured via `env.bindFunctionAst`).
* @param argTypes     The inferred types of each argument.
* @param captures     Map of free-variable name → reconstructed value AST.
*                     The caller (inferExpr) walks the function body for
*                     symbol references, resolves each through the outer
*                     TypeEnv, and converts literal-typed ones via
*                     `literalTypeToAstNode`. Capture entries become
*                     `let name = <valueAst>` bindings wrapped around the
*                     synthesized Call, so the sandbox can resolve them.
* @returns Same contract as `tryFoldBuiltinCall`.
*
* How it works: we build a Block AST `do let c1 = v1; … let cN = vN; (<fn>)(<args>) end`,
* evaluate through the sandbox, and lift the result back to a type.
* Function-parameter shadowing works naturally — when a capture name
* matches a function param, the param wins inside the body.
*
* When `captures` is empty (e.g. the function is closure-free), the
* Block wrapping is skipped and we use the raw Call.
*
* Free variables the caller *didn't* include in `captures` are assumed
* to be builtins (resolved globally by the sandbox's context stack) or
* locally bound inside the function body. If either assumption fails —
* typically because a capture wasn't reconstructible and the caller
* passed a partial map — the sandbox raises ReferenceError, which
* `evaluateNodeForFold` surfaces as `reason: 'error'` (silent fallback),
* not as a warning.
*/
function tryFoldUserFunctionCall(functionAst, argTypes, captures) {
	if (functionAst[0] !== NodeTypes.Function) return null;
	const argAsts = reconstructArgAsts(argTypes);
	if (!argAsts) return null;
	const syntheticCall = [
		NodeTypes.Call,
		[
			functionAst,
			argAsts,
			null
		],
		0
	];
	let rootNode = syntheticCall;
	if (captures.size > 0) {
		const statements = [];
		for (const [name, valueAst] of captures) {
			const symbolBinding = [
				"symbol",
				[[
					NodeTypes.Sym,
					name,
					0
				], void 0],
				0
			];
			const letNode = [
				NodeTypes.Let,
				[symbolBinding, valueAst],
				0
			];
			statements.push(letNode);
		}
		statements.push(syntheticCall);
		rootNode = [
			NodeTypes.Block,
			statements,
			0
		];
	}
	return runFold(rootNode);
}
//#endregion
//#region src/typechecker/subtype.ts
/**
* Subtyping checker for Dvala's set-theoretic type system.
*
* Subtyping is semantic: S <: T iff the set of values denoted by S
* is a subset of the set denoted by T.
*
* Key rules:
* - Never <: T for all T (empty set is subset of everything)
* - T <: Unknown for all T (everything is subset of universal set)
* - Primitive <: Primitive iff same name
* - Literal <: Primitive iff the literal's type matches
* - Function: contravariant params, covariant return
* - Record: width subtyping (more fields <: fewer fields) + depth subtyping
* - Union: S1|S2 <: T iff S1 <: T and S2 <: T
* - Intersection: S <: T1&T2 iff S <: T1 and S <: T2
* - Negation: S <: !T iff S & T = Never (S and T are disjoint)
*/
/**
* Check whether S is a subtype of T (S <: T).
* Uses a visited set for cycle detection (recursive types).
*/
function isSubtype(s, t) {
	return check(s, t, /* @__PURE__ */ new Set());
}
function check(s, t, visited) {
	if (s === t || typeEquals(s, t)) return true;
	if (s.tag === "Never") return true;
	if (t.tag === "Unknown") return true;
	if (s.tag === "Unknown") return false;
	if (t.tag === "Never") {
		if (s.tag === "Inter" && isEmptyIntersection(s.members)) return true;
		return false;
	}
	const key = cacheKey(s, t);
	if (visited.has(key)) return true;
	visited.add(key);
	try {
		return checkBody(s, t, visited);
	} finally {
		visited.delete(key);
	}
}
function checkBody(s, t, visited) {
	if (s.tag === "Alias") return check(s.expanded, t, visited);
	if (t.tag === "Alias") return check(s, t.expanded, visited);
	if (s.tag === "Refined") {
		if (t.tag !== "Refined") return check(s.base, t, visited);
	}
	if (t.tag === "Refined") {
		const sourceBase = s.tag === "Refined" ? s.base : s;
		if (!check(sourceBase, t.base, visited)) return false;
		const folded = tryDischargeRefinedLiteral(sourceBase, t);
		if (folded !== null) return folded;
		const solved = solveRefinedSubtype(s, t);
		if (solved.tag === "Proved") return true;
		if (solved.tag === "Disproved") return false;
		return true;
	}
	if (s.tag === "Union") return s.members.every((m) => check(m, t, visited));
	if (t.tag === "Union") {
		if (t.members.some((m) => check(s, m, visited))) return true;
		return false;
	}
	if (t.tag === "Inter") return t.members.every((m) => check(s, m, visited));
	if (s.tag === "Inter") {
		if (s.members.some((m) => check(m, t, visited))) return true;
		if (isEmptyIntersection(s.members)) return true;
		return false;
	}
	if (s.tag === "Neg" && t.tag === "Neg") return check(t.inner, s.inner, visited);
	if (t.tag === "Neg") return areDisjoint(s, t.inner, visited);
	if (s.tag === "Neg") return false;
	if (s.tag === "Recursive") return check(unfoldRecursive(s), t, visited);
	if (t.tag === "Recursive") return check(s, unfoldRecursive(t), visited);
	if (s.tag === "Keyof") {
		const reduced = keyofType(s.inner);
		if (reduced.tag !== "Keyof") return check(reduced, t, visited);
		return false;
	}
	if (t.tag === "Keyof") {
		const reduced = keyofType(t.inner);
		if (reduced.tag !== "Keyof") return check(s, reduced, visited);
		return false;
	}
	if (s.tag === "Index") {
		const reduced = indexType(s.target, s.key);
		if (reduced.tag !== "Index") return check(reduced, t, visited);
		return false;
	}
	if (t.tag === "Index") {
		const reduced = indexType(t.target, t.key);
		if (reduced.tag !== "Index") return check(s, reduced, visited);
		return false;
	}
	if (s.tag === "Var") return s.upperBounds.some((u) => check(u, t, visited));
	if (t.tag === "Var") return false;
	const sourceSequence = toSequenceType(s);
	const targetSequence = toSequenceType(t);
	if (sourceSequence && targetSequence) return checkSequenceSubtype(sourceSequence, targetSequence, visited);
	return checkStructural(s, t, visited);
}
function checkStructural(s, t, visited) {
	if (s.tag === "Primitive" && t.tag === "Primitive") {
		if (s.name === t.name) return true;
		if (s.name === "Integer" && t.name === "Number") return true;
		return false;
	}
	if (s.tag === "Atom" && t.tag === "Atom") return s.name === t.name;
	if (s.tag === "Literal" && t.tag === "Literal") return s.value === t.value;
	if (s.tag === "Literal" && t.tag === "Primitive") return literalMatchesPrimitive(s.value, t.name);
	if (s.tag === "Function" && t.tag === "AnyFunction") return true;
	if (s.tag === "AnyFunction" && t.tag === "AnyFunction") return true;
	if (s.tag === "Inter" && t.tag === "AnyFunction") {
		if (s.members.some((m) => m.tag === "Function")) return true;
	}
	if (s.tag === "Handler" && t.tag === "Handler") {
		if (s.handled.size !== t.handled.size) return false;
		for (const [name, tSig] of t.handled) {
			const sSig = s.handled.get(name);
			if (!sSig) return false;
			if (!check(tSig.argType, sSig.argType, visited)) return false;
			if (!check(sSig.retType, tSig.retType, visited)) return false;
		}
		if (!isEffectSubset(s.introduced, t.introduced)) return false;
		return check(s.body, t.body, visited) && check(s.output, t.output, visited);
	}
	if (s.tag === "Function" && t.tag === "Function") {
		if (!isSubtypeFunctionArityCompatible(s, t)) return false;
		let paramsOk = true;
		for (let i = 0; i < Math.max(s.params.length, t.params.length); i++) {
			const sourceParam = getFunctionParamType(s, i);
			const targetParam = getFunctionParamType(t, i);
			if (!sourceParam || !targetParam || !check(targetParam, sourceParam, visited)) {
				paramsOk = false;
				break;
			}
		}
		if (paramsOk && t.restParam !== void 0) paramsOk = s.restParam !== void 0 && check(t.restParam, s.restParam, visited);
		const retOk = check(s.ret, t.ret, visited);
		const effectsOk = isEffectSubset(s.effects, t.effects);
		return paramsOk && retOk && effectsOk;
	}
	if (s.tag === "Record" && t.tag === "Record") {
		for (const [key, tType] of t.fields) {
			const sType = s.fields.get(key);
			const tIsOptional = t.optionalFields?.has(key) ?? false;
			if (!sType) {
				if (tIsOptional) continue;
				return false;
			}
			if ((s.optionalFields?.has(key) ?? false) && !tIsOptional) return false;
			if (!check(sType, tType, visited)) return false;
		}
		if (!t.open) {
			if (s.open) return false;
			for (const key of s.fields.keys()) if (!t.fields.has(key)) return false;
		}
		return true;
	}
	if (s.tag === "Regex" && t.tag === "Regex") return true;
	return false;
}
function checkSequenceSubtype(s, t, visited) {
	if (!isLengthIntervalContained(s, t)) return false;
	const relevantPrefixLength = Math.max(s.prefix.length, t.prefix.length);
	for (let index = 0; index < relevantPrefixLength; index++) {
		if (!sequenceMayHaveIndex(s, index)) continue;
		if (!check(sequenceElementAt(s, index), sequenceElementAt(t, index), visited)) return false;
	}
	return !sequenceMayHaveIndex(s, relevantPrefixLength) || check(s.rest, t.rest, visited);
}
/**
* Check if two types are disjoint (their intersection is empty).
* Used for negation subtyping: S <: !T iff S and T are disjoint.
*/
function areDisjoint(s, t, visited) {
	if (s.tag === "Never" || t.tag === "Never") return true;
	if (s.tag === "Unknown" || t.tag === "Unknown") return false;
	if (s.tag === "Primitive" && t.tag === "Primitive") {
		if (s.name === t.name) return false;
		if (s.name === "Integer" && t.name === "Number" || s.name === "Number" && t.name === "Integer") return false;
		return true;
	}
	if (s.tag === "Primitive" && t.tag === "Atom" || s.tag === "Atom" && t.tag === "Primitive") return true;
	if (s.tag === "Primitive" && t.tag === "Regex" || s.tag === "Regex" && t.tag === "Primitive") return true;
	if (s.tag === "Atom" && t.tag === "Atom") return s.name !== t.name;
	if (s.tag === "Literal" && t.tag === "Primitive") return !literalMatchesPrimitive(s.value, t.name);
	if (s.tag === "Primitive" && t.tag === "Literal") return !literalMatchesPrimitive(t.value, s.name);
	if (s.tag === "Literal" && t.tag === "Literal") return s.value !== t.value;
	if (s.tag === "Literal" && t.tag === "Atom" || s.tag === "Atom" && t.tag === "Literal") return true;
	if (s.tag === "Function" && t.tag !== "Function" && t.tag !== "AnyFunction" && isGroundType(t)) return true;
	if (t.tag === "Function" && s.tag !== "Function" && s.tag !== "AnyFunction" && isGroundType(s)) return true;
	if (s.tag === "AnyFunction" && isGroundType(t) && t.tag !== "Function") return true;
	if (t.tag === "AnyFunction" && isGroundType(s) && s.tag !== "Function") return true;
	if (isCompositeKind(s) && isScalarKind(t)) return true;
	if (isCompositeKind(t) && isScalarKind(s)) return true;
	if (s.tag === "Record" && isListKind(t)) return true;
	if (t.tag === "Record" && isListKind(s)) return true;
	if (s.tag === "Handler" && t.tag !== "Handler" && isGroundOrHandler(t)) return true;
	if (t.tag === "Handler" && s.tag !== "Handler" && isGroundOrHandler(s)) return true;
	if (s.tag === "Union") return s.members.every((m) => areDisjoint(m, t, visited));
	if (t.tag === "Union") return t.members.every((m) => areDisjoint(s, m, visited));
	if (s.tag === "Inter") return s.members.some((m) => areDisjoint(m, t, visited));
	if (t.tag === "Inter") return t.members.some((m) => areDisjoint(s, m, visited));
	return false;
}
function isLengthIntervalContained(source, target) {
	if (source.minLength < target.minLength) return false;
	if (target.maxLength !== void 0 && source.maxLength === void 0) return false;
	if (target.maxLength !== void 0 && source.maxLength !== void 0 && source.maxLength > target.maxLength) return false;
	return true;
}
/** Check if a literal value matches a primitive type name. */
function literalMatchesPrimitive(value, name) {
	switch (name) {
		case "Number": return typeof value === "number";
		case "Integer": return typeof value === "number" && Number.isInteger(value);
		case "String": return typeof value === "string";
		case "Boolean": return typeof value === "boolean";
		case "Null": return false;
	}
}
/** A ground type has no type variables, unions, intersections, or negations. */
function isGroundType(t) {
	return t.tag === "Primitive" || t.tag === "Atom" || t.tag === "Literal" || t.tag === "Record" || t.tag === "Tuple" || t.tag === "Array" || t.tag === "Sequence" || t.tag === "Regex" || t.tag === "Function" || t.tag === "AnyFunction";
}
/** Composite kinds — structured values whose runtime shape is an
* object or a list. Disjoint with the scalar kinds at the value
* level. */
function isCompositeKind(t) {
	return t.tag === "Record" || t.tag === "Tuple" || t.tag === "Array" || t.tag === "Sequence";
}
/** Scalar kinds — single-value types that aren't composites or
* functions. Primitives, atoms, and regex values. Literals are
* handled via the earlier literal/primitive branches. */
function isScalarKind(t) {
	return t.tag === "Primitive" || t.tag === "Atom" || t.tag === "Regex";
}
/** List-shaped composite kinds — Tuple/Array/Sequence. Disjoint
* with Record (which is string-keyed), but NOT mutually disjoint
* among themselves (a tuple IS a sequence, etc.). */
function isListKind(t) {
	return t.tag === "Tuple" || t.tag === "Array" || t.tag === "Sequence";
}
/** A handler-disjointness partner — any concrete runtime kind.
* Same set as `isGroundType` plus `Handler` itself, so the predicate
* can be called symmetrically on either side of the Handler branches
* in `areDisjoint` without the caller having to add `|| s.tag ===
* 'Handler'`. The same-kind Handler × Handler fall-through is
* enforced by the `t.tag !== 'Handler'` guard at the call site. */
function isGroundOrHandler(t) {
	return isGroundType(t) || t.tag === "Handler";
}
/** Check if an intersection of types is empty (contains disjoint base types). */
function isEmptyIntersection(members) {
	const primitives = members.filter((m) => m.tag === "Primitive");
	if (primitives.length >= 2) {
		const names = [...new Set(primitives.map((p) => p.name))];
		for (let i = 0; i < names.length; i++) for (let j = i + 1; j < names.length; j++) {
			const a = names[i], b = names[j];
			if (a === "Integer" && b === "Number" || a === "Number" && b === "Integer") continue;
			return true;
		}
	}
	for (const prim of primitives) for (const m of members) {
		if (m.tag === "Literal" && !literalMatchesPrimitive(m.value, prim.name)) return true;
		if (m.tag === "Atom") return true;
	}
	return false;
}
/** Unfold a recursive type by substituting the body's self-reference. */
function unfoldRecursive(rec) {
	return substituteVar(rec.body, rec.id, rec);
}
/** Substitute all occurrences of Var with the given id. */
function substituteVar(t, varId, replacement) {
	switch (t.tag) {
		case "Var": return t.id === varId ? replacement : t;
		case "Union": return {
			tag: "Union",
			members: t.members.map((m) => substituteVar(m, varId, replacement))
		};
		case "Inter": return {
			tag: "Inter",
			members: t.members.map((m) => substituteVar(m, varId, replacement))
		};
		case "Neg": return {
			tag: "Neg",
			inner: substituteVar(t.inner, varId, replacement)
		};
		case "Function": return {
			tag: "Function",
			params: t.params.map((p) => substituteVar(p, varId, replacement)),
			...t.restParam !== void 0 ? { restParam: substituteVar(t.restParam, varId, replacement) } : {},
			ret: substituteVar(t.ret, varId, replacement),
			effects: t.effects,
			handlerWrapper: t.handlerWrapper
		};
		case "Tuple": return {
			tag: "Tuple",
			elements: t.elements.map((e) => substituteVar(e, varId, replacement))
		};
		case "Array": return {
			tag: "Array",
			element: substituteVar(t.element, varId, replacement)
		};
		case "Sequence": return {
			tag: "Sequence",
			prefix: t.prefix.map((member) => substituteVar(member, varId, replacement)),
			rest: substituteVar(t.rest, varId, replacement),
			minLength: t.minLength,
			...t.maxLength !== void 0 ? { maxLength: t.maxLength } : {}
		};
		case "Record": {
			const fields = /* @__PURE__ */ new Map();
			for (const [k, v] of t.fields) fields.set(k, substituteVar(v, varId, replacement));
			const rec = {
				tag: "Record",
				fields,
				open: t.open
			};
			if (t.optionalFields && t.optionalFields.size > 0) rec.optionalFields = new Set(t.optionalFields);
			return rec;
		}
		case "Alias": return {
			tag: "Alias",
			name: t.name,
			args: t.args.map((a) => substituteVar(a, varId, replacement)),
			expanded: substituteVar(t.expanded, varId, replacement)
		};
		case "Recursive":
			if (t.id === varId) return t;
			return {
				tag: "Recursive",
				id: t.id,
				body: substituteVar(t.body, varId, replacement)
			};
		case "Keyof": return {
			tag: "Keyof",
			inner: substituteVar(t.inner, varId, replacement)
		};
		case "Index": return {
			tag: "Index",
			target: substituteVar(t.target, varId, replacement),
			key: substituteVar(t.key, varId, replacement)
		};
		case "Refined": return {
			tag: "Refined",
			base: substituteVar(t.base, varId, replacement),
			binder: t.binder,
			predicate: t.predicate,
			source: t.source
		};
		default: return t;
	}
}
/** Generate a cache key for a pair of types (for cycle detection). */
function cacheKey(s, t) {
	return `${typeId(s)}<:${typeId(t)}`;
}
/** Structural identifier for a type (for cache keys).
* Must be unique enough that distinct types don't collide — otherwise
* the cycle-detection cache returns false positives. */
function typeId(t) {
	switch (t.tag) {
		case "Primitive": return `P:${t.name}`;
		case "Atom": return `A:${t.name}`;
		case "Literal": return `L:${t.value}`;
		case "Function": return `F(${t.params.map(typeId).join(",")}${t.restParam !== void 0 ? `|...${typeId(t.restParam)}` : ""})${typeId(t.ret)}${effectSetToString(t.effects)}${t.handlerWrapper ? `|HW:${t.handlerWrapper.paramIndex}:${[...t.handlerWrapper.handled.entries()].map(([name, sig]) => `${name}:${typeId(sig.argType)}:${typeId(sig.retType)}`).join(",")}` : ""}`;
		case "Tuple": return `T[${t.elements.map(typeId).join(",")}]`;
		case "Record": return `R{${[...t.fields.entries()].map(([k, v]) => `${k}${t.optionalFields?.has(k) ? "?" : ""}:${typeId(v)}`).join(",")}${t.open ? ",.." : ""}}`;
		case "Array": return `Ar[${typeId(t.element)}]`;
		case "Sequence": return `Sq[${t.prefix.map(typeId).join(",")}|${typeId(t.rest)}|${t.minLength}|${t.maxLength ?? "*"}]`;
		case "Regex": return "Rx";
		case "Handler": return `H(${typeId(t.body)}=>${typeId(t.output)}|${[...t.handled.entries()].map(([name, sig]) => `${name}:${typeId(sig.argType)}:${typeId(sig.retType)}`).join(",")}|intro:${effectSetToString(t.introduced)})`;
		case "AnyFunction": return "AF";
		case "Union": return `U(${t.members.map(typeId).join("|")})`;
		case "Inter": return `I(${t.members.map(typeId).join("&")})`;
		case "Neg": return `N:${typeId(t.inner)}`;
		case "Unknown": return "?";
		case "Never": return "!";
		case "Var": return `V:${t.id}`;
		case "Alias": return `Al:${t.name}<${t.args.map(typeId).join(",")}>`;
		case "Recursive": return `Rec:${t.id}:${typeId(t.body)}`;
		case "Keyof": return `K:${typeId(t.inner)}`;
		case "Index": return `X:${typeId(t.target)}|${typeId(t.key)}`;
		case "Refined": return `Rf:${typeId(t.base)}|${t.binder}|${t.source}`;
	}
}
function isSubtypeFunctionArityCompatible(source, target) {
	if (target.restParam !== void 0) return source.restParam !== void 0 && source.params.length <= target.params.length;
	return functionAcceptsArity(source, target.params.length);
}
function tryDischargeRefinedLiteral(source, target) {
	const literalAst = literalTypeToAstNode(source);
	if (!literalAst) return null;
	const outcome = evaluateNodeForFold(substitutePredicateBinder(target.predicate, target.binder, literalAst), createContextStack());
	if (!outcome.ok) return null;
	if (outcome.value === true) return true;
	if (outcome.value === false) return false;
	return null;
}
function substitutePredicateBinder(node, binder, literalAst) {
	const rewritten = /* @__PURE__ */ new Map();
	const stack = [{
		node,
		exiting: false
	}];
	while (stack.length > 0) {
		const frame = stack.pop();
		const current = frame.node;
		if (frame.exiting) {
			rewritten.set(current, rewritePredicateNode$1(current, binder, literalAst, rewritten));
			continue;
		}
		stack.push({
			node: current,
			exiting: true
		});
		if (current[0] === NodeTypes.Call && Array.isArray(current[1])) {
			const [callee, args] = current[1];
			for (let index = args.length - 1; index >= 0; index--) stack.push({
				node: args[index],
				exiting: false
			});
			stack.push({
				node: callee,
				exiting: false
			});
			continue;
		}
		if ((current[0] === NodeTypes.And || current[0] === NodeTypes.Or) && Array.isArray(current[1])) {
			const operands = current[1];
			for (let index = operands.length - 1; index >= 0; index--) stack.push({
				node: operands[index],
				exiting: false
			});
		}
	}
	return rewritten.get(node) ?? node;
}
function rewritePredicateNode$1(node, binder, literalAst, rewritten) {
	if (node[0] === NodeTypes.Sym && node[1] === binder) return literalAst;
	if (node[0] === NodeTypes.Call && Array.isArray(node[1])) {
		const [callee, args, hints] = node[1];
		const rewrittenCallee = rewritten.get(callee) ?? callee;
		const rewrittenArgs = args.map((arg) => rewritten.get(arg) ?? arg);
		if (rewrittenCallee === callee && rewrittenArgs.every((arg, index) => arg === args[index])) return node;
		return [
			NodeTypes.Call,
			[
				rewrittenCallee,
				rewrittenArgs,
				hints
			],
			node[2]
		];
	}
	if ((node[0] === NodeTypes.And || node[0] === NodeTypes.Or) && Array.isArray(node[1])) {
		const operands = node[1];
		const rewrittenOperands = operands.map((operand) => rewritten.get(operand) ?? operand);
		if (rewrittenOperands.every((operand, index) => operand === operands[index])) return node;
		return [
			node[0],
			rewrittenOperands,
			node[2]
		];
	}
	return node;
}
//#endregion
//#region src/typechecker/simplify.ts
/**
* Simplify a type to a normalized, more readable form.
* Does not change the type's meaning — only its representation.
*/
function simplify(t) {
	switch (t.tag) {
		case "Union": return simplifyUnion(t.members.map(simplify));
		case "Inter": return simplifyIntersection(t.members.map(simplify));
		case "Neg": return simplifyNeg(simplify(t.inner));
		case "Function": return {
			tag: "Function",
			params: t.params.map(simplify),
			...t.restParam !== void 0 ? { restParam: simplify(t.restParam) } : {},
			ret: simplify(t.ret),
			effects: t.effects,
			handlerWrapper: t.handlerWrapper,
			...t.asserts !== void 0 ? { asserts: t.asserts } : {}
		};
		case "Handler": {
			const handled = /* @__PURE__ */ new Map();
			for (const [name, sig] of t.handled) handled.set(name, {
				argType: simplify(sig.argType),
				retType: simplify(sig.retType)
			});
			return {
				tag: "Handler",
				body: simplify(t.body),
				output: simplify(t.output),
				handled,
				introduced: t.introduced
			};
		}
		case "Tuple": return {
			tag: "Tuple",
			elements: t.elements.map(simplify)
		};
		case "Array": return {
			tag: "Array",
			element: simplify(t.element)
		};
		case "Sequence": return simplifySequence({
			tag: "Sequence",
			prefix: t.prefix.map(simplify),
			rest: simplify(t.rest),
			minLength: t.minLength,
			...t.maxLength !== void 0 ? { maxLength: t.maxLength } : {}
		});
		case "Record": {
			const fields = /* @__PURE__ */ new Map();
			for (const [k, v] of t.fields) fields.set(k, simplify(v));
			const rec = {
				tag: "Record",
				fields,
				open: t.open
			};
			if (t.optionalFields && t.optionalFields.size > 0) rec.optionalFields = new Set(t.optionalFields);
			return rec;
		}
		case "Alias": return {
			tag: "Alias",
			name: t.name,
			args: t.args.map(simplify),
			expanded: simplify(t.expanded)
		};
		case "Keyof": return keyofType(simplify(t.inner));
		case "Index": return indexType(simplify(t.target), simplify(t.key));
		case "Refined": {
			const base = simplify(t.base);
			let merged;
			if (base.tag === "Refined") {
				const { predicate, source } = mergeRefinementPredicates(base.binder, base.predicate, t.binder, t.predicate);
				merged = {
					tag: "Refined",
					base: base.base,
					binder: base.binder,
					predicate,
					source
				};
			} else merged = {
				tag: "Refined",
				base,
				binder: t.binder,
				predicate: t.predicate,
				source: t.source
			};
			return simplifyRefinedType(merged) ?? merged;
		}
		default: return t;
	}
}
function simplifyUnion(members) {
	const result = union(...members);
	if (result.tag !== "Union") return result;
	const absorbed = absorbSubtypes(result.members);
	if (absorbed.length === 0) return Never;
	if (absorbed.length === 1) return absorbed[0];
	return {
		tag: "Union",
		members: absorbed
	};
}
/**
* Remove members that are subtypes of other members.
* e.g., [42, Number] → [Number] because 42 <: Number
*/
function absorbSubtypes(members) {
	const result = [];
	for (let i = 0; i < members.length; i++) {
		const m = members[i];
		if (!members.some((other, j) => i !== j && !typeEquals(m, other) && isSubtype(m, other))) result.push(m);
	}
	return result;
}
function simplifyIntersection(members) {
	const result = inter(...members);
	if (result.tag !== "Inter") return result;
	const merged = mergeRecordMembers(result.members);
	if (merged.tag === "Never") return Never;
	if (merged.tag !== "Inter") return merged;
	if (hasDisjointKinds(merged.members)) return Never;
	const collapsed = collapseTrivialNegations(merged.members);
	if (collapsed.length === 0) return Unknown;
	if (collapsed.length === 1) return collapsed[0];
	const narrowed = narrowSupertypes(collapsed);
	if (narrowed.length === 0) return Never;
	if (narrowed.length === 1) return narrowed[0];
	return {
		tag: "Inter",
		members: narrowed
	};
}
/**
* Fold all Record members of an intersection into a single Record via
* pairwise structural merge. Non-record members pass through
* unchanged. Returns `Never` only when a shared required field's
* types have empty intersection. Field-only-on-one-side is ALWAYS
* carried through (permissive semantics — the other side's closed
* flag does not veto fields it never declared; see the
* `intersectRecordPair` JSDoc for the full rationale). Returns the
* original `Inter` shape when there's at most one record to merge.
*/
function mergeRecordMembers(members) {
	const records = [];
	const others = [];
	for (const m of members) if (m.tag === "Record") records.push(m);
	else others.push(m);
	if (records.length < 2) return {
		tag: "Inter",
		members
	};
	let combined = records[0];
	for (let i = 1; i < records.length; i++) {
		const merged = intersectRecordPair(combined, records[i]);
		if (merged.tag === "Never") return Never;
		if (merged.tag !== "Record") return {
			tag: "Inter",
			members
		};
		combined = merged;
	}
	if (others.length === 0) return combined;
	return {
		tag: "Inter",
		members: [combined, ...others]
	};
}
/**
* Pairwise structural intersection of two record types for the
* user-facing simplify path. Semantics follow the TS-ish "shape
* merge" convention: `{a: A} & {b: B}` means "has both fields" and
* resolves to `{a: A, b: B}`, independent of either side's `open`
* flag at the source. The `open` flag combines as `a.open && b.open`
* — the result allows extras only if both inputs did.
*
* This differs from the narrowing path's `intersectRecords` in
* `infer.ts`, which uses strict set-theoretic semantics (a closed
* record's values have EXACTLY those fields, so the intersection
* with a different closed record is `Never`). That strictness is
* correct for runtime-value narrowing on tagged unions; it would be
* surprising here where users write intersections to combine shapes.
*/
function intersectRecordPair(a, b) {
	const fields = /* @__PURE__ */ new Map();
	const optionalFields = /* @__PURE__ */ new Set();
	const allKeys = new Set([...a.fields.keys(), ...b.fields.keys()]);
	for (const k of allKeys) {
		const av = a.fields.get(k);
		const bv = b.fields.get(k);
		const aOpt = a.optionalFields?.has(k) ?? false;
		const bOpt = b.optionalFields?.has(k) ?? false;
		if (av && bv) {
			const intersected = simplify(inter(av, bv));
			if (intersected.tag === "Never") {
				if (aOpt && bOpt) continue;
				return Never;
			}
			fields.set(k, intersected);
			if (aOpt && bOpt) optionalFields.add(k);
		} else if (av) {
			fields.set(k, av);
			if (aOpt) optionalFields.add(k);
		} else if (bv) {
			fields.set(k, bv);
			if (bOpt) optionalFields.add(k);
		}
	}
	const out = {
		tag: "Record",
		fields,
		open: a.open && b.open
	};
	if (optionalFields.size > 0) out.optionalFields = optionalFields;
	return out;
}
/** Check if any pair of members in the intersection is disjoint, in
* which case the whole intersection is empty (Never). Handles:
*  - Primitive × Primitive (with the Integer ⊂ Number exception —
*    `narrowSupertypes` then collapses that pair to Integer).
*  - Cross-kind pairs (Record × String, Tuple × Boolean, Record ×
*    Tuple, etc.) via `isSubtype(a, neg(b))`, which runs the
*    `areDisjoint` logic in subtype.ts. Composite-vs-scalar and
*    Record-vs-list-kind are the main added cases (see issue #83).
* Skips pairs where either side is a Var / Neg / Inter / Union /
* Recursive / Alias / Keyof / Index — those don't have a stable
* "kind" at this point and could simplify later. */
function hasDisjointKinds(members) {
	const names = [];
	for (const m of members) if (m.tag === "Primitive") names.push(m.name);
	for (let i = 0; i < names.length; i++) for (let j = i + 1; j < names.length; j++) {
		const a = names[i];
		const b = names[j];
		if (a === b) continue;
		if (a === "Integer" && b === "Number" || a === "Number" && b === "Integer") continue;
		return true;
	}
	const concrete = members.filter(isConcreteKindMember);
	for (let i = 0; i < concrete.length; i++) for (let j = i + 1; j < concrete.length; j++) if (isSubtype(concrete[i], neg(concrete[j]))) return true;
	return false;
}
/**
* Members whose kind alone can drive disjointness via `areDisjoint`.
* `Literal` is deliberately excluded: the primitive-pair loop above
* already catches literal-vs-mismatching-primitive (via the earlier
* `isEmptyIntersection` path and `areDisjoint`'s literal branches),
* and `areDisjoint` has no rules for literal vs composite, so a call
* there would waste work and always return false.
*/
function isConcreteKindMember(t) {
	return t.tag === "Primitive" || t.tag === "Atom" || t.tag === "Record" || t.tag === "Tuple" || t.tag === "Array" || t.tag === "Sequence" || t.tag === "Regex" || t.tag === "Function" || t.tag === "AnyFunction";
}
/**
* Remove negation members that are already disjoint with positive members.
* e.g., Number & !String → Number (Number and String are already disjoint)
*/
function collapseTrivialNegations(members) {
	const positive = members.filter((m) => m.tag !== "Neg");
	const meaningfulNegations = members.filter((m) => m.tag === "Neg").filter((n) => {
		if (positive.length > 0) {
			if (positive.every((p) => isSubtype(p, neg(n.inner)))) return false;
		}
		return true;
	});
	return [...positive, ...meaningfulNegations];
}
/**
* In intersections, remove members that are supertypes of other members.
* e.g., [Number, 42] → [42] because Number is a supertype of 42
*/
function narrowSupertypes(members) {
	const result = [];
	for (let i = 0; i < members.length; i++) {
		const m = members[i];
		if (!members.some((other, j) => i !== j && !typeEquals(m, other) && isSubtype(other, m))) result.push(m);
	}
	return result;
}
function simplifyNeg(inner) {
	return neg(inner);
}
function simplifySequence(type) {
	const normalized = normalizeSequenceType(type);
	if (normalized.maxLength !== void 0 && normalized.minLength > normalized.maxLength) return Never;
	if (normalized.prefix.some((member) => member.tag === "Never")) return Never;
	if (normalized.rest.tag === "Never") return tuple(normalized.prefix);
	if (normalized.prefix.length === 0 && normalized.minLength === 0 && normalized.maxLength === void 0) return array(normalized.rest);
	return normalized;
}
//#endregion
//#region src/typechecker/effectTypes.ts
/** Map from effect name to its type declaration. */
const effectRegistry = /* @__PURE__ */ new Map();
/** Set of builtin effect names (not cleared between typechecks). */
const builtinEffectNames = /* @__PURE__ */ new Set();
/**
* Register an effect's type declaration.
*
* Silently skips names in `builtinEffectNames` so that user-level
* `effect @name(T) -> U` declarations cannot overwrite a builtin
* effect's signature. HandlerWrapperInfo for wrappers like
* `effectHandler.chooseRandom` captures arg/ret types by value at
* module-registration time, but the active handled signatures pushed
* at perform sites are also derived from the registry — letting users
* stomp on those would mis-type perform calls inside handler wrappers.
* `initBuiltinEffects` uses `declareBuiltinEffect` below to install the
* builtins without going through this guard.
*/
function declareEffect(name, argType, retType) {
	if (builtinEffectNames.has(name)) return;
	effectRegistry.set(name, {
		argType,
		retType
	});
}
/** Internal: install a builtin effect, bypassing the user-overwrite guard. */
function declareBuiltinEffect(name, argType, retType) {
	effectRegistry.set(name, {
		argType,
		retType
	});
	builtinEffectNames.add(name);
}
/** Look up an effect's declaration. Returns undefined if not declared. */
function getEffectDeclaration(name) {
	return effectRegistry.get(name);
}
/** Snapshot the current registry so nested import typechecking can restore it. */
function snapshotEffectRegistry() {
	return {
		entries: [...effectRegistry.entries()].map(([name, decl]) => [name, { ...decl }]),
		builtinNames: [...builtinEffectNames]
	};
}
/** Restore a previously captured registry snapshot. */
function restoreEffectRegistry(snapshot) {
	effectRegistry.clear();
	builtinEffectNames.clear();
	for (const [name, decl] of snapshot.entries) effectRegistry.set(name, decl);
	for (const name of snapshot.builtinNames) builtinEffectNames.add(name);
}
/** Reset user-declared effects (called at the start of each typecheck pass).
* Builtin effects are preserved. */
function resetUserEffects() {
	for (const name of effectRegistry.keys()) if (!builtinEffectNames.has(name)) effectRegistry.delete(name);
}
/** Register built-in Dvala effects with known types. */
function initBuiltinEffects() {
	const builtins = [
		[
			"dvala.error",
			Unknown,
			Unknown
		],
		[
			"dvala.io.print",
			Unknown,
			Unknown
		],
		[
			"dvala.io.read",
			Unknown,
			Unknown
		],
		[
			"choose",
			Unknown,
			Unknown
		],
		[
			"dvala.random.item",
			Unknown,
			Unknown
		]
	];
	for (const [name, arg, ret] of builtins) declareBuiltinEffect(name, arg, ret);
}
//#endregion
//#region src/typechecker/refinementFragmentCheck.ts
/**
* Refinement-types Phase 1 — fragment-checker walker.
*
* Walks a Dvala expression AST (the predicate body parsed by the main
* Dvala parser) and either returns silently (the predicate is in the
* accepted fragment) or throws a `RefinementError` with the appropriate
* `kind`.
*
* Design reference: `design/active/2026-04-23_refinement-types.md`,
* Phase 1 ship gate.
*
* Accepted shapes (top-level must reduce to Boolean):
*   - `isX(var)` — any type-guard builtin applied to the binder
*   - `var REL literal`  (REL ∈ ==, !=, <, <=, >, >=)
*   - `lit REL var` (swapped operands) — accepted; the Phase 2.3 solver
*     normalises these to canonical `var REL lit` form internally
*   - `!P`, `P && Q`, `P || Q`  where P, Q are themselves accepted
*   - `count(var) REL literal`
*
* Rejected shapes (each with a distinct error phrasing):
*   - Bare var / non-Boolean body           → kind: `predicate-type`
*   - Arithmetic (`+`, `-`, `*`, `/`, `%`)  → kind: `fragment`
*   - Unknown / non-guard builtin call      → kind: `fragment`
*   - Effect (`perform`)                    → kind: `fragment`
*   - Control flow (`if`, `match`, `let`,
*     `loop`, `for`)                        → kind: `fragment`
*   - Field access on binder                → kind: `fragment`
*                                             (deferred-by-design; Phase 2.5+)
*
* Walker strategy is a simple switch on `NodeTypes`. Each accepted case
* recurses into its children; every other case throws. No classification
* is *stored* (per Phase 1 scope — the `Refined` AST node arrives in
* Phase 2); the walker's return value is unused and its side-effect is
* either "no throw" (accept) or "throw RefinementError" (reject).
*/
/** Relations accepted on the RHS of a refinement binder. */
const RELATION_BUILTINS = new Set([
	"==",
	"!=",
	"<",
	"<=",
	">",
	">="
]);
/**
* Arithmetic operators explicitly named in the rejection message so the
* error points at the operator that triggered it (rather than a generic
* "not in fragment"). Keeps the kind `'fragment'`.
*/
const ARITHMETIC_BUILTINS = new Set([
	"+",
	"-",
	"*",
	"/",
	"%",
	"^",
	"mod",
	"quot"
]);
/**
* Check that `predicate` is in the Phase 1 refinement fragment. The
* binder name is tracked so the walker can verify `isX(var)` and
* `var REL lit` actually reference it (rather than some other symbol).
*
* Throws `RefinementError` on rejection. Returns undefined on accept.
*
* `source` / `position` are threaded through so the error message can
* point at the original refinement annotation (the Dvala AST nodes have
* their own source positions but those reference the predicate body,
* which isn't what the user sees in the type-annotation context).
*/
function fragmentCheckPredicate(predicate, binder, source, position) {
	checkBooleanExpr(predicate, binder, source, position);
}
/** Top-level: the node must produce Boolean through an accepted shape. */
function checkBooleanExpr(node, binder, source, position) {
	switch (node[0]) {
		case NodeTypes.And:
		case NodeTypes.Or: {
			const operands = node[1];
			for (const op of operands) checkBooleanExpr(op, binder, source, position);
			return;
		}
		case NodeTypes.Call:
			checkCall(node, binder, source, position);
			return;
		case NodeTypes.Sym:
			if (node[1] === binder) return;
			throw new RefinementError(`Refinement predicate must be a Boolean expression; got a bare identifier '${node[1]}'. Use a relation (e.g. \`x != 0\`), a type-guard call (e.g. \`isNumber(x)\`), or a Boolean composition of those.`, "predicate-type", source, position);
		case NodeTypes.Num:
		case NodeTypes.Str:
		case NodeTypes.Atom:
		case NodeTypes.TmplStr: throw new RefinementError("Refinement predicate must be a Boolean expression; got a literal. A literal on its own is not a predicate — use a comparison like `x == <literal>`.", "predicate-type", source, position);
		case NodeTypes.If:
		case NodeTypes.Match:
		case NodeTypes.Let:
		case NodeTypes.Loop:
		case NodeTypes.For:
		case NodeTypes.Block: throw new RefinementError(`Refinement predicate uses control-flow construct '${node[0]}', which is not in the accepted fragment. Predicates must be pure Boolean expressions (relations, guards, \`&&\`/\`||\`/\`!\`).`, "fragment", source, position);
		case NodeTypes.Perform:
		case NodeTypes.Resume:
		case NodeTypes.WithHandler:
		case NodeTypes.Handler: throw new RefinementError(`Refinement predicate contains an effect operation ('${node[0]}'). Refinements describe values, not behaviors — effectful predicates are rejected.`, "fragment", source, position);
		default: throw new RefinementError(`Refinement predicate uses a construct ('${node[0]}') that is not in the accepted fragment. Allowed shapes: type-guard calls, relations, \`count(var)\`, and \`&&\` / \`||\` / \`!\` compositions of those.`, "fragment", source, position);
	}
}
/**
* A `Call` node in a predicate must be one of:
*   - `!arg`              — unary not, recurse on the argument
*   - `isX(var)`          — type-guard applied to the binder
*   - `var REL literal`   — relation with binder on the left, literal on the right
*   - `count(var) REL lit`— sequence-length relation (var is the binder)
*/
function checkCall(node, binder, source, position) {
	const [callee, args] = node[1];
	if (callee[0] !== NodeTypes.Builtin) throw new RefinementError("Refinement predicate may only call Dvala builtin functions (type guards, relations, `count`, `!`). User-defined or module-qualified functions are not in the Phase 1 fragment.", "fragment", source, position);
	const name = callee[1];
	if (name === "!") {
		if (args.length !== 1) throw new RefinementError(`Refinement predicate: '!' takes exactly one argument; got ${args.length}.`, "fragment", source, position);
		checkBooleanExpr(args[0], binder, source, position);
		return;
	}
	if (isTypeGuard(name)) {
		if (args.length !== 1) throw new RefinementError(`Refinement predicate: type-guard '${name}' takes exactly one argument; got ${args.length}.`, "fragment", source, position);
		if (!isBinderRef(args[0], binder)) throw new RefinementError(`Refinement predicate: type-guard '${name}' must be applied to the binder '${binder}'. Field access and other sub-expressions are deferred to a later phase.`, "fragment", source, position);
		return;
	}
	if (RELATION_BUILTINS.has(name)) {
		if (args.length !== 2) throw new RefinementError(`Refinement predicate: relation '${name}' must be used with exactly two operands.`, "fragment", source, position);
		checkRelationOperands(args[0], args[1], name, binder, source, position);
		return;
	}
	if (ARITHMETIC_BUILTINS.has(name)) throw new RefinementError(`Refinement predicate contains arithmetic operator '${name}', which is not in the Phase 1 fragment. Phase 1 accepts relations (e.g. \`x > 0\`), type-guards (e.g. \`isInteger(x)\`), and \`count(var)\` only. Arithmetic on refined variables is solved by Phase 3 (multi-variable linear arithmetic).`, "fragment", source, position);
	throw new RefinementError(`Refinement predicate calls builtin '${name}', which is not in the accepted fragment. Phase 1 accepts: type guards, relations (==, !=, <, <=, >, >=), \`count(var)\`, and \`!\` / \`&&\` / \`||\`.`, "fragment", source, position);
}
/**
* Accepted operand shapes for a relation:
*   one side:  `binder` (Sym) OR `count(binder)` (Call of `count` builtin on binder)
*   other side: literal (Num, Str, Atom, or `true`/`false` reserved)
*/
function checkRelationOperands(lhs, rhs, relName, binder, source, position) {
	if (classifyRelationLhs(lhs, binder) !== "other") {
		if (!isLiteralLike(rhs)) throw new RefinementError(`Refinement predicate: relation '${relName}' requires a literal on the non-binder side. References to other variables, arithmetic, and sub-expressions are deferred to a later phase.`, "fragment", source, position);
		return;
	}
	if (classifyRelationLhs(rhs, binder) !== "other") {
		if (!isLiteralLike(lhs)) throw new RefinementError(`Refinement predicate: relation '${relName}' requires a literal on the non-binder side. References to other variables, arithmetic, and sub-expressions are deferred to a later phase.`, "fragment", source, position);
		return;
	}
	if (isArithmeticCall(lhs)) throw new RefinementError(`Refinement predicate: relation '${relName}' has an arithmetic expression on its left-hand side, which is not in the Phase 1 fragment. Phase 1 accepts only \`binder\` or \`count(binder)\` on the LHS. Arithmetic on refined variables (e.g. \`n + 1 > 0\`) is solved by Phase 3 (multi-variable linear arithmetic).`, "fragment", source, position);
	throw new RefinementError(`Refinement predicate: relation '${relName}' must compare the binder '${binder}' (or 'count(${binder})') directly against a literal. References to other variables, arithmetic, and sub-expressions are deferred to a later phase.`, "fragment", source, position);
}
/** Is `node` a reference to the binder `Sym(binder)`? */
function isBinderRef(node, binder) {
	return node[0] === NodeTypes.Sym && node[1] === binder;
}
/** Is `node` a call to one of the arithmetic builtins? */
function isArithmeticCall(node) {
	if (node[0] !== NodeTypes.Call) return false;
	const [callee] = node[1];
	if (callee[0] !== NodeTypes.Builtin) return false;
	return ARITHMETIC_BUILTINS.has(callee[1]);
}
/**
* Classify the LHS of a relation. `binder` if it's a plain binder
* reference; `countBinder` if it's `count(binder)`; `other` for anything
* else (rejection).
*/
function classifyRelationLhs(node, binder) {
	if (isBinderRef(node, binder)) return "binder";
	if (node[0] === NodeTypes.Call) {
		const [callee, args] = node[1];
		if (callee[0] === NodeTypes.Builtin && callee[1] === "count" && args.length === 1 && isBinderRef(args[0], binder)) return "countBinder";
	}
	return "other";
}
/**
* Literal-like RHS operand for a relation: number, string, atom, or
* the reserved-symbol literals `true` / `false` / `null`. The reserved
* keyword forms parse as `Reserved` nodes in Dvala.
*/
function isLiteralLike(node) {
	switch (node[0]) {
		case NodeTypes.Num:
		case NodeTypes.Str:
		case NodeTypes.Atom: return true;
		case NodeTypes.Reserved: {
			const name = node[1];
			return name === "true" || name === "false" || name === "null";
		}
		default: return false;
	}
}
//#endregion
//#region src/typechecker/parseType.ts
/** Registered type aliases: name → { params, body string } */
const typeAliasRegistry = /* @__PURE__ */ new Map();
/** Register a type alias. Called by typecheck.ts from parsed AST. */
function registerTypeAlias(name, params, body) {
	typeAliasRegistry.set(name, {
		params,
		body
	});
}
/** Reset user-registered type aliases (called between typecheck passes). */
function resetTypeAliases() {
	typeAliasRegistry.clear();
}
/** Snapshot the current alias registry so nested import typechecking can restore it. */
function snapshotTypeAliases() {
	return { entries: [...typeAliasRegistry.entries()].map(([name, alias]) => [name, {
		params: alias.params.map((p) => p.bound === void 0 ? { name: p.name } : {
			name: p.name,
			bound: p.bound
		}),
		body: alias.body
	}]) };
}
/** Restore a previously captured alias registry snapshot. */
function restoreTypeAliases(snapshot) {
	typeAliasRegistry.clear();
	for (const [name, alias] of snapshot.entries) typeAliasRegistry.set(name, alias);
}
/**
* Parse a source-level type annotation.
*
* Unknown named types are rejected here so editor diagnostics can report the
* typo instead of silently degrading the annotation to `Unknown`.
*/
function parseUserTypeAnnotation(input, scopedTypeRefs) {
	const parser = new TypeParser(input, scopedTypeRefs, true);
	const result = parser.parseType();
	if (!parser.isAtEnd()) throw new TypeParseError(`Unexpected token: '${parser.remaining()}'`, input, parser.pos);
	return result;
}
function parseFunctionTypeAnnotation(input) {
	const parser = new TypeParser(input);
	const result = parser.parseFunctionOrType();
	if (!parser.isAtEnd()) throw new TypeParseError(`Unexpected token: '${parser.remaining()}'`, input, parser.pos);
	return result;
}
var TypeParser = class TypeParser {
	input;
	pos;
	/** Maps type variable names (A, B, T, etc.) to shared Var nodes within this annotation. */
	typeVarMap = /* @__PURE__ */ new Map();
	nextVarId = 0;
	/**
	* Maps row-variable names (a, b, …, single lowercase) to shared RowVar tails
	* within this annotation. Two `@{e | ρ}` occurrences in one signature
	* reference the same RowVar, enabling positional unification.
	*
	* Kept separate from `typeVarMap` by design: value-type vars have `Type[]`
	* bounds while row vars have `Set<string>[]` bounds. Static dispatch by kind
	* is cleaner than a discriminated union.
	*/
	rowVarMap = /* @__PURE__ */ new Map();
	nextRowVarId = 0;
	scopedTypeRefs;
	strictUnknownTypeNames;
	constructor(input, scopedTypeRefs = /* @__PURE__ */ new Map(), strictUnknownTypeNames = false) {
		this.input = input;
		this.pos = 0;
		this.scopedTypeRefs = scopedTypeRefs;
		this.strictUnknownTypeNames = strictUnknownTypeNames;
	}
	parseType() {
		return this.parseUnion();
	}
	/**
	* Phase 0a — parse an optional `<T, T: Bound, ...>` prefix before a
	* function type. Registers each type variable in `typeVarMap` with its
	* bound (if any) populated in `upperBounds` before the surrounding type
	* is parsed. Called from `parsePrimary` / `parseFunctionOrType` when
	* the parser is at a `<` token at type position.
	*
	* Syntax: `<A, B: Bound, C>` — each param is a single uppercase letter
	* with an optional `: Type` bound. Bounds propagate via the existing
	* `Var.upperBounds` + `constrain` machinery (no new inference code).
	*/
	parseTypeVarPrefix() {
		this.consume("<");
		this.skipWhitespace();
		if (this.tryConsume(">")) return;
		const seen = /* @__PURE__ */ new Set();
		for (;;) {
			this.skipWhitespace();
			const name = this.readIdentifier();
			if (!name) throw this.error("Expected type parameter name in <...> prefix");
			if (!(name.length === 1 && name >= "A" && name <= "Z")) throw this.error(`Type parameter '${name}' must be a single uppercase letter (A, B, ..., Z)`);
			if (seen.has(name)) throw this.error(`Duplicate type parameter '${name}' in <...> prefix`);
			seen.add(name);
			let v = this.typeVarMap.get(name);
			if (!v) {
				v = {
					tag: "Var",
					id: this.nextVarId++,
					level: 0,
					lowerBounds: [],
					upperBounds: []
				};
				this.typeVarMap.set(name, v);
			}
			this.skipWhitespace();
			if (this.tryConsume(":")) {
				this.skipWhitespace();
				const bound = this.parseType();
				v.upperBounds.push(bound);
			}
			this.skipWhitespace();
			if (this.tryConsume(",")) continue;
			break;
		}
		this.skipWhitespace();
		if (!this.tryConsume(">")) throw this.error("Expected \">\" to close type-parameter list");
	}
	/**
	* Parse either a function type (if we see params + ->) or a regular type.
	* Also detects type guard syntax: (x: T) -> x is U
	*/
	parseFunctionOrType() {
		this.skipWhitespace();
		if (this.peek() === "<") {
			this.parseTypeVarPrefix();
			this.skipWhitespace();
		}
		const saved = this.pos;
		if (this.tryConsume("(")) {
			const paramsResult = this.tryParseParams();
			if (paramsResult !== null) {
				let returnTypeAnnotation;
				if (this.tryConsume(":")) {
					this.skipWhitespace();
					returnTypeAnnotation = this.parseType();
					this.skipWhitespace();
				}
				if (this.tryConsume("->")) {
					this.skipWhitespace();
					const guardResult = this.tryParseTypeGuard(paramsResult.params);
					if (guardResult) return {
						type: fn(paramsResult.types, BooleanType, PureEffects, void 0, paramsResult.restType),
						guardParam: guardResult.paramName,
						guardType: guardResult.guardType
					};
					const assertsResult = this.tryParseAsserts(paramsResult.params);
					if (assertsResult) {
						const assertsInfo = {
							paramIndex: paramsResult.params.findIndex((p) => p.name === assertsResult.paramName),
							binder: assertsResult.predicate.binder,
							predicate: assertsResult.predicate.predicate,
							source: assertsResult.predicate.source
						};
						return {
							type: fn(paramsResult.types, BooleanType, PureEffects, void 0, paramsResult.restType, assertsInfo),
							assertsParam: assertsResult.paramName,
							assertsPredicate: assertsResult.predicate
						};
					}
					const effects = this.tryParseEffectSet() ?? void 0;
					const retType = this.parseType();
					return { type: fn(paramsResult.types, returnTypeAnnotation ?? retType, effects, void 0, paramsResult.restType) };
				}
			}
			this.pos = saved;
		}
		return { type: this.parseType() };
	}
	parseUnion() {
		let left = this.parseIntersection();
		this.skipWhitespace();
		while (this.tryConsume("|")) {
			this.skipWhitespace();
			const right = this.parseIntersection();
			left = union(left, right);
			this.skipWhitespace();
		}
		return left;
	}
	parseIntersection() {
		let left = this.parsePrefix();
		this.skipWhitespace();
		while (this.tryConsume("&")) {
			this.skipWhitespace();
			if (this.looksLikeRefinementPredicate()) {
				const { binder, predicate, source } = this.consumeAndCheckRefinementPredicate();
				left = {
					tag: "Refined",
					base: left,
					binder,
					predicate,
					source
				};
				this.skipWhitespace();
				continue;
			}
			const right = this.parsePrefix();
			left = inter(left, right);
			this.skipWhitespace();
		}
		return left;
	}
	/**
	* Refinement-predicate disambiguation — Phase 1 of the refinement-types
	* plan. Returns true if the upcoming tokens look like `{ IDENT |`
	* (a refinement predicate), false otherwise (record literal or any
	* other `{`-led shape). Does NOT advance `pos` — this is pure lookahead.
	*/
	looksLikeRefinementPredicate() {
		if (this.peek() !== "{") return false;
		let i = this.pos + 1;
		while (i < this.input.length && /\s/.test(this.input[i])) i++;
		const identStart = i;
		while (i < this.input.length && this.isIdentChar(this.input[i])) i++;
		if (i === identStart) return false;
		while (i < this.input.length && /\s/.test(this.input[i])) i++;
		if (this.input[i] !== "|") return false;
		if (this.input[i + 1] === "|") return false;
		return true;
	}
	/**
	* Phase 2.1: consume `{ binder | predicate }`, parse the predicate
	* body as a Dvala expression, and run the fragment-checker. On accept,
	* returns the `(binder, predicate, source)` triple the caller wraps
	* into a `Refined` node. On reject, throws `RefinementError`.
	*
	* Brace-depth tracking handles nested braces inside the body (e.g. a
	* record literal on one side of a relation). `{` and `}` inside string
	* literals are ignored — handled by the Dvala tokenizer in the inner
	* parse; our char-level scan here just extracts the substring.
	*/
	consumeAndCheckRefinementPredicate() {
		const startPos = this.pos;
		this.consume("{");
		this.skipWhitespace();
		const binderStart = this.pos;
		while (this.pos < this.input.length && this.isIdentChar(this.input[this.pos])) this.pos++;
		if (this.pos === binderStart) throw new TypeParseError("Expected binder identifier before `|`", this.input, this.pos);
		const binder = this.input.slice(binderStart, this.pos);
		if (binder === "null" || binder === "true" || binder === "false") throw new TypeParseError(`Refinement binder '${binder}' conflicts with a reserved keyword; choose a different name`, this.input, binderStart);
		this.skipWhitespace();
		this.consume("|");
		this.skipWhitespace();
		const bodyStart = this.pos;
		let depth = 1;
		while (this.pos < this.input.length && depth > 0) {
			const ch = this.input[this.pos];
			if (ch === "{") depth++;
			else if (ch === "}") depth--;
			if (depth > 0) this.pos++;
		}
		if (this.pos >= this.input.length) throw new TypeParseError("Unterminated refinement predicate — expected `}`", this.input, this.pos);
		const bodySource = this.input.slice(bodyStart, this.pos).trim();
		this.consume("}");
		if (bodySource.length === 0) throw new TypeParseError("Empty refinement predicate body", this.input, startPos);
		const body = parse(minifyTokenStream(tokenize(bodySource, false, void 0), { removeWhiteSpace: true }));
		if (body.length !== 1) throw new RefinementError(`Refinement predicate must be a single expression; parsed ${body.length} top-level expressions.`, "fragment", this.input, startPos);
		const predicate = body[0];
		fragmentCheckPredicate(predicate, binder, this.input, startPos);
		return {
			binder,
			predicate,
			source: `${binder} | ${bodySource}`
		};
	}
	parsePrefix() {
		this.skipWhitespace();
		if (this.tryConsume("!")) return neg(this.parsePrefix());
		if (this.matchKeyword("keyof")) return keyofType(this.parsePrefix());
		return this.parsePostfix();
	}
	/**
	* Try to match a full keyword (identifier boundary after the match).
	* Prevents matching `keyof` as a prefix of an alias like `keyofThing`.
	*/
	matchKeyword(word) {
		this.skipWhitespace();
		if (!this.input.startsWith(word, this.pos)) return false;
		const next = this.input[this.pos + word.length];
		if (next !== void 0 && this.isIdentChar(next)) return false;
		this.pos += word.length;
		return true;
	}
	parsePostfix() {
		let t = this.parsePrimary();
		for (;;) if (this.tryConsume("[]")) t = array(t);
		else if (this.peek() === "[") {
			this.consume("[");
			this.skipWhitespace();
			if (this.peek() === "]") {
				this.consume("]");
				t = array(t);
			} else {
				const keyType = this.parseType();
				this.consume("]");
				t = indexType(t, keyType);
			}
		} else if (this.peek() === "?" && this.peekAt(1) !== ".") {
			this.advance();
			t = union(t, NullType);
		} else break;
		return t;
	}
	parsePrimary() {
		this.skipWhitespace();
		if (this.peek() === "<") {
			this.parseTypeVarPrefix();
			this.skipWhitespace();
			if (this.peek() !== "(") throw this.error("Expected \"(\" after type-parameter prefix");
			return this.parseParenOrFunction();
		}
		if (this.peek() === "(") return this.parseParenOrFunction();
		if (this.peek() === "[") return this.parseTupleType();
		if (this.peek() === "{") return this.parseRecordType();
		if (this.peek() === ":") {
			this.advance();
			const name = this.readIdentifier();
			if (!name) throw this.error("Expected identifier after \":\"");
			return atom(name);
		}
		if (this.peek() === "\"") return this.parseStringLiteral();
		if (this.isDigit(this.peek()) || this.peek() === "-" && this.isDigit(this.peekAt(1))) return this.parseNumberLiteral();
		const name = this.readIdentifier();
		if (!name) throw this.error("Expected type");
		switch (name) {
			case "Number": return NumberType;
			case "Integer": return IntegerType;
			case "String": return StringType;
			case "Boolean": return BooleanType;
			case "Null": return NullType;
			case "Regex": return RegexType;
			case "Function": return AnyFunction;
			case "Handler": return this.parseHandlerType();
			case "Sequence": return union(array(Unknown), StringType);
			case "Collection": return union(array(Unknown), StringType, {
				tag: "Record",
				fields: /* @__PURE__ */ new Map(),
				open: true
			});
			case "Unknown": return Unknown;
			case "Never": return Never;
			case "true": return literal(true);
			case "false": return literal(false);
			default: return this.makeTypeRef(name);
		}
	}
	parseParenOrFunction() {
		this.consume("(");
		this.skipWhitespace();
		if (this.tryConsume(")")) {
			this.skipWhitespace();
			if (this.tryConsume("->")) {
				this.skipWhitespace();
				const effects = this.tryParseEffectSet() ?? void 0;
				return fn([], this.parseType(), effects);
			}
			throw this.error("Expected \"->\" after \"()\"");
		}
		const saved = this.pos;
		const paramsResult = this.tryParseParamList();
		if (paramsResult !== null && this.tryConsume(")")) {
			this.skipWhitespace();
			if (this.tryConsume("->")) {
				this.skipWhitespace();
				const assertsResult = this.tryParseAsserts(paramsResult.params);
				if (assertsResult) {
					const assertsInfo = {
						paramIndex: paramsResult.params.findIndex((p) => p.name === assertsResult.paramName),
						binder: assertsResult.predicate.binder,
						predicate: assertsResult.predicate.predicate,
						source: assertsResult.predicate.source
					};
					return fn(paramsResult.types, BooleanType, PureEffects, void 0, paramsResult.restType, assertsInfo);
				}
				const effects = this.tryParseEffectSet() ?? void 0;
				const retType = this.parseType();
				return fn(paramsResult.types, retType, effects, void 0, paramsResult.restType);
			}
			if (paramsResult.types.length === 1 && !paramsResult.hasNames) return paramsResult.types[0];
		}
		this.pos = saved;
		const inner = this.parseType();
		this.skipWhitespace();
		this.consume(")");
		return inner;
	}
	parseTupleType() {
		this.consume("[");
		this.skipWhitespace();
		const elements = [];
		if (this.peek() !== "]") {
			elements.push(this.parseType());
			this.skipWhitespace();
			while (this.tryConsume(",")) {
				this.skipWhitespace();
				elements.push(this.parseType());
				this.skipWhitespace();
			}
		}
		this.consume("]");
		return tuple(elements);
	}
	parseRecordType() {
		this.consume("{");
		this.skipWhitespace();
		const fields = /* @__PURE__ */ new Map();
		const optionalFields = /* @__PURE__ */ new Set();
		let open = false;
		if (this.peek() !== "}") if (this.tryConsume("...")) {
			open = true;
			this.skipWhitespace();
		} else {
			const field = this.parseRecordField();
			fields.set(field.name, field.type);
			if (field.optional) optionalFields.add(field.name);
			this.skipWhitespace();
			while (this.tryConsume(",")) {
				this.skipWhitespace();
				if (this.tryConsume("...")) {
					open = true;
					this.skipWhitespace();
					break;
				}
				const next = this.parseRecordField();
				fields.set(next.name, next.type);
				if (next.optional) optionalFields.add(next.name);
				this.skipWhitespace();
			}
		}
		this.consume("}");
		const record = {
			tag: "Record",
			fields,
			open
		};
		if (optionalFields.size > 0) record.optionalFields = optionalFields;
		return record;
	}
	parseRecordField() {
		const name = this.readIdentifier();
		if (!name) throw this.error("Expected field name");
		this.skipWhitespace();
		const optional = this.tryConsume("?");
		this.skipWhitespace();
		this.consume(":");
		this.skipWhitespace();
		return {
			name,
			type: this.parseType(),
			optional
		};
	}
	parseStringLiteral() {
		this.consume("\"");
		let value = "";
		while (this.pos < this.input.length && this.peek() !== "\"") {
			if (this.peek() === "\\") {
				this.advance();
				value += this.peek();
			} else value += this.peek();
			this.advance();
		}
		this.consume("\"");
		return literal(value);
	}
	parseNumberLiteral() {
		const start = this.pos;
		if (this.peek() === "-") this.advance();
		while (this.isDigit(this.peek())) this.advance();
		if (this.peek() === ".") {
			this.advance();
			while (this.isDigit(this.peek())) this.advance();
		}
		return literal(Number(this.input.slice(start, this.pos)));
	}
	parseHandlerType() {
		this.consume("<");
		const bodyType = this.parseType();
		this.skipWhitespace();
		this.consume(",");
		const outputType = this.parseType();
		this.skipWhitespace();
		this.consume(",");
		const handledEffects = this.tryParseEffectSet();
		if (!handledEffects) throw this.error("Expected effect set in Handler type");
		if (handledEffects.tail.tag !== "Closed") throw this.error("Open effect sets are not supported in the handled slot of Handler types");
		this.skipWhitespace();
		let introducedEffects = PureEffects;
		if (this.tryConsume(",")) {
			this.skipWhitespace();
			const parsed = this.tryParseEffectSet();
			if (!parsed) throw this.error("Expected effect set in 4th slot of Handler type (introduced effects)");
			introducedEffects = parsed;
			this.skipWhitespace();
		}
		this.consume(">");
		const handled = /* @__PURE__ */ new Map();
		for (const effectName of handledEffects.effects) {
			const declaration = getEffectDeclaration(effectName);
			if (!declaration) throw this.error(`Unknown effect '${effectName}' in Handler type`);
			handled.set(effectName, {
				argType: declaration.argType,
				retType: declaration.retType
			});
		}
		return handlerType(bodyType, outputType, handled, introducedEffects);
	}
	tryParseEffectSet() {
		const saved = this.pos;
		this.skipWhitespace();
		if (!this.tryConsume("@")) return null;
		this.skipWhitespace();
		if (!this.tryConsume("{")) {
			this.pos = saved;
			return null;
		}
		this.skipWhitespace();
		const effects = [];
		let open = false;
		let rowVarName = null;
		if (this.tryConsume("|")) {
			this.skipWhitespace();
			rowVarName = this.readRowVarName();
			if (!rowVarName) {
				this.pos = saved;
				return null;
			}
			this.skipWhitespace();
		}
		while (!rowVarName && !this.isAtEnd()) {
			if (this.tryConsume("...")) {
				open = true;
				this.skipWhitespace();
				break;
			}
			if (this.peek() === "}") break;
			const effectName = this.readEffectIdentifier();
			if (!effectName) {
				this.pos = saved;
				return null;
			}
			effects.push(effectName);
			this.skipWhitespace();
			if (this.tryConsume(",")) {
				this.skipWhitespace();
				continue;
			}
			if (this.tryConsume("|")) {
				this.skipWhitespace();
				rowVarName = this.readRowVarName();
				if (!rowVarName) {
					this.pos = saved;
					return null;
				}
				this.skipWhitespace();
			}
			break;
		}
		if (!this.tryConsume("}")) {
			this.pos = saved;
			return null;
		}
		this.skipWhitespace();
		if (rowVarName) {
			const rowVar = this.resolveRowVar(rowVarName);
			return {
				effects: new Set(effects),
				tail: rowVar
			};
		}
		return effectSet(effects, open);
	}
	/**
	* Read a row-variable name. Row-var names are a single lowercase Latin
	* letter (`a`–`z`), staying distinct from value-type vars (single uppercase).
	* Returns null if the next token isn't a row-var name.
	*/
	readRowVarName() {
		const c = this.peek();
		if (!c || c < "a" || c > "z") return null;
		const next = this.input[this.pos + 1];
		if (next && this.isIdentChar(next)) return null;
		this.pos++;
		return c;
	}
	/**
	* Resolve a row-var name to a shared `RowVar` tail within this annotation's
	* scope. Repeated uses of the same name return the *same* `RowVar` object,
	* giving positional unification within one parsed signature.
	*/
	resolveRowVar(name) {
		const existing = this.rowVarMap.get(name);
		if (existing) return existing;
		const rowVar = {
			tag: "RowVar",
			id: this.nextRowVarId++,
			level: 0,
			lowerBounds: [],
			upperBounds: [],
			lowerVarBounds: [],
			upperVarBounds: []
		};
		this.rowVarMap.set(name, rowVar);
		return rowVar;
	}
	/**
	* Try to parse function parameters. Returns null if this doesn't
	* look like a parameter list (backtrack).
	*/
	tryParseParams() {
		this.skipWhitespace();
		const result = this.tryParseParamList();
		if (result === null) return null;
		this.skipWhitespace();
		if (!this.tryConsume(")")) return null;
		this.skipWhitespace();
		return result;
	}
	tryParseParamList() {
		const params = [];
		const types = [];
		let hasNames = false;
		let restType;
		const first = this.tryParseParam();
		if (first === null) return null;
		params.push(first);
		if (first.rest) restType = this.extractRestElementType(first.type);
		else types.push(first.type);
		if (first.name) hasNames = true;
		if (first.rest) {
			this.skipWhitespace();
			return {
				params,
				types,
				hasNames,
				restType
			};
		}
		this.skipWhitespace();
		while (this.peek() === ",") {
			const saved = this.pos;
			this.advance();
			this.skipWhitespace();
			const param = this.tryParseParam();
			if (param === null) {
				this.pos = saved;
				break;
			}
			params.push(param);
			if (param.rest) restType = this.extractRestElementType(param.type);
			else types.push(param.type);
			if (param.name) hasNames = true;
			this.skipWhitespace();
			if (param.rest) {
				if (this.peek() === ",") throw this.error("Rest parameter must be last");
				break;
			}
		}
		return {
			params,
			types,
			hasNames,
			restType
		};
	}
	tryParseParam() {
		const saved = this.pos;
		this.skipWhitespace();
		if (this.tryConsume("...")) {
			this.skipWhitespace();
			const name = this.readIdentifier();
			this.skipWhitespace();
			if (name && this.tryConsume(":")) {
				this.skipWhitespace();
				return {
					name,
					type: this.parseType(),
					rest: true
				};
			}
			this.pos = saved + 3;
			return {
				type: this.parseType(),
				rest: true
			};
		}
		const name = this.readIdentifier();
		if (name) {
			this.skipWhitespace();
			const optional = this.tryConsume("?");
			if (this.tryConsume(":")) {
				this.skipWhitespace();
				let type = this.parseType();
				if (optional) type = union(type, NullType);
				return {
					name,
					type,
					optional
				};
			}
			this.pos = saved;
		}
		try {
			return { type: this.parseType() };
		} catch {
			this.pos = saved;
			return null;
		}
	}
	/**
	* Try to parse "paramName is Type" after "->".
	* Returns null if this isn't a type guard.
	*/
	tryParseTypeGuard(params) {
		const saved = this.pos;
		const name = this.readIdentifier();
		if (!name) return null;
		this.skipWhitespace();
		if (this.readIdentifier() !== "is") {
			this.pos = saved;
			return null;
		}
		if (!params.find((p) => p.name === name)) {
			this.pos = saved;
			return null;
		}
		this.skipWhitespace();
		return {
			paramName: name,
			guardType: this.parseType()
		};
	}
	/**
	* Try to parse `asserts {binder | body}` after `->`. Returns the parsed
	* predicate plus the asserted parameter name (which must equal the
	* binder), or `null` if `asserts` isn't the next token.
	*
	* The constraint "binder name equals a parameter name" identifies the
	* asserted parameter unambiguously even for multi-parameter functions
	* (a `(a: Number, b: Number) -> asserts {b | b > 0}` asserts `b`).
	* Reusing the binder as the parameter selector is mildly redundant but
	* keeps `consumeAndCheckRefinementPredicate` callable without a wrapper.
	*/
	tryParseAsserts(params) {
		const saved = this.pos;
		if (this.readIdentifier() !== "asserts") {
			this.pos = saved;
			return null;
		}
		this.skipWhitespace();
		if (this.peek() !== "{") {
			this.pos = saved;
			return null;
		}
		const predicateStart = this.pos;
		const predicate = this.consumeAndCheckRefinementPredicate();
		if (!params.find((p) => p.name === predicate.binder)) {
			const paramNames = params.map((p) => p.name).filter((n) => n !== void 0);
			const hint = paramNames.length > 0 ? ` (parameters: ${paramNames.join(", ")})` : " (function has no named parameters)";
			throw new TypeParseError(`\`asserts\` binder '${predicate.binder}' does not match any parameter name${hint}`, this.input, predicateStart);
		}
		return {
			paramName: predicate.binder,
			predicate
		};
	}
	makeTypeRef(name) {
		const scoped = this.scopedTypeRefs.get(name);
		if (scoped) return scoped;
		if (name.length === 1 && name >= "A" && name <= "Z") {
			const existing = this.typeVarMap.get(name);
			if (existing) return existing;
			const v = {
				tag: "Var",
				id: this.nextVarId++,
				level: 0,
				lowerBounds: [],
				upperBounds: []
			};
			this.typeVarMap.set(name, v);
			return v;
		}
		const alias = typeAliasRegistry.get(name);
		if (alias) {
			const args = this.tryParseTypeArguments();
			if (args.length !== alias.params.length) throw this.error(`Type alias '${name}' expects ${alias.params.length} type argument(s), got ${args.length}`);
			for (let i = 0; i < alias.params.length; i++) {
				const param = alias.params[i];
				if (param.bound === void 0) continue;
				const boundParser = new TypeParser(param.bound, this.scopedTypeRefs, this.strictUnknownTypeNames);
				const boundType = boundParser.parseType();
				if (!boundParser.isAtEnd()) throw this.error(`Invalid bound on type alias '${name}' parameter '${param.name}': '${boundParser.remaining()}'`);
				const argType = args[i];
				if (!isSubtype(argType, boundType)) throw this.error(`Type argument does not satisfy bound on '${name}': parameter '${param.name}' is bounded by '${typeToString(boundType)}', but got '${typeToString(argType)}'`);
			}
			const scopedTypeRefs = new Map(alias.params.map((param, index) => [param.name, args[index]]));
			const parser = new TypeParser(alias.body, scopedTypeRefs, this.strictUnknownTypeNames);
			const expanded = parser.parseType();
			if (!parser.isAtEnd()) throw this.error(`Unexpected token in type alias '${name}': '${parser.remaining()}'`);
			return {
				tag: "Alias",
				name,
				args,
				expanded
			};
		}
		if (this.strictUnknownTypeNames) throw this.error(`Unknown type name '${name}'`);
		return Unknown;
	}
	tryParseTypeArguments() {
		this.skipWhitespace();
		if (!this.tryConsume("<")) return [];
		const args = [];
		this.skipWhitespace();
		if (this.tryConsume(">")) return args;
		for (;;) {
			args.push(this.parseType());
			this.skipWhitespace();
			if (this.tryConsume(",")) {
				this.skipWhitespace();
				continue;
			}
			if (this.tryConsume(">")) return args;
			throw this.error("Expected \",\" or \">\" in type argument list");
		}
	}
	isAtEnd() {
		return this.pos >= this.input.length;
	}
	remaining() {
		return this.input.slice(this.pos);
	}
	peek() {
		return this.input[this.pos] ?? "";
	}
	peekAt(offset) {
		return this.input[this.pos + offset] ?? "";
	}
	advance() {
		this.pos++;
	}
	skipWhitespace() {
		while (this.pos < this.input.length && " 	\n\r".includes(this.input[this.pos])) this.pos++;
	}
	tryConsume(s) {
		this.skipWhitespace();
		if (this.input.startsWith(s, this.pos)) {
			this.pos += s.length;
			return true;
		}
		return false;
	}
	consume(s) {
		this.skipWhitespace();
		if (!this.input.startsWith(s, this.pos)) throw this.error(`Expected '${s}'`);
		this.pos += s.length;
	}
	readIdentifier() {
		const start = this.pos;
		while (this.pos < this.input.length && this.isIdentChar(this.input[this.pos])) this.pos++;
		return this.pos > start ? this.input.slice(start, this.pos) : null;
	}
	readEffectIdentifier() {
		this.skipWhitespace();
		let name = "";
		let sawSegment = false;
		while (!this.isAtEnd()) {
			this.skipWhitespace();
			const start = this.pos;
			while (this.pos < this.input.length && this.isEffectSegmentChar(this.input[this.pos])) this.pos++;
			if (this.pos === start) break;
			name += this.input.slice(start, this.pos);
			sawSegment = true;
			this.skipWhitespace();
			if (this.peek() !== ".") break;
			name += ".";
			this.advance();
		}
		return sawSegment ? name : null;
	}
	isIdentChar(c) {
		return c >= "a" && c <= "z" || c >= "A" && c <= "Z" || c >= "0" && c <= "9" || c === "_";
	}
	isEffectSegmentChar(c) {
		return this.isIdentChar(c) || c === "*";
	}
	isDigit(c) {
		return c >= "0" && c <= "9";
	}
	error(message) {
		return new TypeParseError(message, this.input, this.pos);
	}
	extractRestElementType(type) {
		if (type.tag !== "Array") throw this.error("Rest parameter type must be an array type");
		return type.element;
	}
};
var TypeParseError = class extends Error {
	input;
	position;
	/**
	* The original message without the position/input suffix. The
	* `.message` field carries the decorated form for debug output;
	* callers that convert this to a user-facing diagnostic (e.g.
	* `TypeInferenceError`) should prefer `cleanMessage` to avoid
	* leaking internal parser positions into errors.
	*/
	cleanMessage;
	constructor(message, input, position) {
		super(`${message} at position ${position} in "${input}"`);
		this.input = input;
		this.position = position;
		this.name = "TypeParseError";
		this.cleanMessage = message;
	}
};
/**
* Refinement-specific error raised by the fragment-checker while
* parsing a `Base & { binder | predicate }` annotation. Tests assert
* on `kind` to stay decoupled from exact message wording.
*
*  - `fragment`       — predicate shape is outside the accepted
*                       grammar (arithmetic, effects, control flow,
*                       unknown calls, anything not in the Phase 1
*                       accepted-fixture list).
*  - `predicate-type` — predicate body isn't Boolean-typed.
*  - `obligation`     — reserved for Phase 2; the solver will raise
*                       this when it can't discharge a goal.
*
* Lives in parseType.ts next to `TypeParseError` to keep the
* typechecker→parser import direction one-way (infer.ts already
* depends on parseType.ts).
*/
var RefinementError = class extends TypeParseError {
	kind;
	constructor(message, kind, input, position) {
		super(message, input, position);
		this.name = "RefinementError";
		this.kind = kind;
	}
};
//#endregion
//#region src/typechecker/builtinTypes.ts
/** Map from builtin name to its parsed type info. */
const builtinTypeCache = /* @__PURE__ */ new Map();
/** Whether the cache has been initialized. */
let initialized$1 = false;
/**
* Initialize the builtin type cache from the builtin normal expressions.
* Call once at startup before inference begins.
*/
function initBuiltinTypes(normalExpressions) {
	if (initialized$1) return;
	initialized$1 = true;
	for (const [name, expr] of Object.entries(normalExpressions)) {
		const typeStr = expr.docs?.type;
		if (!typeStr) continue;
		try {
			const parsed = parseFunctionTypeAnnotation(typeStr);
			builtinTypeCache.set(name, {
				type: parsed.type,
				guardParam: parsed.guardParam,
				guardType: parsed.guardType,
				assertsParam: parsed.assertsParam !== void 0 ? parsed.type.tag === "Function" && parsed.type.asserts ? parsed.type.asserts.paramIndex : expr.docs?.asserts?.paramIndex : extractAssertParamFromType(parsed.type) ?? expr.docs?.asserts?.paramIndex
			});
		} catch {}
	}
}
function extractAssertParamFromType(type) {
	if (type.tag === "Function") return type.asserts?.paramIndex;
	if (type.tag !== "Inter") return void 0;
	const assertParams = type.members.filter((member) => member.tag === "Function" && member.asserts !== void 0).map((member) => member.asserts.paramIndex);
	if (assertParams.length === 0) return void 0;
	const [first] = assertParams;
	return assertParams.every((paramIndex) => paramIndex === first) ? first : void 0;
}
/**
* Look up the type of a builtin by name.
* Returns Unknown if the builtin has no type annotation or wasn't parsed.
*/
function getBuiltinType(name) {
	return builtinTypeCache.get(name) ?? { type: Unknown };
}
/**
* Check if a builtin is a type guard (e.g. isNumber narrows to Number).
*/
function isTypeGuard(name) {
	return builtinTypeCache.get(name)?.guardParam !== void 0;
}
/** Map from module name to its export record type. */
const moduleTypeCache = /* @__PURE__ */ new Map();
/**
* Register a module's exports as a record type.
* Called during initialization for each registered module.
*
* `functions` covers TS-implemented entries with inline `docs`. `docs` covers
* source-implemented entries (where the `.dvala` source provides the impl and
* the docs map provides the declared type) and overrides anything in
* `functions` for the same name. Without the second argument, source-impl
* module functions are invisible to the typechecker — `import("effectHandler")`
* would yield a record with no `chooseRandom` etc.
*/
function registerModuleType(moduleName, functions, docs) {
	const fields = /* @__PURE__ */ new Map();
	const setFromDocs = (name, doc) => {
		const typeStr = doc?.type;
		let parsedType;
		if (typeStr) try {
			parsedType = parseFunctionTypeAnnotation(typeStr).type;
		} catch {
			parsedType = Unknown;
		}
		else parsedType = Unknown;
		if (doc?.wrapper && parsedType.tag === "Function") {
			const thunkParam = parsedType.params[doc.wrapper.paramIndex];
			const thunkAlternatives = thunkParam ? thunkParam.tag === "Function" ? [thunkParam] : thunkParam.tag === "Union" ? thunkParam.members.filter((m) => m.tag === "Function") : [] : [];
			if (thunkAlternatives.length > 0) {
				for (const alt of thunkAlternatives) for (const effectName of doc.wrapper.handled) if (!alt.effects.effects.has(effectName)) throw new Error(`Malformed wrapper signature for ${moduleName}.${name}: handled effect '${effectName}' does not appear on the concrete side of the thunk parameter's effect set (${[...alt.effects.effects].sort().join(", ") || "∅"}). Each handled effect must be declared explicitly in the thunk's \`@{...}\` — hiding it inside a row-var tail causes under-subtraction at call sites.`);
			}
			const handled = /* @__PURE__ */ new Map();
			for (const effectName of doc.wrapper.handled) {
				const decl = getEffectDeclaration(effectName);
				handled.set(effectName, {
					argType: decl?.argType ?? Unknown,
					retType: decl?.retType ?? Unknown
				});
			}
			const introduced = doc.wrapper.introduced.length > 0 ? effectSet(doc.wrapper.introduced) : PureEffects;
			const handlerWrapper = {
				paramIndex: doc.wrapper.paramIndex,
				handled,
				introduced
			};
			parsedType = {
				...parsedType,
				handlerWrapper
			};
		}
		fields.set(name, parsedType);
	};
	for (const [name, expr] of Object.entries(functions)) setFromDocs(name, expr.docs);
	if (docs) for (const [name, doc] of Object.entries(docs)) {
		if (fields.has(name)) continue;
		setFromDocs(name, doc);
	}
	moduleTypeCache.set(moduleName, {
		tag: "Record",
		fields,
		open: false
	});
}
/**
* Look up a module's type (record of exports).
* Returns Unknown if the module is not registered.
*/
function getModuleType(moduleName) {
	return moduleTypeCache.get(moduleName) ?? Unknown;
}
/**
* Reset registered module types.
* Called between typecheck passes so each runner sees only its own modules.
*/
function resetModuleTypeCache() {
	moduleTypeCache.clear();
}
//#endregion
//#region src/typechecker/foldToggle.ts
/**
* Typechecker fold toggle. Gates whether `inferExpr` invokes the fold
* sandbox for pure `Call` nodes. See design docs:
*  - design/archive/2026-04-16_constant-folding-in-types.md
*  - design/archive/2026-04-16_fold-toggle-and-differential-tests.md
*
* Default: **on**. Set `DVALA_FOLD=0` to opt out. Per-call override is
* available via `TypecheckOptions.fold`.
*
* Runtime semantics: the toggle only affects the typechecker. It must not
* change evaluator behavior. Any runtime observable difference between
* enabled/disabled indicates a bug.
*/
/**
* Whether folding is enabled for this typecheck process. Read once at
* module load — fold is on the hot path, so per-call env lookups would be
* wasteful. Tests that need to flip the flag can use vitest's `vi.stubEnv`
* before importing the typechecker, or use the `fold` option on
* `TypecheckOptions` for per-call control.
*/
const FOLD_ENABLED = typeof process === "undefined" || !process.env || process.env.DVALA_FOLD !== "0";
//#endregion
//#region src/typechecker/infer.ts
function objectBindingFieldsAsRecord(entries) {
	const record = {};
	for (const { key, target } of entries) record[key] = target;
	return record;
}
function lineColumnToOffset(source, line, column) {
	const lines = source.split("\n");
	let offset = 0;
	for (let index = 0; index < line; index++) offset += (lines[index] ?? "").length + 1;
	return offset + column;
}
function offsetToLineColumn(source, offset) {
	const clamped = Math.max(0, Math.min(offset, source.length));
	const lines = source.split("\n");
	let consumed = 0;
	for (let index = 0; index < lines.length; index++) {
		const lineLength = (lines[index] ?? "").length;
		if (clamped <= consumed + lineLength) return {
			line: index,
			column: clamped - consumed
		};
		consumed += lineLength + 1;
	}
	const lastLine = Math.max(0, lines.length - 1);
	return {
		line: lastLine,
		column: (lines[lastLine] ?? "").length
	};
}
function resolveAnnotationDiagnosticLocation(nodeId, annotation, error, sourceMap) {
	if (!sourceMap) return void 0;
	const ownerPos = sourceMap.positions.get(nodeId);
	if (!ownerPos) return void 0;
	const source = sourceMap.sources[ownerPos.source];
	if (!source) return void 0;
	const nodeStartOffset = lineColumnToOffset(source.content, ownerPos.start[0], ownerPos.start[1]);
	const nodeEndOffset = lineColumnToOffset(source.content, ownerPos.end[0], ownerPos.end[1]);
	const annotationStartInWindow = source.content.slice(nodeStartOffset, Math.max(nodeStartOffset, nodeEndOffset) + annotation.length + 1).indexOf(annotation);
	if (annotationStartInWindow < 0) return void 0;
	let tokenStartInAnnotation = Math.max(0, Math.min(error.position, annotation.length));
	let tokenText = annotation[tokenStartInAnnotation] ?? annotation;
	const unknownTypeMatch = /^Unknown type name '([^']+)'$/.exec(error.cleanMessage);
	if (unknownTypeMatch) {
		tokenText = unknownTypeMatch[1];
		const candidateStart = annotation.lastIndexOf(tokenText, Math.max(0, error.position - 1));
		if (candidateStart >= 0) tokenStartInAnnotation = candidateStart;
	}
	const absoluteOffset = nodeStartOffset + annotationStartInWindow + tokenStartInAnnotation;
	const position = offsetToLineColumn(source.content, absoluteOffset);
	const lineText = source.content.split("\n")[position.line] ?? "";
	return {
		sourceCodeInfo: {
			position: {
				line: position.line + 1,
				column: position.column + 1
			},
			code: lineText,
			filePath: source.path === "<anonymous>" ? void 0 : source.path
		},
		sourceRange: {
			start: {
				line: position.line + 1,
				column: position.column + 1
			},
			end: {
				line: position.line + 1,
				column: position.column + 1 + tokenText.length
			}
		}
	};
}
function typeAnnotationParseError(message, ownerNodeId, annotation, error, ctx, fallbackNodeId) {
	const location = resolveAnnotationDiagnosticLocation(ownerNodeId, annotation, error, ctx.sourceMap);
	return new TypeInferenceError(message, fallbackNodeId, "error", location?.sourceCodeInfo, location?.sourceRange);
}
const typeVarObjectIds = /* @__PURE__ */ new WeakMap();
let nextTypeVarObjectId = 0;
const GENERALIZED_LEVEL = Number.MAX_SAFE_INTEGER;
function typeVarIdentity(typeVar) {
	let objectId = typeVarObjectIds.get(typeVar);
	if (objectId === void 0) {
		objectId = nextTypeVarObjectId++;
		typeVarObjectIds.set(typeVar, objectId);
	}
	return `v${objectId}`;
}
function isGeneralizedTypeVar(typeVar) {
	return typeVar.level === GENERALIZED_LEVEL;
}
/**
* Manages type variable allocation, level tracking, and constraint caching.
*/
var InferenceContext = class {
	nextId = 0;
	_level = 0;
	/** Cycle guard: tracks (lhs, rhs) pairs already processed by constrain. */
	constraintCache = /* @__PURE__ */ new Set();
	/** Type annotations from the parser side-table. Keyed by binding target nodeId. */
	typeAnnotations = /* @__PURE__ */ new Map();
	/**
	* Binding-scoped type parameters from `let f<T: U> = ...` (Phase 0b).
	* Parser side-table — the typechecker reads this when inferring a let
	* binding, creates TypeVars with the declared bounds, and pushes them
	* onto `activeTypeParams` for the whole RHS scope.
	*/
	typeParams;
	/**
	* The currently-active binding-scoped type params. Maps name (e.g.
	* "T") → TypeVar so `parseTypeAnnotation` calls during the RHS
	* resolve the name to the pre-created bounded var rather than a
	* fresh annotation-local one. Shadowing: nested lets with their own
	* `<T>` overlay their parents. Restored on scope exit via a snapshot.
	*/
	activeTypeParams = /* @__PURE__ */ new Map();
	/** Resolves file imports for cross-file type checking. */
	resolveFileType;
	/** Source map for turning annotation parse errors into exact editor ranges. */
	sourceMap;
	/**
	* Whether constant folding runs during this inference pass. Defaults to
	* the `FOLD_ENABLED` env-var value; callers (typecheck entry points)
	* may override via the `fold` option on TypecheckOptions.
	*/
	foldEnabled = FOLD_ENABLED;
	/** Stack of effect sets — each function body pushes a new set. */
	effectStack = [{
		effects: /* @__PURE__ */ new Set(),
		tail: ClosedTail
	}];
	/** Stack of active handler clause resume contexts. */
	resumeStack = [];
	/** Active handled signatures available to direct perform() sites. */
	handledSignatureStack = [];
	/** Parameter vars proven to feed directly into a handler thunk call.
	* Stores both the handled signatures (for subtraction) and the introduced
	* effect set (for the application law's union). */
	wrappedThunkVarHandled = /* @__PURE__ */ new Map();
	/** Recoverable inference errors collected while continuing analysis. */
	deferredErrors = [];
	get level() {
		return this._level;
	}
	/** Get the current (innermost) effect set being built. */
	get currentEffects() {
		return this.effectStack[this.effectStack.length - 1];
	}
	/** Push a fresh effect set (entering a function body). */
	pushEffects() {
		this.effectStack.push({
			effects: /* @__PURE__ */ new Set(),
			tail: ClosedTail
		});
	}
	/** Pop and return the effect set (leaving a function body). */
	popEffects() {
		return this.effectStack.pop() ?? PureEffects;
	}
	get currentResume() {
		return this.resumeStack[this.resumeStack.length - 1];
	}
	pushResume(argType, answerType) {
		this.resumeStack.push({
			argType,
			answerType
		});
	}
	popResume() {
		this.resumeStack.pop();
	}
	get currentHandledSignatures() {
		return this.handledSignatureStack[this.handledSignatureStack.length - 1];
	}
	pushHandledSignatures(signatures) {
		this.handledSignatureStack.push(signatures);
	}
	popHandledSignatures() {
		this.handledSignatureStack.pop();
	}
	noteWrappedThunkVar(varId, signatures, introduced) {
		this.wrappedThunkVarHandled.set(varId, {
			handled: signatures,
			introduced
		});
	}
	getWrappedThunkVar(varId) {
		return this.wrappedThunkVarHandled.get(varId);
	}
	deferError(error) {
		this.deferredErrors.push(error);
	}
	takeDeferredErrors() {
		const errors = this.deferredErrors;
		this.deferredErrors = [];
		return errors;
	}
	/** Record an effect in the current effect set. */
	addEffect(name) {
		this.currentEffects.effects.add(name);
	}
	/** Merge an inferred effect set into the current effect context. */
	addEffects(effects) {
		for (const effectName of effects.effects) this.currentEffects.effects.add(effectName);
		const cur = this.currentEffects.tail;
		const incoming = effects.tail;
		if (incoming.tag === "Open" && cur.tag === "Closed") this.currentEffects.tail = OpenTail;
		else if (incoming.tag === "RowVar" && cur.tag === "Closed") this.currentEffects.tail = incoming;
		else if (incoming.tag === "RowVar" && cur.tag === "RowVar" && incoming.id !== cur.id) {
			addRowVarEdge(incoming, cur);
			addRowVarEdge(cur, incoming);
		}
	}
	/** Remove handled effects from the current set. */
	handleEffects(handled) {
		const current = this.currentEffects;
		const remaining = subtractEffects(current, handled);
		current.effects.clear();
		for (const e of remaining.effects) current.effects.add(e);
	}
	/** Allocate a fresh type variable at the current level. */
	freshVar() {
		return {
			tag: "Var",
			id: this.nextId++,
			level: this._level,
			lowerBounds: [],
			upperBounds: []
		};
	}
	/**
	* Allocate a fresh effect-row variable at the current level. Id namespace
	* is separate from `freshVar()` so row-var ids and value-type-var ids don't
	* collide during display or debugging.
	*/
	freshRowVar() {
		return {
			tag: "RowVar",
			id: this.nextRowVarId++,
			level: this._level,
			lowerBounds: [],
			upperBounds: [],
			lowerVarBounds: [],
			upperVarBounds: []
		};
	}
	nextRowVarId = 0;
	/** Enter a new let-binding scope (raises the level). */
	enterLevel() {
		this._level++;
	}
	/** Leave a let-binding scope (lowers the level). */
	leaveLevel() {
		this._level--;
	}
	/** Reset the constraint cache (for fresh inference passes). */
	resetCache() {
		this.constraintCache.clear();
	}
	/** Snapshot the constraint cache size (for overload rollback). */
	snapshotCacheSize() {
		return this.constraintCache.size;
	}
	/** Roll back the constraint cache to a previous size by removing recent entries. */
	restoreCacheSize(size) {
		if (this.constraintCache.size > size) {
			const entries = [...this.constraintCache];
			this.constraintCache.clear();
			for (let i = 0; i < size; i++) this.constraintCache.add(entries[i]);
		}
	}
	/**
	* Check if a constraint pair has been seen before (cycle guard).
	* Returns true if the pair was already in the cache.
	*/
	checkAndAddConstraint(lhs, rhs) {
		const key = `${varKey(lhs)}<:${varKey(rhs)}`;
		if (this.constraintCache.has(key)) return true;
		this.constraintCache.add(key);
		return false;
	}
};
function varKey(t) {
	if (t.tag === "Var") return typeVarIdentity(t);
	if (t.tag === "Primitive") return `P:${t.name}`;
	if (t.tag === "Atom") return `A:${t.name}`;
	if (t.tag === "Literal") return `L:${String(t.value)}`;
	if (t.tag === "Function") return `F:${t.params.length}:${t.params.map(varKey).join(",")}:${t.restParam !== void 0 ? `...${varKey(t.restParam)}:` : ""}${varKey(t.ret)}:${t.handlerWrapper ? `HW:${t.handlerWrapper.paramIndex}:${[...t.handlerWrapper.handled.entries()].map(([name, sig]) => `${name}:${varKey(sig.argType)}:${varKey(sig.retType)}`).join(",")}` : ""}${t.asserts ? `:AS:${t.asserts.paramIndex}:${t.asserts.source}` : ""}`;
	if (t.tag === "Handler") {
		const tailKey = t.introduced.tail.tag === "Closed" ? "" : t.introduced.tail.tag === "Open" ? ":open" : `:rv${t.introduced.tail.id}`;
		return `H:${varKey(t.body)}:${varKey(t.output)}:${[...t.handled.entries()].map(([name, sig]) => `${name}:${varKey(sig.argType)}:${varKey(sig.retType)}`).join(",")}:I:${[...t.introduced.effects].sort().join(",")}${tailKey}`;
	}
	if (t.tag === "Record") return `R:${[...t.fields.entries()].map(([k, v]) => `${k}=${varKey(v)}`).join(",")}`;
	if (t.tag === "Array") return `Ar:${varKey(t.element)}`;
	if (t.tag === "Tuple") return `Tu:${t.elements.map(varKey).join(",")}`;
	if (t.tag === "Sequence") return `Sq:${t.prefix.map(varKey).join(",")}:${varKey(t.rest)}:${t.minLength}:${t.maxLength ?? "*"}`;
	if (t.tag === "Union") return `U:${t.members.map(varKey).join("|")}`;
	if (t.tag === "Inter") return `I:${t.members.map(varKey).join("&")}`;
	if (t.tag === "Neg") return `N:${varKey(t.inner)}`;
	if (t.tag === "Unknown") return "?";
	if (t.tag === "Never") return "!";
	if (t.tag === "Refined") return `Rf:${varKey(t.base)}:${t.source}`;
	return t.tag;
}
/**
* Biunification over effect rows — **propagation only**. Runs at `constrain`
* sites to accumulate bounds on row-var tails. Does not gate or throw on
* concrete effect-set mismatches; the boolean subtype decision lives in
* `subtype.ts:isSubtype` / `types.ts:isEffectSubset` and is invoked for
* error reporting, not constraint propagation.
*
* Why split the two: `constrain` is run throughout inference to wire up
* variables, including speculatively during overload resolution and type
* guards. Throwing on every concrete mismatch would break those flows.
* The historical Phase A behaviour did not touch effect sets here at all;
* this function preserves that for non-row-var cases and adds row-var
* propagation on top.
*
* The effect lattice is flat: `union = join`, `∅ = bottom`, `subset = order`.
* MLsub rules specialize cleanly to the flat lattice.
*
* @internal Exported for tests that exercise effect-row biunification in
* isolation. Not part of the public typechecker API — callers in source
* should go through `constrain`.
*/
function constrainEffectSet(sub, sup) {
	if (sub === sup) return;
	const subTail = sub.tail;
	const supTail = sup.tail;
	if (subTail.tag !== "RowVar" && supTail.tag !== "RowVar") return;
	if (supTail.tag === "RowVar") {
		const ρ = supTail;
		const extras = /* @__PURE__ */ new Set();
		for (const e of sub.effects) if (!sup.effects.has(e)) extras.add(e);
		if (extras.size > 0) addRowVarLowerBound(ρ, extras);
		if (subTail.tag === "RowVar" && subTail.id !== ρ.id) addRowVarEdge(subTail, ρ);
		return;
	}
	if (subTail.tag === "RowVar") {
		if (supTail.tag === "Closed") addRowVarUpperBound(subTail, sup.effects);
	}
}
/**
* Push a concrete lower-bound set into a row var, propagating:
* - Each concrete upperBound of ρ must contain all new effects (fail otherwise).
* - Each ρ' in upperVarBounds receives the new set as a lower bound.
*
* `visited` guards against cycles in the var graph (bidirectional edges
* from `addEffects` union merging, or transitive var-to-var constraints).
*/
function addRowVarLowerBound(ρ, effects, visited = /* @__PURE__ */ new Set()) {
	if (visited.has(ρ.id)) return;
	visited.add(ρ.id);
	let duplicate = false;
	for (const existing of ρ.lowerBounds) if (setsEqual(existing, effects)) {
		duplicate = true;
		break;
	}
	if (!duplicate) ρ.lowerBounds.push(effects);
	for (const ub of ρ.upperBounds) for (const e of effects) if (!ub.has(e)) throw new TypeInferenceError(`Row-var ρ${ρ.id} lower bound '${e}' violates upper bound @{${[...ub].sort().join(", ")}}`);
	for (const uv of ρ.upperVarBounds) addRowVarLowerBound(uv, effects, visited);
}
/**
* Push a concrete upper-bound set into a row var, propagating symmetrically.
* `visited` guards against cycles.
*/
function addRowVarUpperBound(ρ, effects, visited = /* @__PURE__ */ new Set()) {
	if (visited.has(ρ.id)) return;
	visited.add(ρ.id);
	let duplicate = false;
	for (const existing of ρ.upperBounds) if (setsEqual(existing, effects)) {
		duplicate = true;
		break;
	}
	if (!duplicate) ρ.upperBounds.push(effects);
	for (const lb of ρ.lowerBounds) for (const e of lb) if (!effects.has(e)) throw new TypeInferenceError(`Row-var ρ${ρ.id} upper bound rejects existing lower-bound effect '${e}'`);
	for (const lv of ρ.lowerVarBounds) addRowVarUpperBound(lv, effects, visited);
}
/**
* Add a var-to-var edge σ <: ρ, propagating bounds across it.
* Idempotent — repeated calls are no-ops.
*/
function addRowVarEdge(σ, ρ) {
	if (σ.upperVarBounds.some((v) => v.id === ρ.id)) return;
	σ.upperVarBounds.push(ρ);
	ρ.lowerVarBounds.push(σ);
	for (const lb of σ.lowerBounds) addRowVarLowerBound(ρ, lb);
	for (const ub of ρ.upperBounds) addRowVarUpperBound(σ, ub);
}
function setsEqual(a, b) {
	if (a.size !== b.size) return false;
	for (const x of a) if (!b.has(x)) return false;
	return true;
}
/**
* Boolean-position helper for strict-Boolean positions (`if` cond,
* `match when` guard, `&&` / `||` operands). Runs the usual
* `constrain` but — when it fails because the operand isn't
* Boolean-compatible — re-throws a richer error that includes a
* type-specific "did you mean" suggestion. For Var operands the
* constrain call just adds an upper bound and no error is thrown,
* so this helper is a pass-through in that case.
*/
function constrainBoolean(ctx, type, nodeId, position) {
	try {
		constrain(ctx, type, BooleanType);
	} catch (error) {
		if (error instanceof TypeInferenceError) {
			const expanded = expandType(type);
			const suggestion = suggestBooleanFix(expanded);
			if (suggestion !== void 0) throw new TypeInferenceError(`${position} requires Boolean but got ${typeToString(expanded)}. ${suggestion}`, nodeId);
		}
		throw error;
	}
}
/**
* Generates a context-specific migration hint for a non-Boolean operand
* at a Boolean position. The strict-Boolean cleanup removed the
* `boolean(x)` coercion and the `not(nonBoolean)` truthy pattern, so
* users migrating from those patterns benefit from a targeted nudge:
* - Number / Integer    → `x != 0`
* - String              → `x != ""` or `count(x) > 0`
* - Null                → the condition is always falsy; probably a bug
* - T | Null (nullable) → `x != null` (and for `||` default-value:
*   consider `x ?? default`)
* - Array / Tuple / Sequence → `count(x) > 0`
* - Literal             → the condition folds to a constant; drop the
*   branch that can't run
* - Unknown / anything else → general nudge
*/
function suggestBooleanFix(type) {
	if (type.tag === "Literal") {
		if (typeof type.value === "number") return "Did you mean `x != 0`?";
		if (typeof type.value === "string") return "Did you mean `x != \"\"` or `count(x) > 0`?";
		return;
	}
	if (type.tag === "Primitive" && type.name === "Null") return "The operand is `Null`; this condition never succeeds. Did you mean a comparison like `x != null`?";
	if (type.tag === "Primitive") {
		if (type.name === "Number" || type.name === "Integer") return "Did you mean `x != 0`?";
		if (type.name === "String") return "Did you mean `x != \"\"` or `count(x) > 0`?";
	}
	if (type.tag === "Union" && type.members.some((m) => m.tag === "Primitive" && m.name === "Null")) return "Did you mean `x != null`? (For default-value idioms, `x ?? default` replaces the old `x || default` pattern.)";
	if (type.tag === "Array" || type.tag === "Tuple" || type.tag === "Sequence") return "Did you mean `count(x) > 0`?";
	if (type.tag === "Record") return "Records are always truthy; this condition always succeeds. Did you mean to null-check an optional field?";
	return "Use an explicit comparison (`x != null`, `x == value`) or a type-guard predicate. The old `boolean(x)` coercion was removed.";
}
/**
* The core of Simple-sub: propagate `lhs <: rhs` until everything
* reduces to bounds on type variables.
*
* When a variable appears on the left, it gains an upper bound.
* When a variable appears on the right, it gains a lower bound.
* Existing bounds are then propagated transitively.
*/
function constrain(ctx, lhs, rhs) {
	if (lhs === rhs) return;
	if (lhs.tag === "Never" || rhs.tag === "Unknown") return;
	if (lhs.tag === "Unknown") {
		if (rhs.tag === "Var") rhs.lowerBounds.push(lhs);
		return;
	}
	if (ctx.checkAndAddConstraint(lhs, rhs)) return;
	if (lhs.tag === "Alias") {
		constrain(ctx, lhs.expanded, rhs);
		return;
	}
	if (rhs.tag === "Alias") {
		constrain(ctx, lhs, rhs.expanded);
		return;
	}
	if (rhs.tag === "Refined" && lhs.tag !== "Var") {
		constrain(ctx, lhs, rhs.base);
		if (!isSubtype(lhs, rhs)) throw new TypeInferenceError(`${typeToString(expandTypeForDisplay(lhs))} is not a subtype of ${typeToString(rhs)}`);
		return;
	}
	if (lhs.tag === "Refined") {
		constrain(ctx, lhs.base, rhs);
		return;
	}
	if (lhs.tag === "Keyof" || lhs.tag === "Index") {
		const reduced = lhs.tag === "Keyof" ? keyofType(expandType(lhs.inner)) : indexType(expandType(lhs.target), expandType(lhs.key));
		if (reduced.tag !== lhs.tag) constrain(ctx, reduced, rhs);
		return;
	}
	if (rhs.tag === "Keyof" || rhs.tag === "Index") {
		const reduced = rhs.tag === "Keyof" ? keyofType(expandType(rhs.inner)) : indexType(expandType(rhs.target), expandType(rhs.key));
		if (reduced.tag !== rhs.tag) constrain(ctx, lhs, reduced);
		return;
	}
	if (lhs.tag === "Var") {
		if (rhs.tag === "Var" && lhs === rhs) return;
		lhs.upperBounds.push(rhs);
		for (const lb of lhs.lowerBounds) constrain(ctx, lb, rhs);
		return;
	}
	if (rhs.tag === "Var") {
		rhs.lowerBounds.push(lhs);
		for (const ub of rhs.upperBounds) constrain(ctx, lhs, ub);
		return;
	}
	if (lhs.tag === "Union") {
		for (const m of lhs.members) constrain(ctx, m, rhs);
		return;
	}
	if (lhs.tag === "Inter") {
		const errors = [];
		const successfulDeltas = [];
		let firstSuccessfulDelta;
		for (const m of lhs.members) {
			const boundsSnapshot = snapshotVarBounds(rhs);
			const cacheSnapshot = ctx.snapshotCacheSize();
			try {
				constrain(ctx, m, rhs);
				const delta = captureVarBoundDelta(boundsSnapshot);
				successfulDeltas.push(delta);
				if (!firstSuccessfulDelta) firstSuccessfulDelta = delta;
				restoreVarBounds(boundsSnapshot);
				ctx.restoreCacheSize(cacheSnapshot);
			} catch (e) {
				if (e instanceof TypeInferenceError) {
					errors.push(e);
					restoreVarBounds(boundsSnapshot);
					ctx.restoreCacheSize(cacheSnapshot);
				} else throw e;
			}
		}
		if (firstSuccessfulDelta) {
			replayVarBoundDeltaExact(ctx, firstSuccessfulDelta);
			annotateDisplayVarBoundDeltas(successfulDeltas);
			return;
		}
		throw errors[errors.length - 1] ?? new TypeInferenceError(`No overload matches: ${typeToString(lhs)} is not a subtype of ${typeToString(rhs)}`);
	}
	if (rhs.tag === "Union") {
		for (const m of rhs.members) try {
			constrain(ctx, lhs, m);
			return;
		} catch (e) {
			if (!(e instanceof TypeInferenceError)) throw e;
		}
		throw new TypeInferenceError(`${typeToString(lhs)} is not a subtype of ${typeToString(rhs)}`);
	}
	if (rhs.tag === "Inter") {
		for (const m of rhs.members) constrain(ctx, lhs, m);
		return;
	}
	if (lhs.tag === "Primitive" && rhs.tag === "Primitive") {
		if (lhs.name === rhs.name) return;
		if (lhs.name === "Integer" && rhs.name === "Number") return;
		throw new TypeInferenceError(`${lhs.name} is not a subtype of ${rhs.name}`);
	}
	if (lhs.tag === "Literal" && rhs.tag === "Primitive") {
		if (!(typeof lhs.value === "number" && rhs.name === "Number" || typeof lhs.value === "number" && rhs.name === "Integer" && Number.isInteger(lhs.value) || typeof lhs.value === "string" && rhs.name === "String" || typeof lhs.value === "boolean" && rhs.name === "Boolean")) throw new TypeInferenceError(`${typeToString(lhs)} is not a subtype of ${rhs.name}`);
		return;
	}
	if (lhs.tag === "Literal" && rhs.tag === "Literal") {
		if (lhs.value !== rhs.value) throw new TypeInferenceError(`${typeToString(lhs)} is not a subtype of ${typeToString(rhs)}`);
		return;
	}
	if (lhs.tag === "Atom" && rhs.tag === "Atom") {
		if (lhs.name !== rhs.name) throw new TypeInferenceError(`:${lhs.name} is not a subtype of :${rhs.name}`);
		return;
	}
	if (rhs.tag === "AnyFunction") {
		if (lhs.tag === "Function" || lhs.tag === "AnyFunction") return;
		throw new TypeInferenceError(`${typeToString(lhs)} is not a function`);
	}
	if (lhs.tag === "AnyFunction" && rhs.tag === "Function") {
		constrain(ctx, Unknown, rhs.ret);
		return;
	}
	if (lhs.tag === "Function" && rhs.tag === "Function") {
		constrainEffectSet(lhs.effects, rhs.effects);
		if (!isConstrainedFunctionArityCompatible(lhs, rhs)) throw new TypeInferenceError(`Function arity mismatch: expected ${functionArityLabel(rhs)} params, got ${functionArityLabel(lhs)}`);
		for (let i = 0; i < Math.max(lhs.params.length, rhs.params.length); i++) {
			const lhsParam = getFunctionParamType(lhs, i);
			const rhsParam = getFunctionParamType(rhs, i);
			if (!lhsParam || !rhsParam) throw new TypeInferenceError(`Function arity mismatch: expected ${functionArityLabel(rhs)} params, got ${functionArityLabel(lhs)}`);
			constrain(ctx, rhsParam, lhsParam);
		}
		if (rhs.restParam !== void 0) {
			if (lhs.restParam === void 0) throw new TypeInferenceError(`Function arity mismatch: expected ${functionArityLabel(rhs)} params, got ${functionArityLabel(lhs)}`);
			constrain(ctx, rhs.restParam, lhs.restParam);
		}
		constrain(ctx, lhs.ret, rhs.ret);
		return;
	}
	if (lhs.tag === "Handler" && rhs.tag === "Handler") {
		constrainEffectSet(lhs.introduced, rhs.introduced);
		if (lhs.introduced.tail.tag === "Closed" && rhs.introduced.tail.tag === "Closed" && !isEffectSubset(lhs.introduced, rhs.introduced)) throw new TypeInferenceError(`Handler introduces effects ${effectSetToString(lhs.introduced) || "@{}"} not allowed by ${effectSetToString(rhs.introduced) || "@{}"}`);
		for (const [name, rhsSig] of rhs.handled) {
			const lhsSig = lhs.handled.get(name);
			if (!lhsSig) throw new TypeInferenceError(`Handler is missing clause @${name}`);
			constrain(ctx, rhsSig.argType, lhsSig.argType);
			constrain(ctx, lhsSig.retType, rhsSig.retType);
		}
		constrain(ctx, lhs.body, rhs.body);
		constrain(ctx, lhs.output, rhs.output);
		return;
	}
	if (lhs.tag === "Record" && rhs.tag === "Record") {
		for (const [name, rhsType] of rhs.fields) {
			const lhsType = lhs.fields.get(name);
			if (!lhsType) {
				if (lhs.open) continue;
				if (rhs.optionalFields?.has(name)) continue;
				throw new TypeInferenceError(`Missing field '${name}' in ${typeToString(lhs)}`);
			}
			if (lhs.optionalFields?.has(name) && !rhs.optionalFields?.has(name)) throw new TypeInferenceError(`Field '${name}' is optional in ${typeToString(lhs)} but required in ${typeToString(rhs)}`);
			constrain(ctx, lhsType, rhsType);
		}
		if (!rhs.open) {
			if (lhs.open) throw new TypeInferenceError(`Open record ${typeToString(lhs)} is not a subtype of closed record ${typeToString(rhs)}`);
			for (const name of lhs.fields.keys()) if (!rhs.fields.has(name)) throw new TypeInferenceError(`Extra field '${name}' in ${typeToString(lhs)}`);
		}
		return;
	}
	if (lhs.tag === "Array" && rhs.tag === "Array") {
		constrain(ctx, lhs.element, rhs.element);
		return;
	}
	const lhsSequence = toSequenceType(lhs);
	const rhsSequence = toSequenceType(rhs);
	if (lhsSequence && rhsSequence) {
		constrainSequenceSubtype(ctx, lhsSequence, rhsSequence);
		return;
	}
	if (lhs.tag === "Tuple" && rhs.tag === "Tuple") {
		if (lhs.elements.length !== rhs.elements.length) throw new TypeInferenceError(`Tuple length mismatch: expected ${rhs.elements.length}, got ${lhs.elements.length}`);
		for (let i = 0; i < lhs.elements.length; i++) constrain(ctx, lhs.elements[i], rhs.elements[i]);
		return;
	}
	if (lhs.tag === "Tuple" && rhs.tag === "Array") {
		for (const e of lhs.elements) constrain(ctx, e, rhs.element);
		return;
	}
	if (lhs.tag === "Regex" && rhs.tag === "Regex") return;
	if (lhs.tag === "Record" && rhs.tag === "Function" && rhs.params.length === 1 && rhs.restParam === void 0) {
		const paramType = rhs.params[0];
		if (paramType.tag === "Literal" && typeof paramType.value === "string") {
			const fieldName = paramType.value;
			const fieldType = lhs.fields.get(fieldName);
			if (fieldType) {
				if (lhs.optionalFields?.has(fieldName)) throw new TypeInferenceError(`Field '${fieldName}' is optional in ${typeToString(lhs)} and may be absent; use '?.${fieldName}' for safe access`);
				constrain(ctx, fieldType, rhs.ret);
				return;
			}
			if (lhs.open) return;
			throw new TypeInferenceError(`Field '${fieldName}' not found in ${typeToString(lhs)}`);
		}
	}
	if (lhs.tag === "Array" && rhs.tag === "Function" && rhs.params.length === 1 && rhs.restParam === void 0) {
		constrain(ctx, lhs.element, rhs.ret);
		return;
	}
	if (lhs.tag === "Sequence" && rhs.tag === "Function" && rhs.params.length === 1 && rhs.restParam === void 0) {
		constrain(ctx, sequenceElementType(lhs), rhs.ret);
		return;
	}
	if (lhs.tag === "Tuple" && rhs.tag === "Function" && rhs.params.length === 1 && rhs.restParam === void 0) {
		for (const elem of lhs.elements) constrain(ctx, elem, rhs.ret);
		return;
	}
	throw new TypeInferenceError(`${typeToString(lhs)} is not a subtype of ${typeToString(rhs)}`);
}
/** Maps variable names to their inferred types. Supports scoping via linked list. */
var TypeEnv = class TypeEnv {
	bindings;
	parent;
	/**
	* Side map: function-value ASTs associated with `let` bindings whose RHS
	* is a `Function` node. Used by C6 (user-function fold) to reconstruct a
	* Call AST whose callee is the function body directly. Captured via
	* `bindFunctionAst` alongside the normal type binding.
	*/
	functionAsts;
	constructor(parent = null) {
		this.bindings = /* @__PURE__ */ new Map();
		this.functionAsts = /* @__PURE__ */ new Map();
		this.parent = parent;
	}
	/** Look up a variable's type in this scope or any parent. */
	lookup(name) {
		return this.bindings.get(name) ?? this.parent?.lookup(name);
	}
	/**
	* Look up a function AST associated with a binding. Returns the
	* Function-node AST that was bound via `let name = (...) -> ...` or
	* undefined if the binding isn't a direct function-literal.
	*/
	lookupFunctionAst(name) {
		return this.functionAsts.get(name) ?? this.parent?.lookupFunctionAst(name);
	}
	/** Bind a variable in the current scope. */
	bind(name, type) {
		this.bindings.set(name, type);
	}
	/** Record that `name` was bound to a Function-node AST (C6). */
	bindFunctionAst(name, ast) {
		this.functionAsts.set(name, ast);
	}
	/** Create a child scope. */
	child() {
		return new TypeEnv(this);
	}
};
/**
* Infer the type of an AST expression.
* Returns the inferred type and populates the type map (nodeId → Type).
*/
function inferExpr(node, ctx, env, typeMap) {
	const nodeType = node[0];
	const payload = node[1];
	const nodeId = node[2];
	let result;
	try {
		switch (nodeType) {
			case NodeTypes.Num:
				result = literal(payload);
				break;
			case NodeTypes.Str:
				result = literal(payload);
				break;
			case NodeTypes.Atom:
				result = atom(payload);
				break;
			case NodeTypes.Sym: {
				const name = payload;
				const t = env.lookup(name);
				if (!t) throw new TypeInferenceError(`Undefined variable: ${name}`);
				result = freshen(ctx, t);
				break;
			}
			case NodeTypes.Builtin: {
				const builtinName = payload;
				const shadowed = lookupShadowedBuiltin(env, builtinName);
				if (shadowed) {
					result = freshen(ctx, shadowed);
					break;
				}
				result = freshenAnnotationVars(ctx, getBuiltinType(builtinName).type);
				break;
			}
			case NodeTypes.Reserved: {
				const val = payload;
				if (val === "true") result = literal(true);
				else if (val === "false") result = literal(false);
				else if (val === "null") result = NullType;
				else result = Unknown;
				break;
			}
			case NodeTypes.Block: {
				const { lastType } = inferStatementsWithAssertNarrowing(payload, ctx, env.child(), typeMap);
				result = lastType;
				break;
			}
			case NodeTypes.If: {
				const [cond, thenNode, elseNode] = payload;
				const condType = inferExpr(cond, ctx, env, typeMap);
				constrainBoolean(ctx, condType, cond[2], "The `if` condition");
				const narrowings = extractIfNarrowings(cond, env);
				const thenEnv = narrowings ? narrowEnv(env, narrowings.whenTrue) : env;
				const elseEnv = narrowings ? narrowEnv(env, narrowings.whenFalse) : env;
				const thenType = inferExpr(thenNode, ctx, thenEnv, typeMap);
				const elseType = elseNode ? inferExpr(elseNode, ctx, elseEnv, typeMap) : NullType;
				if (ctx.foldEnabled) {
					const expandedCond = expandType(condType);
					if (expandedCond.tag === "Literal" && expandedCond.value === true) {
						result = thenType;
						break;
					}
					if (expandedCond.tag === "Literal" && expandedCond.value === false) {
						result = elseType;
						break;
					}
				}
				result = union(thenType, elseType);
				break;
			}
			case NodeTypes.Let: {
				const [binding, valueNode] = payload;
				const bindingNodeId = binding[2];
				const bindingTypeParams = ctx.typeParams?.get(bindingNodeId);
				const typeParamsSnapshot = bindingTypeParams ? new Map(ctx.activeTypeParams) : void 0;
				if (bindingTypeParams) for (const param of bindingTypeParams) {
					const fresh = ctx.freshVar();
					if (param.bound !== void 0) try {
						const boundType = parseUserTypeAnnotation(param.bound, ctx.activeTypeParams);
						fresh.upperBounds.push(boundType);
					} catch (error) {
						if (error instanceof TypeParseError) ctx.deferError(new TypeInferenceError(error.cleanMessage, bindingNodeId));
						else throw error;
					}
					ctx.activeTypeParams.set(param.name, fresh);
				}
				let valueType;
				let boundType;
				try {
					ctx.enterLevel();
					try {
						valueType = inferExpr(valueNode, ctx, env, typeMap);
					} catch (e) {
						if (e instanceof TypeInferenceError) {
							if (e.nodeId === void 0) e.nodeId = valueNode[2];
							ctx.deferError(e);
							valueType = Unknown;
						} else throw e;
					}
					ctx.leaveLevel();
					valueType = generalizeTypeVars(valueType, ctx.level);
					const annotation = ctx.typeAnnotations?.get(bindingNodeId);
					boundType = valueType;
					if (annotation) {
						let declaredType;
						try {
							declaredType = simplify(parseUserTypeAnnotation(annotation, ctx.activeTypeParams));
						} catch (error) {
							if (error instanceof TypeParseError) throw typeAnnotationParseError(error.cleanMessage, bindingNodeId, annotation, error, ctx, valueNode[2]);
							throw error;
						}
						try {
							constrain(ctx, valueType, declaredType);
						} catch (error) {
							if (error instanceof TypeInferenceError && error.nodeId === void 0) error.nodeId = valueNode[2];
							throw error;
						}
						const expandedValueType = expandType(valueType);
						if (!isSubtype(expandedValueType, declaredType)) throw new TypeInferenceError(`${typeToString(expandedValueType)} is not a subtype of ${typeToString(declaredType)}`, valueNode[2]);
						if (containsVars(declaredType)) boundType = generalizeTypeVars(declaredType, -1);
						else if (declaredType.tag === "Function" && declaredType.asserts && boundType.tag === "Function") boundType = fn(declaredType.params, declaredType.ret, boundType.effects, boundType.handlerWrapper, declaredType.restParam, declaredType.asserts);
					}
				} finally {
					if (typeParamsSnapshot !== void 0) ctx.activeTypeParams = typeParamsSnapshot;
				}
				bindPattern(binding, boundType, env, ctx, typeMap);
				recordConcretePatternTypes(binding, valueType, typeMap);
				recordLiteralPatternTypes(binding, valueNode, typeMap);
				if (valueNode[0] === NodeTypes.Function && binding[0] === "symbol") {
					const [nameNode] = binding[1];
					env.bindFunctionAst(nameNode[1], valueNode);
				}
				result = valueType;
				break;
			}
			case NodeTypes.Function:
				result = inferFunctionNode(node, ctx, env, typeMap);
				break;
			case NodeTypes.Call: {
				const [calleeNode, argNodes] = payload;
				const calleeType = inferExpr(calleeNode, ctx, env, typeMap);
				const handlerAlternatives = getHandlerAlternatives(calleeType);
				if (handlerAlternatives.length > 0 && argNodes.length === 1 && isZeroArgFunctionNode(argNodes[0])) {
					const guaranteedHandled = intersectHandledSignatures(handlerAlternatives);
					ctx.pushHandledSignatures(guaranteedHandled);
					let thunkType;
					try {
						thunkType = inferFunctionNode(argNodes[0], ctx, env, typeMap, { inheritHandledSignatures: true });
					} finally {
						ctx.popHandledSignatures();
					}
					const thunkAlternatives = getFunctionAlternatives(thunkType);
					if (thunkAlternatives.length > 0 && thunkAlternatives.every((thunk) => thunk.params.length === 0 && thunk.restParam === void 0)) {
						const requiredBodyType = handlerAlternatives.length === 1 ? handlerAlternatives[0].body : inter(...handlerAlternatives.map((handler) => handler.body));
						constrain(ctx, thunkType, fn([], requiredBodyType));
						const residualEffects = subtractEffects(unionEffectSets(thunkAlternatives.map((thunk) => thunk.effects), ctx), new Set(guaranteedHandled.keys()));
						ctx.addEffects(residualEffects);
						ctx.addEffects(unionEffectSets(handlerAlternatives.map((h) => h.introduced), ctx));
						result = handlerAlternatives.length === 1 ? handlerAlternatives[0].output : union(...handlerAlternatives.map((handler) => handler.output));
						break;
					}
				}
				const functionAlternatives = getFunctionAlternatives(calleeType);
				const wrapperInfo = intersectFunctionWrapperInfo(functionAlternatives);
				const argTypes = argNodes.map((arg, index) => {
					if (wrapperInfo && wrapperInfo.paramIndex === index && isZeroArgFunctionNode(arg)) {
						ctx.pushHandledSignatures(wrapperInfo.handled);
						try {
							return inferFunctionNode(arg, ctx, env, typeMap, { inheritHandledSignatures: true });
						} finally {
							ctx.popHandledSignatures();
						}
					}
					return inferExpr(arg, ctx, env, typeMap);
				});
				const thunkAlternatives = argTypes.length === 1 ? getFunctionAlternatives(argTypes[0]) : [];
				let wrapperBranchFired = false;
				if (wrapperInfo) {
					const wrapperThunkType = argTypes[wrapperInfo.paramIndex];
					if (wrapperThunkType) {
						const innerAlts = getFunctionAlternatives(wrapperThunkType);
						if (innerAlts.length > 0) {
							const residual = subtractEffects(unionEffectSets(innerAlts.map((t) => t.effects), ctx), new Set(wrapperInfo.handled.keys()));
							ctx.addEffects(residual);
							ctx.addEffects(wrapperInfo.introduced);
							wrapperBranchFired = true;
						}
					}
				}
				const runtimeAlignedCollectionResult = inferCollectionCall(calleeNode, argTypes, ctx, env, typeMap);
				if (runtimeAlignedCollectionResult) {
					result = runtimeAlignedCollectionResult;
					break;
				}
				if (handlerAlternatives.length > 0 && thunkAlternatives.length > 0 && thunkAlternatives.every((thunk) => thunk.params.length === 0 && thunk.restParam === void 0)) {
					const requiredBodyType = handlerAlternatives.length === 1 ? handlerAlternatives[0].body : inter(...handlerAlternatives.map((handler) => handler.body));
					constrain(ctx, argTypes[0], fn([], requiredBodyType));
					const guaranteedHandled = intersectHandledSignatures(handlerAlternatives);
					const residualEffects = subtractEffects(unionEffectSets(thunkAlternatives.map((thunk) => thunk.effects), ctx), new Set(guaranteedHandled.keys()));
					ctx.addEffects(residualEffects);
					ctx.addEffects(unionEffectSets(handlerAlternatives.map((h) => h.introduced), ctx));
					result = handlerAlternatives.length === 1 ? handlerAlternatives[0].output : union(...handlerAlternatives.map((handler) => handler.output));
					break;
				}
				if (handlerAlternatives.length > 0 && argTypes.length === 1) {
					const requiredBodyType = handlerAlternatives.length === 1 ? handlerAlternatives[0].body : inter(...handlerAlternatives.map((handler) => handler.body));
					const guaranteedHandled = intersectHandledSignatures(handlerAlternatives);
					constrain(ctx, argTypes[0], fn([], requiredBodyType));
					if (argTypes[0].tag === "Var") {
						const introduced = unionEffectSets(handlerAlternatives.map((h) => h.introduced), ctx);
						ctx.noteWrappedThunkVar(argTypes[0].id, guaranteedHandled, introduced);
					}
					result = handlerAlternatives.length === 1 ? handlerAlternatives[0].output : union(...handlerAlternatives.map((handler) => handler.output));
					break;
				}
				const retVar = ctx.freshVar();
				const selectedAlternative = selectFirstSuccessfulFunctionAlternative(ctx, functionAlternatives, argTypes);
				constrain(ctx, calleeType, fn(argTypes, retVar, {
					effects: /* @__PURE__ */ new Set(),
					tail: OpenTail
				}));
				recordSpecializedCalleeType(calleeNode, selectedAlternative, typeMap);
				if (!wrapperBranchFired) {
					const calledEffects = selectedAlternative ? selectedAlternative.effects : unionEffectSets(functionAlternatives.map((alt) => alt.effects), ctx);
					ctx.addEffects(calledEffects);
				}
				result = retVar;
				if (ctx.foldEnabled && functionAlternatives.length > 0 && functionAlternatives.every((alt) => alt.effects.effects.size === 0 && alt.effects.tail.tag === "Closed")) {
					let foldOutcome = tryFoldBuiltinCall(calleeNode, argTypes);
					if (!foldOutcome && calleeNode[0] === NodeTypes.Sym) {
						const functionAst = env.lookupFunctionAst(calleeNode[1]);
						if (functionAst) {
							const captures = /* @__PURE__ */ new Map();
							let capturesReconstructible = true;
							for (const name of collectSymRefs$1(functionAst)) {
								const captureType = env.lookup(name);
								if (!captureType) continue;
								const valueAst = literalTypeToAstNode(expandType(captureType));
								if (!valueAst) {
									capturesReconstructible = false;
									break;
								}
								captures.set(name, valueAst);
							}
							if (capturesReconstructible) foldOutcome = tryFoldUserFunctionCall(functionAst, argTypes, captures);
						}
					}
					if (foldOutcome?.type) result = foldOutcome.type;
					else if (foldOutcome?.effectName !== void 0) ctx.deferError(new TypeInferenceError(`This expression will perform \`@${foldOutcome.effectName}\` at runtime`, nodeId, "warning"));
				}
				break;
			}
			case NodeTypes.Array: {
				const elements = payload;
				if (elements.length === 0) result = array(ctx.freshVar());
				else {
					const hasSpread = elements.some((e) => e[0] === NodeTypes.Spread);
					const elemTypes = elements.map((e) => inferExpr(e, ctx, env, typeMap));
					if (hasSpread) {
						const elemVar = ctx.freshVar();
						for (const et of elemTypes) constrain(ctx, et, elemVar);
						result = array(elemVar);
					} else result = tuple(elemTypes);
				}
				break;
			}
			case NodeTypes.Object: {
				const entries = payload;
				const fields = /* @__PURE__ */ new Map();
				for (const entry of entries) if (Array.isArray(entry) && entry.length === 2) {
					const [keyNode, valueNode] = entry;
					const keyName = keyNode[0] === NodeTypes.Str ? keyNode[1] : keyNode[0] === NodeTypes.Sym ? keyNode[1] : String(keyNode[1]);
					const valueType = inferExpr(valueNode, ctx, env, typeMap);
					fields.set(keyName, valueType);
					const keyNodeId = keyNode[2];
					if (keyNodeId > 0) typeMap.set(keyNodeId, valueType);
				}
				result = {
					tag: "Record",
					fields,
					open: false
				};
				break;
			}
			case NodeTypes.Perform: {
				const [effectExpr, argExpr] = payload;
				if (effectExpr[0] === NodeTypes.Effect) {
					const effectName = effectExpr[1];
					ctx.addEffect(effectName);
					const decl = ctx.currentHandledSignatures?.get(effectName) ?? getEffectDeclaration(effectName);
					if (!decl) throw new TypeInferenceError(`Undeclared effect @${effectName} — add 'effect @${effectName}(ArgType) -> RetType' before use`);
					if (argExpr) {
						const argType = inferExpr(argExpr, ctx, env, typeMap);
						if (decl.argType.tag !== "Unknown") constrain(ctx, argType, decl.argType);
					}
					result = decl.retType;
				} else result = Unknown;
				break;
			}
			case NodeTypes.Effect:
				result = Unknown;
				break;
			case NodeTypes.TmplStr:
				result = StringType;
				break;
			case NodeTypes.And:
			case NodeTypes.Or: {
				const operands = payload;
				const types = operands.map((op) => inferExpr(op, ctx, env, typeMap));
				const opName = nodeType === NodeTypes.And ? "&&" : "||";
				for (let i = 0; i < types.length; i++) constrainBoolean(ctx, types[i], operands[i][2], `The \`${opName}\` operand`);
				if (ctx.foldEnabled && types.length > 0) {
					const wantFalsy = nodeType === NodeTypes.And;
					let narrowed;
					let allLiteral = true;
					for (let i = 0; i < types.length; i++) {
						const expanded = expandType(types[i]);
						const truthy = literalTruthiness(expanded);
						if (truthy === void 0) {
							allLiteral = false;
							break;
						}
						if (wantFalsy ? !truthy : truthy) {
							narrowed = expanded;
							break;
						}
						if (i === types.length - 1) narrowed = expanded;
					}
					if (narrowed && allLiteral) {
						result = narrowed;
						break;
					}
				}
				result = union(...types);
				break;
			}
			case NodeTypes.Qq:
				result = union(...payload.map((op) => inferExpr(op, ctx, env, typeMap)));
				break;
			case NodeTypes.Match: {
				const matchPayload = payload;
				const matchExpr = matchPayload[0];
				const cases = matchPayload[1];
				const matchType = simplify(expandTypeForMatchAnalysis(inferExpr(matchExpr, ctx, env, typeMap)));
				const matchSpace = normalizeTrackableMatchSpace(matchType);
				let remainingType = matchSpace;
				const checkExhaustiveness = isTrackableMatchRemainder(remainingType);
				const branchTypes = [];
				let sawCatchall = false;
				for (const [pattern, body, guard] of cases) {
					const caseEnv = env.child();
					const { matchedType, consumedType } = analyzeMatchCase(pattern, remainingType, guard, env);
					if (matchedType.tag === "Never") {
						if (shouldWarnRedundantMatchCase(pattern, remainingType, matchSpace)) ctx.deferError(new TypeInferenceError("Redundant match case — pattern is unreachable", pattern[2], "warning"));
						continue;
					}
					switch (pattern[0]) {
						case "symbol": {
							const [nameNode] = pattern[1];
							const name = nameNode[1];
							caseEnv.bind(name, matchedType);
							const nameNodeId = nameNode[2];
							if (nameNodeId > 0) typeMap.set(nameNodeId, matchedType);
							break;
						}
						case "array":
						case "object":
							if (!bindMatchCasePattern(pattern, matchedType, caseEnv, ctx, typeMap)) continue;
							break;
						default: break;
					}
					let bodyEnv = caseEnv;
					let guardFoldsToTrue = false;
					if (guard) {
						const guardType = inferExpr(guard, ctx, caseEnv, typeMap);
						constrainBoolean(ctx, guardType, guard[2], "The `match` guard (`when`)");
						const expandedGuard = expandType(guardType);
						if (ctx.foldEnabled && expandedGuard.tag === "Literal" && expandedGuard.value === false) {
							ctx.deferError(new TypeInferenceError("Redundant match case — guard is always false", pattern[2], "warning"));
							continue;
						}
						if (expandedGuard.tag === "Literal" && expandedGuard.value === true) guardFoldsToTrue = true;
						const guardNarrowings = extractIfNarrowings(guard, caseEnv);
						if (guardNarrowings) bodyEnv = narrowEnv(caseEnv, guardNarrowings.whenTrue);
					}
					if (isCatchallPattern(pattern) && (!guard || guardFoldsToTrue)) sawCatchall = true;
					const bodyType = inferExpr(body, ctx, bodyEnv, typeMap);
					branchTypes.push(bodyType);
					remainingType = simplify(subtractType(remainingType, consumedType));
				}
				if (remainingType.tag !== "Never" && !sawCatchall) throw new TypeInferenceError(checkExhaustiveness ? `Non-exhaustive match — unhandled: ${typeToString(remainingType)}. Add a case for each unhandled value, or 'case _ then ...' to handle the rest.` : `Non-exhaustive match — cannot prove every value of ${typeToString(matchType)} is covered. Add 'case _ then ...' to handle the rest.`, nodeId);
				result = branchTypes.length > 0 ? union(...branchTypes) : Never;
				break;
			}
			case NodeTypes.Loop:
				result = Unknown;
				break;
			case NodeTypes.For:
				result = array(Unknown);
				break;
			case NodeTypes.Import: {
				const moduleName = payload;
				if (moduleName.startsWith(".") && ctx.resolveFileType) result = ctx.resolveFileType(moduleName);
				else result = freshenAnnotationVars(ctx, getModuleType(moduleName));
				break;
			}
			case NodeTypes.Handler: {
				const [clauses, transform] = payload;
				const bodyType = ctx.freshVar();
				const answerType = ctx.freshVar();
				const handled = /* @__PURE__ */ new Map();
				const introducedSets = [];
				for (const clause of clauses) {
					const effectDecl = getEffectDeclaration(clause.effectName);
					const clauseEnv = env.child();
					const declaredArgType = effectDecl?.argType ?? ctx.freshVar();
					const declaredRetType = effectDecl?.retType ?? ctx.freshVar();
					handled.set(clause.effectName, {
						argType: declaredArgType,
						retType: declaredRetType
					});
					for (const param of clause.params) {
						bindPattern(param, declaredArgType, clauseEnv, ctx, typeMap);
						const paramAnnotation = ctx.typeAnnotations?.get(param[2]);
						if (paramAnnotation) {
							let annotatedType;
							try {
								annotatedType = simplify(parseUserTypeAnnotation(paramAnnotation, ctx.activeTypeParams));
							} catch (error) {
								if (error instanceof TypeParseError) throw typeAnnotationParseError(error.cleanMessage, param[2], paramAnnotation, error, ctx, param[2]);
								throw error;
							}
							constrain(ctx, declaredArgType, annotatedType);
							constrain(ctx, annotatedType, declaredArgType);
						}
					}
					ctx.pushResume(declaredRetType, answerType);
					ctx.pushEffects();
					const { lastType: clauseBodyType } = inferStatementsWithAssertNarrowing(clause.body, ctx, clauseEnv, typeMap);
					introducedSets.push(ctx.popEffects());
					ctx.popResume();
					constrain(ctx, clauseBodyType, answerType);
				}
				if (transform) {
					const [transformParam, transformBody] = transform;
					const transformEnv = env.child();
					bindPattern(transformParam, bodyType, transformEnv, ctx, typeMap);
					ctx.pushEffects();
					const { lastType: transformResult } = inferStatementsWithAssertNarrowing(transformBody, ctx, transformEnv, typeMap);
					introducedSets.push(ctx.popEffects());
					constrain(ctx, transformResult, answerType);
					constrain(ctx, answerType, transformResult);
				} else {
					constrain(ctx, bodyType, answerType);
					constrain(ctx, answerType, bodyType);
				}
				result = handlerType(bodyType, answerType, finalizeHandledSignatures(handled), unionEffectSets(introducedSets, ctx));
				break;
			}
			case NodeTypes.WithHandler: {
				const [handlerExpr, bodyExprs] = payload;
				const handlerAlternatives = getHandlerAlternatives(inferExpr(handlerExpr, ctx, env, typeMap));
				let bodyType = NullType;
				if (handlerAlternatives.length > 0) {
					const requiredBodyType = handlerAlternatives.length === 1 ? handlerAlternatives[0].body : inter(...handlerAlternatives.map((handler) => handler.body));
					const guaranteedHandled = intersectHandledSignatures(handlerAlternatives);
					ctx.pushHandledSignatures(guaranteedHandled);
					try {
						const { lastType } = inferStatementsWithAssertNarrowing(bodyExprs, ctx, env, typeMap);
						bodyType = lastType;
					} finally {
						ctx.popHandledSignatures();
					}
					constrain(ctx, bodyType, requiredBodyType);
					ctx.handleEffects(new Set(guaranteedHandled.keys()));
					ctx.addEffects(unionEffectSets(handlerAlternatives.map((handler) => handler.introduced), ctx));
					result = handlerAlternatives.length === 1 ? handlerAlternatives[0].output : union(...handlerAlternatives.map((handler) => handler.output));
				} else {
					const { lastType } = inferStatementsWithAssertNarrowing(bodyExprs, ctx, env, typeMap);
					bodyType = lastType;
					result = bodyType;
				}
				break;
			}
			case NodeTypes.Resume: {
				const resumeContext = ctx.currentResume;
				if (!resumeContext) throw new TypeInferenceError("resume can only be used inside a handler clause");
				if (payload === "ref") {
					result = fn([resumeContext.argType], resumeContext.answerType);
					break;
				}
				constrain(ctx, inferExpr(payload, ctx, env, typeMap), resumeContext.argType);
				result = resumeContext.answerType;
				break;
			}
			case NodeTypes.Macro:
				result = AnyFunction;
				break;
			case NodeTypes.MacroCall:
				result = Unknown;
				break;
			case NodeTypes.Recur:
				result = Never;
				break;
			default: result = Unknown;
		}
	} catch (error) {
		if (error instanceof TypeInferenceError && error.nodeId === void 0) error.nodeId = nodeId;
		throw error;
	}
	if (nodeId > 0) typeMap.set(nodeId, result);
	return result;
}
/**
* Create fresh copies of all type variables in a parsed annotation type.
* Each call to a polymorphic builtin like filter(A[], (A)->Boolean) -> A[]
* needs its own set of type variables so constraints from one call don't
* leak into another.
*
* @internal Exported for tests that build annotation types via
* `parseTypeAnnotation` and need fresh instances. Not a public API.
*/
function freshenAnnotationVars(ctx, t) {
	if (!containsVars(t)) return t;
	return freshenAllVars(ctx, t, /* @__PURE__ */ new Map(), /* @__PURE__ */ new Map());
}
function effectSetContainsVars(e) {
	return e.tail.tag === "RowVar";
}
function containsVars(t) {
	switch (t.tag) {
		case "Var": return true;
		case "Function":
			if (t.params.some(containsVars)) return true;
			if (t.restParam !== void 0 && containsVars(t.restParam)) return true;
			if (containsVars(t.ret)) return true;
			if (effectSetContainsVars(t.effects)) return true;
			return false;
		case "Handler":
			if (containsVars(t.body) || containsVars(t.output)) return true;
			for (const sig of t.handled.values()) if (containsVars(sig.argType) || containsVars(sig.retType)) return true;
			if (effectSetContainsVars(t.introduced)) return true;
			return false;
		case "Record": return [...t.fields.values()].some(containsVars);
		case "Array": return containsVars(t.element);
		case "Tuple": return t.elements.some(containsVars);
		case "Sequence": return t.prefix.some(containsVars) || containsVars(t.rest);
		case "Union":
		case "Inter": return t.members.some(containsVars);
		case "Neg": return containsVars(t.inner);
		case "Keyof": return containsVars(t.inner);
		case "Index": return containsVars(t.target) || containsVars(t.key);
		case "Alias": return t.args.some(containsVars) || containsVars(t.expanded);
		case "Refined": return containsVars(t.base);
		default: return false;
	}
}
/**
* Freshen an effect set: if its tail is a RowVar, allocate a fresh RowVar
* keyed through `rowMapping` so that multiple occurrences of the same row
* var within one annotation map to the same fresh var. Identity-preserving
* only — bounds are NOT copied. Annotation-parsed row vars always have
* empty bounds at parse time (bounds accumulate via biunification at call
* sites), so there's nothing to copy.
*/
/**
* Reconstruct a Record type, preserving the `optionalFields` sidecar from
* the source. Used by freshening, generalization, narrowing, expansion —
* every path that produces a new Record from an existing one.
*/
function rebuildRecord(src, fields) {
	const rec = {
		tag: "Record",
		fields,
		open: src.open
	};
	if (src.optionalFields && src.optionalFields.size > 0) rec.optionalFields = new Set(src.optionalFields);
	return rec;
}
function freshenEffectSet(ctx, e, rowMapping) {
	if (e.tail.tag !== "RowVar") return e;
	const existing = rowMapping.get(e.tail.id);
	if (existing) return {
		effects: new Set(e.effects),
		tail: existing
	};
	const fresh = ctx.freshRowVar();
	rowMapping.set(e.tail.id, fresh);
	return {
		effects: new Set(e.effects),
		tail: fresh
	};
}
function freshenAllVars(ctx, t, mapping, rowMapping) {
	switch (t.tag) {
		case "Var": {
			const existing = mapping.get(typeVarIdentity(t));
			if (existing) return existing;
			const fresh = ctx.freshVar();
			mapping.set(typeVarIdentity(t), fresh);
			return fresh;
		}
		case "Function": return fn(t.params.map((p) => freshenAllVars(ctx, p, mapping, rowMapping)), freshenAllVars(ctx, t.ret, mapping, rowMapping), freshenEffectSet(ctx, t.effects, rowMapping), t.handlerWrapper, t.restParam !== void 0 ? freshenAllVars(ctx, t.restParam, mapping, rowMapping) : void 0, t.asserts);
		case "Handler": {
			const handled = /* @__PURE__ */ new Map();
			for (const [name, sig] of t.handled) handled.set(name, {
				argType: freshenAllVars(ctx, sig.argType, mapping, rowMapping),
				retType: freshenAllVars(ctx, sig.retType, mapping, rowMapping)
			});
			return handlerType(freshenAllVars(ctx, t.body, mapping, rowMapping), freshenAllVars(ctx, t.output, mapping, rowMapping), handled, freshenEffectSet(ctx, t.introduced, rowMapping));
		}
		case "Record": {
			const fields = /* @__PURE__ */ new Map();
			for (const [k, v] of t.fields) fields.set(k, freshenAllVars(ctx, v, mapping, rowMapping));
			return rebuildRecord(t, fields);
		}
		case "Array": return array(freshenAllVars(ctx, t.element, mapping, rowMapping));
		case "Tuple": return tuple(t.elements.map((e) => freshenAllVars(ctx, e, mapping, rowMapping)));
		case "Sequence": return sequence(t.prefix.map((member) => freshenAllVars(ctx, member, mapping, rowMapping)), freshenAllVars(ctx, t.rest, mapping, rowMapping), t.minLength, t.maxLength);
		case "Union": return union(...t.members.map((m) => freshenAllVars(ctx, m, mapping, rowMapping)));
		case "Inter": return {
			tag: "Inter",
			members: t.members.map((m) => freshenAllVars(ctx, m, mapping, rowMapping))
		};
		case "Neg": return {
			tag: "Neg",
			inner: freshenAllVars(ctx, t.inner, mapping, rowMapping)
		};
		case "Keyof": return keyofType(freshenAllVars(ctx, t.inner, mapping, rowMapping));
		case "Index": return indexType(freshenAllVars(ctx, t.target, mapping, rowMapping), freshenAllVars(ctx, t.key, mapping, rowMapping));
		case "Alias": return {
			tag: "Alias",
			name: t.name,
			args: t.args.map((a) => freshenAllVars(ctx, a, mapping, rowMapping)),
			expanded: freshenAllVars(ctx, t.expanded, mapping, rowMapping)
		};
		case "Refined": return {
			tag: "Refined",
			base: freshenAllVars(ctx, t.base, mapping, rowMapping),
			binder: t.binder,
			predicate: t.predicate,
			source: t.source
		};
		default: return t;
	}
}
/**
* Copy a type, replacing any type variables at a level higher than the
* current context level with fresh variables. This implements
* let-polymorphism: each use of a polymorphic binding gets its own copy.
*/
function freshen(ctx, t) {
	if (t.tag !== "Var" && !containsVarsAboveLevel(t, ctx.level)) return t;
	return freshenInner(ctx, t, /* @__PURE__ */ new Map(), /* @__PURE__ */ new Map());
}
function freshenInner(ctx, t, mapping, rowMapping) {
	switch (t.tag) {
		case "Var": {
			if (!isGeneralizedTypeVar(t) && t.level <= ctx.level) return t;
			const existing = mapping.get(typeVarIdentity(t));
			if (existing) return existing;
			const fresh = ctx.freshVar();
			mapping.set(typeVarIdentity(t), fresh);
			for (const lb of t.lowerBounds) fresh.lowerBounds.push(freshenInner(ctx, lb, mapping, rowMapping));
			for (const ub of t.upperBounds) fresh.upperBounds.push(freshenInner(ctx, ub, mapping, rowMapping));
			return fresh;
		}
		case "Function": return fn(t.params.map((p) => freshenInner(ctx, p, mapping, rowMapping)), freshenInner(ctx, t.ret, mapping, rowMapping), freshenEffectSet(ctx, t.effects, rowMapping), t.handlerWrapper, t.restParam !== void 0 ? freshenInner(ctx, t.restParam, mapping, rowMapping) : void 0, t.asserts);
		case "Handler": {
			const handled = /* @__PURE__ */ new Map();
			for (const [name, sig] of t.handled) handled.set(name, {
				argType: freshenInner(ctx, sig.argType, mapping, rowMapping),
				retType: freshenInner(ctx, sig.retType, mapping, rowMapping)
			});
			return handlerType(freshenInner(ctx, t.body, mapping, rowMapping), freshenInner(ctx, t.output, mapping, rowMapping), handled, freshenEffectSet(ctx, t.introduced, rowMapping));
		}
		case "Record": {
			const fields = /* @__PURE__ */ new Map();
			for (const [k, v] of t.fields) fields.set(k, freshenInner(ctx, v, mapping, rowMapping));
			return rebuildRecord(t, fields);
		}
		case "Array": return array(freshenInner(ctx, t.element, mapping, rowMapping));
		case "Tuple": return tuple(t.elements.map((e) => freshenInner(ctx, e, mapping, rowMapping)));
		case "Sequence": return sequence(t.prefix.map((member) => freshenInner(ctx, member, mapping, rowMapping)), freshenInner(ctx, t.rest, mapping, rowMapping), t.minLength, t.maxLength);
		case "Union": return union(...t.members.map((m) => freshenInner(ctx, m, mapping, rowMapping)));
		case "Inter": return {
			tag: "Inter",
			members: t.members.map((m) => freshenInner(ctx, m, mapping, rowMapping))
		};
		case "Neg": return {
			tag: "Neg",
			inner: freshenInner(ctx, t.inner, mapping, rowMapping)
		};
		case "Keyof": return keyofType(freshenInner(ctx, t.inner, mapping, rowMapping));
		case "Index": return indexType(freshenInner(ctx, t.target, mapping, rowMapping), freshenInner(ctx, t.key, mapping, rowMapping));
		case "Alias": return {
			tag: "Alias",
			name: t.name,
			args: t.args.map((a) => freshenInner(ctx, a, mapping, rowMapping)),
			expanded: freshenInner(ctx, t.expanded, mapping, rowMapping)
		};
		case "Refined": return {
			tag: "Refined",
			base: freshenInner(ctx, t.base, mapping, rowMapping),
			binder: t.binder,
			predicate: t.predicate,
			source: t.source
		};
		default: return t;
	}
}
function effectSetContainsVarsAboveLevel(e, level) {
	return e.tail.tag === "RowVar" && e.tail.level > level;
}
/** Check if a type contains any variables above the given level. */
function containsVarsAboveLevel(t, level) {
	switch (t.tag) {
		case "Var": return isGeneralizedTypeVar(t) || t.level > level;
		case "Function":
			if (t.params.some((p) => containsVarsAboveLevel(p, level))) return true;
			if (t.restParam !== void 0 && containsVarsAboveLevel(t.restParam, level)) return true;
			if (containsVarsAboveLevel(t.ret, level)) return true;
			if (effectSetContainsVarsAboveLevel(t.effects, level)) return true;
			return false;
		case "Handler":
			if (containsVarsAboveLevel(t.body, level) || containsVarsAboveLevel(t.output, level)) return true;
			for (const sig of t.handled.values()) if (containsVarsAboveLevel(sig.argType, level) || containsVarsAboveLevel(sig.retType, level)) return true;
			if (effectSetContainsVarsAboveLevel(t.introduced, level)) return true;
			return false;
		case "Record": return [...t.fields.values()].some((v) => containsVarsAboveLevel(v, level));
		case "Array": return containsVarsAboveLevel(t.element, level);
		case "Tuple": return t.elements.some((e) => containsVarsAboveLevel(e, level));
		case "Sequence": return t.prefix.some((member) => containsVarsAboveLevel(member, level)) || containsVarsAboveLevel(t.rest, level);
		case "Union":
		case "Inter": return t.members.some((m) => containsVarsAboveLevel(m, level));
		case "Neg": return containsVarsAboveLevel(t.inner, level);
		case "Keyof": return containsVarsAboveLevel(t.inner, level);
		case "Index": return containsVarsAboveLevel(t.target, level) || containsVarsAboveLevel(t.key, level);
		case "Alias": return t.args.some((a) => containsVarsAboveLevel(a, level)) || containsVarsAboveLevel(t.expanded, level);
		case "Refined": return containsVarsAboveLevel(t.base, level);
		default: return false;
	}
}
/**
* Create a copy of the type where all type variables above `level` are replaced
* with fresh copies at GENERALIZED_LEVEL. Original type variables and their
* bounds are never mutated — the returned type is a new tree that shares
* structure with the original wherever no generalization was needed.
*/
function generalizeTypeVars(t, level) {
	return generalizeInner(t, level, /* @__PURE__ */ new Map());
}
function generalizeInner(t, level, mapping) {
	switch (t.tag) {
		case "Var": {
			if (t.level <= level) return t;
			const identity = typeVarIdentity(t);
			const existing = mapping.get(identity);
			if (existing) return existing;
			const copy = {
				tag: "Var",
				id: t.id,
				level: GENERALIZED_LEVEL,
				lowerBounds: [],
				upperBounds: []
			};
			mapping.set(identity, copy);
			copy.lowerBounds = t.lowerBounds.map((lb) => generalizeInner(lb, level, mapping));
			copy.upperBounds = t.upperBounds.map((ub) => generalizeInner(ub, level, mapping));
			if (t.displayLowerBounds) copy.displayLowerBounds = t.displayLowerBounds.map((lb) => generalizeInner(lb, level, mapping));
			if (t.displayUpperBounds) copy.displayUpperBounds = t.displayUpperBounds.map((ub) => generalizeInner(ub, level, mapping));
			return copy;
		}
		case "Function": {
			const params = t.params.map((p) => generalizeInner(p, level, mapping));
			const restParam = t.restParam !== void 0 ? generalizeInner(t.restParam, level, mapping) : void 0;
			const ret = generalizeInner(t.ret, level, mapping);
			if (params.every((p, i) => p === t.params[i]) && restParam === t.restParam && ret === t.ret) return t;
			return fn(params, ret, t.effects, t.handlerWrapper, restParam, t.asserts);
		}
		case "Handler": {
			const body = generalizeInner(t.body, level, mapping);
			const output = generalizeInner(t.output, level, mapping);
			let handledChanged = false;
			const handled = /* @__PURE__ */ new Map();
			for (const [name, sig] of t.handled) {
				const argType = generalizeInner(sig.argType, level, mapping);
				const retType = generalizeInner(sig.retType, level, mapping);
				if (argType !== sig.argType || retType !== sig.retType) handledChanged = true;
				handled.set(name, {
					argType,
					retType
				});
			}
			if (body === t.body && output === t.output && !handledChanged) return t;
			return handlerType(body, output, handled, t.introduced);
		}
		case "Record": {
			let changed = false;
			const fields = /* @__PURE__ */ new Map();
			for (const [k, v] of t.fields) {
				const gv = generalizeInner(v, level, mapping);
				if (gv !== v) changed = true;
				fields.set(k, gv);
			}
			if (!changed) return t;
			return rebuildRecord(t, fields);
		}
		case "Array": {
			const element = generalizeInner(t.element, level, mapping);
			return element === t.element ? t : array(element);
		}
		case "Tuple": {
			const elements = t.elements.map((e) => generalizeInner(e, level, mapping));
			return elements.every((e, i) => e === t.elements[i]) ? t : tuple(elements);
		}
		case "Sequence": {
			const prefix = t.prefix.map((m) => generalizeInner(m, level, mapping));
			const rest = generalizeInner(t.rest, level, mapping);
			if (prefix.every((m, i) => m === t.prefix[i]) && rest === t.rest) return t;
			return sequence(prefix, rest, t.minLength, t.maxLength);
		}
		case "Union": {
			const members = t.members.map((m) => generalizeInner(m, level, mapping));
			return members.every((m, i) => m === t.members[i]) ? t : union(...members);
		}
		case "Inter": {
			const members = t.members.map((m) => generalizeInner(m, level, mapping));
			return members.every((m, i) => m === t.members[i]) ? t : inter(...members);
		}
		case "Neg": {
			const inner = generalizeInner(t.inner, level, mapping);
			return inner === t.inner ? t : neg(inner);
		}
		case "Keyof": {
			const inner = generalizeInner(t.inner, level, mapping);
			return inner === t.inner ? t : keyofType(inner);
		}
		case "Index": {
			const target = generalizeInner(t.target, level, mapping);
			const key = generalizeInner(t.key, level, mapping);
			return target === t.target && key === t.key ? t : indexType(target, key);
		}
		case "Alias": {
			const args = t.args.map((a) => generalizeInner(a, level, mapping));
			const expanded = generalizeInner(t.expanded, level, mapping);
			if (args.every((a, i) => a === t.args[i]) && expanded === t.expanded) return t;
			return {
				tag: "Alias",
				name: t.name,
				args,
				expanded
			};
		}
		case "Recursive": {
			const body = generalizeInner(t.body, level, mapping);
			return body === t.body ? t : {
				tag: "Recursive",
				id: t.id,
				body
			};
		}
		case "Refined": {
			const base = generalizeInner(t.base, level, mapping);
			return base === t.base ? t : {
				tag: "Refined",
				base,
				binder: t.binder,
				predicate: t.predicate,
				source: t.source
			};
		}
		default: return t;
	}
}
/** Snapshot the bounds of all type variables reachable from a type. */
function snapshotVarBounds(t) {
	const result = [];
	collectVars(t, result, /* @__PURE__ */ new Set());
	return result;
}
function collectVars(t, result, visited) {
	switch (t.tag) {
		case "Var":
			if (visited.has(typeVarIdentity(t))) return;
			visited.add(typeVarIdentity(t));
			result.push({
				var: t,
				lowerLen: t.lowerBounds.length,
				upperLen: t.upperBounds.length
			});
			for (const lb of t.lowerBounds) collectVars(lb, result, visited);
			for (const ub of t.upperBounds) collectVars(ub, result, visited);
			break;
		case "Function":
			for (const p of t.params) collectVars(p, result, visited);
			if (t.restParam !== void 0) collectVars(t.restParam, result, visited);
			collectVars(t.ret, result, visited);
			break;
		case "Handler":
			collectVars(t.body, result, visited);
			collectVars(t.output, result, visited);
			for (const sig of t.handled.values()) {
				collectVars(sig.argType, result, visited);
				collectVars(sig.retType, result, visited);
			}
			break;
		case "Record":
			for (const v of t.fields.values()) collectVars(v, result, visited);
			break;
		case "Array":
			collectVars(t.element, result, visited);
			break;
		case "Tuple":
			for (const e of t.elements) collectVars(e, result, visited);
			break;
		case "Sequence":
			for (const member of t.prefix) collectVars(member, result, visited);
			collectVars(t.rest, result, visited);
			break;
		case "Union":
		case "Inter":
			for (const m of t.members) collectVars(m, result, visited);
			break;
	}
}
/** Restore variable bounds from a snapshot (truncate back to saved lengths). */
function restoreVarBounds(snapshot) {
	for (const s of snapshot) {
		s.var.lowerBounds.length = s.lowerLen;
		s.var.upperBounds.length = s.upperLen;
	}
}
function captureVarBoundDelta(snapshot) {
	const deltas = [];
	for (const s of snapshot) {
		const lowerBounds = s.var.lowerBounds.slice(s.lowerLen);
		const upperBounds = s.var.upperBounds.slice(s.upperLen);
		if (lowerBounds.length === 0 && upperBounds.length === 0) continue;
		deltas.push({
			var: s.var,
			lowerBounds,
			upperBounds
		});
	}
	return deltas;
}
function replayVarBoundDeltaExact(ctx, deltas) {
	for (const delta of deltas) {
		for (const lowerBound of delta.lowerBounds) constrain(ctx, lowerBound, delta.var);
		for (const upperBound of delta.upperBounds) constrain(ctx, delta.var, upperBound);
	}
}
function annotateDisplayVarBoundDeltas(successfulDeltas) {
	const merged = /* @__PURE__ */ new Map();
	for (const deltas of successfulDeltas) for (const delta of deltas) {
		const entry = merged.get(delta.var.id) ?? {
			var: delta.var,
			lowerBounds: [],
			upperBounds: []
		};
		entry.lowerBounds.push(...delta.lowerBounds);
		entry.upperBounds.push(...delta.upperBounds);
		merged.set(delta.var.id, entry);
	}
	for (const entry of merged.values()) {
		if (entry.lowerBounds.length > 0) entry.var.displayLowerBounds = dedupeDisplayBounds(entry.var.displayLowerBounds ?? [], entry.lowerBounds);
		if (entry.upperBounds.length > 0) entry.var.displayUpperBounds = dedupeDisplayBounds(entry.var.displayUpperBounds ?? [], entry.upperBounds);
	}
}
function dedupeDisplayBounds(existing, additions) {
	const merged = [...existing];
	for (const candidate of additions) if (!merged.some((bound) => typeEquals(bound, candidate))) merged.push(candidate);
	return merged;
}
function getHandlerAlternatives(type) {
	const resolved = resolveHandlerCarrier(type);
	if (resolved.tag === "Handler") return [resolved];
	if (resolved.tag === "Union" && resolved.members.every((member) => member.tag === "Handler")) return resolved.members;
	return [];
}
function getFunctionAlternatives(type) {
	const resolved = resolveCallableCarrier(type);
	if (resolved.tag === "Function") return [resolved];
	if (resolved.tag === "Inter" && resolved.members.every((member) => member.tag === "Function")) return resolved.members;
	if (resolved.tag === "Union" && resolved.members.every((member) => member.tag === "Function")) return resolved.members;
	return [];
}
function lookupShadowedBuiltin(env, name) {
	return env.lookup(name);
}
function resolveHandlerCarrier(type, visited = /* @__PURE__ */ new Set()) {
	return resolveCallableCarrier(type, visited);
}
function resolveCallableCarrier(type, visited = /* @__PURE__ */ new Set()) {
	if (type.tag === "Var") {
		if (visited.has(typeVarIdentity(type))) return type;
		visited.add(typeVarIdentity(type));
		if (type.lowerBounds.length === 0) return type;
		const resolvedBounds = type.lowerBounds.map((bound) => resolveCallableCarrier(bound, visited));
		return resolvedBounds.length === 1 ? resolvedBounds[0] : union(...resolvedBounds);
	}
	if (type.tag === "Alias") return resolveCallableCarrier(type.expanded, visited);
	return type;
}
function intersectHandledSignatures(handlers) {
	if (handlers.length === 0) return /* @__PURE__ */ new Map();
	const intersection = /* @__PURE__ */ new Map();
	const [first, ...rest] = handlers;
	for (const [name, sig] of first.handled) if (rest.every((handler) => {
		const other = handler.handled.get(name);
		return other && typeEquals(sig.argType, other.argType) && typeEquals(sig.retType, other.retType);
	})) intersection.set(name, sig);
	return intersection;
}
function intersectFunctionWrapperInfo(functions) {
	if (functions.length === 0) return void 0;
	const first = functions[0].handlerWrapper;
	if (!first) return void 0;
	if (!functions.every((fnType) => {
		const wrapper = fnType.handlerWrapper;
		if (!wrapper || wrapper.paramIndex !== first.paramIndex) return false;
		if (wrapper.handled.size !== first.handled.size) return false;
		for (const [name, sig] of first.handled) {
			const other = wrapper.handled.get(name);
			if (!other) return false;
			if (!typeEquals(sig.argType, other.argType) || !typeEquals(sig.retType, other.retType)) return false;
		}
		return true;
	})) return void 0;
	return {
		paramIndex: first.paramIndex,
		handled: first.handled,
		introduced: unionEffectSets(functions.map((f) => f.handlerWrapper?.introduced ?? PureEffects))
	};
}
function finalizeHandledSignatures(handled) {
	const finalized = /* @__PURE__ */ new Map();
	for (const [name, sig] of handled) finalized.set(name, {
		argType: finalizeInferredHandledType(sig.argType, "negative"),
		retType: finalizeInferredHandledType(sig.retType, "positive")
	});
	return finalized;
}
function finalizeInferredHandledType(type, polarity) {
	if (type.tag !== "Var") return type;
	if (type.lowerBounds.length === 0 && type.upperBounds.length === 0) return Unknown;
	return expandType(type, polarity);
}
/**
* Aggregate multiple effect sets into their least upper bound.
*
* - All Closed/Open inputs: union the effect names, pick Open if any input
*   is Open, else Closed.
* - Any RowVar inputs (with a `ctx` available): allocate a fresh row var
*   `ρ_new` and constrain each input as its lower bound — MLsub-style union
*   over the flat effect lattice. The concrete side is also unioned.
*   Result: `{ effects: union-of-all-concrete, tail: RowVar(ρ_new) }`.
* - RowVar inputs without a `ctx`: conservatively fall back to Open tail.
*   Callers pass `ctx` whenever they care about row-var propagation.
*/
function unionEffectSets(effectSets, ctx) {
	if (effectSets.length === 0) return PureEffects;
	const effects = /* @__PURE__ */ new Set();
	let openResult = false;
	const rowVars = [];
	const rowVarConcreteContributions = [];
	for (const effectSet of effectSets) {
		for (const effectName of effectSet.effects) effects.add(effectName);
		if (effectSet.tail.tag === "Open") openResult = true;
		if (effectSet.tail.tag === "RowVar") {
			rowVars.push(effectSet.tail);
			if (effectSet.effects.size > 0) rowVarConcreteContributions.push(new Set(effectSet.effects));
		}
	}
	if (rowVars.length === 0) return {
		effects,
		tail: openResult ? OpenTail : ClosedTail
	};
	if (openResult) return {
		effects,
		tail: OpenTail
	};
	if (!ctx) return {
		effects,
		tail: OpenTail
	};
	const sharedId = rowVars[0].id;
	if (rowVars.every((ρ) => ρ.id === sharedId)) {
		const shared = rowVars[0];
		for (const concrete of rowVarConcreteContributions) addRowVarLowerBound(shared, concrete);
		return {
			effects,
			tail: shared
		};
	}
	const ρNew = ctx.freshRowVar();
	for (const ρ of rowVars) addRowVarEdge(ρ, ρNew);
	for (const concrete of rowVarConcreteContributions) addRowVarLowerBound(ρNew, concrete);
	return {
		effects,
		tail: ρNew
	};
}
function recordSpecializedCalleeType(calleeNode, selectedAlternative, typeMap) {
	const calleeNodeId = calleeNode[2];
	if (calleeNodeId <= 0 || !selectedAlternative) return;
	typeMap.set(calleeNodeId, selectedAlternative);
}
function selectFirstSuccessfulFunctionAlternative(ctx, functionAlternatives, argTypes) {
	if (functionAlternatives.length <= 1) return void 0;
	for (const alternative of functionAlternatives) {
		const target = fn(argTypes, ctx.freshVar());
		const boundsSnapshot = snapshotVarBounds(target);
		const cacheSnapshot = ctx.snapshotCacheSize();
		try {
			constrain(ctx, alternative, target);
			restoreVarBounds(boundsSnapshot);
			ctx.restoreCacheSize(cacheSnapshot);
			return alternative;
		} catch (error) {
			if (!(error instanceof TypeInferenceError)) throw error;
			restoreVarBounds(boundsSnapshot);
			ctx.restoreCacheSize(cacheSnapshot);
		}
	}
}
function isZeroArgFunctionNode(node) {
	return node[0] === NodeTypes.Function && node[1][0].length === 0;
}
function inferFunctionNode(node, ctx, env, typeMap, options) {
	const [params, bodyNodes] = node[1];
	const funcEnv = env.child();
	const paramTypes = [];
	const suspendedHandled = (options?.inheritHandledSignatures ? ctx.currentHandledSignatures : void 0) ? void 0 : ctx.currentHandledSignatures;
	if (suspendedHandled) ctx.popHandledSignatures();
	try {
		for (const param of params) {
			const paramVar = ctx.freshVar();
			paramTypes.push(paramVar);
			bindPattern(param, paramVar, funcEnv, ctx, typeMap);
			const paramAnnotation = ctx.typeAnnotations?.get(param[2]);
			if (paramAnnotation) {
				let declaredType;
				try {
					declaredType = simplify(parseUserTypeAnnotation(paramAnnotation, ctx.activeTypeParams));
				} catch (error) {
					if (error instanceof TypeParseError) throw typeAnnotationParseError(error.cleanMessage, param[2], paramAnnotation, error, ctx, param[2]);
					throw error;
				}
				try {
					constrain(ctx, paramVar, declaredType);
				} catch (error) {
					if (error instanceof TypeInferenceError && error.nodeId === void 0) error.nodeId = param[2];
					throw error;
				}
			}
		}
		ctx.pushEffects();
		const { lastType: retType, lastStmt: lastBodyNode } = inferStatementsWithAssertNarrowing(bodyNodes, ctx, funcEnv, typeMap);
		const bodyEffects = ctx.popEffects();
		const rawAnnotation = ctx.typeAnnotations?.get(node[2]);
		if (rawAnnotation?.startsWith("return:")) {
			let declaredType;
			try {
				declaredType = parseUserTypeAnnotation(rawAnnotation.slice(7), ctx.activeTypeParams);
			} catch (error) {
				if (error instanceof TypeParseError) throw typeAnnotationParseError(error.cleanMessage, node[2], rawAnnotation.slice(7), error, ctx, node[2]);
				throw error;
			}
			let expandedRet = expandType(retType, "positive");
			if (expandedRet.tag === "Never") expandedRet = expandType(retType, "negative");
			if (!isSubtype(expandedRet, declaredType)) throw new TypeInferenceError(`${typeToString(expandedRet)} is not a subtype of ${typeToString(declaredType)}`, lastBodyNode?.[2] ?? node[2]);
			try {
				constrain(ctx, retType, declaredType);
			} catch (error) {
				if (error instanceof TypeInferenceError && error.nodeId === void 0) error.nodeId = lastBodyNode?.[2] ?? node[2];
				throw error;
			}
		}
		const handlerWrapper = inferFunctionWrapperInfo(paramTypes, ctx);
		const overloads = synthesizeFunctionOverloads(paramTypes, retType, bodyEffects, handlerWrapper);
		if (overloads) return inter(...overloads);
		return fn(paramTypes, retType, bodyEffects, handlerWrapper);
	} finally {
		if (suspendedHandled) ctx.pushHandledSignatures(suspendedHandled);
	}
}
function synthesizeFunctionOverloads(params, ret, effects, handlerWrapper) {
	const paramAlternatives = params.map(getDisplayUpperAlternatives);
	const retAlternatives = getDisplayLowerAlternatives(ret);
	if (!retAlternatives || retAlternatives.length < 2) return void 0;
	if (paramAlternatives.some((alternatives) => !alternatives || alternatives.length !== retAlternatives.length)) return;
	return dedupeDisplayBounds([], retAlternatives.map((retAlternative, index) => fn(paramAlternatives.map((alternatives) => alternatives[index]), retAlternative, effects, handlerWrapper)));
}
function getDisplayUpperAlternatives(type) {
	return type.tag === "Var" && type.displayUpperBounds && type.displayUpperBounds.length > 0 ? type.displayUpperBounds : void 0;
}
function getDisplayLowerAlternatives(type) {
	return type.tag === "Var" && type.displayLowerBounds && type.displayLowerBounds.length > 0 ? type.displayLowerBounds : void 0;
}
function inferFunctionWrapperInfo(params, ctx) {
	for (let index = 0; index < params.length; index++) {
		const param = params[index];
		if (param.tag !== "Var") continue;
		const captured = ctx.getWrappedThunkVar(param.id);
		if (captured) return {
			paramIndex: index,
			handled: captured.handled,
			introduced: captured.introduced
		};
	}
}
function isConstrainedFunctionArityCompatible(lhs, rhs) {
	if (rhs.restParam !== void 0) return lhs.restParam !== void 0 && lhs.params.length <= rhs.params.length;
	return functionAcceptsArity(lhs, rhs.params.length);
}
/**
* Extract type narrowing from a guard expression.
* Recognizes patterns like `isNumber(n)` → returns NumberType.
* Uses the builtin type guard info from the type registry.
*/
/**
* Flow-sensitive narrowing for `if` conditions. Analyzes the condition AST
* and returns the refinements that should apply in the then and else branches.
*
* Recognised shapes:
* - `isX(sym)` — builtin type guard. Then: sym & X. Else: sym & !X.
* - `isX(sym.field…)` — same, but the narrowing is wrapped in open
*   records along the `.`-access path so it intersects with the outer
*   record at the right depth.
* - `sym == atomOrLiteral` / `sym.field… == atomOrLiteral` — equality
*   test. Then: refines the sym (or root sym) to match the literal at
*   that field. Else: refines to its complement.
* - `!(cond)` — swaps then/else from the inner narrowing.
* - `a && b` — then branch sees the conjunction of operand narrowings;
*   else branch can't be narrowed (`!(a && b)` = `!a || !b`).
* - `a || b` — dual: else branch sees the conjunction of operand-false
*   narrowings; then branch can't be narrowed.
*
* Returns undefined if the condition isn't a recognised narrowing shape,
* in which case the If case falls back to normal inference of both branches.
*/
function extractIfNarrowings(cond, env) {
	if (cond[0] === NodeTypes.And) {
		const operands = cond[1];
		const whenTrue = composeNarrowings(operands.map((op) => extractIfNarrowings(op, env)?.whenTrue));
		if (whenTrue.size === 0) return void 0;
		return {
			whenTrue,
			whenFalse: /* @__PURE__ */ new Map()
		};
	}
	if (cond[0] === NodeTypes.Or) {
		const operands = cond[1];
		const whenFalse = composeNarrowings(operands.map((op) => extractIfNarrowings(op, env)?.whenFalse));
		if (whenFalse.size === 0) return void 0;
		return {
			whenTrue: /* @__PURE__ */ new Map(),
			whenFalse
		};
	}
	if (cond[0] !== NodeTypes.Call) return void 0;
	const [calleeNode, argNodes] = cond[1];
	if (calleeNode[0] !== NodeTypes.Builtin) return void 0;
	const builtinName = calleeNode[1];
	if (lookupShadowedBuiltin(env, builtinName)) return void 0;
	if (builtinName === "!" && argNodes.length === 1) {
		const inner = extractIfNarrowings(argNodes[0], env);
		if (!inner) return void 0;
		return {
			whenTrue: inner.whenFalse,
			whenFalse: inner.whenTrue
		};
	}
	if (argNodes.length === 1) {
		const argNode = argNodes[0];
		const path = extractFieldPath(argNode);
		if (!path) return void 0;
		const info = getBuiltinType(builtinName);
		if (info.guardType) return {
			whenTrue: new Map([[path.symName, wrapNarrowingInFieldPath(info.guardType, path.fields)]]),
			whenFalse: new Map([[path.symName, wrapNarrowingInFieldPath(neg(info.guardType), path.fields)]])
		};
		return;
	}
	if (argNodes.length === 2 && (builtinName === "==" || builtinName === "!=")) {
		const [leftNode, rightNode] = argNodes;
		const narrow = extractEqualityNarrowing(leftNode, rightNode) ?? extractEqualityNarrowing(rightNode, leftNode);
		if (narrow) {
			const negated = builtinName === "!=";
			return {
				whenTrue: new Map([[narrow.symName, negated ? neg(narrow.value) : narrow.value]]),
				whenFalse: new Map([[narrow.symName, negated ? narrow.value : neg(narrow.value)]])
			};
		}
	}
	return extractRefinementNarrowing(cond, env);
}
/**
* Block-level narrowing from `assert(P)`.
*
* Phase 2.5a: when a `do` block walker encounters `assert(P)` (the
* builtin in `core/assertion.ts`), we treat the predicate `P` as an
* assumed fact for subsequent statements in the block. The narrowing
* is expressed as a `Refined` wrapper around the variable's current
* type — exactly the same shape the user could write as an
* annotation. The solver from PR #96 then handles downstream subtype
* queries against refined targets without any extra plumbing.
*
* Scope (MVP):
*   - `P` must reference exactly one free symbol bound in `env`.
*     Multi-variable predicates (`x > y`) need cross-variable
*     reasoning that's deferred to Phase 3 anyway.
*   - `P` must pass `fragmentCheckPredicate` with the free symbol as
*     the binder. If it doesn't, the runtime `assert` still works,
*     but no static narrowing applies — clean fallback.
*
* Silent-no-op caveat: a fragment-eligible predicate that the solver
* can't extract a domain from (e.g. `isValidId(x)` — type-guard call
* to a non-narrowing builtin) still narrows structurally to a
* `Refined` wrapper, but downstream subtype queries hit the inert
* pass-through and accept everything. The narrowing is technically
* present; it just has no end-to-end observable effect today. When
* the solver gains coverage in later phases, these refinements will
* automatically start tightening the static analysis.
*
* Returns the narrowing map (sym → Refined) or `undefined` if the
* statement isn't an `assert(P)` call we can narrow.
*/
function extractAssertNarrowings(stmt, env) {
	if (stmt[0] !== NodeTypes.Call) return void 0;
	const [calleeNode, argNodes] = stmt[1];
	let paramIndex;
	let predicateLabel;
	let predicate;
	if (calleeNode[0] === NodeTypes.Builtin) {
		const builtinName = calleeNode[1];
		paramIndex = getBuiltinType(builtinName).assertsParam;
		if (paramIndex === void 0) return void 0;
		if (argNodes.length <= paramIndex) return void 0;
		if (lookupShadowedBuiltin(env, builtinName)) return void 0;
		predicateLabel = `<${builtinName}>`;
		predicate = argNodes[paramIndex];
	} else if (calleeNode[0] === NodeTypes.Sym) {
		const calleeType = expandType(env.lookup(calleeNode[1]) ?? Unknown);
		if (calleeType.tag !== "Function" || !calleeType.asserts) return void 0;
		paramIndex = calleeType.asserts.paramIndex;
		if (argNodes.length <= paramIndex) return void 0;
		const assertedArg = argNodes[paramIndex];
		if (assertedArg[0] !== NodeTypes.Sym) return void 0;
		const binderName = assertedArg[1];
		const baseType = env.lookup(binderName);
		if (!baseType) return void 0;
		predicate = rewritePredicateBinder(calleeType.asserts.predicate, calleeType.asserts.binder, assertedArg);
		const source = `${binderName} | ${prettyPrint(predicate).trim()}`;
		return new Map([[binderName, {
			tag: "Refined",
			base: baseType,
			binder: binderName,
			predicate,
			source
		}]]);
	} else return;
	if (!predicate) return void 0;
	const freeSymbols = collectFreeBoundSymbols(predicate, env);
	if (freeSymbols.size !== 1) return void 0;
	const binderName = [...freeSymbols][0];
	try {
		fragmentCheckPredicate(predicate, binderName, predicateLabel, 0);
	} catch {
		return;
	}
	const baseType = env.lookup(binderName);
	if (!baseType) return void 0;
	const source = `${binderName} | ${prettyPrint(predicate).trim()}`;
	return new Map([[binderName, {
		tag: "Refined",
		base: baseType,
		binder: binderName,
		predicate,
		source
	}]]);
}
function rewritePredicateBinder(node, binder, replacement) {
	const rewritten = /* @__PURE__ */ new Map();
	const stack = [{
		node,
		exiting: false
	}];
	while (stack.length > 0) {
		const frame = stack.pop();
		const current = frame.node;
		if (frame.exiting) {
			rewritten.set(current, rewritePredicateNode(current, binder, replacement, rewritten));
			continue;
		}
		stack.push({
			node: current,
			exiting: true
		});
		if (current[0] === NodeTypes.Call && Array.isArray(current[1])) {
			const [callee, args] = current[1];
			for (let index = args.length - 1; index >= 0; index--) stack.push({
				node: args[index],
				exiting: false
			});
			stack.push({
				node: callee,
				exiting: false
			});
			continue;
		}
		if ((current[0] === NodeTypes.And || current[0] === NodeTypes.Or) && Array.isArray(current[1])) {
			const operands = current[1];
			for (let index = operands.length - 1; index >= 0; index--) stack.push({
				node: operands[index],
				exiting: false
			});
		}
	}
	return rewritten.get(node) ?? node;
}
function rewritePredicateNode(node, binder, replacement, rewritten) {
	if (node[0] === NodeTypes.Sym && node[1] === binder) return replacement;
	if (node[0] === NodeTypes.Call && Array.isArray(node[1])) {
		const [callee, args, hints] = node[1];
		const rewrittenCallee = rewritten.get(callee) ?? callee;
		const rewrittenArgs = args.map((arg) => rewritten.get(arg) ?? arg);
		if (rewrittenCallee === callee && rewrittenArgs.every((arg, index) => arg === args[index])) return node;
		return [
			NodeTypes.Call,
			[
				rewrittenCallee,
				rewrittenArgs,
				hints
			],
			node[2]
		];
	}
	if ((node[0] === NodeTypes.And || node[0] === NodeTypes.Or) && Array.isArray(node[1])) {
		const operands = node[1];
		const rewrittenOperands = operands.map((operand) => rewritten.get(operand) ?? operand);
		if (rewrittenOperands.every((operand, index) => operand === operands[index])) return node;
		return [
			node[0],
			rewrittenOperands,
			node[2]
		];
	}
	return node;
}
/**
* Phase 2.5b — flow narrowing from `if` and `match` guards whose
* condition is a fragment-eligible refinement predicate.
*
* Mirrors `extractAssertNarrowings` but produces both whenTrue and
* whenFalse maps — the else branch sees the negated predicate
* `!(P)` wrapped as a Refined node (the fragment-checker accepts `!P`,
* so this stays in-fragment).
*
* Why a separate helper from `extractAssertNarrowings`: assert is a
* statement form that produces narrowing only forward; if/match
* conditions branch into two worlds, each with its own narrowing.
* Sharing the `Refined`-construction logic via a small inline factory
* would only obscure the symmetry — so this is duplicated by design.
*
* Returns undefined if the cond doesn't qualify (multi-symbol,
* out-of-fragment, etc.), letting the surrounding `extractIfNarrowings`
* fall through to its default "no narrowing".
*/
function extractRefinementNarrowing(cond, env) {
	const free = collectFreeBoundSymbols(cond, env);
	if (free.size !== 1) return void 0;
	const binderName = [...free][0];
	try {
		fragmentCheckPredicate(cond, binderName, "<if>", 0);
	} catch {
		return;
	}
	const baseType = env.lookup(binderName);
	if (!baseType) return void 0;
	const trueRefined = {
		tag: "Refined",
		base: baseType,
		binder: binderName,
		predicate: cond,
		source: `${binderName} | ${prettyPrint(cond).trim()}`
	};
	const notCond = isNegationCall(cond) ? cond[1][1][0] : [
		NodeTypes.Call,
		[
			[
				NodeTypes.Builtin,
				"!",
				0
			],
			[cond],
			null
		],
		0
	];
	const falseRefined = {
		tag: "Refined",
		base: baseType,
		binder: binderName,
		predicate: notCond,
		source: `${binderName} | ${prettyPrint(notCond).trim()}`
	};
	return {
		whenTrue: new Map([[binderName, trueRefined]]),
		whenFalse: new Map([[binderName, falseRefined]])
	};
}
/**
* `true` iff `node` is a `Call(Builtin('!'), [inner])` — the AST shape
* for `!P`. Used to peephole double-negation when synthesizing the
* else-branch predicate in `extractRefinementNarrowing`.
*/
function isNegationCall(node) {
	if (node[0] !== NodeTypes.Call) return false;
	const [callee, args] = node[1];
	return callee[0] === NodeTypes.Builtin && callee[1] === "!" && args.length === 1;
}
/**
* Walk a list of statements left-to-right, calling `inferExpr` on each
* and threading any `assert(P)` narrowings from earlier statements
* into the env used by later ones. Used by every block-shaped node
* (Block, function body, handler clauses, transform, WithHandler) so
* the narrowing semantics are uniform across all sequence-of-stmt
* sites in the typechecker.
*
* Returns the type of the LAST statement (the value of the block) and
* the final env so callers can inspect it if they need the narrowed
* bindings later (e.g. for return-type inference). Most callers
* discard the env.
*/
function inferStatementsWithAssertNarrowing(stmts, ctx, env, typeMap) {
	let lastType = NullType;
	let lastStmt;
	let currentEnv = env;
	for (const stmt of stmts) {
		lastType = inferExpr(stmt, ctx, currentEnv, typeMap);
		lastStmt = stmt;
		const narrowings = extractAssertNarrowings(stmt, currentEnv);
		if (narrowings) currentEnv = narrowEnv(currentEnv, narrowings);
	}
	return {
		lastType,
		lastStmt,
		finalEnv: currentEnv
	};
}
/**
* Walk a predicate AST and return the set of identifiers that are
* bound in `env` (i.e. real variables), ignoring builtins and
* unresolved names. Used to find which variable an `assert(P)` is
* narrowing.
*/
function collectFreeBoundSymbols(node, env) {
	const out = /* @__PURE__ */ new Set();
	const visit = (n) => {
		switch (n[0]) {
			case NodeTypes.Sym: {
				const name = n[1];
				if (env.lookup(name)) out.add(name);
				return;
			}
			case NodeTypes.Call: {
				const [callee, args] = n[1];
				visit(callee);
				for (const arg of args) visit(arg);
				return;
			}
			case NodeTypes.And:
			case NodeTypes.Or: {
				const operands = n[1];
				for (const op of operands) visit(op);
				return;
			}
			default: return;
		}
	};
	visit(node);
	return out;
}
/**
* JS-style truthiness for a fully-expanded type. Returns `undefined`
* when the truthiness cannot be statically determined (e.g. a non-literal
* primitive, a union, or a record). Falsy literals: `false`, `0`, `""`,
* `null`. Everything else with a single concrete value is truthy.
*/
function literalTruthiness(t) {
	if (t.tag === "Primitive" && t.name === "Null") return false;
	if (t.tag === "Literal") {
		if (typeof t.value === "boolean") return t.value;
		if (typeof t.value === "number") return t.value !== 0;
		if (typeof t.value === "string") return t.value !== "";
	}
}
/**
* Combine multiple per-symbol narrowing maps. Each operand may contribute
* a refinement for some subset of symbols; on collision, intersect — both
* refinements must hold simultaneously. Operands that contributed no
* narrowing show up as `undefined` and are skipped.
*/
function composeNarrowings(maps) {
	const out = /* @__PURE__ */ new Map();
	for (const m of maps) {
		if (!m) continue;
		for (const [sym, narrow] of m) {
			const existing = out.get(sym);
			out.set(sym, existing ? mergeNarrowingPair(existing, narrow) : narrow);
		}
	}
	return out;
}
/**
* Combine two narrowings for the same symbol from sibling operands
* of an `&&` compose. Critical invariant: if EITHER input is
* `Refined`, the output must also be `Refined`. Going through
* `inter(a, b)` produces `Inter<Refined, T>` whose top tag is
* `Inter`, and downstream `narrowEnv` only direct-binds when the top
* tag is `Refined`. Without the lift, the Phase 2.3 inert pass-
* through would silently discard the refinement on mixed
* type-guard + refinement operands.
*
* Cases:
*   - Both Refined: merge predicates via `mergeRefinementPredicates`
*     (alpha-renames if binders differ); base = inter of the two
*     bases, identity-shared when equal.
*   - One Refined + one regular type: keep the Refined's predicate,
*     intersect its base with the regular type (which becomes an
*     additional base constraint).
*   - Neither Refined: fall through to `inter(...)` — the existing
*     compose semantics.
*/
function mergeNarrowingPair(a, b) {
	const aIsRef = a.tag === "Refined";
	const bIsRef = b.tag === "Refined";
	if (!aIsRef && !bIsRef) return inter(a, b);
	const aBase = aIsRef ? a.base : a;
	const bBase = bIsRef ? b.base : b;
	const newBase = typeEquals(aBase, bBase) ? aBase : inter(aBase, bBase);
	if (aIsRef && bIsRef) {
		const merged = mergeRefinementPredicates(a.binder, a.predicate, b.binder, b.predicate);
		return {
			tag: "Refined",
			base: newBase,
			binder: a.binder,
			predicate: merged.predicate,
			source: merged.source
		};
	}
	const refined = aIsRef ? a : b;
	return {
		tag: "Refined",
		base: newBase,
		binder: refined.binder,
		predicate: refined.predicate,
		source: refined.source
	};
}
/**
* Extract a chain of field names rooted in a Sym from a `.`-access AST.
* `r` → `{symName: 'r', fields: []}`. `r.a` → `{symName: 'r', fields: ['a']}`.
* `r.a.b` → `{symName: 'r', fields: ['a', 'b']}`. Returns null if the
* receiver isn't a Sym at the bottom or if any segment isn't a Str-keyed
* single-arg Call (the surface form of `.`-access).
*/
function extractFieldPath(node) {
	if (node[0] === NodeTypes.Sym) return {
		symName: node[1],
		fields: []
	};
	if (node[0] !== NodeTypes.Call) return null;
	const [calleeNode, argNodes] = node[1];
	if (!Array.isArray(argNodes) || argNodes.length !== 1) return null;
	const argNode = argNodes[0];
	if (argNode[0] !== NodeTypes.Str) return null;
	const inner = extractFieldPath(calleeNode);
	if (!inner) return null;
	return {
		symName: inner.symName,
		fields: [...inner.fields, argNode[1]]
	};
}
/**
* Wrap a leaf narrowing type in a chain of open records following
* `fieldPath`. With an empty path, returns `leaf` unchanged. With path
* `['a', 'b']`, wraps to `{a: {b: leaf, ...}, ...}` — open at every
* level so the narrowing intersects cleanly with whatever extra fields
* the outer record carries.
*/
function wrapNarrowingInFieldPath(leaf, fields) {
	let t = leaf;
	for (let i = fields.length - 1; i >= 0; i--) t = record({ [fields[i]]: t }, true);
	return t;
}
/**
* If `symNode` is a Sym reference (or a `.`-chain rooted in one) and
* `valueNode` is a literal/atom, return the narrowing keyed on the root
* Sym, with the leaf value wrapped in the field path so it intersects
* with the outer record at the right depth.
*/
function extractEqualityNarrowing(symNode, valueNode) {
	const path = extractFieldPath(symNode);
	if (!path) return null;
	const kind = valueNode[0];
	let leaf = null;
	if (kind === NodeTypes.Atom) leaf = atom(valueNode[1]);
	else if (kind === NodeTypes.Num || kind === NodeTypes.Str) leaf = literal(valueNode[1]);
	else if (kind === NodeTypes.Reserved) {
		const lit = valueNode[1];
		if (lit === "true") leaf = literal(true);
		else if (lit === "false") leaf = literal(false);
	}
	if (!leaf) return null;
	return {
		symName: path.symName,
		value: wrapNarrowingInFieldPath(leaf, path.fields)
	};
}
/**
* Create a child env where each entry in `narrowings` intersects the
* outer type with the narrowing type. Used to thread flow-narrowed types
* into branch inference.
*/
function narrowEnv(env, narrowings) {
	if (narrowings.size === 0) return env;
	const narrowed = env.child();
	for (const [name, narrow] of narrowings) {
		const outer = env.lookup(name);
		if (!outer) continue;
		if (narrow.tag === "Refined") {
			narrowed.bind(name, narrow);
			continue;
		}
		const intersected = intersectMatchTypes(outer, narrow);
		if (!typeEquals(intersected, outer)) narrowed.bind(name, intersected);
	}
	return narrowed;
}
function extractGuardNarrowing(guard, boundName, env) {
	if (guard[0] !== NodeTypes.Call) return null;
	const [calleeNode, argNodes] = guard[1];
	if (calleeNode[0] !== NodeTypes.Builtin) return null;
	if (argNodes.length !== 1) return null;
	const argNode = argNodes[0];
	if (argNode[0] !== NodeTypes.Sym) return null;
	if (argNode[1] !== boundName) return null;
	const builtinName = calleeNode[1];
	if (lookupShadowedBuiltin(env, builtinName)) return null;
	const info = getBuiltinType(builtinName);
	if (info.guardType) return info.guardType;
	return null;
}
function analyzeMatchCase(pattern, candidateType, guard, env) {
	const matchedByPattern = matchedTypeForPattern(pattern, candidateType);
	if (matchedByPattern.tag === "Never") return {
		matchedType: Never,
		consumedType: Never
	};
	if (!guard) return {
		matchedType: matchedByPattern,
		consumedType: matchedByPattern
	};
	const narrowed = narrowMatchedTypeByGuard(pattern, matchedByPattern, guard, env);
	if (narrowed) return {
		matchedType: narrowed,
		consumedType: narrowed
	};
	return {
		matchedType: matchedByPattern,
		consumedType: Never
	};
}
/**
* Attempt to narrow a matched type using guard information.
* Returns the narrowed type if any guard narrowing applies, or null if
* the guard is opaque (can't extract narrowing information).
*/
function narrowMatchedTypeByGuard(pattern, matchedType, guard, env) {
	switch (pattern[0]) {
		case "symbol": {
			const [nameNode] = pattern[1];
			const guardNarrow = extractGuardNarrowing(guard, nameNode[1], env);
			return guardNarrow ? intersectMatchTypes(matchedType, guardNarrow) : null;
		}
		case "array": {
			const [elements] = pattern[1];
			return narrowArrayMatchTypeByGuard(elements, matchedType, guard, env);
		}
		case "object": {
			const [rawEntries] = pattern[1];
			return narrowObjectMatchTypeByGuard(objectBindingFieldsAsRecord(rawEntries), matchedType, guard, env);
		}
		default: return null;
	}
}
/**
* For array destructuring patterns, check if the guard narrows any element
* binding. If so, refine the matched type at that position.
*
* Example: `case [x, y] when isNumber(x)` with matched type `[Unknown, Unknown]`
* narrows to `[Number, Unknown]`.
*/
function narrowArrayMatchTypeByGuard(elements, matchedType, guard, env) {
	const elementNarrowings = /* @__PURE__ */ new Map();
	for (let i = 0; i < elements.length; i++) {
		const elem = elements[i];
		if (!elem || elem[0] === "rest") continue;
		if (elem[0] !== "symbol") continue;
		const [nameNode] = elem[1];
		const boundName = nameNode[1];
		const guardNarrow = extractGuardNarrowing(guard, boundName, env);
		if (guardNarrow) elementNarrowings.set(i, guardNarrow);
	}
	if (elementNarrowings.size === 0) return null;
	return narrowSequenceElementTypes(matchedType, elementNarrowings);
}
function narrowSequenceElementTypes(type, elementNarrowings) {
	if (type.tag === "Union") {
		const narrowed = type.members.map((m) => narrowSequenceElementTypes(m, elementNarrowings)).filter((m) => m.tag !== "Never");
		return narrowed.length === 0 ? Never : simplify(union(...narrowed));
	}
	const seq = toSequenceType(type);
	if (!seq) return type;
	const narrowedPrefix = [...seq.prefix];
	for (const [index, guardType] of elementNarrowings) {
		while (narrowedPrefix.length <= index) narrowedPrefix.push(seq.rest);
		const narrowed = intersectMatchTypes(narrowedPrefix[index], guardType);
		if (narrowed.tag === "Never") return Never;
		narrowedPrefix[index] = narrowed;
	}
	return simplify(sequence(narrowedPrefix, seq.rest, seq.minLength, seq.maxLength));
}
/**
* For object destructuring patterns, check if the guard narrows any field
* binding. If so, refine the matched type at that field.
*
* Example: `case { x, y } when isNumber(x)` with matched type `{x: Unknown, y: Unknown}`
* narrows to `{x: Number, y: Unknown}`.
*/
function narrowObjectMatchTypeByGuard(fieldsObj, matchedType, guard, env) {
	const fieldNarrowings = /* @__PURE__ */ new Map();
	for (const [fieldName, fieldPattern] of Object.entries(fieldsObj)) {
		if (fieldPattern[0] !== "symbol") continue;
		const [nameNode] = fieldPattern[1];
		const boundName = nameNode[1];
		const guardNarrow = extractGuardNarrowing(guard, boundName, env);
		if (guardNarrow) fieldNarrowings.set(fieldName, guardNarrow);
	}
	if (fieldNarrowings.size === 0) return null;
	return narrowRecordFieldTypes(matchedType, fieldNarrowings);
}
function narrowRecordFieldTypes(type, fieldNarrowings) {
	if (type.tag === "Union") {
		const narrowed = type.members.map((m) => narrowRecordFieldTypes(m, fieldNarrowings)).filter((m) => m.tag !== "Never");
		return narrowed.length === 0 ? Never : simplify(union(...narrowed));
	}
	if (type.tag !== "Record") return type;
	const narrowedFields = new Map(type.fields);
	for (const [fieldName, guardType] of fieldNarrowings) {
		const narrowed = intersectMatchTypes(narrowedFields.get(fieldName) ?? Unknown, guardType);
		if (narrowed.tag === "Never") return Never;
		narrowedFields.set(fieldName, narrowed);
	}
	return rebuildRecord(type, narrowedFields);
}
function matchedTypeForPattern(pattern, candidateType) {
	const expandedCandidate = simplify(expandTypeForMatchAnalysis(candidateType));
	if (expandedCandidate.tag === "Never") return Never;
	switch (pattern[0]) {
		case "wildcard":
		case "symbol": return expandedCandidate;
		case "literal": {
			const literalType = getLiteralMatchPatternType(pattern);
			return literalType ? intersectMatchTypes(expandedCandidate, literalType) : Never;
		}
		case "object": return matchedObjectPatternType(pattern, expandedCandidate);
		case "array": return matchedArrayPatternType(pattern, expandedCandidate);
		default: return expandedCandidate;
	}
}
function getLiteralMatchPatternType(pattern) {
	const [litNode] = pattern[1];
	if (litNode[0] === NodeTypes.Num) return literal(litNode[1]);
	if (litNode[0] === NodeTypes.Str) return literal(litNode[1]);
	if (litNode[0] === NodeTypes.Atom) return atom(litNode[1]);
	if (litNode[0] === NodeTypes.Reserved) {
		if (litNode[1] === "true") return literal(true);
		if (litNode[1] === "false") return literal(false);
		if (litNode[1] === "null") return NullType;
	}
	return null;
}
function matchedObjectPatternType(pattern, candidateType) {
	const [rawEntries] = pattern[1];
	const fieldsObj = objectBindingFieldsAsRecord(rawEntries);
	if (candidateType.tag === "Unknown" || candidateType.tag === "Var") return candidateType;
	if (candidateType.tag === "Union") {
		const narrowedMembers = candidateType.members.filter((member) => member.tag === "Record" && recordTypeSupportsMatchPattern(fieldsObj, member)).map((member) => narrowRecordTypeForMatchPattern(fieldsObj, member)).filter((member) => member.tag !== "Never");
		return narrowedMembers.length === 0 ? Never : simplify(union(...narrowedMembers));
	}
	return candidateType.tag === "Record" && recordTypeSupportsMatchPattern(fieldsObj, candidateType) ? narrowRecordTypeForMatchPattern(fieldsObj, candidateType) : Never;
}
function matchedArrayPatternType(pattern, candidateType) {
	const [elements] = pattern[1];
	const explicitVariants = explicitArrayPatternVariants(elements);
	if (explicitVariants.length > 1) {
		const matchedVariants = explicitVariants.map((variant) => matchedExplicitArrayPatternType(variant, candidateType)).filter((member) => member.tag !== "Never");
		return matchedVariants.length === 0 ? Never : simplify(union(...matchedVariants));
	}
	return matchedExplicitArrayPatternType(elements, candidateType);
}
function matchedExplicitArrayPatternType(elements, candidateType) {
	if (candidateType.tag === "Unknown" || candidateType.tag === "Var") return candidateType;
	if (candidateType.tag === "Union") {
		const narrowedMembers = candidateType.members.filter((member) => arrayTypeSupportsMatchPattern(elements, member)).map((member) => narrowArrayLikeTypeForMatchPattern(elements, member)).filter((member) => member.tag !== "Never");
		return narrowedMembers.length === 0 ? Never : simplify(union(...narrowedMembers));
	}
	if ((candidateType.tag === "Array" || candidateType.tag === "Tuple" || candidateType.tag === "Sequence") && arrayTypeSupportsMatchPattern(elements, candidateType)) return narrowArrayLikeTypeForMatchPattern(elements, candidateType);
	return Never;
}
function explicitArrayPatternVariants(elements) {
	if (arrayPatternRestIndex(elements) !== -1) return [elements];
	const variantLengths = [];
	const minLength = arrayPatternLengthInterval(elements).minLength;
	for (let length = minLength; length <= elements.length; length++) {
		if (elements.slice(length).some((element) => element && !patternHasDefault(element))) continue;
		variantLengths.push(length);
	}
	if (variantLengths.length <= 1) return [elements];
	return variantLengths.map((length) => elements.slice(0, length).map((element) => element ? stripPatternDefault(element) : element));
}
function stripPatternDefault(pattern) {
	const payload = pattern[1];
	if (!Array.isArray(payload) || payload.length < 2 || payload[1] === void 0) return pattern;
	return [
		pattern[0],
		[payload[0], void 0],
		pattern[2]
	];
}
function narrowRecordTypeForMatchPattern(fieldsObj, type) {
	const narrowedFields = new Map(type.fields);
	for (const [fieldName, fieldPattern] of Object.entries(fieldsObj)) {
		const fieldType = type.fields.get(fieldName);
		if (fieldType === void 0) {
			if (!type.open && !patternHasDefault(fieldPattern)) return Never;
			continue;
		}
		const narrowedFieldType = matchedTypeForPattern(fieldPattern, fieldType);
		if (narrowedFieldType.tag === "Never") return Never;
		narrowedFields.set(fieldName, narrowedFieldType);
	}
	return rebuildRecord(type, narrowedFields);
}
function narrowArrayLikeTypeForMatchPattern(elements, type) {
	const seq = toSequenceType(type);
	if (!seq) return Never;
	const patternInterval = arrayPatternLengthInterval(elements);
	const minLength = Math.max(seq.minLength, patternInterval.minLength);
	const maxLength = minOptionalLength(seq.maxLength, patternInterval.maxLength);
	if (maxLength !== void 0 && minLength > maxLength) return Never;
	const restIndex = arrayPatternRestIndex(elements);
	const requiredPrefixLength = restIndex === -1 ? elements.length : restIndex;
	const prefixLength = Math.max(seq.prefix.length, requiredPrefixLength);
	const narrowedPrefix = Array.from({ length: prefixLength }, (_, index) => sequenceElementAt(seq, index));
	for (let i = 0; i < elements.length; i++) {
		const elementPattern = elements[i];
		if (!elementPattern || elementPattern[0] === "rest") continue;
		if (minLength <= i && patternHasDefault(elementPattern)) continue;
		const narrowedElementType = matchedTypeForPattern(elementPattern, sequenceElementAt(seq, i));
		if (narrowedElementType.tag === "Never") return Never;
		if (i < narrowedPrefix.length) narrowedPrefix[i] = narrowedElementType;
	}
	return simplify(sequence(narrowedPrefix, restIndex === -1 ? Never : seq.rest, minLength, maxLength));
}
function intersectMatchTypes(left, right) {
	const expandedLeft = simplify(expandType(left));
	const expandedRight = simplify(expandType(right));
	if (expandedLeft.tag === "Never" || expandedRight.tag === "Never") return Never;
	if (expandedLeft.tag === "Unknown") return expandedRight;
	if (expandedRight.tag === "Unknown") return expandedLeft;
	if (areMatchTypesDisjoint(expandedLeft, expandedRight)) return Never;
	if (isSubtype(expandedLeft, expandedRight)) return expandedLeft;
	if (isSubtype(expandedRight, expandedLeft)) return expandedRight;
	if (expandedLeft.tag === "Union") return simplify(union(...expandedLeft.members.map((member) => intersectMatchTypes(member, expandedRight)).filter((member) => member.tag !== "Never")));
	if (expandedRight.tag === "Union") return simplify(union(...expandedRight.members.map((member) => intersectMatchTypes(expandedLeft, member)).filter((member) => member.tag !== "Never")));
	if (expandedLeft.tag === "Record" && expandedRight.tag === "Record") return simplify(intersectRecords(expandedLeft, expandedRight));
	return simplify(inter(expandedLeft, expandedRight));
}
/**
* Structural intersection of two record types. Field-by-field: a field
* present in both becomes the intersection of its types; a field present
* in only one is carried over only if the OTHER record is open. The
* result is closed iff at least one input is closed. `optionalFields`
* is preserved only when BOTH sides agree the field is optional —
* otherwise the side that requires the field wins.
*/
function intersectRecords(a, b) {
	const fields = /* @__PURE__ */ new Map();
	const optionalFields = /* @__PURE__ */ new Set();
	const allKeys = new Set([...a.fields.keys(), ...b.fields.keys()]);
	for (const k of allKeys) {
		const av = a.fields.get(k);
		const bv = b.fields.get(k);
		const aOpt = a.optionalFields?.has(k) ?? false;
		const bOpt = b.optionalFields?.has(k) ?? false;
		if (av && bv) {
			const intersected = intersectMatchTypes(av, bv);
			if (intersected.tag === "Never") {
				if (aOpt && bOpt) continue;
				return Never;
			}
			fields.set(k, intersected);
			if (aOpt && bOpt) optionalFields.add(k);
		} else if (av) {
			if (!b.open) {
				if (aOpt) continue;
				return Never;
			}
			fields.set(k, av);
			if (aOpt) optionalFields.add(k);
		} else if (bv) {
			if (!a.open) {
				if (bOpt) continue;
				return Never;
			}
			fields.set(k, bv);
			if (bOpt) optionalFields.add(k);
		}
	}
	const out = {
		tag: "Record",
		fields,
		open: a.open && b.open
	};
	if (optionalFields.size > 0) out.optionalFields = optionalFields;
	return out;
}
function areMatchTypesDisjoint(left, right) {
	if (left.tag === "Never" || right.tag === "Never") return true;
	if (left.tag === "Literal" && right.tag === "Literal") return left.value !== right.value;
	if (left.tag === "Atom" && right.tag === "Atom") return left.name !== right.name;
	if (left.tag === "Literal" && right.tag === "Primitive") return !isSubtype(left, right);
	if (left.tag === "Primitive" && right.tag === "Literal") return !isSubtype(right, left);
	if (left.tag === "Union") return left.members.every((member) => areMatchTypesDisjoint(member, right));
	if (right.tag === "Union") return right.members.every((member) => areMatchTypesDisjoint(left, member));
	if (left.tag === "Tuple" && right.tag === "Tuple") {
		if (left.elements.length !== right.elements.length) return true;
		return left.elements.some((element, index) => areMatchTypesDisjoint(element, right.elements[index]));
	}
	const leftSequence = toSequenceType(left);
	const rightSequence = toSequenceType(right);
	if (leftSequence && rightSequence) return areSequenceMatchTypesDisjoint(leftSequence, rightSequence);
	if (left.tag === "Record" && right.tag === "Record") {
		for (const [key, leftField] of left.fields) {
			const rightField = right.fields.get(key);
			if (rightField && areMatchTypesDisjoint(leftField, rightField)) return true;
		}
		for (const key of left.fields.keys()) if (!right.fields.has(key) && !right.open) return true;
		for (const key of right.fields.keys()) if (!left.fields.has(key) && !left.open) return true;
	}
	return false;
}
function normalizeTrackableMatchSpace(type) {
	const expanded = simplify(expandTypeForMatchAnalysis(type));
	if (expanded.tag === "Primitive" && expanded.name === "Boolean") return union(literal(true), literal(false));
	if (expanded.tag === "Union") return simplify(union(...expanded.members.map(normalizeTrackableMatchSpace)));
	return expanded;
}
/**
* A clause's pattern is a "catchall" for the post-loop exhaustiveness
* check iff it's a wildcard (`case _`) or a bare symbol (`case name`).
* Destructuring (`case [a, b]`, `case {x}`) and literal (`case 5`)
* patterns constrain shape and are never catchalls. The caller pairs
* this with the clause's guard: a guard the fold pass reduces to
* `Literal(true)` is treated as no guard.
*
* Top-level `rest` patterns (`case ...x`) are also runtime catchalls
* but they're handled via the structural-subtract path, not this flag:
* `matchedTypeForPattern` returns the whole remaining type as
* `consumedType`, so `remainingType` reaches `Never` after the subtract
* and the exhaustiveness check accepts via the `remainingType.tag ===
* 'Never'` arm without consulting `sawCatchall`.
*/
function isCatchallPattern(pattern) {
	const kind = pattern[0];
	return kind === "wildcard" || kind === "symbol";
}
function isTrackableMatchRemainder(type) {
	const expanded = simplify(expandTypeForMatchAnalysis(type));
	if (expanded.tag === "Literal" || expanded.tag === "Atom" || expanded.tag === "Primitive" && expanded.name === "Null") return true;
	if (expanded.tag === "Tuple") return expanded.elements.every((member) => isTrackableMatchRemainder(member));
	if (expanded.tag === "Sequence") return expanded.rest.tag === "Never" && expanded.prefix.every((member) => isTrackableMatchRemainder(member));
	if (expanded.tag === "Record") return [...expanded.fields.values()].every((member) => isTrackableMatchRemainder(member));
	return expanded.tag === "Union" && expanded.members.every((member) => isTrackableMatchRemainder(member));
}
function shouldWarnRedundantMatchCase(pattern, remainingType, overallMatchType) {
	if (simplify(expandTypeForMatchAnalysis(remainingType)).tag === "Never") return true;
	const patternType = pattern[0];
	if (patternType === "literal" || patternType === "symbol" || patternType === "wildcard") return true;
	if (patternType === "array" || patternType === "object") return matchedTypeForPattern(pattern, overallMatchType).tag !== "Never";
	return false;
}
/**
* Subtract one type from another: remainingType \ narrowedType.
* Used for exhaustiveness checking — each match clause subtracts
* its pattern type from the remaining unmatched type.
*/
function subtractType(from, subtract) {
	const expandedFrom = simplify(expandTypeForMatchAnalysis(from));
	const expandedSubtract = simplify(expandTypeForMatchAnalysis(subtract));
	if (expandedSubtract.tag === "Never") return expandedFrom;
	if (expandedFrom.tag === "Never") return Never;
	if (isSubtype(expandedFrom, expandedSubtract)) return Never;
	if (expandedSubtract.tag === "Union") return simplify(expandedSubtract.members.reduce((remaining, member) => subtractType(remaining, member), expandedFrom));
	if (expandedFrom.tag === "Union") {
		const remaining = expandedFrom.members.map((member) => subtractType(member, expandedSubtract)).filter((member) => member.tag !== "Never");
		return remaining.length === 0 ? Never : simplify(union(...remaining));
	}
	if (expandedFrom.tag === "Record" && expandedSubtract.tag === "Record") return subtractRecordProductType(expandedFrom, expandedSubtract);
	const fromSequence = toSequenceType(expandedFrom);
	const subtractSequence = toSequenceType(expandedSubtract);
	if (fromSequence && subtractSequence) return subtractSequenceProductType(fromSequence, subtractSequence);
	if (typeEquals(expandedFrom, expandedSubtract)) return Never;
	return expandedFrom;
}
function subtractRecordProductType(from, subtract) {
	if (!isSubtype(subtract, from)) return from;
	const constrainedKeys = [...subtract.fields.keys()].filter((key) => from.fields.has(key));
	if (constrainedKeys.length === 0) return from;
	const branches = [];
	const exactPrefix = /* @__PURE__ */ new Map();
	for (const key of constrainedKeys) {
		const fromField = from.fields.get(key);
		const subtractField = subtract.fields.get(key);
		const exactField = intersectMatchTypes(fromField, subtractField);
		const remainderField = subtractType(fromField, subtractField);
		if (remainderField.tag !== "Never") {
			const branchFields = new Map(from.fields);
			for (const [prefixKey, prefixType] of exactPrefix) branchFields.set(prefixKey, prefixType);
			branchFields.set(key, remainderField);
			branches.push(rebuildRecord(from, branchFields));
		}
		if (exactField.tag === "Never") return branches.length === 0 ? Never : simplify(union(...branches));
		exactPrefix.set(key, exactField);
	}
	return branches.length === 0 ? Never : simplify(union(...branches));
}
function subtractSequenceProductType(from, subtract) {
	if (!isSubtype(subtract, from)) return from;
	const overlapMin = Math.max(from.minLength, subtract.minLength);
	const overlapMax = minOptionalLength(from.maxLength, subtract.maxLength);
	if (overlapMax !== void 0 && overlapMin > overlapMax) return from;
	const branches = [];
	if (from.minLength < overlapMin) branches.push(simplify(sequence([...from.prefix], from.rest, from.minLength, overlapMin - 1)));
	if (overlapMax !== void 0 && (from.maxLength === void 0 || overlapMax < from.maxLength)) branches.push(simplify(sequence([...from.prefix], from.rest, overlapMax + 1, from.maxLength)));
	const prefixLimit = Math.max(from.prefix.length, subtract.prefix.length);
	const exactPrefix = [];
	for (let index = 0; index < prefixLimit; index++) {
		if (overlapMax !== void 0 && overlapMax <= index) break;
		const fromElement = sequenceElementAt(from, index);
		const subtractElement = sequenceElementAt(subtract, index);
		const exactElement = intersectMatchTypes(fromElement, subtractElement);
		const remainderElement = subtractType(fromElement, subtractElement);
		if (remainderElement.tag !== "Never") {
			const branchPrefix = Array.from({ length: Math.max(prefixLimit, index + 1) }, (_, prefixIndex) => {
				if (prefixIndex < exactPrefix.length) return exactPrefix[prefixIndex];
				if (prefixIndex === index) return remainderElement;
				return sequenceElementAt(from, prefixIndex);
			});
			branches.push(simplify(sequence(branchPrefix, from.rest, Math.max(overlapMin, index + 1), overlapMax)));
		}
		if (exactElement.tag === "Never") return branches.length === 0 ? Never : simplify(union(...branches.filter((branch) => branch.tag !== "Never")));
		exactPrefix.push(exactElement);
	}
	return branches.length === 0 ? Never : simplify(union(...branches.filter((branch) => branch.tag !== "Never")));
}
/**
* Bind a let-binding pattern to a type in the environment.
* Binding targets are: ["symbol", [nameNode, default], id]
*                      ["object", [fields, default], id]
*                      ["array", [elements, default], id]
*                      ["rest", [name, default], id]
*/
function bindPattern(pattern, type, env, ctx, typeMap) {
	const patternType = pattern[0];
	const expandedType = expandType(type);
	switch (patternType) {
		case "symbol": {
			const [nameNode] = pattern[1];
			const name = nameNode[1];
			env.bind(name, type);
			const nameNodeId = nameNode[2];
			if (typeMap && nameNodeId > 0) typeMap.set(nameNodeId, type);
			break;
		}
		case "object": {
			const [rawEntries] = pattern[1];
			const fieldsObj = objectBindingFieldsAsRecord(rawEntries);
			for (const [fieldName, fieldPattern] of Object.entries(fieldsObj)) if (ctx) {
				const fieldVar = ctx.freshVar();
				constrain(ctx, type, {
					tag: "Record",
					fields: new Map([[fieldName, fieldVar]]),
					open: true
				});
				bindPattern(fieldPattern, fieldVar, env, ctx, typeMap);
			} else bindPattern(fieldPattern, Unknown, env, void 0, typeMap);
			break;
		}
		case "array": {
			const [elements] = pattern[1];
			for (let i = 0; i < elements.length; i++) {
				const elem = elements[i];
				if (!elem) continue;
				if (elem[0] === "rest") {
					const [restName] = elem[1];
					if (restName && restName !== "rest") if (expandedType.tag === "Array" || expandedType.tag === "Tuple" || expandedType.tag === "Sequence") env.bind(restName, getArrayPatternRestType(expandedType, i));
					else env.bind(restName, type);
				} else if (ctx) {
					const elemVar = ctx.freshVar();
					if (expandedType.tag === "Array" || expandedType.tag === "Tuple" || expandedType.tag === "Sequence") constrain(ctx, getArrayElementPatternType(expandedType, i, elem), elemVar);
					bindPattern(elem, elemVar, env, ctx, typeMap);
				} else if (expandedType.tag === "Array" || expandedType.tag === "Tuple" || expandedType.tag === "Sequence") bindPattern(elem, getArrayElementPatternType(expandedType, i, elem), env, void 0, typeMap);
				else bindPattern(elem, Unknown, env, void 0, typeMap);
			}
			break;
		}
		case "rest": {
			const [restName] = pattern[1];
			if (restName) env.bind(restName, type);
			break;
		}
		default: break;
	}
}
function inferCollectionCall(calleeNode, argTypes, ctx, env, typeMap) {
	if (calleeNode[0] !== NodeTypes.Builtin) return null;
	const builtinName = calleeNode[1];
	if (lookupShadowedBuiltin(env, builtinName)) return null;
	switch (builtinName) {
		case "map": return inferCollectionMapCall(calleeNode, argTypes, ctx, typeMap);
		case "reduce": return inferCollectionReduceCall(calleeNode, argTypes, ctx, typeMap);
		default: return null;
	}
}
function inferCollectionMapCall(calleeNode, argTypes, ctx, typeMap) {
	if (argTypes.length < 2) return null;
	const functionType = argTypes[argTypes.length - 1];
	const collectionTypes = argTypes.slice(0, -1).map((type) => expandType(type));
	if (collectionTypes.length === 0) return null;
	if (collectionTypes.every(isStringCollectionType)) {
		constrain(ctx, functionType, fn(new Array(collectionTypes.length).fill(StringType), StringType));
		if (calleeNode[2] > 0) typeMap.set(calleeNode[2], fn([...collectionTypes, fn(new Array(collectionTypes.length).fill(StringType), StringType)], StringType));
		return StringType;
	}
	if (collectionTypes.every(isArrayCollectionType)) {
		const callbackParamTypes = collectionTypes.map(collectionElementType);
		const callbackRet = ctx.freshVar();
		constrain(ctx, functionType, fn(callbackParamTypes, callbackRet));
		const result = array(callbackRet);
		if (calleeNode[2] > 0) typeMap.set(calleeNode[2], fn([...collectionTypes, fn(callbackParamTypes, callbackRet)], result));
		return result;
	}
	if (collectionTypes.some((type) => type.tag !== "Record")) return null;
	const records = collectionTypes;
	assertCompatibleClosedRecordKeys(records);
	const callbackParamTypes = records.map(collectionValueType);
	const callbackRet = ctx.freshVar();
	constrain(ctx, functionType, fn(callbackParamTypes, callbackRet));
	const result = buildMappedRecordResult(records, callbackRet);
	const calleeNodeId = calleeNode[2];
	if (calleeNodeId > 0) typeMap.set(calleeNodeId, fn([...records, fn(callbackParamTypes, callbackRet)], result));
	return result;
}
function inferCollectionReduceCall(calleeNode, argTypes, ctx, typeMap) {
	if (argTypes.length !== 3) return null;
	const collectionType = expandType(argTypes[0]);
	const reducerType = argTypes[1];
	const initialType = argTypes[2];
	let valueType;
	if (isStringCollectionType(collectionType)) valueType = StringType;
	else if (isArrayCollectionType(collectionType)) valueType = collectionElementType(collectionType);
	else if (collectionType.tag === "Record") valueType = collectionValueType(collectionType);
	else return null;
	const accType = ctx.freshVar();
	constrain(ctx, initialType, accType);
	constrain(ctx, reducerType, fn([accType, valueType], accType));
	const calleeNodeId = calleeNode[2];
	if (calleeNodeId > 0) typeMap.set(calleeNodeId, fn([
		collectionType,
		fn([accType, valueType], accType),
		initialType
	], accType));
	return accType;
}
function collectionValueType(rec) {
	const fieldTypes = [...rec.fields.values()];
	return fieldTypes.length === 0 ? Unknown : union(...fieldTypes);
}
function collectionElementType(type) {
	if (type.tag === "Array") return type.element;
	if (type.tag === "Tuple") return type.elements.length === 0 ? Unknown : union(...type.elements);
	return sequenceElementType(type);
}
function isStringCollectionType(type) {
	return type.tag === "Primitive" && type.name === "String" || type.tag === "Literal" && typeof type.value === "string";
}
function isArrayCollectionType(type) {
	return type.tag === "Array" || type.tag === "Tuple" || type.tag === "Sequence";
}
function assertCompatibleClosedRecordKeys(records) {
	const closedRecords = records.filter((record) => !record.open);
	if (closedRecords.length < 2) return;
	const expectedKeys = [...closedRecords[0].fields.keys()].sort();
	for (const record of closedRecords.slice(1)) {
		const keys = [...record.fields.keys()].sort();
		if (expectedKeys.length !== keys.length || expectedKeys.some((key, index) => key !== keys[index])) throw new TypeInferenceError(`All objects must have the same keys. Expected: ${expectedKeys.join(", ")}. Found: ${keys.join(", ")}`);
	}
}
function buildMappedRecordResult(records, valueType) {
	if (!records.every((record) => !record.open)) return {
		tag: "Record",
		fields: /* @__PURE__ */ new Map(),
		open: true
	};
	const keys = [...records[0].fields.keys()];
	return {
		tag: "Record",
		fields: new Map(keys.map((key) => [key, valueType])),
		open: false
	};
}
function bindMatchCasePattern(pattern, type, env, ctx, typeMap) {
	const patternType = pattern[0];
	const expandedType = expandType(type);
	if (type.tag === "Var" && expandedType.tag === "Never") {
		bindUnknownPattern(pattern, env, typeMap);
		return true;
	}
	if (expandedType.tag === "Unknown") {
		bindUnknownPattern(pattern, env, typeMap);
		return true;
	}
	switch (patternType) {
		case "symbol": {
			const [nameNode, defaultNode] = pattern[1];
			const name = nameNode[1];
			const bindType = getPatternBindingType(type, defaultNode, env, ctx, typeMap);
			env.bind(name, bindType);
			const nameNodeId = nameNode[2];
			if (nameNodeId > 0) typeMap.set(nameNodeId, bindType);
			return true;
		}
		case "wildcard":
		case "literal": return true;
		case "rest": {
			const [restName] = pattern[1];
			if (restName) env.bind(restName, type);
			return true;
		}
		case "object": {
			const [rawEntries] = pattern[1];
			const fieldsObj = objectBindingFieldsAsRecord(rawEntries);
			if (expandedType.tag === "Union") {
				const compatibleMembers = expandedType.members.filter((member) => member.tag === "Record" && recordTypeSupportsMatchPattern(fieldsObj, member));
				if (compatibleMembers.length === 0) return false;
				for (const [fieldName, fieldPattern] of Object.entries(fieldsObj)) if (!bindMatchCasePattern(fieldPattern, mergePatternTypes(compatibleMembers.map((member) => getRecordFieldPatternType(member, fieldName, fieldPattern))), env, ctx, typeMap)) return false;
				return true;
			}
			if (expandedType.tag !== "Record" || !recordTypeSupportsMatchPattern(fieldsObj, expandedType)) return false;
			for (const [fieldName, fieldPattern] of Object.entries(fieldsObj)) if (!bindMatchCasePattern(fieldPattern, getRecordFieldPatternType(expandedType, fieldName, fieldPattern), env, ctx, typeMap)) return false;
			return true;
		}
		case "array": {
			const [elements] = pattern[1];
			if (expandedType.tag === "Union") {
				const compatibleMembers = expandedType.members.filter((member) => arrayTypeSupportsMatchPattern(elements, member));
				if (compatibleMembers.length === 0) return false;
				for (let i = 0; i < elements.length; i++) {
					const elementPattern = elements[i];
					if (!elementPattern) continue;
					if (elementPattern[0] === "rest") {
						if (!bindMatchCasePattern(elementPattern, mergePatternTypes(compatibleMembers.map((member) => getArrayPatternRestType(member, i))), env, ctx, typeMap)) return false;
						continue;
					}
					if (!bindMatchCasePattern(elementPattern, mergePatternTypes(compatibleMembers.map((member) => getArrayElementPatternType(member, i, elementPattern))), env, ctx, typeMap)) return false;
				}
				return true;
			}
			if (!arrayTypeSupportsMatchPattern(elements, expandedType)) return false;
			if (expandedType.tag !== "Array" && expandedType.tag !== "Tuple" && expandedType.tag !== "Sequence") return false;
			for (let i = 0; i < elements.length; i++) {
				const elementPattern = elements[i];
				if (!elementPattern) continue;
				if (elementPattern[0] === "rest") {
					if (!bindMatchCasePattern(elementPattern, getArrayPatternRestType(expandedType, i), env, ctx, typeMap)) return false;
					continue;
				}
				if (!bindMatchCasePattern(elementPattern, getArrayElementPatternType(expandedType, i, elementPattern), env, ctx, typeMap)) return false;
			}
			return true;
		}
		default: return true;
	}
}
function bindUnknownPattern(pattern, env, typeMap) {
	switch (pattern[0]) {
		case "symbol": {
			const [nameNode] = pattern[1];
			const name = nameNode[1];
			env.bind(name, Unknown);
			const nameNodeId = nameNode[2];
			if (nameNodeId > 0) typeMap.set(nameNodeId, Unknown);
			break;
		}
		case "object": {
			const [rawEntries] = pattern[1];
			const fieldsObj = objectBindingFieldsAsRecord(rawEntries);
			for (const fieldPattern of Object.values(fieldsObj)) bindUnknownPattern(fieldPattern, env, typeMap);
			break;
		}
		case "array": {
			const [elements] = pattern[1];
			for (const elementPattern of elements) if (elementPattern) bindUnknownPattern(elementPattern, env, typeMap);
			break;
		}
		case "rest": {
			const [restName] = pattern[1];
			if (restName) env.bind(restName, Unknown);
			break;
		}
		default: break;
	}
}
function mergePatternTypes(types) {
	if (types.length === 0) return Unknown;
	return types.length === 1 ? types[0] : union(...types);
}
function getPatternBindingType(type, defaultNode, env, ctx, typeMap) {
	if (!defaultNode) return type;
	const defaultType = inferExpr(defaultNode, ctx, env, typeMap);
	if (type.tag === "Never") return defaultType;
	return union(type, defaultType);
}
function patternHasDefault(pattern) {
	const payload = pattern[1];
	return Array.isArray(payload) && payload[1] !== void 0;
}
function getRecordFieldPatternType(type, fieldName, pattern) {
	const fieldType = type.fields.get(fieldName);
	if (fieldType !== void 0) return fieldType;
	if (!type.open && patternHasDefault(pattern)) return Never;
	return Unknown;
}
function getArrayElementPatternType(type, index, pattern) {
	if (type.tag === "Array") return type.element;
	if (type.tag === "Tuple") {
		const elementType = type.elements[index];
		if (elementType !== void 0) return elementType;
		if (patternHasDefault(pattern)) return Never;
		return Unknown;
	}
	if (sequenceMayHaveIndex(type, index)) return sequenceElementAt(type, index);
	return patternHasDefault(pattern) ? Never : Unknown;
}
function getArrayPatternRestType(type, index) {
	if (type.tag === "Array") return type;
	if (type.tag === "Tuple") return tuple(type.elements.slice(index));
	return simplify(sequence(type.prefix.slice(index), type.rest, Math.max(0, type.minLength - index), type.maxLength !== void 0 ? Math.max(0, type.maxLength - index) : void 0));
}
function arrayPatternRestIndex(elements) {
	return elements.findIndex((element) => element && element[0] === "rest");
}
function arrayPatternLengthInterval(elements) {
	const restIndex = arrayPatternRestIndex(elements);
	const minLength = elements.reduce((count, elementPattern, index) => {
		if (!elementPattern || elementPattern[0] === "rest" || patternHasDefault(elementPattern)) return count;
		return index + 1;
	}, 0);
	if (restIndex !== -1) return { minLength };
	return {
		minLength,
		maxLength: elements.length
	};
}
function minOptionalLength(left, right) {
	if (left === void 0) return right;
	if (right === void 0) return left;
	return Math.min(left, right);
}
function recordTypeSupportsMatchPattern(fieldsObj, type) {
	for (const [fieldName, fieldPattern] of Object.entries(fieldsObj)) if (!type.fields.has(fieldName) && !type.open && !patternHasDefault(fieldPattern)) return false;
	return true;
}
function arrayTypeSupportsMatchPattern(elements, type) {
	const seq = toSequenceType(type);
	if (!seq) return false;
	const patternInterval = arrayPatternLengthInterval(elements);
	const overlapMin = Math.max(seq.minLength, patternInterval.minLength);
	const overlapMax = minOptionalLength(seq.maxLength, patternInterval.maxLength);
	return overlapMax === void 0 || overlapMin <= overlapMax;
}
/**
* For hover, prefer concrete destructured field/element types when they can be
* recovered from the bound value's expanded shape.
*/
function recordConcretePatternTypes(pattern, type, typeMap) {
	const concreteType = expandType(type);
	switch (pattern[0]) {
		case "symbol": {
			const [nameNode] = pattern[1];
			const nameNodeId = nameNode[2];
			if (nameNodeId > 0 && concreteType.tag !== "Never" && concreteType.tag !== "Unknown") typeMap.set(nameNodeId, concreteType);
			break;
		}
		case "object": {
			if (concreteType.tag !== "Record") return;
			const [rawEntries] = pattern[1];
			const fieldsObj = objectBindingFieldsAsRecord(rawEntries);
			for (const [fieldName, fieldPattern] of Object.entries(fieldsObj)) {
				const fieldType = concreteType.fields.get(fieldName);
				if (fieldType) recordConcretePatternTypes(fieldPattern, fieldType, typeMap);
			}
			break;
		}
		case "array": {
			const [elements] = pattern[1];
			for (let i = 0; i < elements.length; i++) {
				const elem = elements[i];
				if (!elem || elem[0] === "rest") continue;
				if (concreteType.tag === "Tuple" && i < concreteType.elements.length) recordConcretePatternTypes(elem, concreteType.elements[i], typeMap);
				else if (concreteType.tag === "Array") recordConcretePatternTypes(elem, concreteType.element, typeMap);
				else if (concreteType.tag === "Sequence" && sequenceMayHaveIndex(concreteType, i)) recordConcretePatternTypes(elem, sequenceElementAt(concreteType, i), typeMap);
			}
			break;
		}
		default: break;
	}
}
/**
* Walk a destructuring pattern alongside its literal value AST to propagate
* positional types from the value's inferred types into the pattern bindings.
*
* Supported AST forms:
* - symbol: ["symbol", [nameNode, defaultNode | null], id]
* - object: ["object", [fieldsObj, restNode | undefined], id]  (value must be NodeTypes.Object)
* - array:  ["array",  [elements[], restNode | undefined], id] (value must be NodeTypes.Array)
*
* Spread elements and computed keys in the value AST are silently skipped.
*/
function recordLiteralPatternTypes(pattern, valueNode, typeMap) {
	switch (pattern[0]) {
		case "symbol": {
			const [nameNode] = pattern[1];
			const bindingType = typeMap.get(valueNode[2]);
			if (bindingType && nameNode[2] > 0) typeMap.set(nameNode[2], bindingType);
			break;
		}
		case "object": {
			if (valueNode[0] !== NodeTypes.Object) return;
			const [rawEntries] = pattern[1];
			const fieldsObj = objectBindingFieldsAsRecord(rawEntries);
			const entries = valueNode[1];
			const fieldNodes = /* @__PURE__ */ new Map();
			for (const entry of entries) {
				if (!Array.isArray(entry) || entry.length !== 2) continue;
				const [keyNode, fieldValueNode] = entry;
				const keyName = keyNode[0] === NodeTypes.Str ? keyNode[1] : keyNode[0] === NodeTypes.Sym ? keyNode[1] : String(keyNode[1]);
				fieldNodes.set(keyName, fieldValueNode);
			}
			for (const [fieldName, fieldPattern] of Object.entries(fieldsObj)) {
				const fieldValueNode = fieldNodes.get(fieldName);
				if (fieldValueNode) recordLiteralPatternTypes(fieldPattern, fieldValueNode, typeMap);
			}
			break;
		}
		case "array": {
			if (valueNode[0] !== NodeTypes.Array) return;
			const [elements] = pattern[1];
			const valueElements = valueNode[1];
			for (let i = 0; i < elements.length; i++) {
				const elementPattern = elements[i];
				if (!elementPattern) continue;
				if (elementPattern[0] === "rest") {
					const restTypes = valueElements.slice(i).map((elementNode) => typeMap.get(elementNode[2]) ?? Unknown);
					if (elementPattern[2] > 0) typeMap.set(elementPattern[2], tuple(restTypes));
					continue;
				}
				const elementValueNode = valueElements[i];
				if (elementValueNode) recordLiteralPatternTypes(elementPattern, elementValueNode, typeMap);
			}
			break;
		}
		default: break;
	}
}
function expandTypeForMatchAnalysis(t, visited = /* @__PURE__ */ new Set()) {
	switch (t.tag) {
		case "Var":
			if (visited.has(typeVarIdentity(t))) return Never;
			visited.add(typeVarIdentity(t));
			if (t.lowerBounds.length > 0) return simplify(union(...t.lowerBounds.map((bound) => expandTypeForMatchAnalysis(bound, new Set(visited)))));
			if (t.upperBounds.length > 0) return simplify(inter(...t.upperBounds.map((bound) => expandTypeForMatchAnalysis(bound, new Set(visited)))));
			return Never;
		case "Function": return fn(t.params.map((param) => expandTypeForMatchAnalysis(param, new Set(visited))), expandTypeForMatchAnalysis(t.ret, new Set(visited)), t.effects, t.handlerWrapper, t.restParam !== void 0 ? expandTypeForMatchAnalysis(t.restParam, new Set(visited)) : void 0, t.asserts);
		case "Handler": {
			const handled = /* @__PURE__ */ new Map();
			for (const [name, sig] of t.handled) handled.set(name, {
				argType: expandTypeForMatchAnalysis(sig.argType, new Set(visited)),
				retType: expandTypeForMatchAnalysis(sig.retType, new Set(visited))
			});
			return handlerType(expandTypeForMatchAnalysis(t.body, new Set(visited)), expandTypeForMatchAnalysis(t.output, new Set(visited)), handled, t.introduced);
		}
		case "Record": {
			const fields = /* @__PURE__ */ new Map();
			for (const [key, value] of t.fields) fields.set(key, expandTypeForMatchAnalysis(value, new Set(visited)));
			return rebuildRecord(t, fields);
		}
		case "Array": return array(expandTypeForMatchAnalysis(t.element, new Set(visited)));
		case "Tuple": return tuple(t.elements.map((element) => expandTypeForMatchAnalysis(element, new Set(visited))));
		case "Sequence": return sequence(t.prefix.map((element) => expandTypeForMatchAnalysis(element, new Set(visited))), expandTypeForMatchAnalysis(t.rest, new Set(visited)), t.minLength, t.maxLength);
		case "Union": return simplify(union(...t.members.map((member) => expandTypeForMatchAnalysis(member, new Set(visited)))));
		case "Inter": return simplify(inter(...t.members.map((member) => expandTypeForMatchAnalysis(member, new Set(visited)))));
		case "Neg": return neg(expandTypeForMatchAnalysis(t.inner, new Set(visited)));
		case "Alias": return expandTypeForMatchAnalysis(t.expanded, visited);
		case "Refined": return expandTypeForMatchAnalysis(t.base, visited);
		default: return t;
	}
}
/**
* Expand an effect set at a given polarity: fold any row-var tail's
* concrete bounds into the effect name set.
*
* - Positive polarity: union all transitive lowerBounds (the minimum that
*   the row var must contain). If nothing is known, the tail stays as the
*   row var — preserving polymorphism for display of generalized types.
* - Negative polarity: intersect transitive upperBounds (the maximum). If
*   no upper bound is known, tail becomes Open (any extras allowed).
*
* `visited` tracks row-var ids to prevent infinite recursion through
* var-to-var edges.
*
* @internal Exported for tests that verify row-var expansion behaviour
* directly. Callers in source should use `expandType` which handles the
* effect sets on Function and Handler nodes automatically.
*/
function expandEffectSet(e, polarity) {
	if (e.tail.tag !== "RowVar") return e;
	const ρ = e.tail;
	const visited = /* @__PURE__ */ new Set();
	const accumulated = new Set(e.effects);
	if (polarity === "positive") {
		collectRowVarLowerBounds(ρ, accumulated, visited);
		if (accumulated.size === e.effects.size && ρ.lowerBounds.length === 0 && ρ.lowerVarBounds.length === 0) return e;
		return {
			effects: accumulated,
			tail: ClosedTail
		};
	}
	const upper = collectRowVarUpperBounds(ρ, visited);
	if (upper === null) return {
		effects: new Set(e.effects),
		tail: OpenTail
	};
	const result = new Set(e.effects);
	for (const u of upper) result.add(u);
	return {
		effects: result,
		tail: ClosedTail
	};
}
function collectRowVarLowerBounds(ρ, out, visited) {
	if (visited.has(ρ.id)) return;
	visited.add(ρ.id);
	for (const lb of ρ.lowerBounds) for (const e of lb) out.add(e);
	for (const lv of ρ.lowerVarBounds) collectRowVarLowerBounds(lv, out, visited);
}
/**
* Two-pass intersection: first collect every concrete upper-bound set
* reachable from ρ via the upper-var-bound graph, then intersect them all.
* Splitting the traversal from the folding side-steps the prior ambiguity
* where "cycle-visited" and "no-constraint-contributed" both returned null
* and became indistinguishable to the caller.
*
* Returns null if the transitive closure has no concrete upper bounds at
* all (unconstrained) — this is genuine "no upper bound known" and is
* distinct from "already visited in cycle" (which simply doesn't contribute
* but also doesn't preempt bounds from other paths).
*/
function collectRowVarUpperBounds(ρ, visited) {
	const bounds = [];
	gatherRowVarUpperBounds(ρ, visited, bounds);
	if (bounds.length === 0) return null;
	const intersection = new Set(bounds[0]);
	for (let i = 1; i < bounds.length; i++) {
		const next = bounds[i];
		for (const e of intersection) if (!next.has(e)) intersection.delete(e);
	}
	return intersection;
}
function gatherRowVarUpperBounds(ρ, visited, out) {
	if (visited.has(ρ.id)) return;
	visited.add(ρ.id);
	for (const ub of ρ.upperBounds) out.push(ub);
	for (const uv of ρ.upperVarBounds) gatherRowVarUpperBounds(uv, visited, out);
}
function expandType(t, polarity = "positive", visited = /* @__PURE__ */ new Set()) {
	switch (t.tag) {
		case "Var":
			if (visited.has(typeVarIdentity(t))) return polarity === "positive" ? Never : Unknown;
			visited.add(typeVarIdentity(t));
			if (polarity === "positive") {
				const expanded = t.lowerBounds.map((lb) => expandType(lb, "positive", visited));
				return expanded.length === 0 ? Never : expanded.length === 1 ? expanded[0] : union(...expanded);
			} else {
				const expanded = t.upperBounds.map((ub) => expandType(ub, "negative", visited));
				return expanded.length === 0 ? Unknown : expanded.length === 1 ? expanded[0] : inter(...expanded);
			}
		case "Function": return fn(t.params.map((p) => expandType(p, polarity === "positive" ? "negative" : "positive", new Set(visited))), expandType(t.ret, polarity, new Set(visited)), expandEffectSet(t.effects, polarity), t.handlerWrapper, t.restParam !== void 0 ? expandType(t.restParam, polarity === "positive" ? "negative" : "positive", new Set(visited)) : void 0, t.asserts);
		case "Handler": {
			const handled = /* @__PURE__ */ new Map();
			for (const [name, sig] of t.handled) handled.set(name, {
				argType: expandType(sig.argType, "negative", new Set(visited)),
				retType: expandType(sig.retType, "positive", new Set(visited))
			});
			return handlerType(expandType(t.body, "positive", new Set(visited)), expandType(t.output, "positive", new Set(visited)), handled, expandEffectSet(t.introduced, polarity));
		}
		case "Record": {
			const fields = /* @__PURE__ */ new Map();
			for (const [k, v] of t.fields) fields.set(k, expandType(v, polarity, new Set(visited)));
			return rebuildRecord(t, fields);
		}
		case "Array": return array(expandType(t.element, polarity, new Set(visited)));
		case "Tuple": return tuple(t.elements.map((e) => expandType(e, polarity, new Set(visited))));
		case "Sequence": return sequence(t.prefix.map((member) => expandType(member, polarity, new Set(visited))), expandType(t.rest, polarity, new Set(visited)), t.minLength, t.maxLength);
		case "Union": return union(...t.members.map((m) => expandType(m, polarity, new Set(visited))));
		case "Inter": return {
			tag: "Inter",
			members: t.members.map((m) => expandType(m, polarity, new Set(visited)))
		};
		case "Keyof": return keyofType(expandType(t.inner, polarity, new Set(visited)));
		case "Index": return indexType(expandType(t.target, polarity, new Set(visited)), expandType(t.key, polarity, new Set(visited)));
		case "Refined": {
			const base = expandType(t.base, polarity, new Set(visited));
			return base === t.base ? t : {
				tag: "Refined",
				base,
				binder: t.binder,
				predicate: t.predicate,
				source: t.source
			};
		}
		default: return t;
	}
}
/**
* Expand a type for IDE display. Unlike semantic expansion, this prefers
* readable upper-bound information over `Never` when a variable has no lower
* bounds yet, and reconstructs record shapes from property-access constraints.
*/
function expandTypeForDisplay(t, polarity = "positive", visited = /* @__PURE__ */ new Set()) {
	switch (t.tag) {
		case "Var": {
			if (visited.has(typeVarIdentity(t))) return polarity === "positive" ? Never : Unknown;
			visited.add(typeVarIdentity(t));
			const recordDisplay = synthesizeRecordDisplayType(t, visited);
			if (recordDisplay) return recordDisplay;
			if ((t.displayLowerBounds?.length ?? 0) > 0) return normalizeDisplayCandidates(t.displayLowerBounds.map((lb) => expandTypeForDisplay(lb, "positive", new Set(visited))));
			if ((t.displayUpperBounds?.length ?? 0) > 0) return intersectDisplayCandidates(t.displayUpperBounds.map((ub) => expandTypeForDisplay(ub, "positive", new Set(visited))));
			if (t.lowerBounds.length === 0 && t.upperBounds.length > 0) return intersectDisplayCandidates(t.upperBounds.map((ub) => expandTypeForDisplay(ub, "positive", new Set(visited))));
			if (polarity === "positive") {
				if (t.lowerBounds.length > 0) {
					const expanded = t.lowerBounds.map((lb) => expandTypeForDisplay(lb, "positive", new Set(visited)));
					return expanded.length === 1 ? expanded[0] : union(...expanded);
				}
				if (t.upperBounds.length > 0) {
					const expanded = t.upperBounds.map((ub) => expandTypeForDisplay(ub, "negative", new Set(visited)));
					return expanded.length === 1 ? expanded[0] : inter(...expanded);
				}
				return Unknown;
			}
			const expanded = t.upperBounds.map((ub) => expandTypeForDisplay(ub, "negative", new Set(visited)));
			return expanded.length === 0 ? Unknown : expanded.length === 1 ? expanded[0] : inter(...expanded);
		}
		case "Function": return fn(t.params.map((p) => expandTypeForDisplay(p, polarity === "positive" ? "negative" : "positive", new Set(visited))), expandTypeForDisplay(t.ret, polarity, new Set(visited)), expandEffectSet(t.effects, polarity), t.handlerWrapper, t.restParam !== void 0 ? expandTypeForDisplay(t.restParam, polarity === "positive" ? "negative" : "positive", new Set(visited)) : void 0, t.asserts);
		case "Handler": {
			const handled = /* @__PURE__ */ new Map();
			for (const [name, sig] of t.handled) handled.set(name, {
				argType: expandTypeForDisplay(sig.argType, "negative", new Set(visited)),
				retType: expandTypeForDisplay(sig.retType, "positive", new Set(visited))
			});
			return handlerType(expandTypeForDisplay(t.body, "positive", new Set(visited)), expandTypeForDisplay(t.output, "positive", new Set(visited)), handled, expandEffectSet(t.introduced, polarity));
		}
		case "Record": {
			const fields = /* @__PURE__ */ new Map();
			for (const [name, fieldType] of t.fields) fields.set(name, expandTypeForDisplay(fieldType, "positive", new Set(visited)));
			return rebuildRecord(t, fields);
		}
		case "Array": return array(expandTypeForDisplay(t.element, "positive", new Set(visited)));
		case "Tuple": return tuple(t.elements.map((element) => expandTypeForDisplay(element, "positive", new Set(visited))));
		case "Sequence": return sequence(t.prefix.map((member) => expandTypeForDisplay(member, "positive", new Set(visited))), expandTypeForDisplay(t.rest, "positive", new Set(visited)), t.minLength, t.maxLength);
		case "Union": return normalizeDisplayUnion(t.members.map((member) => expandTypeForDisplay(member, polarity, new Set(visited))));
		case "Inter": return inter(...t.members.map((member) => expandTypeForDisplay(member, polarity, new Set(visited))));
		case "Neg": return neg(expandTypeForDisplay(t.inner, polarity === "positive" ? "negative" : "positive", new Set(visited)));
		case "Alias": return {
			tag: "Alias",
			name: t.name,
			args: t.args.map((arg) => expandTypeForDisplay(arg, "positive", new Set(visited))),
			expanded: expandTypeForDisplay(t.expanded, polarity, new Set(visited))
		};
		case "Recursive": return {
			tag: "Recursive",
			id: t.id,
			body: expandTypeForDisplay(t.body, polarity, new Set(visited))
		};
		case "Refined": return {
			tag: "Refined",
			base: expandTypeForDisplay(t.base, polarity, new Set(visited)),
			binder: t.binder,
			predicate: t.predicate,
			source: t.source
		};
		default: return t;
	}
}
function sanitizeDisplayType(t, nested = false) {
	if (nested && t.tag === "Never") return Unknown;
	switch (t.tag) {
		case "Function": return fn(t.params.map((param) => sanitizeDisplayType(param, true)), sanitizeDisplayType(t.ret, true), t.effects, t.handlerWrapper, t.restParam !== void 0 ? sanitizeDisplayType(t.restParam, true) : void 0, t.asserts);
		case "Handler": {
			const handled = /* @__PURE__ */ new Map();
			for (const [name, sig] of t.handled) handled.set(name, {
				argType: sanitizeDisplayType(sig.argType, true),
				retType: sanitizeDisplayType(sig.retType, true)
			});
			return handlerType(sanitizeDisplayType(t.body, true), sanitizeDisplayType(t.output, true), handled, t.introduced);
		}
		case "Record": {
			const fields = /* @__PURE__ */ new Map();
			for (const [name, fieldType] of t.fields) fields.set(name, sanitizeDisplayType(fieldType, true));
			return rebuildRecord(t, fields);
		}
		case "Array": return array(sanitizeDisplayType(t.element, true));
		case "Tuple": return tuple(t.elements.map((element) => sanitizeDisplayType(element, true)));
		case "Sequence": return sequence(t.prefix.map((member) => sanitizeDisplayType(member, true)), sanitizeDisplayType(t.rest, true), t.minLength, t.maxLength);
		case "Union": return union(...t.members.map((member) => sanitizeDisplayType(member, true)));
		case "Inter": return inter(...t.members.map((member) => sanitizeDisplayType(member, true)));
		case "Neg": return neg(sanitizeDisplayType(t.inner, true));
		case "Alias": return {
			tag: "Alias",
			name: t.name,
			args: t.args.map((arg) => sanitizeDisplayType(arg, true)),
			expanded: sanitizeDisplayType(t.expanded, true)
		};
		case "Recursive": return {
			tag: "Recursive",
			id: t.id,
			body: sanitizeDisplayType(t.body, true)
		};
		default: return t;
	}
}
function normalizeDisplayUnion(members) {
	if (members.length === 0) return Never;
	if (members.every((member) => member.tag === "Record")) {
		const first = members[0];
		const firstKeys = [...first.fields.keys()];
		if (members.every((m) => m.fields.size === firstKeys.length && firstKeys.every((k) => m.fields.has(k)))) {
			let varyingFields = 0;
			for (const key of firstKeys) {
				const firstType = first.fields.get(key);
				if (members.some((m) => !typeEquals(m.fields.get(key), firstType))) varyingFields++;
			}
			if (varyingFields <= 1) {
				const open = members.some((member) => member.open);
				const mergedFields = /* @__PURE__ */ new Map();
				for (const fieldName of firstKeys) {
					const candidates = members.map((m) => m.fields.get(fieldName));
					mergedFields.set(fieldName, normalizeDisplayCandidates(candidates));
				}
				return {
					tag: "Record",
					fields: mergedFields,
					open
				};
			}
		}
	}
	if (members.every((member) => member.tag === "Array")) return array(union(...members.map((member) => member.element)));
	return union(...members);
}
function constrainSequenceSubtype(ctx, lhs, rhs) {
	if (lhs.minLength < rhs.minLength) throw new TypeInferenceError(`Sequence length mismatch: expected minimum ${rhs.minLength}, got ${lhs.minLength}`);
	if (rhs.maxLength !== void 0 && lhs.maxLength === void 0) throw new TypeInferenceError(`Sequence length mismatch: expected maximum ${rhs.maxLength}, got unbounded`);
	if (rhs.maxLength !== void 0 && lhs.maxLength !== void 0 && lhs.maxLength > rhs.maxLength) throw new TypeInferenceError(`Sequence length mismatch: expected maximum ${rhs.maxLength}, got ${lhs.maxLength}`);
	const relevantPrefixLength = Math.max(lhs.prefix.length, rhs.prefix.length);
	for (let index = 0; index < relevantPrefixLength; index++) {
		if (!sequenceMayHaveIndex(lhs, index)) continue;
		constrain(ctx, sequenceElementAt(lhs, index), sequenceElementAt(rhs, index));
	}
	if (sequenceMayHaveIndex(lhs, relevantPrefixLength)) constrain(ctx, lhs.rest, rhs.rest);
}
function areSequenceMatchTypesDisjoint(left, right) {
	const overlapMin = Math.max(left.minLength, right.minLength);
	const overlapMax = minOptionalLength(left.maxLength, right.maxLength);
	if (overlapMax !== void 0 && overlapMin > overlapMax) return true;
	const prefixLimit = Math.max(left.prefix.length, right.prefix.length);
	for (let index = 0; index < prefixLimit; index++) {
		if (!sequenceMayHaveIndex(left, index) || !sequenceMayHaveIndex(right, index)) continue;
		if (areMatchTypesDisjoint(sequenceElementAt(left, index), sequenceElementAt(right, index))) return true;
	}
	return false;
}
function sequenceElementType(type) {
	if (type.prefix.length === 0) return type.rest;
	const members = [...type.prefix];
	if (type.rest.tag !== "Never") members.push(type.rest);
	return members.length === 1 ? members[0] : union(...members);
}
function synthesizeRecordDisplayType(t, visited) {
	const fieldTypes = /* @__PURE__ */ new Map();
	let sawRecordLikeInfo = false;
	let open = false;
	for (const lowerBound of t.lowerBounds) {
		const expanded = expandTypeForDisplay(lowerBound, "positive", new Set(visited));
		if (expanded.tag !== "Record") continue;
		sawRecordLikeInfo = true;
		open = open || expanded.open;
		for (const [fieldName, fieldType] of expanded.fields) {
			const existing = fieldTypes.get(fieldName) ?? [];
			existing.push(fieldType);
			fieldTypes.set(fieldName, existing);
		}
	}
	for (const upperBound of t.upperBounds) {
		if (upperBound.tag === "Record") {
			sawRecordLikeInfo = true;
			open = open || upperBound.open;
			for (const [fieldName, fieldType] of upperBound.fields) {
				const existing = fieldTypes.get(fieldName) ?? [];
				existing.push(expandTypeForDisplay(fieldType, "positive", new Set(visited)));
				fieldTypes.set(fieldName, existing);
			}
			continue;
		}
		if (upperBound.tag !== "Function" || upperBound.params.length !== 1 || upperBound.restParam !== void 0) continue;
		const fieldParam = upperBound.params[0];
		if (!fieldParam || fieldParam.tag !== "Literal" || typeof fieldParam.value !== "string") continue;
		sawRecordLikeInfo = true;
		const existing = fieldTypes.get(fieldParam.value) ?? [];
		existing.push(expandTypeForDisplay(upperBound.ret, "positive", new Set(visited)));
		fieldTypes.set(fieldParam.value, existing);
	}
	if (!sawRecordLikeInfo) return void 0;
	const mergedFields = /* @__PURE__ */ new Map();
	for (const [fieldName, candidates] of fieldTypes) mergedFields.set(fieldName, normalizeDisplayCandidates(candidates));
	return {
		tag: "Record",
		fields: mergedFields,
		open
	};
}
function normalizeDisplayCandidates(candidates) {
	if (candidates.length === 0) return Never;
	if (candidates.length === 1) return candidates[0];
	return normalizeDisplayUnion(candidates);
}
/**
* Combine multiple upper-bound candidates for display. A Var is a
* subtype of every upper bound, so the displayable value is their
* INTERSECTION (`Number & (Number | String)` simplifies to `Number`),
* not their union. Using union here was a bug: an assert-narrowed
* parameter Var picks up the call-site signature's wider arg type
* (e.g. `Number | String` from a multi-overload `assert` builtin) as
* an extra upper bound, and a union display would surface that
* spurious `String` in error messages.
*
* `simplify` collapses the literal `inter(...)` to its tightest
* representation when possible (subset absorption etc.), giving the
* cleanest display for the common case of `[narrow, wider]` bounds.
*/
function intersectDisplayCandidates(candidates) {
	if (candidates.length === 0) return Unknown;
	if (candidates.length === 1) return candidates[0];
	return simplify(inter(...candidates));
}
var TypeInferenceError = class extends Error {
	nodeId;
	severity;
	sourceCodeInfo;
	sourceRange;
	constructor(message, nodeId, severity = "error", sourceCodeInfo, sourceRange) {
		super(message);
		this.name = "TypeInferenceError";
		this.nodeId = nodeId;
		this.severity = severity;
		this.sourceCodeInfo = sourceCodeInfo;
		this.sourceRange = sourceRange;
	}
};
//#endregion
//#region src/shared/typeDisplay.ts
/** Format a Type for hover display: expand → sanitize → simplify → stringify.
* For literal types, appends the base type (e.g. '54 : Number'). */
function formatHoverType(type) {
	const display = typeToString(simplify(sanitizeDisplayType(expandTypeForDisplay(type))));
	if (type.tag === "Literal") return `${display} : ${typeof type.value === "number" ? "Number" : typeof type.value === "string" ? "String" : "Boolean"}`;
	if (type.tag === "Inter") {
		for (const part of type.members) if (part.tag === "Literal") return `${display} : ${typeof part.value === "number" ? "Number" : typeof part.value === "string" ? "String" : "Boolean"}`;
	}
	return display;
}
/**
* Find the Type whose source span covers the (1-based) cursor position.
*
* Picks the smallest covering node. When `preferredRange` is set, an
* additional preference is applied: among covering nodes, prefer the one
* whose start aligns most closely with the preferred range's start (this is
* how the extension biases toward the word-at-cursor over an enclosing call).
*/
function findTypeAtPosition(typeMap, sourceMap, position, preferredRange) {
	if (!sourceMap) return void 0;
	const line = position.line - 1;
	const col = position.column - 1;
	const preferredStartLine = preferredRange ? preferredRange.start.line - 1 : 0;
	const preferredStartCol = preferredRange ? preferredRange.start.column - 1 : 0;
	let bestPreferredType;
	let bestType;
	let bestSize = Infinity;
	for (const [nodeId, type] of typeMap) {
		if (type.tag === "Unknown") continue;
		const sourcePos = sourceMap.get(nodeId);
		if (!sourcePos) continue;
		const [startLine, startCol] = sourcePos.start;
		const [endLine, endCol] = sourcePos.end;
		if (line < startLine || line > endLine) continue;
		if (!((line > startLine || col >= startCol) && (line < endLine || col <= endCol))) continue;
		const size = (endLine - startLine) * 1e3 + (endCol - startCol);
		if (preferredRange) {
			if (startLine !== preferredStartLine || startCol < preferredStartCol || startCol > preferredRange.end.column - 1) continue;
			bestPreferredType = type;
		}
		if (size < bestSize) {
			bestSize = size;
			bestType = type;
		}
	}
	return bestPreferredType ?? bestType;
}
/**
* Find the Type at a definition's location. Equivalent to
* `findTypeAtPosition` with `position` and `preferredRange` both anchored
* at the definition's name span.
*/
function findTypeAtDefinition(typeMap, sourceMap, def) {
	const start = {
		line: def.location.line,
		column: def.location.column
	};
	return findTypeAtPosition(typeMap, sourceMap, start, {
		start,
		end: {
			line: def.location.line,
			column: def.location.column + def.name.length
		}
	});
}
//#endregion
//#region src/prelude.dvala
var prelude_default = "// Dvala standard prelude — refined type aliases auto-loaded into every\n// typecheck session.\n//\n// Aliases declared here are available everywhere without explicit\n// import, like core builtins. They're a starter set covering the most\n// common refinement patterns; users can shadow any of them by writing\n// their own `type X = ...` with the same name in their source file.\n\n// Strictly positive numbers (n > 0). Common precondition for division\n// safety (`x / n`) and quantity-style values where zero is meaningless.\ntype Positive = Number & {n | n > 0};\n\n// Non-negative numbers (n >= 0). Array-indexing precondition; the\n// \"natural numbers\" sense from CS (where 0 ∈ ℕ).\ntype NonNegative = Number & {n | n >= 0};\n\n// Non-zero numbers (n != 0). The minimal precondition for division —\n// `Positive` is stricter than needed when negatives are also fine.\ntype NonZero = Number & {n | n != 0};\n\n// Non-empty sequences. Generic over Array AND String via the Sequence\n// upper bound, so `head(xs: NonEmpty<Array<Number>>)` and\n// `firstChar(s: NonEmpty<String>)` both pass the count(xs) > 0 check.\ntype NonEmpty<T: Sequence> = T & {xs | count(xs) > 0};\n";
//#endregion
//#region src/typechecker/prelude.ts
let cached = null;
/**
* Parse the prelude source on first call, register every type alias
* it declares. Idempotent on the cache; the registration calls are
* cheap Map.set operations.
*
* If parsing the prelude fails (shouldn't — it ships with the
* codebase), emit a `console.warn` and leave the registry alone
* rather than crashing the entire typechecker. The prelude must be
* loud when it breaks, otherwise users see `Positive` etc. resolve
* to "type alias not found" with no clue why.
*
* Cache scope: module-level. Vitest isolates workers per file, so
* each test file gets a fresh instance — no cross-file leakage.
*/
function installPreludeAliases() {
	if (!cached) try {
		const ast = parseToAst(minifyTokenStream(tokenize(prelude_default, false, "<prelude>"), { removeWhiteSpace: true }));
		cached = ast.typeAliases ? [...ast.typeAliases].map(([name, { params, body }]) => ({
			name,
			params,
			body
		})) : [];
	} catch (e) {
		console.warn(`[dvala prelude] failed to parse src/prelude.dvala: ${e instanceof Error ? e.message : String(e)}`);
		cached = [];
	}
	for (const alias of cached) registerTypeAlias(alias.name, alias.params, alias.body);
}
//#endregion
//#region src/ast/expandMacros.ts
/**
* Build-time macro expansion pass.
*
* Walks the entire AST, finds macro definitions (let name = macro ...),
* evaluates them to get macro functions, then expands all calls
* to those macros using macroexpand. The macro definitions are
* removed from the output.
*
* When a fileResolver is provided, also discovers macros imported from
* other files (e.g. let { double } = import("./macros")).
*
* Macros that fail to evaluate are left unexpanded.
*/
function expandMacros(ast, options) {
	const macroDefs = [];
	const importBindings = [];
	collectMacroDefs(ast.body, macroDefs);
	if (options?.fileResolver) collectImportBindings(ast.body, importBindings);
	if (macroDefs.length === 0 && importBindings.length === 0) return ast;
	const createDvala = options?.createDvala;
	if (!createDvala) return ast;
	const dvala = createDvala({
		fileResolver: options?.fileResolver,
		fileResolverBaseDir: options?.fileResolverBaseDir,
		typecheck: false
	});
	const macroFunctions = /* @__PURE__ */ new Map();
	const allNames = [];
	const sourceFragments = [];
	for (const ib of importBindings) {
		sourceFragments.push(prettyPrint(ib.node));
		allNames.push(...ib.names);
	}
	for (const def of macroDefs) {
		sourceFragments.push(prettyPrint(def.node));
		allNames.push(def.name);
	}
	const uniqueNames = [...new Set(allNames)];
	const returnObj = uniqueNames.map((n) => `${n}: ${n}`).join(", ");
	const evalSource = `${sourceFragments.join(";\n")};\n{ ${returnObj} }`;
	try {
		const result = dvala.run(evalSource);
		for (const name of uniqueNames) {
			const fn = result[name];
			if (isMacroFunction(fn)) macroFunctions.set(name, fn);
		}
	} catch {
		return ast;
	}
	if (macroFunctions.size === 0) return ast;
	const positions = ast.sourceMap?.positions;
	return {
		body: processNodes(ast.body, macroFunctions, dvala, positions),
		sourceMap: ast.sourceMap
	};
}
/** Recursively find all let-bound macro definitions in the AST */
function collectMacroDefs(nodes, result) {
	for (const node of nodes) {
		const name = extractMacroDefName(node);
		if (name) result.push({
			name,
			node
		});
		recurseForCollection(node[1], result);
	}
}
function recurseForCollection(payload, result) {
	if (!Array.isArray(payload)) return;
	for (const item of payload) if (isAstNode$1(item)) {
		const name = extractMacroDefName(item);
		if (name) result.push({
			name,
			node: item
		});
		recurseForCollection(item[1], result);
	} else if (Array.isArray(item)) recurseForCollection(item, result);
}
/**
* Collect `let { name, ... } = import("...")` bindings.
* These may bring macros from other files. The actual nodes are kept
* so we can pretty-print and evaluate them to discover which values are macros.
*/
function collectImportBindings(nodes, result) {
	for (const node of nodes) {
		if (node[0] !== NodeTypes.Let) continue;
		const [target, value] = node[1];
		if (!Array.isArray(value) || value[0] !== NodeTypes.Import) continue;
		if (!value[1].startsWith(".")) continue;
		if (!Array.isArray(target) || target[0] !== "object") continue;
		const [entries] = target[1];
		if (!Array.isArray(entries)) continue;
		const names = [];
		for (const entry of entries) if (entry && typeof entry === "object" && "key" in entry) names.push(entry.key);
		if (names.length > 0) result.push({
			names,
			node
		});
	}
}
/** Check if a node is `let <name> = macro ...` and return the name */
function extractMacroDefName(node) {
	if (node[0] !== NodeTypes.Let) return null;
	const [target, value] = node[1];
	if (!Array.isArray(value) || value[0] !== NodeTypes.Macro) return null;
	if (!Array.isArray(target)) return null;
	if (target[0] !== "symbol") return null;
	const symNode = target[1][0];
	if (!Array.isArray(symNode) || symNode[0] !== NodeTypes.Sym) return null;
	return symNode[1];
}
/** Process a list of nodes: expand macro calls, keep definitions (treeshaking removes them later) */
function processNodes(nodes, macros, dvala, positions) {
	return nodes.map((node) => expandNodeRecursive(node, macros, dvala, positions));
}
/** Recursively expand macro calls in an AST node */
function expandNodeRecursive(node, macros, dvala, positions) {
	const [type, payload, nodeId] = node;
	if (type === NodeTypes.Call || type === NodeTypes.MacroCall) {
		const [fnNode, args] = payload;
		if (fnNode[0] === NodeTypes.Sym) {
			const name = fnNode[1];
			const macroFn = macros.get(name);
			if (macroFn) try {
				const argsAsPV = PersistentVector.from(args.map((arg) => fromJS(arg)));
				const expanded = dvala.run("let __m__ = perform(@dvala.host, \"__m__\"); let args = perform(@dvala.host, \"args\"); macroexpand(__m__, ...args)", { effectHandlers: [hostHandler({
					__m__: macroFn,
					args: argsAsPV
				})] });
				if (Array.isArray(expanded) && expanded.length === 3 && typeof expanded[0] === "string") {
					const expandedNode = expandNodeRecursive(expanded, macros, dvala, positions);
					if (positions) {
						const callSitePos = positions.get(nodeId);
						if (callSitePos) stampMissingPositions(expandedNode, callSitePos, positions);
					}
					return expandedNode;
				}
				return node;
			} catch {
				return node;
			}
		}
	}
	if (type === NodeTypes.Block && Array.isArray(payload)) return [
		type,
		processNodes(payload, macros, dvala, positions),
		nodeId
	];
	if (Array.isArray(payload)) return [
		type,
		payload.map((item) => recurseInto(item, macros, dvala, positions)),
		nodeId
	];
	return node;
}
/**
* Walk an expanded AST subtree and add the call-site position to any node
* not already present in the sourceMap. This ensures that expressions
* introduced by macro expansion are visible to coverage tools.
*/
function stampMissingPositions(node, pos, positions) {
	const nodeId = node[2];
	if (!positions.has(nodeId)) positions.set(nodeId, pos);
	const payload = node[1];
	if (Array.isArray(payload)) {
		for (const item of payload) if (isAstNode$1(item)) stampMissingPositions(item, pos, positions);
		else if (Array.isArray(item)) stampNestedPositions(item, pos, positions);
	}
}
function stampNestedPositions(items, pos, positions) {
	for (const item of items) if (isAstNode$1(item)) stampMissingPositions(item, pos, positions);
	else if (Array.isArray(item)) stampNestedPositions(item, pos, positions);
}
function recurseInto(value, macros, dvala, positions) {
	if (!Array.isArray(value)) return value;
	if (isAstNode$1(value)) return expandNodeRecursive(value, macros, dvala, positions);
	return value.map((item) => recurseInto(item, macros, dvala, positions));
}
function isAstNode$1(value) {
	return Array.isArray(value) && value.length === 3 && typeof value[0] === "string" && typeof value[2] === "number";
}
//#endregion
//#region src/typechecker/assertsBodyVerify.ts
function verifyAssertionFunctionBodies(ast) {
	if (!ast.typeAnnotations || ast.typeAnnotations.size === 0) return [];
	const diagnostics = [];
	verifyStatementList(ast.body, ast, diagnostics);
	return diagnostics;
}
function verifyStatementList(nodes, ast, diagnostics) {
	const assertionFunctions = collectAssertionFunctions(nodes, ast);
	const cyclicFunctions = findRecursiveAssertionFunctions(assertionFunctions);
	for (const info of assertionFunctions) if (!emitPreflightDiagnostics(info, ast, diagnostics, cyclicFunctions) && !bodyProvesAssertion(info, assertionFunctions, /* @__PURE__ */ new Set())) diagnostics.push({
		message: `Assertion function '${info.name ?? "<anonymous>"}' does not prove ${info.asserts.source} on all normal-return paths.`,
		severity: "error",
		sourceCodeInfo: resolveNodeSourceInfo$1(info.valueNode, ast)
	});
	for (const node of nodes) visitNestedStatementLists(node, ast, diagnostics);
}
function collectAssertionFunctions(nodes, ast) {
	const out = [];
	for (const node of nodes) {
		if (node[0] !== NodeTypes.Let) continue;
		const [binding, valueNode] = node[1];
		if (valueNode[0] !== NodeTypes.Function) continue;
		const annotation = ast.typeAnnotations?.get(binding[2]);
		if (!annotation) continue;
		try {
			const declaredType = simplify(parseUserTypeAnnotation(annotation));
			if (declaredType.tag !== "Function" || !declaredType.asserts) continue;
			const [, bodyNodes] = valueNode[1];
			out.push({
				binding,
				bodyNodes,
				name: getSymbolBindingName(binding),
				node,
				valueNode,
				asserts: declaredType.asserts
			});
		} catch (error) {
			if (error instanceof TypeParseError) continue;
			throw error;
		}
	}
	return out;
}
function emitPreflightDiagnostics(info, ast, diagnostics, cyclicFunctions) {
	let blocked = false;
	if (cyclicFunctions.has(info)) {
		diagnostics.push({
			message: `Assertion function '${info.name ?? "<anonymous>"}' may not recurse or participate in recursive assertion cycles.`,
			severity: "error",
			sourceCodeInfo: resolveNodeSourceInfo$1(info.binding, ast)
		});
		blocked = true;
	}
	for (const withHandlerNode of findWithHandlerNodes(info.bodyNodes)) {
		diagnostics.push({
			message: "Assertion function bodies may not install handlers with `do with ... end`.",
			severity: "error",
			sourceCodeInfo: resolveNodeSourceInfo$1(withHandlerNode, ast)
		});
		blocked = true;
	}
	return blocked;
}
function findWithHandlerNodes(nodes) {
	const hits = [];
	for (const node of nodes) visitBodyNode(node, hits, true);
	return hits;
}
function findRecursiveAssertionFunctions(infos) {
	const byName = new Map(infos.flatMap((info) => info.name ? [[info.name, info]] : []));
	const edges = /* @__PURE__ */ new Map();
	for (const info of infos) {
		const callees = /* @__PURE__ */ new Set();
		for (const calleeName of collectCalledAssertionNames(info.bodyNodes, new Set(byName.keys()))) {
			const callee = byName.get(calleeName);
			if (callee) callees.add(callee);
		}
		edges.set(info, callees);
	}
	const cyclic = /* @__PURE__ */ new Set();
	for (const info of infos) if (hasCycle(info, info, edges, /* @__PURE__ */ new Set())) {
		cyclic.add(info);
		for (const other of infos) if (other !== info && hasCycle(info, other, edges, /* @__PURE__ */ new Set()) && hasCycle(other, info, edges, /* @__PURE__ */ new Set())) cyclic.add(other);
	}
	return cyclic;
}
function visitBodyNode(node, hits, allowNestedFunctions, bindingName) {
	if (node[0] === NodeTypes.WithHandler) hits.push(node);
	if (bindingName && node[0] === NodeTypes.Call) {
		const [calleeNode] = node[1];
		if (calleeNode[0] === NodeTypes.Sym && calleeNode[1] === bindingName) hits.push(node);
	}
	if (node[0] === NodeTypes.Function && !allowNestedFunctions) return;
	walkBodyChildren(node[1], hits, node[0] === NodeTypes.Function ? false : allowNestedFunctions, bindingName);
}
function collectCalledAssertionNames(nodes, names) {
	const hits = /* @__PURE__ */ new Set();
	const visit = (node, allowNestedFunctions) => {
		if (node[0] === NodeTypes.Call) {
			const [calleeNode] = node[1];
			if (calleeNode[0] === NodeTypes.Sym && names.has(calleeNode[1])) hits.add(calleeNode[1]);
		}
		if (node[0] === NodeTypes.Function && !allowNestedFunctions) return;
		walkValue(node[1], node[0] === NodeTypes.Function ? false : allowNestedFunctions);
	};
	const walkValue = (value, allowNestedFunctions) => {
		if (isAstNode(value)) {
			if (value[0] === NodeTypes.Function && !allowNestedFunctions) return;
			visit(value, allowNestedFunctions);
			return;
		}
		if (!Array.isArray(value)) return;
		for (const item of value) walkValue(item, allowNestedFunctions);
	};
	for (const node of nodes) walkValue(node, false);
	return hits;
}
function hasCycle(current, target, edges, visited) {
	if (visited.has(current)) return false;
	visited.add(current);
	for (const next of edges.get(current) ?? []) {
		if (next === target) return true;
		if (hasCycle(next, target, edges, visited)) return true;
	}
	return false;
}
function bodyProvesAssertion(info, assertionFunctions, stack) {
	if (stack.has(info)) return false;
	stack.add(info);
	try {
		return sequenceProves(info.bodyNodes, info, assertionFunctions, false, stack);
	} finally {
		stack.delete(info);
	}
}
function sequenceProves(nodes, info, assertionFunctions, proven, stack) {
	if (nodes.length === 0) return proven;
	let currentProven = proven;
	for (let i = 0; i < nodes.length - 1; i++) currentProven = statementGuarantees(nodes[i], info, assertionFunctions, currentProven, stack);
	return terminalProves(nodes.at(-1), info, assertionFunctions, currentProven, stack);
}
function statementGuarantees(node, info, assertionFunctions, proven, stack) {
	if (proven) return true;
	if (node[0] === NodeTypes.Block) return sequenceProves(node[1], info, assertionFunctions, proven, stack);
	if (node[0] === NodeTypes.If) {
		const [cond, thenNode, elseNode] = node[1];
		const thenProven = terminalProves(thenNode, info, assertionFunctions, proven || predicateMatchesTarget(cond, info), stack);
		const elseProven = terminalProves(elseNode ?? thenNode, info, assertionFunctions, proven, stack);
		return thenProven && elseProven;
	}
	if (node[0] === NodeTypes.Match) return matchProves(node, info, assertionFunctions, proven, stack);
	return establishesTargetPredicate(node, info, assertionFunctions, stack);
}
function terminalProves(node, info, assertionFunctions, proven, stack) {
	if (proven) return true;
	if (node[0] === NodeTypes.Block) return sequenceProves(node[1], info, assertionFunctions, proven, stack);
	if (node[0] === NodeTypes.If) {
		const [cond, thenNode, elseNode] = node[1];
		const thenProven = terminalProves(thenNode, info, assertionFunctions, proven || predicateMatchesTarget(cond, info), stack);
		const elseProven = terminalProves(elseNode ?? thenNode, info, assertionFunctions, proven, stack);
		return thenProven && elseProven;
	}
	if (node[0] === NodeTypes.Match) return matchProves(node, info, assertionFunctions, proven, stack);
	return establishesTargetPredicate(node, info, assertionFunctions, stack);
}
/**
* `match` proof check — every case body must prove the asserted
* predicate, since each case is a possible normal-return path. Mirrors
* the `If` treatment with both branches required to prove.
*
* Called from both `terminalProves` (the match is the function's
* terminal expression) and `statementGuarantees` (the match is in
* non-terminal position; if every case proves P, downstream
* statements inherit proven=true via the `sequenceProves` loop).
* Each case body is itself a terminal expression *within* its case,
* so we recurse via `terminalProves` regardless of caller — the
* same shape `If` uses for its non-terminal `statementGuarantees`
* case.
*
* Two narrowing-related bonuses are applied per case:
*
*   1. **Guard narrowing.** A `case n when cond then ...` whose
*      `cond` matches the target predicate starts the case body
*      with `proven=true`. Mirrors the `If` treatment of the
*      condition.
*   2. **Pattern-binding-aware substitution.** When the match's
*      scrutinee is the asserted parameter and the case binds a
*      single Sym (`case n then ...`), the binder `n` is recognised
*      as a local alias for the outer parameter. Predicates inside
*      the case body that reference `n` are rewritten as if they
*      referenced the outer parameter, so `assert(n > 0)` correctly
*      proves `x > 0`. Limited to simple Sym bindings — destructuring
*      / nested patterns / literal patterns don't substitute.
*/
function matchProves(node, info, assertionFunctions, proven, stack) {
	const [scrutinee, cases] = node[1];
	if (cases.length === 0) return false;
	const scrutineeIsAssertedParam = scrutinee[0] === NodeTypes.Sym && scrutinee[1] === info.asserts.binder;
	return cases.every(([binding, body, guard]) => {
		let caseInfo = info;
		if (scrutineeIsAssertedParam) caseInfo = applyCaseBinderAlias(info, binding);
		const caseProven = proven || guard !== void 0 && predicateMatchesTarget(guard, caseInfo);
		return terminalProves(body, caseInfo, assertionFunctions, caseProven, stack);
	});
}
/**
* If a case's binding is a single Sym (`case n then ...`), treat the
* bound name as a local alias for the outer asserted parameter. The
* info's binder + predicate are rewritten so the existing
* `predicateMatchesTarget` structural compare matches user-written
* predicates referring to the case binding.
*
* Other binding shapes (object/array destructuring, literal patterns,
* wildcards, rest) don't introduce a single aliasing name, so they
* leave info untouched — predicates referring to the outer parameter
* by its original name still match.
*/
function applyCaseBinderAlias(info, binding) {
	if (binding[0] !== bindingTargetTypes.symbol) return info;
	const [symbolNode] = binding[1];
	if (symbolNode[0] !== NodeTypes.Sym) return info;
	const aliasName = symbolNode[1];
	if (aliasName === info.asserts.binder) return info;
	const renamedPredicate = renameBinderInPredicate(info.asserts.predicate, info.asserts.binder, aliasName);
	return {
		...info,
		asserts: {
			...info.asserts,
			binder: aliasName,
			predicate: renamedPredicate,
			source: `${aliasName} | ${prettyPrint(renamedPredicate).trim()}`
		}
	};
}
/**
* Rewrite every `Sym(oldName)` in a predicate AST to `Sym(newName)`.
* Walks the structural shapes that can contain binder references:
* Sym (the leaf substitution case), And, Or, Call. Leaves that can't
* transitively contain a Sym (Num, Str, Atom, TmplStr, etc.) are
* returned unchanged via the bottom of the walk.
*
* Mirrors the structure of `substitutePredicateBinder` in subtype.ts
* (which substitutes Sym for a literal). Kept private — refinement-
* solver work that needs a similar utility should pull this out.
*/
function renameBinderInPredicate(node, oldName, newName) {
	const rewritten = /* @__PURE__ */ new Map();
	const stack = [{
		node,
		exiting: false
	}];
	while (stack.length > 0) {
		const frame = stack.pop();
		const current = frame.node;
		if (frame.exiting) {
			rewritten.set(current, rewriteRenamedNode(current, oldName, newName, rewritten));
			continue;
		}
		stack.push({
			node: current,
			exiting: true
		});
		if (current[0] === NodeTypes.Call && Array.isArray(current[1])) {
			const [callee, args] = current[1];
			for (let index = args.length - 1; index >= 0; index--) stack.push({
				node: args[index],
				exiting: false
			});
			stack.push({
				node: callee,
				exiting: false
			});
			continue;
		}
		if ((current[0] === NodeTypes.And || current[0] === NodeTypes.Or) && Array.isArray(current[1])) {
			const operands = current[1];
			for (let index = operands.length - 1; index >= 0; index--) stack.push({
				node: operands[index],
				exiting: false
			});
		}
	}
	return rewritten.get(node) ?? node;
}
function rewriteRenamedNode(node, oldName, newName, rewritten) {
	if (node[0] === NodeTypes.Sym && node[1] === oldName) return [
		NodeTypes.Sym,
		newName,
		node[2]
	];
	if (node[0] === NodeTypes.Call && Array.isArray(node[1])) {
		const [callee, args, hints] = node[1];
		const rewrittenCallee = rewritten.get(callee) ?? callee;
		const rewrittenArgs = args.map((arg) => rewritten.get(arg) ?? arg);
		if (rewrittenCallee === callee && rewrittenArgs.every((arg, index) => arg === args[index])) return node;
		return [
			NodeTypes.Call,
			[
				rewrittenCallee,
				rewrittenArgs,
				hints
			],
			node[2]
		];
	}
	if ((node[0] === NodeTypes.And || node[0] === NodeTypes.Or) && Array.isArray(node[1])) {
		const operands = node[1];
		const rewrittenOperands = operands.map((operand) => rewritten.get(operand) ?? operand);
		if (rewrittenOperands.every((operand, index) => operand === operands[index])) return node;
		return [
			node[0],
			rewrittenOperands,
			node[2]
		];
	}
	return node;
}
function establishesTargetPredicate(node, info, assertionFunctions, stack) {
	if (isExactBuiltinAssert(node, info)) return true;
	if (node[0] !== NodeTypes.Call) return false;
	const [calleeNode, argNodes] = node[1];
	if (calleeNode[0] !== NodeTypes.Sym) return false;
	const helper = assertionFunctions.find((candidate) => candidate.name === calleeNode[1]);
	if (!helper) return false;
	const helperRenamed = renameBinderInPredicate(helper.asserts.predicate, helper.asserts.binder, info.asserts.binder);
	if (`${info.asserts.binder} | ${prettyPrint(helperRenamed).trim()}` !== info.asserts.source) return false;
	const helperArg = argNodes[helper.asserts.paramIndex];
	if (!helperArg || helperArg[0] !== NodeTypes.Sym || helperArg[1] !== info.asserts.binder) return false;
	return bodyProvesAssertion(helper, assertionFunctions, stack);
}
function isExactBuiltinAssert(node, info) {
	if (node[0] !== NodeTypes.Call) return false;
	const [calleeNode, argNodes] = node[1];
	if (calleeNode[0] !== NodeTypes.Builtin || calleeNode[1] !== "assert") return false;
	const predicate = argNodes[0];
	return predicate ? predicateMatchesTarget(predicate, info) : false;
}
function predicateMatchesTarget(node, info) {
	if (prettyPrint(node).trim() === prettyPrint(info.asserts.predicate).trim()) return true;
	const guardConjuncts = flattenConjuncts(node).map((c) => prettyPrint(c).trim());
	if (guardConjuncts.length === 1) return false;
	return flattenConjuncts(info.asserts.predicate).map((c) => prettyPrint(c).trim()).every((t) => guardConjuncts.includes(t));
}
function flattenConjuncts(node) {
	if (node[0] !== NodeTypes.And || !Array.isArray(node[1])) return [node];
	return node[1].flatMap(flattenConjuncts);
}
function walkAstChildren(node, ast, diagnostics, allowNestedFunctions) {
	if (node[0] === NodeTypes.Function && !allowNestedFunctions) return;
	walkAstValue(node[1], ast, diagnostics, node[0] === NodeTypes.Function ? true : allowNestedFunctions);
}
function walkAstValue(value, ast, diagnostics, allowNestedFunctions) {
	if (isAstNode(value)) {
		if (value[0] === NodeTypes.Function && !allowNestedFunctions) return;
		visitNestedStatementLists(value, ast, diagnostics);
		return;
	}
	if (!Array.isArray(value)) return;
	for (const item of value) walkAstValue(item, ast, diagnostics, allowNestedFunctions);
}
function walkBodyChildren(value, hits, allowNestedFunctions, bindingName) {
	if (isAstNode(value)) {
		visitBodyNode(value, hits, allowNestedFunctions, bindingName);
		return;
	}
	if (!Array.isArray(value)) return;
	for (const item of value) walkBodyChildren(item, hits, allowNestedFunctions, bindingName);
}
function visitNestedStatementLists(node, ast, diagnostics) {
	switch (node[0]) {
		case NodeTypes.Function: {
			const [, bodyNodes] = node[1];
			verifyStatementList(bodyNodes, ast, diagnostics);
			return;
		}
		case NodeTypes.Block:
			verifyStatementList(node[1], ast, diagnostics);
			return;
		case NodeTypes.WithHandler: {
			const [handlerExpr, bodyNodes] = node[1];
			visitNestedStatementLists(handlerExpr, ast, diagnostics);
			verifyStatementList(bodyNodes, ast, diagnostics);
			return;
		}
		default: walkAstChildren(node, ast, diagnostics, true);
	}
}
function isAstNode(value) {
	return Array.isArray(value) && value.length >= 3 && isNodeType(value[0]);
}
function getSymbolBindingName(binding) {
	if (binding[0] !== bindingTargetTypes.symbol) return void 0;
	const [nameNode] = binding[1];
	return nameNode[0] === NodeTypes.Sym ? nameNode[1] : void 0;
}
function resolveNodeSourceInfo$1(node, ast) {
	const nodeId = node[2];
	if (!ast.sourceMap || nodeId <= 0) return void 0;
	return resolveSourceCodeInfo(nodeId, ast.sourceMap) ?? void 0;
}
//#endregion
//#region src/typechecker/typecheck.ts
let initialized = false;
/** Initialize the type system — call once before first typecheck. */
function initTypeSystem() {
	if (initialized) return;
	initialized = true;
	initBuiltinTypes(builtin.normalExpressions);
	initBuiltinEffects();
}
/**
* Typecheck an AST. Returns diagnostics and a type map.
*
* Type errors are recovered per-subexpression (assigned Unknown),
* so the type map is populated even when errors are found.
* This enables IDE features (hover, completions) on partially-typed code.
*/
function typecheck(ast, options) {
	initTypeSystem();
	const resolver = options?.fileResolver;
	const baseDir = options?.fileResolverBaseDir ?? ".";
	const createDvala = options?.createDvala;
	const expandedAst = expandMacros(ast, resolver ? {
		fileResolver: resolver,
		fileResolverBaseDir: baseDir,
		createDvala
	} : createDvala ? { createDvala } : void 0);
	const sourceMap = expandedAst.sourceMap ?? ast.sourceMap;
	const diagnostics = [];
	const reportedImportDiagnostics = /* @__PURE__ */ new Set();
	const fileTypeCache = /* @__PURE__ */ new Map();
	const ctx = new InferenceContext();
	ctx.sourceMap = sourceMap;
	if (options?.fold !== void 0) ctx.foldEnabled = options.fold;
	if (resolver) {
		const makeResolveFileType = (fromDir) => (importPath) => {
			const cacheKey = `${fromDir}:${importPath}`;
			const cached = fileTypeCache.get(cacheKey);
			if (cached) {
				for (const diagnostic of cached.diagnostics) pushUniqueDiagnostic(diagnostics, diagnostic, reportedImportDiagnostics);
				return cached.type;
			}
			try {
				const rawAst = parseToAst(minifyTokenStream(tokenize(resolver(importPath, fromDir), true, resolveImportedFilePath(fromDir, importPath)), { removeWhiteSpace: true }));
				const nestedDir = resolveImportedFileDir(fromDir, importPath);
				const importedAst = expandMacros(rawAst, {
					fileResolver: resolver,
					fileResolverBaseDir: nestedDir,
					createDvala
				});
				const importCtx = new InferenceContext();
				importCtx.sourceMap = importedAst.sourceMap ?? rawAst.sourceMap;
				importCtx.resolveFileType = makeResolveFileType(nestedDir);
				if (importedAst.typeAnnotations) importCtx.typeAnnotations = importedAst.typeAnnotations;
				if (importedAst.typeParams) importCtx.typeParams = importedAst.typeParams;
				const importEnv = new TypeEnv();
				const importTypeMap = /* @__PURE__ */ new Map();
				const importDiagnostics = [];
				const effectSnapshot = snapshotEffectRegistry();
				const typeAliasSnapshot = snapshotTypeAliases();
				try {
					if (importedAst.typeAliases) for (const [name, { params, body }] of importedAst.typeAliases) registerTypeAlias(name, params, body);
					if (importedAst.effectDeclarations) for (const [name, { argType, retType }] of importedAst.effectDeclarations) try {
						declareEffect(name, parseUserTypeAnnotation(argType), parseUserTypeAnnotation(retType));
					} catch (error) {
						if (error instanceof TypeParseError) importDiagnostics.push({
							message: `Invalid effect type for @${name}: ${error.cleanMessage}`,
							severity: "error"
						});
						else throw error;
					}
					const cachedResult = {
						type: normalizeImportedExportType(inferNodesRecoveringErrors(importedAst.body, importCtx, importEnv, importTypeMap, importDiagnostics, importedAst.sourceMap ?? rawAst.sourceMap)),
						diagnostics: importDiagnostics
					};
					fileTypeCache.set(cacheKey, cachedResult);
					for (const diagnostic of importDiagnostics) pushUniqueDiagnostic(diagnostics, diagnostic, reportedImportDiagnostics);
					return cachedResult.type;
				} finally {
					restoreEffectRegistry(effectSnapshot);
					restoreTypeAliases(typeAliasSnapshot);
				}
			} catch {
				fileTypeCache.set(cacheKey, {
					type: Unknown,
					diagnostics: []
				});
				return Unknown;
			}
		};
		ctx.resolveFileType = makeResolveFileType(baseDir);
	}
	resetUserEffects();
	resetTypeAliases();
	installPreludeAliases();
	resetModuleTypeCache();
	for (const mod of options?.modules ?? []) registerModuleType(mod.name, mod.functions, mod.docs);
	if (ast.typeAnnotations) ctx.typeAnnotations = ast.typeAnnotations;
	if (ast.typeParams) ctx.typeParams = ast.typeParams;
	if (ast.typeAliases) for (const [name, { params, body }] of ast.typeAliases) registerTypeAlias(name, params, body);
	if (ast.effectDeclarations) for (const [name, { argType, retType }] of ast.effectDeclarations) try {
		declareEffect(name, parseUserTypeAnnotation(argType), parseUserTypeAnnotation(retType));
	} catch (error) {
		if (error instanceof TypeParseError) diagnostics.push({
			message: `Invalid effect type for @${name}: ${error.cleanMessage}`,
			severity: "error"
		});
		else throw error;
	}
	const env = new TypeEnv();
	const typeMap = /* @__PURE__ */ new Map();
	inferNodesRecoveringErrors(expandedAst.body, ctx, env, typeMap, diagnostics, sourceMap);
	diagnostics.push(...verifyAssertionFunctionBodies(expandedAst));
	return {
		diagnostics,
		typeMap,
		sourceMap: sourceMap?.positions
	};
}
/**
* Typecheck a single expression (for REPL / quick checks).
* Returns the inferred type and any diagnostics.
*/
function typecheckExpr(nodes, sourceMap, options) {
	initTypeSystem();
	const ctx = new InferenceContext();
	ctx.sourceMap = sourceMap;
	if (options?.fold !== void 0) ctx.foldEnabled = options.fold;
	resetUserEffects();
	resetTypeAliases();
	installPreludeAliases();
	resetModuleTypeCache();
	for (const mod of options?.modules ?? []) registerModuleType(mod.name, mod.functions, mod.docs);
	const env = new TypeEnv();
	const typeMap = /* @__PURE__ */ new Map();
	const diagnostics = [];
	return {
		diagnostics,
		typeMap,
		type: inferNodesRecoveringErrors(nodes, ctx, env, typeMap, diagnostics, sourceMap)
	};
}
/** Try to resolve source info for an AST node. */
function resolveNodeSourceInfo(node, sourceMap) {
	const nodeId = node[2];
	if (!sourceMap || nodeId <= 0) return void 0;
	return resolveSourceCodeInfo(nodeId, sourceMap) ?? void 0;
}
function resolveErrorSourceInfo(error, fallbackNode, sourceMap) {
	if (error.sourceCodeInfo) return error.sourceCodeInfo;
	if (sourceMap && error.nodeId && error.nodeId > 0) {
		const resolved = resolveSourceCodeInfo(error.nodeId, sourceMap);
		if (resolved) return resolved;
	}
	return resolveNodeSourceInfo(fallbackNode, sourceMap);
}
function resolveImportedFileDir(fromDir, importPath) {
	const normalizedFromDir = normalizePath(fromDir);
	const normalizedImportPath = normalizePath(importPath);
	if (isAbsolutePath(normalizedImportPath)) return dirnamePath(normalizedImportPath);
	return dirnamePath(joinPath(normalizedFromDir, normalizedImportPath));
}
function resolveImportedFilePath(fromDir, importPath) {
	const normalizedFromDir = normalizePath(fromDir);
	const normalizedImportPath = normalizePath(importPath);
	if (isAbsolutePath(normalizedImportPath)) return normalizedImportPath;
	return joinPath(normalizedFromDir, normalizedImportPath);
}
function pushUniqueDiagnostic(diagnostics, diagnostic, seen) {
	const key = JSON.stringify({
		message: diagnostic.message,
		severity: diagnostic.severity,
		filePath: diagnostic.sourceCodeInfo?.filePath,
		line: diagnostic.sourceCodeInfo?.position.line,
		column: diagnostic.sourceCodeInfo?.position.column
	});
	if (seen.has(key)) return;
	seen.add(key);
	diagnostics.push(diagnostic);
}
function inferNodesRecoveringErrors(nodes, ctx, env, typeMap, diagnostics, sourceMap) {
	let resultType = Unknown;
	for (const node of nodes) try {
		resultType = inferExpr(node, ctx, env, typeMap);
		drainDeferredDiagnostics(ctx, diagnostics, node, sourceMap);
	} catch (error) {
		if (!(error instanceof TypeInferenceError)) throw error;
		diagnostics.push({
			message: error.message,
			severity: error.severity,
			sourceCodeInfo: resolveErrorSourceInfo(error, node, sourceMap),
			sourceRange: error.sourceRange
		});
		resultType = Unknown;
	}
	return resultType;
}
function drainDeferredDiagnostics(ctx, diagnostics, fallbackNode, sourceMap) {
	for (const error of ctx.takeDeferredErrors()) diagnostics.push({
		message: error.message,
		severity: error.severity,
		sourceCodeInfo: resolveErrorSourceInfo(error, fallbackNode, sourceMap),
		sourceRange: error.sourceRange
	});
}
function normalizeHandledSignatures(handled) {
	return new Map([...handled.entries()].map(([name, sig]) => [name, {
		argType: expandType(sig.argType, "negative"),
		retType: expandType(sig.retType, "positive")
	}]));
}
function normalizeImportedExportType(type) {
	switch (type.tag) {
		case "Function": {
			const handlerWrapper = type.handlerWrapper ? {
				paramIndex: type.handlerWrapper.paramIndex,
				handled: normalizeHandledSignatures(type.handlerWrapper.handled),
				introduced: type.handlerWrapper.introduced
			} : void 0;
			return {
				...type,
				params: type.params.map(normalizeImportedExportType),
				...type.restParam !== void 0 ? { restParam: normalizeImportedExportType(type.restParam) } : {},
				ret: normalizeImportedExportType(type.ret),
				...handlerWrapper ? { handlerWrapper } : {}
			};
		}
		case "Handler": return {
			...type,
			body: normalizeImportedExportType(type.body),
			output: normalizeImportedExportType(type.output),
			handled: normalizeHandledSignatures(type.handled),
			introduced: type.introduced
		};
		case "Record": return {
			...type,
			fields: new Map([...type.fields.entries()].map(([name, fieldType]) => [name, normalizeImportedExportType(fieldType)]))
		};
		case "Array": return {
			...type,
			element: normalizeImportedExportType(type.element)
		};
		case "Sequence": return {
			...type,
			prefix: type.prefix.map(normalizeImportedExportType),
			rest: normalizeImportedExportType(type.rest)
		};
		case "Tuple": return {
			...type,
			elements: type.elements.map(normalizeImportedExportType)
		};
		case "Union": return {
			...type,
			members: type.members.map(normalizeImportedExportType)
		};
		case "Inter": return {
			...type,
			members: type.members.map(normalizeImportedExportType)
		};
		case "Neg": return {
			...type,
			inner: normalizeImportedExportType(type.inner)
		};
		case "Alias": return {
			...type,
			args: type.args.map(normalizeImportedExportType),
			expanded: normalizeImportedExportType(type.expanded)
		};
		case "Recursive": return {
			...type,
			body: normalizeImportedExportType(type.body)
		};
		default: return type;
	}
}
function normalizePath(pathLike) {
	return pathLike.replaceAll("\\", "/");
}
function isAbsolutePath(pathLike) {
	return pathLike.startsWith("/") || /^[A-Za-z]:\//.test(pathLike);
}
function dirnamePath(pathLike) {
	const normalized = normalizePath(pathLike);
	const root = getPathRoot(normalized);
	const segments = splitPathSegments(normalized);
	if (segments.length === 0) return root || ".";
	return buildPath(root, segments.slice(0, -1));
}
function joinPath(baseDir, importPath) {
	const root = getPathRoot(baseDir);
	const combinedSegments = [...splitPathSegments(baseDir), ...splitPathSegments(importPath)];
	const resolvedSegments = [];
	for (const segment of combinedSegments) {
		if (segment === "." || segment === "") continue;
		if (segment === "..") {
			if (resolvedSegments.length > 0 && resolvedSegments[resolvedSegments.length - 1] !== "..") resolvedSegments.pop();
			else if (!root) resolvedSegments.push("..");
			continue;
		}
		resolvedSegments.push(segment);
	}
	return buildPath(root, resolvedSegments);
}
function getPathRoot(pathLike) {
	if (pathLike.startsWith("/")) return "/";
	return pathLike.match(/^[A-Za-z]:/)?.[0] ?? "";
}
function splitPathSegments(pathLike) {
	const normalized = normalizePath(pathLike);
	const root = getPathRoot(normalized);
	return (root === "/" ? normalized.slice(1) : root ? normalized.slice(root.length).replace(/^\//, "") : normalized).split("/").filter(Boolean);
}
function buildPath(root, segments) {
	const joined = segments.join("/");
	if (root === "/") return joined ? `/${joined}` : "/";
	if (root) return joined ? `${root}/${joined}` : `${root}/`;
	return joined || ".";
}
//#endregion
//#region src/allModules.ts
const allBuiltinModules = [
	assertModule,
	gridModule,
	vectorModule,
	linearAlgebraModule,
	matrixModule,
	numberTheoryModule,
	mathUtilsModule,
	functionalUtilsModule,
	stringUtilsModule,
	collectionUtilsModule,
	sequenceUtilsModule,
	bitwiseUtilsModule,
	convertModule,
	jsonModule,
	timeModule,
	handlerModule,
	astModule,
	macrosModule
];
//#endregion
//#region src/initReferenceData.ts
function initReferenceData() {
	setNormalExpressionReference(normalExpressionReference);
	setEffectReference(effectReference);
}
//#endregion
//#region src/ast/treeShake.ts
/**
* Tree-shaking pass: remove unused let bindings from a bundled AST.
*
* Uses a mark-and-sweep (graph-based) algorithm:
* 1. Build dependency graph: each let binding → names it references
* 2. Find root: the last expression in the body (program output)
* 3. Mark: BFS from root's references through the graph
* 4. Sweep: remove unmarked Let nodes with side-effect-free values
*/
function treeShake(ast) {
	if (ast.body.length === 0) return ast;
	const graph = /* @__PURE__ */ new Map();
	const bindingIndices = /* @__PURE__ */ new Map();
	const allDefinedNames = /* @__PURE__ */ new Set();
	for (let i = 0; i < ast.body.length; i++) {
		const node = ast.body[i];
		if (node[0] === NodeTypes.Let) {
			const [target, value] = node[1];
			const names = extractBindingNames(target);
			const refs = collectSymRefs(value);
			for (const name of names) {
				graph.set(name, refs);
				bindingIndices.set(name, i);
				allDefinedNames.add(name);
			}
		}
	}
	const rootRefs = /* @__PURE__ */ new Set();
	for (let i = 0; i < ast.body.length; i++) {
		const node = ast.body[i];
		if (node[0] !== NodeTypes.Let) for (const ref of collectSymRefs(node)) rootRefs.add(ref);
	}
	const live = /* @__PURE__ */ new Set();
	const queue = [...rootRefs];
	while (queue.length > 0) {
		const name = queue.pop();
		if (live.has(name)) continue;
		if (!allDefinedNames.has(name)) continue;
		live.add(name);
		const deps = graph.get(name);
		if (deps) {
			for (const dep of deps) if (!live.has(dep)) queue.push(dep);
		}
	}
	const indicesToRemove = /* @__PURE__ */ new Set();
	const indexToNames = /* @__PURE__ */ new Map();
	for (const [name, index] of bindingIndices) {
		if (!indexToNames.has(index)) indexToNames.set(index, []);
		indexToNames.get(index).push(name);
	}
	for (const [index, names] of indexToNames) if (names.every((n) => !live.has(n))) {
		const [, value] = ast.body[index][1];
		if (isSideEffectFree(value)) indicesToRemove.add(index);
	}
	if (indicesToRemove.size === 0) return ast;
	return {
		body: ast.body.filter((_, i) => !indicesToRemove.has(i)),
		sourceMap: ast.sourceMap
	};
}
/**
* Extract all binding names from a binding target (handles destructuring).
*/
function extractBindingNames(target) {
	if (!Array.isArray(target)) return [];
	const type = target[0];
	const payload = target[1];
	switch (type) {
		case "symbol": {
			const symNode = payload[0];
			if (Array.isArray(symNode) && symNode[0] === NodeTypes.Sym) return [symNode[1]];
			return [];
		}
		case "rest": {
			const name = payload[0];
			return typeof name === "string" ? [name] : [];
		}
		case "object": {
			const entries = payload[0];
			const names = [];
			for (const entry of entries) names.push(...extractBindingNames(entry.target));
			return names;
		}
		case "array": {
			const targets = payload[0];
			const names = [];
			for (const t of targets) if (t) names.push(...extractBindingNames(t));
			return names;
		}
		default: return [];
	}
}
/**
* Collect all Sym references in an AST node (recursively).
* Returns the set of symbol names referenced.
*/
function collectSymRefs(node) {
	const refs = /* @__PURE__ */ new Set();
	walkForRefs(node, refs);
	return refs;
}
function walkForRefs(value, refs) {
	if (!Array.isArray(value)) return;
	if (value.length === 3 && typeof value[0] === "string" && typeof value[2] === "number") {
		const [type, payload] = value;
		if (type === NodeTypes.Sym) {
			refs.add(payload);
			return;
		}
		if (type === NodeTypes.Let) {
			const [, letValue] = payload;
			walkForRefs(letValue, refs);
			return;
		}
		walkForRefs(payload, refs);
		return;
	}
	for (const item of value) walkForRefs(item, refs);
}
/**
* Conservative check: is this expression side-effect-free?
* If uncertain, return false (keep the binding).
*/
function isSideEffectFree(node) {
	switch (node[0]) {
		case NodeTypes.Num:
		case NodeTypes.Str:
		case NodeTypes.Sym:
		case NodeTypes.Builtin:
		case NodeTypes.Function:
		case NodeTypes.Macro:
		case NodeTypes.Effect: return true;
		case NodeTypes.Array: return node[1].every(isSideEffectFree);
		case NodeTypes.Object: return node[1].every(([k, v]) => isSideEffectFree(k) && isSideEffectFree(v));
		case NodeTypes.Block: return node[1].every((n) => {
			if (n[0] === NodeTypes.Let) {
				const [, value] = n[1];
				return isSideEffectFree(value);
			}
			return isSideEffectFree(n);
		});
		case NodeTypes.Import: return true;
		case NodeTypes.Call: {
			const [fnNode, args] = node[1];
			if (fnNode[0] === NodeTypes.Builtin) return args.every(isSideEffectFree);
			return false;
		}
		case NodeTypes.MacroCall: return false;
		case NodeTypes.If: return node[1].every(isSideEffectFree);
		case NodeTypes.Let: {
			const [, value] = node[1];
			return isSideEffectFree(value);
		}
		default: return false;
	}
}
//#endregion
//#region src/debugger/Debugger.ts
/**
* Core debugger — runtime-agnostic controller that uses the `onNodeEval` hook
* to implement breakpoints, stepping, and variable inspection.
*
* Usage:
* 1. Create a Debugger instance with a `onStopped` callback
* 2. Pass `debugger.onNodeEval` as the `onNodeEval` option to `dvala.runAsync()`
* 3. When execution hits a breakpoint or step, `onStopped` fires
* 4. Call `debugger.continue()`, `stepOver()`, `stepInto()`, or `stepOut()`
*    to resume execution
*/
var Debugger = class Debugger {
	breakpoints = /* @__PURE__ */ new Map();
	onStopped;
	conditionEvaluator;
	pendingResolve = null;
	stepCommand = null;
	stepDepth = 0;
	constructor(onStopped, conditionEvaluator) {
		this.onStopped = onStopped;
		this.conditionEvaluator = conditionEvaluator ?? null;
	}
	setBreakpoint(nodeId, condition) {
		this.breakpoints.set(nodeId, { condition });
	}
	removeBreakpoint(nodeId) {
		this.breakpoints.delete(nodeId);
	}
	clearBreakpoints() {
		this.breakpoints.clear();
	}
	getBreakpoints() {
		return new Set(this.breakpoints.keys());
	}
	/** Resume execution until next breakpoint. */
	continue() {
		this.stepCommand = "continue";
		this.resume();
	}
	/** Step to next node at the same or shallower depth (skip over function calls). */
	stepOver() {
		this.stepCommand = "stepOver";
		this.resume();
	}
	/** Step to the very next evaluated node (descend into function calls). */
	stepInto() {
		this.stepCommand = "stepInto";
		this.resume();
	}
	/** Step until we return to a shallower depth (exit current function). */
	stepOut() {
		this.stepCommand = "stepOut";
		this.resume();
	}
	/**
	* Get visible variables at the current stop point.
	* Walks the context chain from innermost to outermost scope.
	*/
	static getVariables(continuation) {
		const contexts = continuation.env.getContextsRaw();
		const seen = /* @__PURE__ */ new Set();
		const variables = [];
		for (const context of contexts) for (const [name, entry] of Object.entries(context)) {
			if (name === "self") continue;
			if (!seen.has(name)) {
				seen.add(name);
				variables.push({
					name,
					value: entry.value
				});
			}
		}
		return variables;
	}
	/** Get the call stack at the current stop point. */
	static getCallStack(continuation) {
		return reconstructCallStack(continuation.k);
	}
	/**
	* Extract visible variable bindings as a plain record — suitable for passing
	* to `dvala.runAsync(expr, { bindings })` for expression evaluation.
	*/
	static extractBindings(continuation) {
		const vars = Debugger.getVariables(continuation);
		const bindings = {};
		for (const { name, value } of vars) bindings[name] = value;
		return bindings;
	}
	/** Count call depth by counting FnBody frames in the continuation stack. */
	static countCallDepth(continuation) {
		let depth = 0;
		let node = continuation.k;
		while (node !== null) {
			if (node.head.type === "FnBody") depth++;
			node = node.tail;
		}
		return depth;
	}
	static asContinuation(continuation) {
		return continuation;
	}
	/**
	* The `onNodeEval` hook function. Pass as `onNodeEval` in run options.
	*
	* Determines whether to stop based on breakpoints and step commands.
	* For stepOver/stepOut, calls getContinuation() to measure call depth.
	*/
	onNodeEval = (node, getContinuation) => {
		const nodeId = node[2];
		const bp = this.breakpoints.get(nodeId);
		if (bp !== void 0) {
			const continuation = Debugger.asContinuation(getContinuation());
			if (!bp.condition) return this.stop("breakpoint", node, continuation);
			if (this.conditionEvaluator) return this.evaluateCondition(bp.condition, node, continuation);
			return this.stop("breakpoint", node, continuation);
		}
		if (this.stepCommand === null || this.stepCommand === "continue") return;
		switch (this.stepCommand) {
			case "stepInto": return this.stop("step", node, Debugger.asContinuation(getContinuation()));
			case "stepOver": {
				const continuation = Debugger.asContinuation(getContinuation());
				if (Debugger.countCallDepth(continuation) <= this.stepDepth) return this.stop("step", node, continuation);
				return;
			}
			case "stepOut": {
				const continuation = Debugger.asContinuation(getContinuation());
				if (Debugger.countCallDepth(continuation) < this.stepDepth) return this.stop("step", node, continuation);
				return;
			}
		}
	};
	async evaluateCondition(condition, node, continuation) {
		try {
			if (await this.conditionEvaluator(condition, continuation)) return this.stop("breakpoint", node, continuation);
		} catch {}
	}
	stop(reason, node, continuation) {
		this.stepDepth = Debugger.countCallDepth(continuation);
		this.stepCommand = null;
		return new Promise((resolve) => {
			this.pendingResolve = resolve;
			this.onStopped({
				reason,
				node,
				continuation
			});
		});
	}
	resume() {
		if (this.pendingResolve) {
			const resolve = this.pendingResolve;
			this.pendingResolve = null;
			resolve();
		}
	}
};
//#endregion
//#region src/debugger/SourceMapUtils.ts
/**
* Source map utilities for debugger frontends.
* Resolves AST node positions from the source map — shared by VS Code DAP adapter,
* playground debugger, and any future debug frontend.
*/
/** Resolve a node's source file path from the source map. */
function getNodeFile(node, sourceMap) {
	const pos = sourceMap.positions.get(node[2]);
	if (!pos) return null;
	return sourceMap.sources[pos.source]?.path ?? null;
}
/** Resolve a node's 0-based start line from the source map. */
function getNodeLine(node, sourceMap) {
	const pos = sourceMap.positions.get(node[2]);
	if (!pos) return null;
	return pos.start[0];
}
/** Resolve a node's 0-based end line from the source map. */
function getNodeEndLine(node, sourceMap) {
	const pos = sourceMap.positions.get(node[2]);
	if (!pos) return null;
	return pos.end[0];
}
/**
* Find the first evaluatable node ID on a given line in a given file.
* Skips structuralLeaf nodes (Sym, Builtin, etc.) since the evaluator's
* onNodeEval hook never visits them — setting a breakpoint on one would never fire.
* DAP lines are 1-based, source map positions are 0-based.
* Returns null if no node found on that line.
*/
function findNodeIdForLine(line, filePath, sourceMap) {
	const line0 = line - 1;
	let bestNodeId = null;
	let bestCol = Infinity;
	for (const [nodeId, pos] of sourceMap.positions) {
		if (pos.structuralLeaf) continue;
		const source = sourceMap.sources[pos.source];
		if (source && source.path !== filePath) continue;
		if (pos.start[0] === line0 && pos.start[1] < bestCol) {
			bestNodeId = nodeId;
			bestCol = pos.start[1];
		}
	}
	return bestNodeId;
}
//#endregion
//#region src/shared/replCore.ts
function applyReplBinding(bindings, repl) {
	bindings.REPL = {
		result: repl.result,
		error: repl.error,
		history: [...repl.history]
	};
}
async function executeReplLine(params) {
	const runScope = { ...params.scope };
	applyReplBinding(runScope, params.repl);
	const runResult = await params.run(params.expression, runScope);
	const historySlots = params.historySlots ?? 9;
	const formatError = params.formatError ?? ((error) => String(error));
	if (runResult.type === "error") {
		const errorEntry = {
			type: "error",
			error: formatError(runResult.error)
		};
		const repl = {
			result: params.repl.result,
			error: errorEntry.error,
			history: [errorEntry, ...params.repl.history].slice(0, historySlots)
		};
		const scope = { ...params.scope };
		applyReplBinding(scope, repl);
		return {
			ok: false,
			error: runResult.error,
			scope,
			repl
		};
	}
	const value = runResult.type === "completed" ? runResult.value : null;
	const repl = {
		result: value,
		error: null,
		history: [{
			type: "result",
			value
		}, ...params.repl.history].slice(0, historySlots)
	};
	const scope = {
		...runScope,
		...runResult.type === "completed" ? runResult.scope ?? {} : {}
	};
	applyReplBinding(scope, repl);
	return {
		ok: true,
		runResult,
		value,
		scope,
		repl
	};
}
//#endregion
//#region src/bundler/interface.ts
function isDvalaBundle(value) {
	return typeof value === "object" && value !== null && value.version === 1 && typeof value.ast === "object" && Array.isArray(value.ast?.body);
}
//#endregion
//#region src/host/runtime/createRuntimeRunner.ts
function createRuntimeRunner(options) {
	function mergeEffectHandlers(runEffectHandlers) {
		if (!options.factoryEffectHandlers && !runEffectHandlers) return void 0;
		return [...runEffectHandlers ?? [], ...options.factoryEffectHandlers ?? []];
	}
	function assertNotPureWithHandlers(pure, effectHandlers) {
		if (!pure) return;
		if (effectHandlers && effectHandlers.length > 0) throw new TypeError("Cannot use pure mode with effect handlers");
	}
	return {
		run(source, runOptions) {
			const effectHandlers = mergeEffectHandlers(runOptions?.effectHandlers);
			const pure = runOptions?.pure ?? false;
			assertNotPureWithHandlers(pure, effectHandlers);
			const onNodeEval = options.factoryOnNodeEval;
			const coverage = !!onNodeEval;
			const effectiveDebug = options.debug;
			const contextStack = createContextStack({
				globalContext: options.scopeToGlobalContext(runOptions?.scope),
				modules: options.modules,
				pure,
				fileResolver: options.factoryFileResolver,
				currentFileDir: options.factoryFileResolverBaseDir,
				allocateNodeId: effectiveDebug ? options.allocateNodeId : void 0,
				debug: effectiveDebug,
				coverage,
				parseSource: options.parseSource,
				prettyPrint: options.prettyPrint
			});
			if (coverage) {
				const accumulated = options.getAccumulatedSourceMap();
				if (accumulated) contextStack.sourceMap = accumulated;
			}
			if (isDvalaBundle(source)) {
				const ast = source.ast;
				if (effectHandlers) return toJS(evaluateWithSyncEffects(ast, contextStack, effectHandlers, onNodeEval));
				const result = evaluate(ast, contextStack, onNodeEval);
				if (result instanceof Promise) throw new TypeError("Unexpected async result in run(). Use runAsync() for async operations.");
				return toJS(result);
			}
			const ast = options.buildAst(source, runOptions?.filePath, effectiveDebug);
			options.emitTypeDiagnostics(ast);
			if (effectHandlers) return toJS(evaluateWithSyncEffects(ast, contextStack, effectHandlers, onNodeEval));
			const result = evaluate(ast, contextStack, onNodeEval);
			if (result instanceof Promise) throw new TypeError("Unexpected async result in run(). Use runAsync() for async operations.");
			return toJS(result);
		},
		async runAsync(source, runOptions) {
			const effectHandlers = mergeEffectHandlers(runOptions?.effectHandlers);
			const pure = runOptions?.pure ?? false;
			assertNotPureWithHandlers(pure, effectHandlers);
			try {
				const perRunOnNodeEval = runOptions?.onNodeEval;
				const factoryOnNodeEval = options.factoryOnNodeEval;
				const onNodeEval = factoryOnNodeEval && perRunOnNodeEval ? (node, getContinuation) => {
					factoryOnNodeEval(node, getContinuation);
					return perRunOnNodeEval(node, getContinuation);
				} : factoryOnNodeEval ?? perRunOnNodeEval;
				const forceDebug = options.debug || !!perRunOnNodeEval;
				const effectiveDebug = forceDebug;
				const coverage = !!factoryOnNodeEval;
				const contextStack = createContextStack({
					globalContext: options.scopeToGlobalContext(runOptions?.scope),
					modules: options.modules,
					pure,
					fileResolver: options.factoryFileResolver,
					currentFileDir: options.factoryFileResolverBaseDir,
					allocateNodeId: effectiveDebug ? options.allocateNodeId : void 0,
					debug: effectiveDebug,
					coverage,
					parseSource: options.parseSource,
					prettyPrint: options.prettyPrint
				});
				const ast = isDvalaBundle(source) ? source.ast : options.buildAst(source, runOptions?.filePath, forceDebug);
				if (!isDvalaBundle(source)) options.emitTypeDiagnostics(ast);
				if (isDvalaBundle(source) && source.ast.sourceMap && forceDebug) {
					const accumulatedSourceMap = options.getAccumulatedSourceMap();
					if (!accumulatedSourceMap) options.setAccumulatedSourceMap({
						sources: [...source.ast.sourceMap.sources],
						positions: new Map(source.ast.sourceMap.positions)
					});
					else {
						const sourceOffset = accumulatedSourceMap.sources.length;
						accumulatedSourceMap.sources.push(...source.ast.sourceMap.sources);
						for (const [nodeId, pos] of source.ast.sourceMap.positions) accumulatedSourceMap.positions.set(nodeId, {
							...pos,
							source: pos.source + sourceOffset
						});
					}
				}
				const accumulatedSourceMap = options.getAccumulatedSourceMap();
				if (accumulatedSourceMap) contextStack.sourceMap = accumulatedSourceMap;
				const disableAutoCheckpoint = runOptions?.disableAutoCheckpoint ?? options.factoryDisableTimeTravel;
				const terminalSnapshot = runOptions?.terminalSnapshot;
				const result = await evaluateWithEffects(ast, contextStack, effectHandlers, runOptions?.maxSnapshots, { modules: options.modules }, !disableAutoCheckpoint, terminalSnapshot, onNodeEval);
				const sourceMap = options.getAccumulatedSourceMap();
				if (result.type === "completed") return {
					...result,
					value: toJS(result.value),
					scope: contextStack.getModuleScopeBindings(),
					sourceMap
				};
				return {
					...result,
					sourceMap
				};
			} catch (error) {
				if (error instanceof DvalaError) return {
					type: "error",
					error
				};
				if (error instanceof TypeError) throw error;
				return {
					type: "error",
					error: new DvalaError(`${error}`, void 0)
				};
			}
		}
	};
}
//#endregion
//#region src/host/Cache.ts
function toNonNegativeInteger(num) {
	return Math.max(0, Math.ceil(num));
}
var Cache = class {
	cache = {};
	firstEntry = void 0;
	lastEntry = void 0;
	_size = 0;
	maxSize;
	constructor(maxSize) {
		this.maxSize = maxSize === null ? null : toNonNegativeInteger(maxSize);
		if (typeof this.maxSize === "number" && this.maxSize < 1) throw new Error(`1 is the minimum maxSize, got ${valueToString(maxSize)}`);
	}
	getContent() {
		return Object.entries(this.cache).reduce((result, [key, entry]) => {
			result[key] = entry.value;
			return result;
		}, {});
	}
	get size() {
		return this._size;
	}
	get(key) {
		return this.cache[key]?.value;
	}
	clear() {
		this.cache = {};
		this.firstEntry = void 0;
		this.lastEntry = void 0;
		this._size = 0;
	}
	has(key) {
		return !!this.cache[key];
	}
	set(key, value) {
		if (this.has(key)) throw new Error(`AstCache - key already present: ${key}`);
		const newEntry = {
			value,
			nextEntry: void 0,
			key
		};
		this.cache[key] = newEntry;
		this._size += 1;
		if (this.lastEntry) this.lastEntry.nextEntry = newEntry;
		this.lastEntry = newEntry;
		if (!this.firstEntry) this.firstEntry = this.lastEntry;
		while (this.maxSize !== null && this.size > this.maxSize) this.dropFirstEntry();
	}
	dropFirstEntry() {
		const firstEntry = this.firstEntry;
		delete this.cache[firstEntry.key];
		this._size -= 1;
		this.firstEntry = firstEntry.nextEntry;
	}
};
//#endregion
//#region src/host/runtime/createAstBuilder.ts
function createAstBuilder(options) {
	const cache = new Cache(options.cacheSize);
	let accumulatedSourceMap;
	function buildAst(source, filePath, forceDebug) {
		const effectiveDebug = options.debug || (forceDebug ?? false);
		if (!filePath && !forceDebug) {
			const cached = cache.get(source);
			if (cached) return cached;
		}
		const ast = parseToAst(minifyTokenStream(tokenize(source, effectiveDebug, filePath), { removeWhiteSpace: true }), options.allocateNodeId);
		if (ast.sourceMap) {
			if (!accumulatedSourceMap) accumulatedSourceMap = {
				sources: [...ast.sourceMap.sources],
				positions: new Map(ast.sourceMap.positions)
			};
			else {
				const sourceOffset = accumulatedSourceMap.sources.length;
				accumulatedSourceMap.sources.push(...ast.sourceMap.sources);
				for (const [nodeId, pos] of ast.sourceMap.positions) accumulatedSourceMap.positions.set(nodeId, {
					...pos,
					source: pos.source + sourceOffset
				});
			}
			ast.sourceMap = accumulatedSourceMap;
		}
		if (!filePath && !forceDebug) cache.set(source, ast);
		return ast;
	}
	return {
		buildAst,
		getAccumulatedSourceMap: () => accumulatedSourceMap,
		setAccumulatedSourceMap: (sourceMap) => {
			accumulatedSourceMap = sourceMap;
		}
	};
}
//#endregion
//#region src/host/dvalaCoverage.ts
/**
* Process-global `.dvala` coverage accumulator for the suite-wide UNION baseline
* (`DVALA_COVERAGE=1`). Each `createDvala` instance, when coverage is active, records
* the *source spans* of evaluated builtin `.dvala` nodes here. A vitest setup file
* dumps this to disk at worker exit; a globalSetup teardown merges all workers' dumps
* (by span) and computes the report against a denominator parsed fresh from disk.
*
* Keyed by **source span** (path + start/end), NOT node ID: builtin module node IDs
* vary across instances (import order + which module set was registered + the per-
* module parse cache), so an ID-keyed cross-instance merge would conflate positions.
* Spans are stable across instances/workers. Core builtins could be ID-keyed (their
* IDs are deterministic) but are span-keyed too, so the whole union is uniform.
*
* DOM/Node-free (no `fs`): only touches `process` defensively for the env check.
*/
/** True when the suite-wide union baseline is requested via `DVALA_COVERAGE=1`. */
function isGlobalDvalaCoverageEnabled() {
	return typeof process !== "undefined" && process.env?.DVALA_COVERAGE === "1";
}
/** True for engine builtin `.dvala` source paths (core + modules). */
function isBuiltinDvalaPath(path) {
	return path.includes("packages/dvala-engine/src/builtin/") && path.endsWith(".dvala");
}
/** Stable key for a builtin `.dvala` node's source span (0-based positions). */
function dvalaSpanKey(path, start, end) {
	return `${path}\t${start[0]},${start[1]}\t${end[0]},${end[1]}`;
}
const globalHitSpans = /* @__PURE__ */ new Map();
/** Record one evaluation of a builtin `.dvala` expression, keyed by source span. */
function recordGlobalDvalaSpan(key) {
	globalHitSpans.set(key, (globalHitSpans.get(key) ?? 0) + 1);
}
/** Snapshot the accumulated union hit spans for dumping at worker exit. */
function getGlobalDvalaHits() {
	return globalHitSpans;
}
//#endregion
//#region src/host/createDvala.ts
/**
* Host implementation of the `ParseSource` capability — the seam that lets the
* engine compile source → Ast without depending on the parser directly.
*/
const parseSource = (source, opts = {}) => {
	return parseToAst(minifyTokenStream(tokenize(source, opts.debug ?? false, opts.filePath), { removeWhiteSpace: true }), opts.allocateNodeId);
};
function createDvala(options) {
	let nodeIdCounter = 0;
	const allocateNodeId = () => nodeIdCounter++;
	const modules = options?.modules ? new Map(options.modules.map((m) => [m.name, m])) : void 0;
	const factoryEffectHandlers = options?.effectHandlers;
	const factoryDisableTimeTravel = options?.disableAutoCheckpoint ?? false;
	const factoryFileResolver = options?.fileResolver;
	const factoryFileResolverBaseDir = options?.fileResolverBaseDir;
	const explicitCoverage = options?.coverage ?? false;
	const globalCoverage = isGlobalDvalaCoverageEnabled();
	const debug = (options?.debug ?? false) || explicitCoverage;
	const typecheckEnabled = options?.typecheck ?? true;
	const onTypeDiagnostic = options?.onTypeDiagnostic;
	const registeredModules = modules ? [...modules.values()] : void 0;
	const astBuilder = createAstBuilder({
		debug,
		cacheSize: options?.cache ?? 100,
		allocateNodeId
	});
	const coverageMap = explicitCoverage ? /* @__PURE__ */ new Map() : void 0;
	const builtinSourceMap = initCoreDvalaSources(parseSource, {
		debug: explicitCoverage || globalCoverage,
		allocateNodeId,
		recordBuiltinNode: explicitCoverage || globalCoverage ? (nodeId, path, start, end) => {
			if (coverageMap) coverageMap.set(nodeId, (coverageMap.get(nodeId) ?? 0) + 1);
			if (globalCoverage && isBuiltinDvalaPath(path)) recordGlobalDvalaSpan(dvalaSpanKey(path, start, end));
		} : void 0
	});
	if (builtinSourceMap) {
		if (explicitCoverage || globalCoverage) astBuilder.setAccumulatedSourceMap(builtinSourceMap);
	}
	const factoryOnNodeEval = !!coverageMap || globalCoverage ? (node) => {
		const id = node[2];
		if (coverageMap) coverageMap.set(id, (coverageMap.get(id) ?? 0) + 1);
		if (globalCoverage) {
			const sm = astBuilder.getAccumulatedSourceMap();
			const pos = sm?.positions.get(id);
			if (sm && pos && !pos.structuralLeaf) {
				const src = sm.sources[pos.source];
				if (src && isBuiltinDvalaPath(src.path)) recordGlobalDvalaSpan(dvalaSpanKey(src.path, pos.start, pos.end));
			}
		}
	} : void 0;
	function emitTypeDiagnostics(ast) {
		if (!typecheckEnabled) return;
		const { diagnostics } = typecheck(ast, { modules: registeredModules });
		if (!onTypeDiagnostic) return;
		for (const diagnostic of diagnostics) onTypeDiagnostic(diagnostic);
	}
	return {
		...createRuntimeRunner({
			modules,
			factoryEffectHandlers,
			factoryDisableTimeTravel,
			factoryFileResolver,
			factoryFileResolverBaseDir,
			debug,
			allocateNodeId,
			parseSource,
			prettyPrint,
			buildAst: astBuilder.buildAst,
			emitTypeDiagnostics,
			scopeToGlobalContext,
			getAccumulatedSourceMap: astBuilder.getAccumulatedSourceMap,
			setAccumulatedSourceMap: astBuilder.setAccumulatedSourceMap,
			factoryOnNodeEval
		}),
		getCoverage() {
			if (!coverageMap) return void 0;
			return {
				coverageMap,
				sourceMap: astBuilder.getAccumulatedSourceMap()
			};
		},
		getUndefinedSymbols(source, symbolsOptions) {
			const modulesList = modules ? [...modules.values()] : void 0;
			return getUndefinedSymbols(source, {
				scope: symbolsOptions?.scope,
				modules: modulesList
			});
		},
		getAutoCompleter(program, position) {
			return new AutoCompleter(program, position, {});
		},
		typecheck(source, typecheckOptions) {
			return typecheck(astBuilder.buildAst(source, typecheckOptions?.filePath, true), {
				modules: registeredModules,
				fileResolver: factoryFileResolver,
				fileResolverBaseDir: typecheckOptions?.fileResolverBaseDir ?? factoryFileResolverBaseDir,
				fold: typecheckOptions?.fold,
				createDvala
			});
		}
	};
}
//#endregion
//#region src/host/runtime/createDefaultRuntimeBridgeAdapter.ts
function withRuntimeModules(modules) {
	return modules;
}
function toRuntimeRunOptions(context) {
	if (!context) return void 0;
	return {
		...context.programRunOptions,
		effectHandlers: context.handlers
	};
}
function toRuntimeResumeOptions(modules, context) {
	return {
		handlers: context?.handlers,
		modules: withRuntimeModules(modules),
		maxSnapshots: context?.programRunOptions?.maxSnapshots,
		disableAutoCheckpoint: context?.programRunOptions?.disableAutoCheckpoint,
		terminalSnapshot: context?.programRunOptions?.terminalSnapshot,
		scope: context?.programRunOptions?.scope
	};
}
function toRootResumeOptions(modules, context) {
	return {
		...toRuntimeResumeOptions(modules, context),
		modules: withRuntimeModules(modules)
	};
}
function createDefaultRuntimeBridgeAdapter(options) {
	const runner = createDvala(options);
	return {
		runProgram(source, context) {
			return runner.runAsync(source, toRuntimeRunOptions(context));
		},
		resumeProgram(snapshot, value, context) {
			return resume(snapshot, value, toRootResumeOptions(options.modules, context));
		}
	};
}
function createRootRuntimeBridgeCallbacks(options, overrides = {}) {
	const defaultAdapter = overrides.runProgram || overrides.resumeProgram ? void 0 : createDefaultRuntimeBridgeAdapter(options);
	return {
		runProgram(source, context) {
			if (overrides.runProgram) return overrides.runProgram(source, toRuntimeRunOptions(context));
			return defaultAdapter.runProgram(source, context);
		},
		resumeProgram(snapshot, value, context) {
			if (overrides.resumeProgram) return overrides.resumeProgram(snapshot, value, toRuntimeResumeOptions(options.modules, context));
			return defaultAdapter.resumeProgram(snapshot, value, context);
		}
	};
}
//#endregion
//#region src/host/runtime/createPackageRuntimeBridge.ts
function createPackageRuntimeBridge(options) {
	const callbacks = createRootRuntimeBridgeCallbacks(options, {
		runProgram: options.runProgram,
		resumeProgram: options.resumeProgram
	});
	return createPackageRuntimeBridge$1({
		identity: options.identity,
		artifactBridge: options.artifactBridge,
		hostToHandlers: options.hostToHandlers,
		programRunOptions: options.programRunOptions,
		runProgram: callbacks.runProgram,
		resumeProgram: callbacks.resumeProgram
	});
}
//#endregion
//#region src/bundler/serialize.ts
/** Serialize a DvalaBundle to a JSON string. Converts Map to array of entries. */
function serializeBundle(bundle) {
	const serialized = {
		version: bundle.version,
		ast: {
			body: bundle.ast.body,
			sourceMap: bundle.ast.sourceMap ? {
				sources: bundle.ast.sourceMap.sources,
				positions: [...bundle.ast.sourceMap.positions.entries()]
			} : void 0
		}
	};
	return JSON.stringify(serialized, null, 2);
}
/** Deserialize a parsed JSON object back to a DvalaBundle. Returns null if invalid. */
function deserializeBundle(parsed) {
	if (typeof parsed !== "object" || parsed === null) return null;
	const obj = parsed;
	if (obj.version !== 1) return null;
	const ast = obj.ast;
	if (!ast || !Array.isArray(ast.body)) return null;
	let sourceMap;
	const rawSourceMap = ast.sourceMap;
	if (rawSourceMap && Array.isArray(rawSourceMap.sources) && Array.isArray(rawSourceMap.positions)) sourceMap = {
		sources: rawSourceMap.sources,
		positions: new Map(rawSourceMap.positions)
	};
	return {
		version: 1,
		ast: {
			body: ast.body,
			sourceMap
		}
	};
}
//#endregion
//#region src/tooling.ts
/**
* Parse a token stream with error recovery.
* Returns a partial AST (successfully parsed statements) and a list of errors.
* Useful for language service features that need to work on broken files.
*/
function parseTokenStreamRecoverable(tokenStream) {
	return parseRecoverable(minifyTokenStream(tokenStream, { removeWhiteSpace: true }));
}
//#endregion
export { AutoCompleter, Debugger, WorkspaceIndex, allBuiltinModules, applyReplBinding, asEffectNameToken, asTemplateStringToken, assertEffectNameToken, assertTemplateStringToken, buildBuiltinCompletions, buildDocTree, buildParseDiagnostics, buildSymbolDiagnostics, buildTypeDiagnostics, createDvala, createPackageRuntimeBridge, deserializeBundle, dvalaSpanKey, executeReplLine, expandMacros, findCallContext, findNodeIdForLine, findTypeAtDefinition, findTypeAtPosition, formatHoverType, format as formatSource, getAutoCompleter, getGlobalDvalaHits, getNodeEndLine, getNodeFile, getNodeLine, getUndefinedSymbols, initReferenceData, isBuiltinDvalaPath, isDvalaBundle, isDvalaIdentifierName, isGlobalDvalaCoverageEnabled, isSymbolicOperator, minifyTokenStream, parse, parseToAst, parseToCst, parseTokenStream, parseTokenStreamRecoverable, polishSymbolCharacterClass, polishSymbolFirstCharacterClass, prettyPrint, referenceToCompletion, serializeBundle, splitSegments, symbolDefToCompletion, tokenize, tokenizeSource, treeShake, typecheck, typecheckExpr, untokenize };

//# sourceMappingURL=index.js.map